/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ma = window, Ih = ma.ShadowRoot && (ma.ShadyCSS === void 0 || ma.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Th = Symbol(), Jp = /* @__PURE__ */ new WeakMap();
let aw = class {
  constructor(t, n, r) {
    if (this._$cssResult$ = !0, r !== Th)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = n;
  }
  get styleSheet() {
    let t = this.o;
    const n = this.t;
    if (Ih && t === void 0) {
      const r = n !== void 0 && n.length === 1;
      r && (t = Jp.get(n)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), r && Jp.set(n, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const UA = (e) => new aw(typeof e == "string" ? e : e + "", void 0, Th), HA = (e, ...t) => {
  const n = e.length === 1 ? e[0] : t.reduce((r, i, a) => r + ((o) => {
    if (o._$cssResult$ === !0)
      return o.cssText;
    if (typeof o == "number")
      return o;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + o + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + e[a + 1], e[0]);
  return new aw(n, e, Th);
}, GA = (e, t) => {
  Ih ? e.adoptedStyleSheets = t.map((n) => n instanceof CSSStyleSheet ? n : n.styleSheet) : t.forEach((n) => {
    const r = document.createElement("style"), i = ma.litNonce;
    i !== void 0 && r.setAttribute("nonce", i), r.textContent = n.cssText, e.appendChild(r);
  });
}, zp = Ih ? (e) => e : (e) => e instanceof CSSStyleSheet ? ((t) => {
  let n = "";
  for (const r of t.cssRules)
    n += r.cssText;
  return UA(n);
})(e) : e;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Xo;
const Da = window, Kp = Da.trustedTypes, VA = Kp ? Kp.emptyScript : "", Xp = Da.reactiveElementPolyfillSupport, kd = { toAttribute(e, t) {
  switch (t) {
    case Boolean:
      e = e ? VA : null;
      break;
    case Object:
    case Array:
      e = e == null ? e : JSON.stringify(e);
  }
  return e;
}, fromAttribute(e, t) {
  let n = e;
  switch (t) {
    case Boolean:
      n = e !== null;
      break;
    case Number:
      n = e === null ? null : Number(e);
      break;
    case Object:
    case Array:
      try {
        n = JSON.parse(e);
      } catch {
        n = null;
      }
  }
  return n;
} }, xh = (e, t) => t !== e && (t == t || e == e), Yo = { attribute: !0, type: String, converter: kd, reflect: !1, hasChanged: xh }, Fd = "finalized";
let kr = class extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this.u();
  }
  static addInitializer(t) {
    var n;
    this.finalize(), ((n = this.h) !== null && n !== void 0 ? n : this.h = []).push(t);
  }
  static get observedAttributes() {
    this.finalize();
    const t = [];
    return this.elementProperties.forEach((n, r) => {
      const i = this._$Ep(r, n);
      i !== void 0 && (this._$Ev.set(i, r), t.push(i));
    }), t;
  }
  static createProperty(t, n = Yo) {
    if (n.state && (n.attribute = !1), this.finalize(), this.elementProperties.set(t, n), !n.noAccessor && !this.prototype.hasOwnProperty(t)) {
      const r = typeof t == "symbol" ? Symbol() : "__" + t, i = this.getPropertyDescriptor(t, r, n);
      i !== void 0 && Object.defineProperty(this.prototype, t, i);
    }
  }
  static getPropertyDescriptor(t, n, r) {
    return { get() {
      return this[n];
    }, set(i) {
      const a = this[t];
      this[n] = i, this.requestUpdate(t, a, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) || Yo;
  }
  static finalize() {
    if (this.hasOwnProperty(Fd))
      return !1;
    this[Fd] = !0;
    const t = Object.getPrototypeOf(this);
    if (t.finalize(), t.h !== void 0 && (this.h = [...t.h]), this.elementProperties = new Map(t.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const n = this.properties, r = [...Object.getOwnPropertyNames(n), ...Object.getOwnPropertySymbols(n)];
      for (const i of r)
        this.createProperty(i, n[i]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(t) {
    const n = [];
    if (Array.isArray(t)) {
      const r = new Set(t.flat(1 / 0).reverse());
      for (const i of r)
        n.unshift(zp(i));
    } else
      t !== void 0 && n.push(zp(t));
    return n;
  }
  static _$Ep(t, n) {
    const r = n.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  u() {
    var t;
    this._$E_ = new Promise((n) => this.enableUpdating = n), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (t = this.constructor.h) === null || t === void 0 || t.forEach((n) => n(this));
  }
  addController(t) {
    var n, r;
    ((n = this._$ES) !== null && n !== void 0 ? n : this._$ES = []).push(t), this.renderRoot !== void 0 && this.isConnected && ((r = t.hostConnected) === null || r === void 0 || r.call(t));
  }
  removeController(t) {
    var n;
    (n = this._$ES) === null || n === void 0 || n.splice(this._$ES.indexOf(t) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((t, n) => {
      this.hasOwnProperty(n) && (this._$Ei.set(n, this[n]), delete this[n]);
    });
  }
  createRenderRoot() {
    var t;
    const n = (t = this.shadowRoot) !== null && t !== void 0 ? t : this.attachShadow(this.constructor.shadowRootOptions);
    return GA(n, this.constructor.elementStyles), n;
  }
  connectedCallback() {
    var t;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$ES) === null || t === void 0 || t.forEach((n) => {
      var r;
      return (r = n.hostConnected) === null || r === void 0 ? void 0 : r.call(n);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$ES) === null || t === void 0 || t.forEach((n) => {
      var r;
      return (r = n.hostDisconnected) === null || r === void 0 ? void 0 : r.call(n);
    });
  }
  attributeChangedCallback(t, n, r) {
    this._$AK(t, r);
  }
  _$EO(t, n, r = Yo) {
    var i;
    const a = this.constructor._$Ep(t, r);
    if (a !== void 0 && r.reflect === !0) {
      const o = (((i = r.converter) === null || i === void 0 ? void 0 : i.toAttribute) !== void 0 ? r.converter : kd).toAttribute(n, r.type);
      this._$El = t, o == null ? this.removeAttribute(a) : this.setAttribute(a, o), this._$El = null;
    }
  }
  _$AK(t, n) {
    var r;
    const i = this.constructor, a = i._$Ev.get(t);
    if (a !== void 0 && this._$El !== a) {
      const o = i.getPropertyOptions(a), s = typeof o.converter == "function" ? { fromAttribute: o.converter } : ((r = o.converter) === null || r === void 0 ? void 0 : r.fromAttribute) !== void 0 ? o.converter : kd;
      this._$El = a, this[a] = s.fromAttribute(n, o.type), this._$El = null;
    }
  }
  requestUpdate(t, n, r) {
    let i = !0;
    t !== void 0 && (((r = r || this.constructor.getPropertyOptions(t)).hasChanged || xh)(this[t], n) ? (this._$AL.has(t) || this._$AL.set(t, n), r.reflect === !0 && this._$El !== t && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(t, r))) : i = !1), !this.isUpdatePending && i && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (n) {
      Promise.reject(n);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var t;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((i, a) => this[a] = i), this._$Ei = void 0);
    let n = !1;
    const r = this._$AL;
    try {
      n = this.shouldUpdate(r), n ? (this.willUpdate(r), (t = this._$ES) === null || t === void 0 || t.forEach((i) => {
        var a;
        return (a = i.hostUpdate) === null || a === void 0 ? void 0 : a.call(i);
      }), this.update(r)) : this._$Ek();
    } catch (i) {
      throw n = !1, this._$Ek(), i;
    }
    n && this._$AE(r);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var n;
    (n = this._$ES) === null || n === void 0 || n.forEach((r) => {
      var i;
      return (i = r.hostUpdated) === null || i === void 0 ? void 0 : i.call(r);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$EC !== void 0 && (this._$EC.forEach((n, r) => this._$EO(r, this[r], n)), this._$EC = void 0), this._$Ek();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
kr[Fd] = !0, kr.elementProperties = /* @__PURE__ */ new Map(), kr.elementStyles = [], kr.shadowRootOptions = { mode: "open" }, Xp == null || Xp({ ReactiveElement: kr }), ((Xo = Da.reactiveElementVersions) !== null && Xo !== void 0 ? Xo : Da.reactiveElementVersions = []).push("1.6.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Qo;
const qa = window, Kr = qa.trustedTypes, Yp = Kr ? Kr.createPolicy("lit-html", { createHTML: (e) => e }) : void 0, Bd = "$lit$", kn = `lit$${(Math.random() + "").slice(9)}$`, ow = "?" + kn, WA = `<${ow}>`, _r = document, Bi = () => _r.createComment(""), Ui = (e) => e === null || typeof e != "object" && typeof e != "function", sw = Array.isArray, JA = (e) => sw(e) || typeof (e == null ? void 0 : e[Symbol.iterator]) == "function", Zo = `[ 	
\f\r]`, gi = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Qp = /-->/g, Zp = />/g, tr = RegExp(`>|${Zo}(?:([^\\s"'>=/]+)(${Zo}*=${Zo}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), eg = /'/g, tg = /"/g, uw = /^(?:script|style|textarea|title)$/i, lw = (e) => (t, ...n) => ({ _$litType$: e, strings: t, values: n }), Je = lw(1), jt = lw(2), Xr = Symbol.for("lit-noChange"), Ae = Symbol.for("lit-nothing"), ng = /* @__PURE__ */ new WeakMap(), sr = _r.createTreeWalker(_r, 129, null, !1);
function fw(e, t) {
  if (!Array.isArray(e) || !e.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return Yp !== void 0 ? Yp.createHTML(t) : t;
}
const zA = (e, t) => {
  const n = e.length - 1, r = [];
  let i, a = t === 2 ? "<svg>" : "", o = gi;
  for (let s = 0; s < n; s++) {
    const u = e[s];
    let l, f, c = -1, p = 0;
    for (; p < u.length && (o.lastIndex = p, f = o.exec(u), f !== null); )
      p = o.lastIndex, o === gi ? f[1] === "!--" ? o = Qp : f[1] !== void 0 ? o = Zp : f[2] !== void 0 ? (uw.test(f[2]) && (i = RegExp("</" + f[2], "g")), o = tr) : f[3] !== void 0 && (o = tr) : o === tr ? f[0] === ">" ? (o = i ?? gi, c = -1) : f[1] === void 0 ? c = -2 : (c = o.lastIndex - f[2].length, l = f[1], o = f[3] === void 0 ? tr : f[3] === '"' ? tg : eg) : o === tg || o === eg ? o = tr : o === Qp || o === Zp ? o = gi : (o = tr, i = void 0);
    const d = o === tr && e[s + 1].startsWith("/>") ? " " : "";
    a += o === gi ? u + WA : c >= 0 ? (r.push(l), u.slice(0, c) + Bd + u.slice(c) + kn + d) : u + kn + (c === -2 ? (r.push(void 0), s) : d);
  }
  return [fw(e, a + (e[n] || "<?>") + (t === 2 ? "</svg>" : "")), r];
};
class Hi {
  constructor({ strings: t, _$litType$: n }, r) {
    let i;
    this.parts = [];
    let a = 0, o = 0;
    const s = t.length - 1, u = this.parts, [l, f] = zA(t, n);
    if (this.el = Hi.createElement(l, r), sr.currentNode = this.el.content, n === 2) {
      const c = this.el.content, p = c.firstChild;
      p.remove(), c.append(...p.childNodes);
    }
    for (; (i = sr.nextNode()) !== null && u.length < s; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) {
          const c = [];
          for (const p of i.getAttributeNames())
            if (p.endsWith(Bd) || p.startsWith(kn)) {
              const d = f[o++];
              if (c.push(p), d !== void 0) {
                const y = i.getAttribute(d.toLowerCase() + Bd).split(kn), v = /([.?@])?(.*)/.exec(d);
                u.push({ type: 1, index: a, name: v[2], strings: y, ctor: v[1] === "." ? XA : v[1] === "?" ? QA : v[1] === "@" ? ZA : io });
              } else
                u.push({ type: 6, index: a });
            }
          for (const p of c)
            i.removeAttribute(p);
        }
        if (uw.test(i.tagName)) {
          const c = i.textContent.split(kn), p = c.length - 1;
          if (p > 0) {
            i.textContent = Kr ? Kr.emptyScript : "";
            for (let d = 0; d < p; d++)
              i.append(c[d], Bi()), sr.nextNode(), u.push({ type: 2, index: ++a });
            i.append(c[p], Bi());
          }
        }
      } else if (i.nodeType === 8)
        if (i.data === ow)
          u.push({ type: 2, index: a });
        else {
          let c = -1;
          for (; (c = i.data.indexOf(kn, c + 1)) !== -1; )
            u.push({ type: 7, index: a }), c += kn.length - 1;
        }
      a++;
    }
  }
  static createElement(t, n) {
    const r = _r.createElement("template");
    return r.innerHTML = t, r;
  }
}
function Yr(e, t, n = e, r) {
  var i, a, o, s;
  if (t === Xr)
    return t;
  let u = r !== void 0 ? (i = n._$Co) === null || i === void 0 ? void 0 : i[r] : n._$Cl;
  const l = Ui(t) ? void 0 : t._$litDirective$;
  return (u == null ? void 0 : u.constructor) !== l && ((a = u == null ? void 0 : u._$AO) === null || a === void 0 || a.call(u, !1), l === void 0 ? u = void 0 : (u = new l(e), u._$AT(e, n, r)), r !== void 0 ? ((o = (s = n)._$Co) !== null && o !== void 0 ? o : s._$Co = [])[r] = u : n._$Cl = u), u !== void 0 && (t = Yr(e, u._$AS(e, t.values), u, r)), t;
}
class KA {
  constructor(t, n) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = n;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    var n;
    const { el: { content: r }, parts: i } = this._$AD, a = ((n = t == null ? void 0 : t.creationScope) !== null && n !== void 0 ? n : _r).importNode(r, !0);
    sr.currentNode = a;
    let o = sr.nextNode(), s = 0, u = 0, l = i[0];
    for (; l !== void 0; ) {
      if (s === l.index) {
        let f;
        l.type === 2 ? f = new Nh(o, o.nextSibling, this, t) : l.type === 1 ? f = new l.ctor(o, l.name, l.strings, this, t) : l.type === 6 && (f = new eR(o, this, t)), this._$AV.push(f), l = i[++u];
      }
      s !== (l == null ? void 0 : l.index) && (o = sr.nextNode(), s++);
    }
    return sr.currentNode = _r, a;
  }
  v(t) {
    let n = 0;
    for (const r of this._$AV)
      r !== void 0 && (r.strings !== void 0 ? (r._$AI(t, r, n), n += r.strings.length - 2) : r._$AI(t[n])), n++;
  }
}
let Nh = class cw {
  constructor(t, n, r, i) {
    var a;
    this.type = 2, this._$AH = Ae, this._$AN = void 0, this._$AA = t, this._$AB = n, this._$AM = r, this.options = i, this._$Cp = (a = i == null ? void 0 : i.isConnected) === null || a === void 0 || a;
  }
  get _$AU() {
    var t, n;
    return (n = (t = this._$AM) === null || t === void 0 ? void 0 : t._$AU) !== null && n !== void 0 ? n : this._$Cp;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const n = this._$AM;
    return n !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = n.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, n = this) {
    t = Yr(this, t, n), Ui(t) ? t === Ae || t == null || t === "" ? (this._$AH !== Ae && this._$AR(), this._$AH = Ae) : t !== this._$AH && t !== Xr && this._(t) : t._$litType$ !== void 0 ? this.g(t) : t.nodeType !== void 0 ? this.$(t) : JA(t) ? this.T(t) : this._(t);
  }
  k(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  $(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.k(t));
  }
  _(t) {
    this._$AH !== Ae && Ui(this._$AH) ? this._$AA.nextSibling.data = t : this.$(_r.createTextNode(t)), this._$AH = t;
  }
  g(t) {
    var n;
    const { values: r, _$litType$: i } = t, a = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = Hi.createElement(fw(i.h, i.h[0]), this.options)), i);
    if (((n = this._$AH) === null || n === void 0 ? void 0 : n._$AD) === a)
      this._$AH.v(r);
    else {
      const o = new KA(a, this), s = o.u(this.options);
      o.v(r), this.$(s), this._$AH = o;
    }
  }
  _$AC(t) {
    let n = ng.get(t.strings);
    return n === void 0 && ng.set(t.strings, n = new Hi(t)), n;
  }
  T(t) {
    sw(this._$AH) || (this._$AH = [], this._$AR());
    const n = this._$AH;
    let r, i = 0;
    for (const a of t)
      i === n.length ? n.push(r = new cw(this.k(Bi()), this.k(Bi()), this, this.options)) : r = n[i], r._$AI(a), i++;
    i < n.length && (this._$AR(r && r._$AB.nextSibling, i), n.length = i);
  }
  _$AR(t = this._$AA.nextSibling, n) {
    var r;
    for ((r = this._$AP) === null || r === void 0 || r.call(this, !1, !0, n); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var n;
    this._$AM === void 0 && (this._$Cp = t, (n = this._$AP) === null || n === void 0 || n.call(this, t));
  }
};
class io {
  constructor(t, n, r, i, a) {
    this.type = 1, this._$AH = Ae, this._$AN = void 0, this.element = t, this.name = n, this._$AM = i, this.options = a, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = Ae;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t, n = this, r, i) {
    const a = this.strings;
    let o = !1;
    if (a === void 0)
      t = Yr(this, t, n, 0), o = !Ui(t) || t !== this._$AH && t !== Xr, o && (this._$AH = t);
    else {
      const s = t;
      let u, l;
      for (t = a[0], u = 0; u < a.length - 1; u++)
        l = Yr(this, s[r + u], n, u), l === Xr && (l = this._$AH[u]), o || (o = !Ui(l) || l !== this._$AH[u]), l === Ae ? t = Ae : t !== Ae && (t += (l ?? "") + a[u + 1]), this._$AH[u] = l;
    }
    o && !i && this.j(t);
  }
  j(t) {
    t === Ae ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class XA extends io {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === Ae ? void 0 : t;
  }
}
const YA = Kr ? Kr.emptyScript : "";
class QA extends io {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    t && t !== Ae ? this.element.setAttribute(this.name, YA) : this.element.removeAttribute(this.name);
  }
}
class ZA extends io {
  constructor(t, n, r, i, a) {
    super(t, n, r, i, a), this.type = 5;
  }
  _$AI(t, n = this) {
    var r;
    if ((t = (r = Yr(this, t, n, 0)) !== null && r !== void 0 ? r : Ae) === Xr)
      return;
    const i = this._$AH, a = t === Ae && i !== Ae || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive, o = t !== Ae && (i === Ae || a);
    a && this.element.removeEventListener(this.name, this, i), o && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var n, r;
    typeof this._$AH == "function" ? this._$AH.call((r = (n = this.options) === null || n === void 0 ? void 0 : n.host) !== null && r !== void 0 ? r : this.element, t) : this._$AH.handleEvent(t);
  }
}
class eR {
  constructor(t, n, r) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = n, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    Yr(this, t);
  }
}
const rg = qa.litHtmlPolyfillSupport;
rg == null || rg(Hi, Nh), ((Qo = qa.litHtmlVersions) !== null && Qo !== void 0 ? Qo : qa.litHtmlVersions = []).push("2.8.0");
const tR = (e, t, n) => {
  var r, i;
  const a = (r = n == null ? void 0 : n.renderBefore) !== null && r !== void 0 ? r : t;
  let o = a._$litPart$;
  if (o === void 0) {
    const s = (i = n == null ? void 0 : n.renderBefore) !== null && i !== void 0 ? i : null;
    a._$litPart$ = o = new Nh(t.insertBefore(Bi(), s), s, void 0, n ?? {});
  }
  return o._$AI(e), o;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var es, ts;
class Mi extends kr {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t, n;
    const r = super.createRenderRoot();
    return (t = (n = this.renderOptions).renderBefore) !== null && t !== void 0 || (n.renderBefore = r.firstChild), r;
  }
  update(t) {
    const n = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = tR(n, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) === null || t === void 0 || t.setConnected(!1);
  }
  render() {
    return Xr;
  }
}
Mi.finalized = !0, Mi._$litElement$ = !0, (es = globalThis.litElementHydrateSupport) === null || es === void 0 || es.call(globalThis, { LitElement: Mi });
const ig = globalThis.litElementPolyfillSupport;
ig == null || ig({ LitElement: Mi });
((ts = globalThis.litElementVersions) !== null && ts !== void 0 ? ts : globalThis.litElementVersions = []).push("3.3.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const nR = (e) => (t) => typeof t == "function" ? ((n, r) => (customElements.define(n, r), r))(e, t) : ((n, r) => {
  const { kind: i, elements: a } = r;
  return { kind: i, elements: a, finisher(o) {
    customElements.define(n, o);
  } };
})(e, t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const rR = (e, t) => t.kind === "method" && t.descriptor && !("value" in t.descriptor) ? { ...t, finisher(n) {
  n.createProperty(t.key, e);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: t.key, initializer() {
  typeof t.initializer == "function" && (this[t.key] = t.initializer.call(this));
}, finisher(n) {
  n.createProperty(t.key, e);
} }, iR = (e, t, n) => {
  t.constructor.createProperty(n, e);
};
function Oh(e) {
  return (t, n) => n !== void 0 ? iR(e, t, n) : rR(e, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function $h(e) {
  return Oh({ ...e, state: !0 });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var ns;
((ns = window.HTMLSlotElement) === null || ns === void 0 ? void 0 : ns.prototype.assignedElements) != null;
const aR = HA`
    .docmaps-widget {
        width: 500px;
        height: 500px;
        background: #EDEDED;
        border: 1px solid;
        overflow-x: hidden;
        overflow-y: hidden;
        text-align: start;
    }

    .clickable {
        cursor: pointer;
    }

    .no-select {
        -webkit-user-select: none; /* Safari */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* IE10+/Edge */
        user-select: none; /* Standard */
    }

    .not-found-message {
        margin-top: 121px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 16px;
        font-weight: 400;
        text-align: center;
        color: #777777;
    }

    .not-found-message p {
        margin: 0;
    }

    button {
        border-radius: 8px;
        border: 1px solid transparent;
        padding: 0.6em 1.2em;
        font-size: 1em;
        font-weight: 500;
        font-family: inherit;
        background-color: rgb(175, 105, 248);
        cursor: pointer;
        color: white;
        transition: border-color 0.25s;
    }

    button:hover {
        border-color: #646cff;
    }

    button:focus,
    button:focus-visible {
        outline: 4px auto -webkit-focus-ring-color;
    }

    .widget-header {
        width: 500px;
        height: 25px;
        background: #043945;
        display: flex;
        align-items: center;
    }

    .widget-header span {
        color: white;
        font-family: 'IBM Plex Mono', 'SF Pro Display', monospace;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: 2.4px;
        text-transform: uppercase;
    }

    .detail-timeline {
        height: 67px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 12px;
    }

    .detail-header {
        width: 500px;
        height: 50px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .detail-header span {
        font-family: 'IBM Plex Mono', 'SF Pro Display', monospace;
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        text-transform: uppercase;
        margin-left: 36px;
    }

    .detail-body {
        color: #000;
        width: 500px;
        font-family: 'IBM Plex Mono', 'SF Pro Display', monospace;
        font-size: 14px;
        font-style: normal;
        line-height: normal;
        height: 358px; // 500px (whole widget) - 67px (detail timeline) - 50px (detail-header) - 25px (widget-header)
    }

    .metadata-grid {
        max-height: 100%;
        max-width: 100%;
        display: grid;
        grid-template-columns: 131px auto;
        grid-gap: 0;
        overflow-y: scroll;
        overflow-x: hidden;
    }

    .no-metadata-found-grid {
        max-height: 100%;
        max-width: 100%;
        display: grid;
        grid-template-columns: auto;
        grid-gap: 0;
        overflow-y: scroll;
        overflow-x: hidden;
    }


    .metadata-grid-item {
        min-height: 47px;
        max-width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding-left: 36px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-all;
        white-space: normal;
        border-bottom: 0.5px solid #C1C1C1;
    }

    .metadata-grid-item.key {
        font-weight: 400;
        border-right: 0.5px solid #C1C1C1;
    }

    .metadata-grid-item.value {
        font-weight: 300;
        padding-right: 25px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }

    .metadata-grid-item.value .content {
        font-size: 12px;
        font-style: italic;
        font-weight: 300;
        line-height: normal;
        padding-top: 7px;
        padding-bottom: 7px;
    }

    .metadata-link {
        max-width: 89%;
        color: inherit;
        text-decoration: underline;
        cursor: pointer;
    }

    .metadata-link:hover {
        color: #C1C1C1;
    }


    .copy-to-clipboard-button:hover .copy-to-clipboard-button-path {
        fill: #474747;
    }

    .detail-header .close-button {
        margin-right: 25px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    .detail-header .close-button:hover {
        stroke: #ffffff;
        fill: #ffffff;
    }

    .docmap-logo-small {
        display: inline-block;
        margin: 7px 13px 6px 11px
    }

    .large {
        display: inline-block;
        margin: 7px 13px 6px 11px
    }

    .not-found-screen {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 55px;
    }

    .tooltip {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 14px;
        font-weight: 300;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        padding: 4px 8px;
        border-radius: 4px;
        position: absolute;
        text-align: center;
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.3s;
    }

    .labels text {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 30px;
        font-style: normal;
        font-weight: 600;
        text-anchor: middle;
        text-transform: uppercase;
    }

    .labels text:first-child {
        font-size: 50px;
    }
`, oR = (e, t) => jt`
  <svg @click='${(n) => sR(n, e, t)}' 
    class='copy-to-clipboard-button clickable' width='12' height='14' viewBox='0 0 12 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path class='copy-to-clipboard-button-path' fill='#989898' fill-rule='evenodd' clip-rule='evenodd' d='M7 3H2C1.44772 3 1 3.44772 1 4V12C1 12.5523 1.44772 13 2 13H7C7.55228 13 8 12.5523 8 12V4C8 3.44772 7.55228 3 7 3ZM2 2C0.895431 2 0 2.89543 0 4V12C0 13.1046 0.89543 14 2 14H7C8.10457 14 9 13.1046 9 12V4C9 2.89543 8.10457 2 7 2H2ZM10 1H5C4.44772 1 4 1.44772 4 2V10C4 10.5523 4.44772 11 5 11H10C10.5523 11 11 10.5523 11 10V2C11 1.44772 10.5523 1 10 1ZM5 0C3.89543 0 3 0.895431 3 2V10C3 11.1046 3.89543 12 5 12H10C11.1046 12 12 11.1046 12 10V2C12 0.895431 11.1046 0 10 0H5Z'/>
  </svg>
`, sR = (e, t, n) => {
  navigator.clipboard.writeText(t).then(() => {
    const r = e.pageX, i = e.pageY;
    n("Copied!", r, i);
  }).catch((r) => {
    console.log(`Failed to copy to clipboard

`, r);
  });
}, uR = (e) => jt`
  <svg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle stroke='${e}' cx='15' cy='15' r='14.5'/>
    <path fill='${e}' fill-rule='evenodd' clip-rule='evenodd' d='M16.41 15L20.7 10.71C20.89 10.53 21 10.28 21 10C21 9.45 20.55 9 20 9C19.72 9 19.47 9.11 19.29 9.29L15 13.59L10.71 9.29C10.53 9.11 10.28 9 10 9C9.45 9 9 9.45 9 10C9 10.28 9.11 10.53 9.29 10.71L13.59 15L9.3 19.29C9.11 19.47 9 19.72 9 20C9 20.55 9.45 21 10 21C10.28 21 10.53 20.89 10.71 20.71L15 16.41L19.29 20.7C19.47 20.89 19.72 21 20 21C20.55 21 21 20.55 21 20C21 19.72 20.89 19.47 20.71 19.29L16.41 15Z'/>
  </svg>
`, lR = jt`
  <svg class='docmap-logo-small' width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path fill-rule='evenodd' clip-rule='evenodd' d='M3.78445 0.521739H1.28598C0.859842 0.521739 0.514391 0.872125 0.514391 1.30435V3.83851C0.514391 4.27073 0.859842 4.62112 1.28598 4.62112H3.78445C4.21058 4.62112 4.55603 4.27073 4.55603 3.83851V1.30435C4.55603 0.872125 4.21058 0.521739 3.78445 0.521739ZM1.28598 0C0.575752 0 0 0.583976 0 1.30435V3.83851C0 4.55888 0.575751 5.14286 1.28598 5.14286H3.78445C4.49467 5.14286 5.07043 4.55888 5.07043 3.83851V1.30435C5.07043 0.583977 4.49467 0 3.78445 0H1.28598Z' fill='white'/>
    <path d='M0 9.42857C0 8.00841 1.13505 6.85714 2.53521 6.85714C3.93537 6.85714 5.07043 8.00841 5.07043 9.42857C5.07043 10.8487 3.93537 12 2.53521 12C1.13505 12 0 10.8487 0 9.42857Z' fill='white'/>
    <path d='M6.92957 8.16149C6.92957 7.44112 7.50533 6.85714 8.21555 6.85714H10.714C11.4242 6.85714 12 7.44112 12 8.16149V10.6957C12 11.416 11.4242 12 10.714 12H8.21555C7.50533 12 6.92957 11.416 6.92957 10.6957V8.16149Z' fill='white'/>
    <path d='M0 9.42857C0 8.00841 1.13505 6.85714 2.53521 6.85714C3.93537 6.85714 5.07043 8.00841 5.07043 9.42857C5.07043 10.8487 3.93537 12 2.53521 12C1.13505 12 0 10.8487 0 9.42857Z' fill='white'/>
    <path d='M6.92957 8.16149C6.92957 7.44112 7.50533 6.85714 8.21555 6.85714H10.714C11.4242 6.85714 12 7.44112 12 8.16149V10.6957C12 11.416 11.4242 12 10.714 12H8.21555C7.50533 12 6.92957 11.416 6.92957 10.6957V8.16149Z' fill='white'/>
  </svg>
`, fR = jt`
  <svg class='docmap-logo-large' width='133' height='133' viewBox='0 0 133 133' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path fill-rule='evenodd' clip-rule='evenodd' d='M41.9443 5.78261H14.2529C9.52991 5.78261 5.70117 9.66605 5.70117 14.4565V42.5435C5.70117 47.334 9.52991 51.2174 14.2529 51.2174H41.9443C46.6673 51.2174 50.496 47.334 50.496 42.5435V14.4565C50.496 9.66605 46.6673 5.78261 41.9443 5.78261ZM14.2529 0C6.38125 0 0 6.4724 0 14.4565V42.5435C0 50.5276 6.38124 57 14.2529 57H41.9443C49.816 57 56.1972 50.5276 56.1972 42.5435V14.4565C56.1972 6.47241 49.816 0 41.9443 0H14.2529Z' fill='#E2E2E2'/>
    <path d='M0 104.5C0 88.7599 12.5802 76 28.0986 76C43.617 76 56.1972 88.7599 56.1972 104.5C56.1972 120.24 43.617 133 28.0986 133C12.5802 133 0 120.24 0 104.5Z' fill='#E2E2E2'/>
    <path d='M76.8028 90.4565C76.8028 82.4724 83.184 76 91.0557 76H118.747C126.619 76 133 82.4724 133 90.4565V118.543C133 126.528 126.619 133 118.747 133H91.0557C83.184 133 76.8028 126.528 76.8028 118.543V90.4565Z' fill='#E2E2E2'/>
    <path d='M0 104.5C0 88.7599 12.5802 76 28.0986 76C43.617 76 56.1972 88.7599 56.1972 104.5C56.1972 120.24 43.617 133 28.0986 133C12.5802 133 0 120.24 0 104.5Z' fill='#E2E2E2'/>
    <path d='M76.8028 90.4565C76.8028 82.4724 83.184 76 91.0557 76H118.747C126.619 76 133 82.4724 133 90.4565V118.543C133 126.528 126.619 133 118.747 133H91.0557C83.184 133 76.8028 126.528 76.8028 118.543V90.4565Z' fill='#E2E2E2'/>
  </svg>
`;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const cR = Symbol();
class dR {
  get taskComplete() {
    return this.t || (this.status === 1 ? this.t = new Promise((t, n) => {
      this.i = t, this.o = n;
    }) : this.status === 3 ? this.t = Promise.reject(this.h) : this.t = Promise.resolve(this.l)), this.t;
  }
  constructor(t, n, r) {
    var a;
    this.u = 0, this.status = 0, (this.p = t).addController(this);
    const i = typeof n == "object" ? n : { task: n, args: r };
    this._ = i.task, this.v = i.args, this.j = i.argsEqual ?? hR, this.m = i.onComplete, this.g = i.onError, this.autoRun = i.autoRun ?? !0, "initialValue" in i && (this.l = i.initialValue, this.status = 2, this.k = (a = this.A) == null ? void 0 : a.call(this));
  }
  hostUpdate() {
    this.autoRun === !0 && this.O();
  }
  hostUpdated() {
    this.autoRun === "afterUpdate" && this.O();
  }
  A() {
    if (this.v === void 0)
      return;
    const t = this.v();
    if (!Array.isArray(t))
      throw Error("The args function must return an array");
    return t;
  }
  async O() {
    const t = this.A(), n = this.k;
    this.k = t, t === n || t === void 0 || n !== void 0 && this.j(n, t) || await this.run(t);
  }
  async run(t) {
    var o, s, u, l, f;
    let n, r;
    t ?? (t = this.A()), this.k = t, this.status === 1 ? (o = this.T) == null || o.abort() : (this.t = void 0, this.i = void 0, this.o = void 0), this.status = 1, this.autoRun === "afterUpdate" ? queueMicrotask(() => this.p.requestUpdate()) : this.p.requestUpdate();
    const i = ++this.u;
    this.T = new AbortController();
    let a = !1;
    try {
      n = await this._(t, { signal: this.T.signal });
    } catch (c) {
      a = !0, r = c;
    }
    if (this.u === i) {
      if (n === cR)
        this.status = 0;
      else {
        if (a === !1) {
          try {
            (s = this.m) == null || s.call(this, n);
          } catch {
          }
          this.status = 2, (u = this.i) == null || u.call(this, n);
        } else {
          try {
            (l = this.g) == null || l.call(this, r);
          } catch {
          }
          this.status = 3, (f = this.o) == null || f.call(this, r);
        }
        this.l = n, this.h = r;
      }
      this.p.requestUpdate();
    }
  }
  abort(t) {
    var n;
    this.status === 1 && ((n = this.T) == null || n.abort(t));
  }
  get value() {
    return this.l;
  }
  get error() {
    return this.h;
  }
  render(t) {
    var n, r, i, a;
    switch (this.status) {
      case 0:
        return (n = t.initial) == null ? void 0 : n.call(t);
      case 1:
        return (r = t.pending) == null ? void 0 : r.call(t);
      case 2:
        return (i = t.complete) == null ? void 0 : i.call(t, this.value);
      case 3:
        return (a = t.error) == null ? void 0 : a.call(t, this.error);
      default:
        throw Error("Unexpected status: " + this.status);
    }
  }
}
const hR = (e, t) => e === t || e.length === t.length && e.every((n, r) => !xh(n, t[r])), rs = (e) => typeof (e == null ? void 0 : e.passthrough) == "function", pR = (e, t) => rs(e) ? rs(t) ? e.merge(t) : e : rs(t) ? t : Object.assign({}, e, t), dw = (e) => "method" in e && "path" in e, hw = (e, t) => Object.fromEntries(Object.entries(e).map(([n, r]) => {
  var i, a;
  return dw(r) ? [
    n,
    {
      ...r,
      path: t != null && t.pathPrefix ? t.pathPrefix + r.path : r.path,
      headers: pR(t == null ? void 0 : t.baseHeaders, r.headers),
      strictStatusCodes: (i = r.strictStatusCodes) !== null && i !== void 0 ? i : t == null ? void 0 : t.strictStatusCodes,
      validateResponseOnClient: (a = r.validateResponseOnClient) !== null && a !== void 0 ? a : t == null ? void 0 : t.validateResponseOnClient
    }
  ] : [n, hw(r, t)];
})), is = Symbol("ContractPlainType"), gR = () => ({
  router: (e, t) => hw(e, t),
  query: (e) => e,
  mutation: (e) => e,
  response: () => is,
  body: () => is,
  type: () => is,
  otherResponse: ({ contentType: e, body: t }) => ({
    contentType: e,
    body: t
  })
}), yR = ({ path: e, params: t }) => e.replace(/:([^/]+)/g, (n, r) => t[r] || "").replace(/\/\//g, "/"), vR = (e, t = !1) => {
  const n = t ? _R(e) : bR(e);
  return (n == null ? void 0 : n.length) > 0 ? "?" + n : "";
}, _R = (e) => e ? Object.entries(e).filter(([, t]) => t !== void 0).map(([t, n]) => {
  let r;
  return typeof n == "string" && !["true", "false", "null"].includes(n.trim()) && isNaN(Number(n)) ? r = n : r = JSON.stringify(n), `${encodeURIComponent(t)}=${encodeURIComponent(r)}`;
}).join("&") : "", bR = (e) => e ? Object.keys(e).flatMap((t) => Ud(t, e[t])).map(([t, n]) => `${encodeURIComponent(t)}=${encodeURIComponent(n)}`).join("&") : "", Ud = (e, t) => Array.isArray(t) ? t.flatMap((n, r) => Ud(`${e}[${r}]`, n)) : t instanceof Date ? [[`${e}`, t.toISOString()]] : t === null ? [[`${e}`, ""]] : t === void 0 ? [] : typeof t == "object" ? Object.keys(t).flatMap((n) => Ud(`${e}[${n}]`, t[n])) : [[`${e}`, `${t}`]];
class mR extends Error {
  constructor(t, n) {
    const r = n.join(",");
    super(`Server returned unexpected response. Expected one of: ${r} got: ${t.status}`), this.response = t;
  }
}
const wR = async ({ path: e, method: t, headers: n, body: r, credentials: i, signal: a, cache: o, next: s, route: u }) => {
  const l = await fetch(e, {
    method: t,
    headers: n,
    body: r,
    credentials: i,
    signal: a,
    cache: o,
    next: s
  }), f = l.headers.get("content-type");
  if (f != null && f.includes("application/") && (f != null && f.includes("json"))) {
    if (!u.validateResponseOnClient)
      return {
        status: l.status,
        body: await l.json(),
        headers: l.headers
      };
    const c = await l.json(), p = l.status, d = u.responses[p];
    return {
      status: p,
      body: d && typeof d != "symbol" && "parse" in d ? d == null ? void 0 : d.parse(c) : c,
      headers: l.headers
    };
  }
  return f != null && f.includes("text/plain") ? {
    status: l.status,
    body: await l.text(),
    headers: l.headers
  } : {
    status: l.status,
    body: await l.blob(),
    headers: l.headers
  };
}, ER = (e) => {
  const t = new FormData();
  return Object.entries(e).forEach(([n, r]) => {
    r instanceof File ? t.append(n, r) : t.append(n, JSON.stringify(r));
  }), t;
}, ag = (e) => Object.fromEntries(Object.entries(e).map(([t, n]) => [t.toLowerCase(), n])), SR = ({ path: e, clientArgs: t, route: n, body: r, query: i, extraInputArgs: a, headers: o, signal: s, next: u }) => {
  const l = t.api || wR, f = {
    ...ag(t.baseHeaders),
    ...ag(o)
  };
  if (Object.keys(f).forEach((p) => {
    f[p] === void 0 && delete f[p];
  }), n.method !== "GET" && n.contentType === "multipart/form-data")
    return l({
      route: n,
      path: e,
      method: n.method,
      credentials: t.credentials,
      headers: f,
      body: r instanceof FormData ? r : ER(r),
      rawBody: r,
      rawQuery: i,
      contentType: "multipart/form-data",
      signal: s,
      next: u,
      ...a
    });
  const c = n.method !== "GET" && r !== null && r !== void 0;
  return l({
    route: n,
    path: e,
    method: n.method,
    credentials: t.credentials,
    headers: {
      ...c && { "content-type": "application/json" },
      ...f
    },
    body: r != null ? JSON.stringify(r) : void 0,
    rawBody: r,
    rawQuery: i,
    contentType: c ? "application/json" : void 0,
    signal: s,
    next: u,
    ...a
  });
}, AR = (e, t, n, r, i) => {
  const a = yR({
    path: r.path,
    params: n
  }), o = vR(e, i);
  return `${t}${a}${o}`;
}, RR = (e, t) => {
  const n = Object.keys(e.responses);
  return async (r) => {
    const { query: i, params: a, body: o, headers: s, extraHeaders: u, next: l, ...f } = r || {}, c = AR(i, t.baseUrl, a, e, !!t.jsonQuery), p = await SR({
      path: c,
      clientArgs: t,
      route: e,
      body: o,
      query: i,
      extraInputArgs: f,
      next: l,
      headers: {
        ...u,
        ...s
      }
    });
    if (!t.throwOnUnknownStatus || n.includes(p.status.toString()))
      return p;
    throw new mR(p, n);
  };
}, pw = (e, t) => Object.fromEntries(Object.entries(e).map(([n, r]) => dw(r) ? [n, RR(r, t)] : [n, pw(r, t)])), nr = gR(), IR = nr.router({
  getInfo: {
    method: "GET",
    path: "/info",
    responses: {
      200: nr.type()
    },
    summary: "Get information about this Docmaps API server"
  },
  getDocmapById: {
    method: "GET",
    path: "/docmap/:id",
    responses: {
      200: nr.type(),
      404: nr.type()
    },
    summary: "Get a docmap matching an IRI exactly"
  },
  getDocmapForDoi: {
    method: "GET",
    path: "/docmap_for/doi",
    query: nr.type(),
    responses: {
      200: nr.type(),
      404: nr.type()
    },
    summary: "Get a docmap that describes a research artifact with this DOI"
  }
});
function gw(e) {
  return pw(IR, e);
}
gw({
  baseUrl: "http://localhost:3000",
  baseHeaders: {}
});
globalThis && globalThis.__spreadArray;
var TR = function(e) {
  return e._tag === "Left";
}, xR = function(e) {
  return { _tag: "Left", left: e };
}, NR = function(e) {
  return { _tag: "Right", right: e };
}, OR = xR, yw = NR, $e = TR, he = globalThis && globalThis.__extends || function() {
  var e = function(t, n) {
    return e = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(r, i) {
      r.__proto__ = i;
    } || function(r, i) {
      for (var a in i)
        Object.prototype.hasOwnProperty.call(i, a) && (r[a] = i[a]);
    }, e(t, n);
  };
  return function(t, n) {
    if (typeof n != "function" && n !== null)
      throw new TypeError("Class extends value " + String(n) + " is not a constructor or null");
    e(t, n);
    function r() {
      this.constructor = t;
    }
    t.prototype = n === null ? Object.create(n) : (r.prototype = n.prototype, new r());
  };
}(), Qr = globalThis && globalThis.__assign || function() {
  return Qr = Object.assign || function(e) {
    for (var t, n = 1, r = arguments.length; n < r; n++) {
      t = arguments[n];
      for (var i in t)
        Object.prototype.hasOwnProperty.call(t, i) && (e[i] = t[i]);
    }
    return e;
  }, Qr.apply(this, arguments);
}, $R = globalThis && globalThis.__spreadArray || function(e, t, n) {
  if (n || arguments.length === 2)
    for (var r = 0, i = t.length, a; r < i; r++)
      (a || !(r in t)) && (a || (a = Array.prototype.slice.call(t, 0, r)), a[r] = t[r]);
  return e.concat(a || Array.prototype.slice.call(t));
}, an = OR, Le = function(e, t, n) {
  return an([{ value: e, context: t, message: n }]);
}, pe = yw, le = (
  /** @class */
  function() {
    function e(t, n, r, i) {
      this.name = t, this.is = n, this.validate = r, this.encode = i, this.decode = this.decode.bind(this);
    }
    return e.prototype.pipe = function(t, n) {
      var r = this;
      return n === void 0 && (n = "pipe(".concat(this.name, ", ").concat(t.name, ")")), new e(n, t.is, function(i, a) {
        var o = r.validate(i, a);
        return $e(o) ? o : t.validate(o.right, a);
      }, this.encode === ae && t.encode === ae ? ae : function(i) {
        return r.encode(t.encode(i));
      });
    }, e.prototype.asDecoder = function() {
      return this;
    }, e.prototype.asEncoder = function() {
      return this;
    }, e.prototype.decode = function(t) {
      return this.validate(t, [{ key: "", type: this, actual: t }]);
    }, e;
  }()
), ae = function(e) {
  return e;
};
function vw(e) {
  return e.displayName || e.name || "<function".concat(e.length, ">");
}
function LR(e, t) {
  return { key: e, type: t };
}
function Pt(e, t, n, r) {
  for (var i = e.length, a = Array(i + 1), o = 0; o < i; o++)
    a[o] = e[o];
  return a[i] = { key: t, type: n, actual: r }, a;
}
function Sn(e, t) {
  for (var n = t.length, r = 0; r < n; r++)
    e.push(t[r]);
}
var br = Object.prototype.hasOwnProperty;
function Hd(e) {
  return Object.keys(e).map(function(t) {
    return "".concat(t, ": ").concat(e[t].name);
  }).join(", ");
}
function Gi(e) {
  for (var t = 0; t < e.length; t++)
    if (e[t].encode !== ae)
      return !1;
  return !0;
}
function _w(e) {
  return "{ ".concat(Hd(e), " }");
}
function bw(e) {
  return "Partial<".concat(e, ">");
}
function MR(e, t, n, r) {
  r === void 0 && (r = "{ [K in ".concat(t.name, "]: ").concat(n.name, " }"));
  var i = e.length;
  return new qh(r, function(a) {
    return Ye.is(a) && e.every(function(o) {
      return n.is(a[o]);
    });
  }, function(a, o) {
    var s = Ye.validate(a, o);
    if ($e(s))
      return s;
    for (var u = s.right, l = {}, f = [], c = !1, p = 0; p < i; p++) {
      var d = e[p], y = u[d], v = n.validate(y, Pt(o, d, n, y));
      if ($e(v))
        Sn(f, v.left);
      else {
        var _ = v.right;
        c = c || _ !== y, l[d] = _;
      }
    }
    return f.length > 0 ? an(f) : pe(c || Object.keys(u).length !== i ? l : u);
  }, n.encode === ae ? ae : function(a) {
    for (var o = {}, s = 0; s < i; s++) {
      var u = e[s];
      o[u] = n.encode(a[u]);
    }
    return o;
  }, t, n);
}
function Lh(e) {
  var t;
  if (Ew(e)) {
    var n = e.value;
    if (Re.is(n))
      return t = {}, t[n] = null, t;
  } else {
    if (kR(e))
      return e.keys;
    if (Aw(e)) {
      var r = e.types.map(function(i) {
        return Lh(i);
      });
      return r.some(Pa.is) ? void 0 : Object.assign.apply(Object, $R([{}], r, !1));
    }
  }
}
function CR(e, t, n) {
  return n === void 0 && (n = "{ [K in ".concat(e.name, "]: ").concat(t.name, " }")), new qh(n, function(r) {
    return Ye.is(r) ? Object.keys(r).every(function(i) {
      return e.is(i) && t.is(r[i]);
    }) : sg(t) && Array.isArray(r);
  }, function(r, i) {
    if (Ye.is(r)) {
      for (var a = {}, o = [], s = Object.keys(r), u = s.length, l = !1, f = 0; f < u; f++) {
        var c = s[f], p = r[c], d = e.validate(c, Pt(i, c, e, c));
        if ($e(d))
          Sn(o, d.left);
        else {
          var y = d.right;
          l = l || y !== c, c = y;
          var v = t.validate(p, Pt(i, c, t, p));
          if ($e(v))
            Sn(o, v.left);
          else {
            var _ = v.right;
            l = l || _ !== p, a[c] = _;
          }
        }
      }
      return o.length > 0 ? an(o) : pe(l ? a : r);
    }
    return sg(t) && Array.isArray(r) ? pe(r) : Le(r, i);
  }, e.encode === ae && t.encode === ae ? ae : function(r) {
    for (var i = {}, a = Object.keys(r), o = a.length, s = 0; s < o; s++) {
      var u = a[s];
      i[String(e.encode(u))] = t.encode(r[u]);
    }
    return i;
  }, e, t);
}
function mw(e) {
  return "(" + e.map(function(t) {
    return t.name;
  }).join(" | ") + ")";
}
function Gd(e, t) {
  for (var n = !0, r = !0, i = !Ye.is(e), a = 0, o = t; a < o.length; a++) {
    var s = o[a];
    s !== e && (n = !1), Ye.is(s) && (r = !1);
  }
  if (n)
    return e;
  if (r)
    return t[t.length - 1];
  for (var u = {}, l = 0, f = t; l < f.length; l++) {
    var s = f[l];
    for (var c in s)
      (!br.call(u, c) || i || s[c] !== e[c]) && (u[c] = s[c]);
  }
  return u;
}
function Vd(e) {
  switch (e._tag) {
    case "RefinementType":
    case "ReadonlyType":
      return Vd(e.type);
    case "InterfaceType":
    case "StrictType":
    case "PartialType":
      return e.props;
    case "IntersectionType":
      return e.types.reduce(function(t, n) {
        return Object.assign(t, Vd(n));
      }, {});
  }
}
function og(e, t) {
  for (var n = Object.getOwnPropertyNames(e), r = !1, i = {}, a = 0; a < n.length; a++) {
    var o = n[a];
    br.call(t, o) ? i[o] = e[o] : r = !0;
  }
  return r ? i : e;
}
function DR(e) {
  return Sw(e) ? "{| ".concat(Hd(e.props), " |}") : FR(e) ? bw("{| ".concat(Hd(e.props), " |}")) : "Exact<".concat(e.name, ">");
}
function qR(e) {
  return e.length > 0;
}
var tt = {};
function ww(e, t) {
  for (var n = [], r = 0, i = e; r < i.length; r++) {
    var a = i[r];
    t.indexOf(a) !== -1 && n.push(a);
  }
  return n;
}
function jR(e, t) {
  if (e === tt)
    return t;
  if (t === tt)
    return e;
  var n = Object.assign({}, e);
  for (var r in t)
    if (br.call(e, r)) {
      var i = ww(e[r], t[r]);
      if (qR(i))
        n[r] = i;
      else {
        n = tt;
        break;
      }
    } else
      n[r] = t[r];
  return n;
}
function PR(e, t) {
  if (e === tt || t === tt)
    return tt;
  var n = tt;
  for (var r in e)
    if (br.call(t, r)) {
      var i = ww(e[r], t[r]);
      i.length === 0 && (n === tt && (n = {}), n[r] = e[r].concat(t[r]));
    }
  return n;
}
function sg(e) {
  return e._tag === "AnyType";
}
function Ew(e) {
  return e._tag === "LiteralType";
}
function kR(e) {
  return e._tag === "KeyofType";
}
function Sw(e) {
  return e._tag === "InterfaceType";
}
function FR(e) {
  return e._tag === "PartialType";
}
function BR(e) {
  return e._tag === "StrictType";
}
function UR(e) {
  return e._tag === "ExactType";
}
function HR(e) {
  return e._tag === "RefinementType";
}
function GR(e) {
  return e._tag === "IntersectionType";
}
function Aw(e) {
  return e._tag === "UnionType";
}
function VR(e) {
  return e._tag === "RecursiveType";
}
var as = [];
function Fn(e) {
  if (as.indexOf(e) !== -1)
    return tt;
  if (Sw(e) || BR(e)) {
    var t = tt;
    for (var n in e.props) {
      var r = e.props[n];
      Ew(r) && (t === tt && (t = {}), t[n] = [r.value]);
    }
    return t;
  } else {
    if (UR(e) || HR(e))
      return Fn(e.type);
    if (GR(e))
      return e.types.reduce(function(a, o) {
        return jR(a, Fn(o));
      }, tt);
    if (Aw(e))
      return e.types.slice(1).reduce(function(a, o) {
        return PR(a, Fn(o));
      }, Fn(e.types[0]));
    if (VR(e)) {
      as.push(e);
      var i = Fn(e.type);
      return as.pop(), i;
    }
  }
  return tt;
}
function Rw(e) {
  var t = Fn(e[0]), n = Object.keys(t), r = e.length, i = function(l) {
    for (var f = t[l].slice(), c = [t[l]], p = 1; p < r; p++) {
      var d = e[p], y = Fn(d), v = y[l];
      if (v === void 0)
        return "continue-keys";
      if (v.some(function(_) {
        return f.indexOf(_) !== -1;
      }))
        return "continue-keys";
      f.push.apply(f, v), c.push(v);
    }
    return { value: [l, c] };
  };
  e:
    for (var a = 0, o = n; a < o.length; a++) {
      var s = o[a], u = i(s);
      if (typeof u == "object")
        return u.value;
      switch (u) {
        case "continue-keys":
          continue e;
      }
    }
}
var Iw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "null", function(r) {
        return r === null;
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "NullType", n;
    }
    return t;
  }(le)
), ja = new Iw(), Tw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "undefined", function(r) {
        return r === void 0;
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "UndefinedType", n;
    }
    return t;
  }(le)
), Pa = new Tw(), xw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "void", Pa.is, Pa.validate, ae) || this;
      return n._tag = "VoidType", n;
    }
    return t;
  }(le)
), ug = new xw(), Nw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "unknown", function(r) {
        return !0;
      }, pe, ae) || this;
      return n._tag = "UnknownType", n;
    }
    return t;
  }(le)
), Mh = new Nw(), Ow = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "string", function(r) {
        return typeof r == "string";
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "StringType", n;
    }
    return t;
  }(le)
), Re = new Ow(), $w = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "number", function(r) {
        return typeof r == "number";
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "NumberType", n;
    }
    return t;
  }(le)
), Ch = new $w(), Lw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(
        this,
        "bigint",
        // tslint:disable-next-line: valid-typeof
        function(r) {
          return typeof r == "bigint";
        },
        function(r, i) {
          return n.is(r) ? pe(r) : Le(r, i);
        },
        ae
      ) || this;
      return n._tag = "BigIntType", n;
    }
    return t;
  }(le)
), WR = new Lw(), Mw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "boolean", function(r) {
        return typeof r == "boolean";
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "BooleanType", n;
    }
    return t;
  }(le)
), JR = new Mw(), Cw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "UnknownArray", Array.isArray, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "AnyArrayType", n;
    }
    return t;
  }(le)
), Zr = new Cw(), Dw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "UnknownRecord", function(r) {
        return r !== null && typeof r == "object" && !Array.isArray(r);
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "AnyDictionaryType", n;
    }
    return t;
  }(le)
), Ye = new Dw(), qw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.value = o, s._tag = "LiteralType", s;
    }
    return t;
  }(le)
);
function Hn(e, t) {
  t === void 0 && (t = JSON.stringify(e));
  var n = function(r) {
    return r === e;
  };
  return new qw(t, n, function(r, i) {
    return n(r) ? pe(e) : Le(r, i);
  }, ae, e);
}
var jw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.keys = o, s._tag = "KeyofType", s;
    }
    return t;
  }(le)
);
function zR(e, t) {
  t === void 0 && (t = Object.keys(e).map(function(r) {
    return JSON.stringify(r);
  }).join(" | "));
  var n = function(r) {
    return Re.is(r) && br.call(e, r);
  };
  return new jw(t, n, function(r, i) {
    return n(r) ? pe(r) : Le(r, i);
  }, ae, e);
}
var Pw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o, s) {
      var u = e.call(this, n, r, i, a) || this;
      return u.type = o, u.predicate = s, u._tag = "RefinementType", u;
    }
    return t;
  }(le)
);
function kw(e, t, n) {
  return kh(e, t, n);
}
var KR = kw(Ch, function(e) {
  return Number.isInteger(e);
}, "Int"), Dh = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.runDefinition = o, s._tag = "RecursiveType", s;
    }
    return t;
  }(le)
);
Object.defineProperty(Dh.prototype, "type", {
  get: function() {
    return this.runDefinition();
  },
  enumerable: !0,
  configurable: !0
});
function XR(e, t) {
  var n, r = function() {
    return n || (n = t(i), n.name = e), n;
  }, i = new Dh(e, function(a) {
    return r().is(a);
  }, function(a, o) {
    return r().validate(a, o);
  }, function(a) {
    return r().encode(a);
  }, r);
  return i;
}
var Fw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.type = o, s._tag = "ArrayType", s;
    }
    return t;
  }(le)
);
function mr(e, t) {
  return t === void 0 && (t = "Array<".concat(e.name, ">")), new Fw(t, function(n) {
    return Zr.is(n) && n.every(e.is);
  }, function(n, r) {
    var i = Zr.validate(n, r);
    if ($e(i))
      return i;
    for (var a = i.right, o = a.length, s = a, u = [], l = 0; l < o; l++) {
      var f = a[l], c = e.validate(f, Pt(r, String(l), e, f));
      if ($e(c))
        Sn(u, c.left);
      else {
        var p = c.right;
        p !== f && (s === a && (s = a.slice()), s[l] = p);
      }
    }
    return u.length > 0 ? an(u) : pe(s);
  }, e.encode === ae ? ae : function(n) {
    return n.map(e.encode);
  }, e);
}
var Bw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.props = o, s._tag = "InterfaceType", s;
    }
    return t;
  }(le)
);
function rt(e, t) {
  t === void 0 && (t = _w(e));
  var n = Object.keys(e), r = n.map(function(a) {
    return e[a];
  }), i = n.length;
  return new Bw(t, function(a) {
    if (Ye.is(a)) {
      for (var o = 0; o < i; o++) {
        var s = n[o], u = a[s];
        if (u === void 0 && !br.call(a, s) || !r[o].is(u))
          return !1;
      }
      return !0;
    }
    return !1;
  }, function(a, o) {
    var s = Ye.validate(a, o);
    if ($e(s))
      return s;
    for (var u = s.right, l = u, f = [], c = 0; c < i; c++) {
      var p = n[c], d = l[p], y = r[c], v = y.validate(d, Pt(o, p, y, d));
      if ($e(v))
        Sn(f, v.left);
      else {
        var _ = v.right;
        (_ !== d || _ === void 0 && !br.call(l, p)) && (l === u && (l = Qr({}, u)), l[p] = _);
      }
    }
    return f.length > 0 ? an(f) : pe(l);
  }, Gi(r) ? ae : function(a) {
    for (var o = Qr({}, a), s = 0; s < i; s++) {
      var u = n[s], l = r[s].encode;
      l !== ae && (o[u] = l(a[u]));
    }
    return o;
  }, e);
}
var Uw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.props = o, s._tag = "PartialType", s;
    }
    return t;
  }(le)
);
function on(e, t) {
  t === void 0 && (t = bw(_w(e)));
  var n = Object.keys(e), r = n.map(function(a) {
    return e[a];
  }), i = n.length;
  return new Uw(t, function(a) {
    if (Ye.is(a)) {
      for (var o = 0; o < i; o++) {
        var s = n[o], u = a[s];
        if (u !== void 0 && !e[s].is(u))
          return !1;
      }
      return !0;
    }
    return !1;
  }, function(a, o) {
    var s = Ye.validate(a, o);
    if ($e(s))
      return s;
    for (var u = s.right, l = u, f = [], c = 0; c < i; c++) {
      var p = n[c], d = l[p], y = e[p], v = y.validate(d, Pt(o, p, y, d));
      if ($e(v))
        d !== void 0 && Sn(f, v.left);
      else {
        var _ = v.right;
        _ !== d && (l === u && (l = Qr({}, u)), l[p] = _);
      }
    }
    return f.length > 0 ? an(f) : pe(l);
  }, Gi(r) ? ae : function(a) {
    for (var o = Qr({}, a), s = 0; s < i; s++) {
      var u = n[s], l = a[u];
      l !== void 0 && (o[u] = r[s].encode(l));
    }
    return o;
  }, e);
}
var qh = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o, s) {
      var u = e.call(this, n, r, i, a) || this;
      return u.domain = o, u.codomain = s, u._tag = "DictionaryType", u;
    }
    return t;
  }(le)
);
function jh(e, t, n) {
  var r = Lh(e);
  return r ? MR(Object.keys(r), e, t, n) : CR(e, t, n);
}
var Ph = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.types = o, s._tag = "UnionType", s;
    }
    return t;
  }(le)
);
function tn(e, t) {
  t === void 0 && (t = mw(e));
  var n = Rw(e);
  if (n !== void 0 && e.length > 0) {
    var r = n[0], i = n[1], a = i.length, o = function(s) {
      for (var u = 0; u < a; u++)
        if (i[u].indexOf(s) !== -1)
          return u;
    };
    return new ka(t, function(s) {
      if (Ye.is(s)) {
        var u = o(s[r]);
        return u !== void 0 ? e[u].is(s) : !1;
      }
      return !1;
    }, function(s, u) {
      var l = Ye.validate(s, u);
      if ($e(l))
        return l;
      var f = l.right, c = o(f[r]);
      if (c === void 0)
        return Le(s, u);
      var p = e[c];
      return p.validate(f, Pt(u, String(c), p, f));
    }, Gi(e) ? ae : function(s) {
      var u = o(s[r]);
      if (u === void 0)
        throw new Error("no codec found to encode value in union codec ".concat(t));
      return e[u].encode(s);
    }, e, r);
  } else
    return new Ph(t, function(s) {
      return e.some(function(u) {
        return u.is(s);
      });
    }, function(s, u) {
      for (var l = [], f = 0; f < e.length; f++) {
        var c = e[f], p = c.validate(s, Pt(u, String(f), c, s));
        if ($e(p))
          Sn(l, p.left);
        else
          return pe(p.right);
      }
      return an(l);
    }, Gi(e) ? ae : function(s) {
      for (var u = 0, l = e; u < l.length; u++) {
        var f = l[u];
        if (f.is(s))
          return f.encode(s);
      }
      throw new Error("no codec found to encode value in union type ".concat(t));
    }, e);
}
var Hw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.types = o, s._tag = "IntersectionType", s;
    }
    return t;
  }(le)
);
function sn(e, t) {
  t === void 0 && (t = "(".concat(e.map(function(r) {
    return r.name;
  }).join(" & "), ")"));
  var n = e.length;
  return new Hw(t, function(r) {
    return e.every(function(i) {
      return i.is(r);
    });
  }, e.length === 0 ? pe : function(r, i) {
    for (var a = [], o = [], s = 0; s < n; s++) {
      var u = e[s], l = u.validate(r, Pt(i, String(s), u, r));
      $e(l) ? Sn(o, l.left) : a.push(l.right);
    }
    return o.length > 0 ? an(o) : pe(Gd(r, a));
  }, e.length === 0 ? ae : function(r) {
    return Gd(r, e.map(function(i) {
      return i.encode(r);
    }));
  }, e);
}
var Gw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.types = o, s._tag = "TupleType", s;
    }
    return t;
  }(le)
);
function YR(e, t) {
  t === void 0 && (t = "[".concat(e.map(function(r) {
    return r.name;
  }).join(", "), "]"));
  var n = e.length;
  return new Gw(t, function(r) {
    return Zr.is(r) && r.length === n && e.every(function(i, a) {
      return i.is(r[a]);
    });
  }, function(r, i) {
    var a = Zr.validate(r, i);
    if ($e(a))
      return a;
    for (var o = a.right, s = o.length > n ? o.slice(0, n) : o, u = [], l = 0; l < n; l++) {
      var f = o[l], c = e[l], p = c.validate(f, Pt(i, String(l), c, f));
      if ($e(p))
        Sn(u, p.left);
      else {
        var d = p.right;
        d !== f && (s === o && (s = o.slice()), s[l] = d);
      }
    }
    return u.length > 0 ? an(u) : pe(s);
  }, Gi(e) ? ae : function(r) {
    return e.map(function(i, a) {
      return i.encode(r[a]);
    });
  }, e);
}
var Vw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.type = o, s._tag = "ReadonlyType", s;
    }
    return t;
  }(le)
);
function QR(e, t) {
  return t === void 0 && (t = "Readonly<".concat(e.name, ">")), new Vw(t, e.is, e.validate, e.encode, e);
}
var Ww = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.type = o, s._tag = "ReadonlyArrayType", s;
    }
    return t;
  }(le)
);
function ZR(e, t) {
  t === void 0 && (t = "ReadonlyArray<".concat(e.name, ">"));
  var n = mr(e);
  return new Ww(t, n.is, n.validate, n.encode, e);
}
var eI = function(e, t) {
  return zw(rt(e), t);
}, Jw = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.type = o, s._tag = "ExactType", s;
    }
    return t;
  }(le)
);
function zw(e, t) {
  t === void 0 && (t = DR(e));
  var n = Vd(e);
  return new Jw(t, e.is, function(r, i) {
    var a = Ye.validate(r, i);
    if ($e(a))
      return a;
    var o = e.validate(r, i);
    return $e(o) ? o : yw(og(o.right, n));
  }, function(r) {
    return e.encode(og(r, n));
  }, e);
}
var Kw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(
        this,
        "Function",
        // tslint:disable-next-line:strict-type-predicates
        function(r) {
          return typeof r == "function";
        },
        function(r, i) {
          return n.is(r) ? pe(r) : Le(r, i);
        },
        ae
      ) || this;
      return n._tag = "FunctionType", n;
    }
    return t;
  }(le)
), tI = new Kw(), Xw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(
        this,
        "never",
        function(r) {
          return !1;
        },
        function(r, i) {
          return Le(r, i);
        },
        /* istanbul ignore next */
        function() {
          throw new Error("cannot encode never");
        }
      ) || this;
      return n._tag = "NeverType", n;
    }
    return t;
  }(le)
), nI = new Xw(), Yw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "any", function(r) {
        return !0;
      }, pe, ae) || this;
      return n._tag = "AnyType", n;
    }
    return t;
  }(le)
), rI = new Yw();
function kh(e, t, n) {
  return n === void 0 && (n = "(".concat(e.name, " | ").concat(vw(t), ")")), new Pw(n, function(r) {
    return e.is(r) && t(r);
  }, function(r, i) {
    var a = e.validate(r, i);
    if ($e(a))
      return a;
    var o = a.right;
    return t(o) ? pe(o) : Le(o, i);
  }, e.encode, e, t);
}
var iI = kh(Ch, Number.isInteger, "Integer"), ka = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o, s) {
      var u = e.call(this, n, r, i, a, o) || this;
      return u.tag = s, u;
    }
    return t;
  }(Ph)
), aI = function(e, t, n) {
  n === void 0 && (n = mw(t));
  var r = tn(t, n);
  return r instanceof ka ? r : (console.warn("[io-ts] Cannot build a tagged union for ".concat(n, ", returning a de-optimized union")), new ka(n, r.is, r.validate, r.encode, t, e));
}, oI = function(e, t) {
  return {
    value: e,
    context: t
  };
}, Qw = function(e) {
  return [
    { key: "", type: e }
  ];
}, sI = Ye, Zw = (
  /** @class */
  function(e) {
    he(t, e);
    function t() {
      var n = e.call(this, "object", function(r) {
        return r !== null && typeof r == "object";
      }, function(r, i) {
        return n.is(r) ? pe(r) : Le(r, i);
      }, ae) || this;
      return n._tag = "ObjectType", n;
    }
    return t;
  }(le)
), uI = new Zw(), lI = jh, fI = (
  /** @class */
  function(e) {
    he(t, e);
    function t(n, r, i, a, o) {
      var s = e.call(this, n, r, i, a) || this;
      return s.props = o, s._tag = "StrictType", s;
    }
    return t;
  }(le)
);
function cI(e) {
  return e;
}
function dI(e) {
  return function() {
    return e;
  };
}
const hI = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  AnyArrayType: Cw,
  AnyDictionaryType: Dw,
  AnyType: Yw,
  Array: Zr,
  ArrayType: Fw,
  BigIntType: Lw,
  BooleanType: Mw,
  Dictionary: sI,
  DictionaryType: qh,
  ExactType: Jw,
  Function: tI,
  FunctionType: Kw,
  Int: KR,
  Integer: iI,
  InterfaceType: Bw,
  IntersectionType: Hw,
  KeyofType: jw,
  LiteralType: qw,
  NeverType: Xw,
  NullType: Iw,
  NumberType: $w,
  ObjectType: Zw,
  PartialType: Uw,
  ReadonlyArrayType: Ww,
  ReadonlyType: Vw,
  RecursiveType: Dh,
  RefinementType: Pw,
  StrictType: fI,
  StringType: Ow,
  TaggedUnionType: ka,
  TupleType: Gw,
  Type: le,
  UndefinedType: Tw,
  UnionType: Ph,
  UnknownArray: Zr,
  UnknownRecord: Ye,
  UnknownType: Nw,
  VoidType: xw,
  alias: dI,
  any: rI,
  appendContext: Pt,
  array: mr,
  bigint: WR,
  boolean: JR,
  brand: kw,
  clean: cI,
  dictionary: lI,
  emptyTags: tt,
  exact: zw,
  failure: Le,
  failures: an,
  getContextEntry: LR,
  getDefaultContext: Qw,
  getDomainKeys: Lh,
  getFunctionName: vw,
  getIndex: Rw,
  getTags: Fn,
  getValidationError: oI,
  identity: ae,
  interface: rt,
  intersection: sn,
  keyof: zR,
  literal: Hn,
  mergeAll: Gd,
  never: nI,
  null: ja,
  nullType: ja,
  number: Ch,
  object: uI,
  partial: on,
  readonly: QR,
  readonlyArray: ZR,
  record: jh,
  recursion: XR,
  refinement: kh,
  strict: eI,
  string: Re,
  success: pe,
  taggedUnion: aI,
  tuple: YR,
  type: rt,
  undefined: Pa,
  union: tn,
  unknown: Mh,
  void: ug,
  voidType: ug
}, Symbol.toStringTag, { value: "Module" }));
var X = typeof globalThis < "u" ? globalThis : typeof window < "u" ? window : typeof global < "u" ? global : typeof self < "u" ? self : {};
function e1(e) {
  if (e.__esModule)
    return e;
  var t = e.default;
  if (typeof t == "function") {
    var n = function r() {
      return this instanceof r ? Reflect.construct(t, arguments, this.constructor) : t.apply(this, arguments);
    };
    n.prototype = t.prototype;
  } else
    n = {};
  return Object.defineProperty(n, "__esModule", { value: !0 }), Object.keys(e).forEach(function(r) {
    var i = Object.getOwnPropertyDescriptor(e, r);
    Object.defineProperty(n, r, i.get ? i : {
      enumerable: !0,
      get: function() {
        return e[r];
      }
    });
  }), n;
}
var Fh = {};
const t1 = /* @__PURE__ */ e1(hI);
var ao = {}, oo = {};
Object.defineProperty(oo, "__esModule", { value: !0 });
oo.clone = void 0;
function pI(e) {
  var t = Object.create(Object.getPrototypeOf(e));
  return Object.assign(t, e), t;
}
oo.clone = pI;
var gI = X && X.__createBinding || (Object.create ? function(e, t, n, r) {
  r === void 0 && (r = n), Object.defineProperty(e, r, { enumerable: !0, get: function() {
    return t[n];
  } });
} : function(e, t, n, r) {
  r === void 0 && (r = n), e[r] = t[n];
}), yI = X && X.__setModuleDefault || (Object.create ? function(e, t) {
  Object.defineProperty(e, "default", { enumerable: !0, value: t });
} : function(e, t) {
  e.default = t;
}), vI = X && X.__importStar || function(e) {
  if (e && e.__esModule)
    return e;
  var t = {};
  if (e != null)
    for (var n in e)
      Object.hasOwnProperty.call(e, n) && gI(t, e, n);
  return yI(t, e), t;
};
Object.defineProperty(ao, "__esModule", { value: !0 });
ao.withValidate = void 0;
var _I = vI(t1), bI = oo;
function mI(e, t, n) {
  n === void 0 && (n = e.name);
  var r = bI.clone(e);
  return r.validate = t, r.decode = function(i) {
    return t(i, _I.getDefaultContext(r));
  }, r.name = n, r;
}
ao.withValidate = mI;
var wI = X && X.__createBinding || (Object.create ? function(e, t, n, r) {
  r === void 0 && (r = n), Object.defineProperty(e, r, { enumerable: !0, get: function() {
    return t[n];
  } });
} : function(e, t, n, r) {
  r === void 0 && (r = n), e[r] = t[n];
}), EI = X && X.__setModuleDefault || (Object.create ? function(e, t) {
  Object.defineProperty(e, "default", { enumerable: !0, value: t });
} : function(e, t) {
  e.default = t;
}), SI = X && X.__importStar || function(e) {
  if (e && e.__esModule)
    return e;
  var t = {};
  if (e != null)
    for (var n in e)
      Object.hasOwnProperty.call(e, n) && wI(t, e, n);
  return EI(t, e), t;
};
Object.defineProperty(Fh, "__esModule", { value: !0 });
var n1 = Fh.fromNullable = void 0, AI = SI(t1), RI = ao;
function II(e, t, n) {
  return n === void 0 && (n = "fromNullable(" + e.name + ")"), RI.withValidate(e, function(r, i) {
    return r == null ? AI.success(t) : e.validate(r, i);
  }, n);
}
n1 = Fh.fromNullable = II;
var un = {};
(function(e) {
  var t = X && X.__spreadArray || function(O, $, q) {
    if (q || arguments.length === 2)
      for (var N = 0, P = $.length, H; N < P; N++)
        (H || !(N in $)) && (H || (H = Array.prototype.slice.call($, 0, N)), H[N] = $[N]);
    return O.concat(H || Array.prototype.slice.call($));
  };
  Object.defineProperty(e, "__esModule", { value: !0 }), e.dual = e.getEndomorphismMonoid = e.not = e.SK = e.hole = e.pipe = e.untupled = e.tupled = e.absurd = e.decrement = e.increment = e.tuple = e.flow = e.flip = e.constVoid = e.constUndefined = e.constNull = e.constFalse = e.constTrue = e.constant = e.unsafeCoerce = e.identity = e.apply = e.getRing = e.getSemiring = e.getMonoid = e.getSemigroup = e.getBooleanAlgebra = void 0;
  var n = function(O) {
    return function() {
      return {
        meet: function($, q) {
          return function(N) {
            return O.meet($(N), q(N));
          };
        },
        join: function($, q) {
          return function(N) {
            return O.join($(N), q(N));
          };
        },
        zero: function() {
          return O.zero;
        },
        one: function() {
          return O.one;
        },
        implies: function($, q) {
          return function(N) {
            return O.implies($(N), q(N));
          };
        },
        not: function($) {
          return function(q) {
            return O.not($(q));
          };
        }
      };
    };
  };
  e.getBooleanAlgebra = n;
  var r = function(O) {
    return function() {
      return {
        concat: function($, q) {
          return function(N) {
            return O.concat($(N), q(N));
          };
        }
      };
    };
  };
  e.getSemigroup = r;
  var i = function(O) {
    var $ = (0, e.getSemigroup)(O);
    return function() {
      return {
        concat: $().concat,
        empty: function() {
          return O.empty;
        }
      };
    };
  };
  e.getMonoid = i;
  var a = function(O) {
    return {
      add: function($, q) {
        return function(N) {
          return O.add($(N), q(N));
        };
      },
      zero: function() {
        return O.zero;
      },
      mul: function($, q) {
        return function(N) {
          return O.mul($(N), q(N));
        };
      },
      one: function() {
        return O.one;
      }
    };
  };
  e.getSemiring = a;
  var o = function(O) {
    var $ = (0, e.getSemiring)(O);
    return {
      add: $.add,
      mul: $.mul,
      one: $.one,
      zero: $.zero,
      sub: function(q, N) {
        return function(P) {
          return O.sub(q(P), N(P));
        };
      }
    };
  };
  e.getRing = o;
  var s = function(O) {
    return function($) {
      return $(O);
    };
  };
  e.apply = s;
  function u(O) {
    return O;
  }
  e.identity = u, e.unsafeCoerce = u;
  function l(O) {
    return function() {
      return O;
    };
  }
  e.constant = l, e.constTrue = l(!0), e.constFalse = l(!1), e.constNull = l(null), e.constUndefined = l(void 0), e.constVoid = e.constUndefined;
  function f(O) {
    return function() {
      for (var $ = [], q = 0; q < arguments.length; q++)
        $[q] = arguments[q];
      return $.length > 1 ? O($[1], $[0]) : function(N) {
        return O(N)($[0]);
      };
    };
  }
  e.flip = f;
  function c(O, $, q, N, P, H, G, z, W) {
    switch (arguments.length) {
      case 1:
        return O;
      case 2:
        return function() {
          return $(O.apply(this, arguments));
        };
      case 3:
        return function() {
          return q($(O.apply(this, arguments)));
        };
      case 4:
        return function() {
          return N(q($(O.apply(this, arguments))));
        };
      case 5:
        return function() {
          return P(N(q($(O.apply(this, arguments)))));
        };
      case 6:
        return function() {
          return H(P(N(q($(O.apply(this, arguments))))));
        };
      case 7:
        return function() {
          return G(H(P(N(q($(O.apply(this, arguments)))))));
        };
      case 8:
        return function() {
          return z(G(H(P(N(q($(O.apply(this, arguments))))))));
        };
      case 9:
        return function() {
          return W(z(G(H(P(N(q($(O.apply(this, arguments)))))))));
        };
    }
  }
  e.flow = c;
  function p() {
    for (var O = [], $ = 0; $ < arguments.length; $++)
      O[$] = arguments[$];
    return O;
  }
  e.tuple = p;
  function d(O) {
    return O + 1;
  }
  e.increment = d;
  function y(O) {
    return O - 1;
  }
  e.decrement = y;
  function v(O) {
    throw new Error("Called `absurd` function which should be uncallable");
  }
  e.absurd = v;
  function _(O) {
    return function($) {
      return O.apply(void 0, $);
    };
  }
  e.tupled = _;
  function m(O) {
    return function() {
      for (var $ = [], q = 0; q < arguments.length; q++)
        $[q] = arguments[q];
      return O($);
    };
  }
  e.untupled = m;
  function E(O, $, q, N, P, H, G, z, W) {
    switch (arguments.length) {
      case 1:
        return O;
      case 2:
        return $(O);
      case 3:
        return q($(O));
      case 4:
        return N(q($(O)));
      case 5:
        return P(N(q($(O))));
      case 6:
        return H(P(N(q($(O)))));
      case 7:
        return G(H(P(N(q($(O))))));
      case 8:
        return z(G(H(P(N(q($(O)))))));
      case 9:
        return W(z(G(H(P(N(q($(O))))))));
      default: {
        for (var se = arguments[0], ge = 1; ge < arguments.length; ge++)
          se = arguments[ge](se);
        return se;
      }
    }
  }
  e.pipe = E, e.hole = v;
  var L = function(O, $) {
    return $;
  };
  e.SK = L;
  function I(O) {
    return function($) {
      return !O($);
    };
  }
  e.not = I;
  var j = function() {
    return {
      concat: function(O, $) {
        return c(O, $);
      },
      empty: u
    };
  };
  e.getEndomorphismMonoid = j;
  var M = function(O, $) {
    var q = typeof O == "number" ? function(N) {
      return N.length >= O;
    } : O;
    return function() {
      var N = Array.from(arguments);
      return q(arguments) ? $.apply(this, N) : function(P) {
        return $.apply(void 0, t([P], N, !1));
      };
    };
  };
  e.dual = M;
})(un);
var Bh = {}, ei = {}, Be = {}, Xi = {};
(function(e) {
  var t = X && X.__spreadArray || function(O, $, q) {
    if (q || arguments.length === 2)
      for (var N = 0, P = $.length, H; N < P; N++)
        (H || !(N in $)) && (H || (H = Array.prototype.slice.call($, 0, N)), H[N] = $[N]);
    return O.concat(H || Array.prototype.slice.call($));
  };
  Object.defineProperty(e, "__esModule", { value: !0 }), e.flatMapReader = e.flatMapTask = e.flatMapIO = e.flatMapEither = e.flatMapOption = e.flatMapNullable = e.liftOption = e.liftNullable = e.fromReadonlyNonEmptyArray = e.has = e.emptyRecord = e.emptyReadonlyArray = e.tail = e.head = e.isNonEmpty = e.singleton = e.right = e.left = e.isRight = e.isLeft = e.some = e.none = e.isSome = e.isNone = void 0;
  var n = un, r = function(O) {
    return O._tag === "None";
  };
  e.isNone = r;
  var i = function(O) {
    return O._tag === "Some";
  };
  e.isSome = i, e.none = { _tag: "None" };
  var a = function(O) {
    return { _tag: "Some", value: O };
  };
  e.some = a;
  var o = function(O) {
    return O._tag === "Left";
  };
  e.isLeft = o;
  var s = function(O) {
    return O._tag === "Right";
  };
  e.isRight = s;
  var u = function(O) {
    return { _tag: "Left", left: O };
  };
  e.left = u;
  var l = function(O) {
    return { _tag: "Right", right: O };
  };
  e.right = l;
  var f = function(O) {
    return [O];
  };
  e.singleton = f;
  var c = function(O) {
    return O.length > 0;
  };
  e.isNonEmpty = c;
  var p = function(O) {
    return O[0];
  };
  e.head = p;
  var d = function(O) {
    return O.slice(1);
  };
  e.tail = d, e.emptyReadonlyArray = [], e.emptyRecord = {}, e.has = Object.prototype.hasOwnProperty;
  var y = function(O) {
    return t([O[0]], O.slice(1), !0);
  };
  e.fromReadonlyNonEmptyArray = y;
  var v = function(O) {
    return function($, q) {
      return function() {
        for (var N = [], P = 0; P < arguments.length; P++)
          N[P] = arguments[P];
        var H = $.apply(void 0, N);
        return O.fromEither(H == null ? (0, e.left)(q.apply(void 0, N)) : (0, e.right)(H));
      };
    };
  };
  e.liftNullable = v;
  var _ = function(O) {
    return function($, q) {
      return function() {
        for (var N = [], P = 0; P < arguments.length; P++)
          N[P] = arguments[P];
        var H = $.apply(void 0, N);
        return O.fromEither((0, e.isNone)(H) ? (0, e.left)(q.apply(void 0, N)) : (0, e.right)(H.value));
      };
    };
  };
  e.liftOption = _;
  var m = function(O, $) {
    return (0, n.dual)(3, function(q, N, P) {
      return $.flatMap(q, (0, e.liftNullable)(O)(N, P));
    });
  };
  e.flatMapNullable = m;
  var E = function(O, $) {
    return (0, n.dual)(3, function(q, N, P) {
      return $.flatMap(q, (0, e.liftOption)(O)(N, P));
    });
  };
  e.flatMapOption = E;
  var L = function(O, $) {
    return (0, n.dual)(2, function(q, N) {
      return $.flatMap(q, function(P) {
        return O.fromEither(N(P));
      });
    });
  };
  e.flatMapEither = L;
  var I = function(O, $) {
    return (0, n.dual)(2, function(q, N) {
      return $.flatMap(q, function(P) {
        return O.fromIO(N(P));
      });
    });
  };
  e.flatMapIO = I;
  var j = function(O, $) {
    return (0, n.dual)(2, function(q, N) {
      return $.flatMap(q, function(P) {
        return O.fromTask(N(P));
      });
    });
  };
  e.flatMapTask = j;
  var M = function(O, $) {
    return (0, n.dual)(2, function(q, N) {
      return $.flatMap(q, function(P) {
        return O.fromReader(N(P));
      });
    });
  };
  e.flatMapReader = M;
})(Xi);
var TI = X && X.__createBinding || (Object.create ? function(e, t, n, r) {
  r === void 0 && (r = n);
  var i = Object.getOwnPropertyDescriptor(t, n);
  (!i || ("get" in i ? !t.__esModule : i.writable || i.configurable)) && (i = { enumerable: !0, get: function() {
    return t[n];
  } }), Object.defineProperty(e, r, i);
} : function(e, t, n, r) {
  r === void 0 && (r = n), e[r] = t[n];
}), xI = X && X.__setModuleDefault || (Object.create ? function(e, t) {
  Object.defineProperty(e, "default", { enumerable: !0, value: t });
} : function(e, t) {
  e.default = t;
}), NI = X && X.__importStar || function(e) {
  if (e && e.__esModule)
    return e;
  var t = {};
  if (e != null)
    for (var n in e)
      n !== "default" && Object.prototype.hasOwnProperty.call(e, n) && TI(t, e, n);
  return xI(t, e), t;
};
Object.defineProperty(Be, "__esModule", { value: !0 });
Be.sequenceS = Be.sequenceT = Be.getApplySemigroup = Be.apS = Be.apSecond = Be.apFirst = Be.ap = void 0;
var OI = un, $I = NI(Xi);
function LI(e, t) {
  return function(n) {
    return function(r) {
      return e.ap(e.map(r, function(i) {
        return function(a) {
          return t.ap(i, a);
        };
      }), n);
    };
  };
}
Be.ap = LI;
function MI(e) {
  return function(t) {
    return function(n) {
      return e.ap(e.map(n, function(r) {
        return function() {
          return r;
        };
      }), t);
    };
  };
}
Be.apFirst = MI;
function CI(e) {
  return function(t) {
    return function(n) {
      return e.ap(e.map(n, function() {
        return function(r) {
          return r;
        };
      }), t);
    };
  };
}
Be.apSecond = CI;
function DI(e) {
  return function(t, n) {
    return function(r) {
      return e.ap(e.map(r, function(i) {
        return function(a) {
          var o;
          return Object.assign({}, i, (o = {}, o[t] = a, o));
        };
      }), n);
    };
  };
}
Be.apS = DI;
function qI(e) {
  return function(t) {
    return {
      concat: function(n, r) {
        return e.ap(e.map(n, function(i) {
          return function(a) {
            return t.concat(i, a);
          };
        }), r);
      }
    };
  };
}
Be.getApplySemigroup = qI;
function Uh(e, t, n) {
  return function(r) {
    for (var i = Array(n.length + 1), a = 0; a < n.length; a++)
      i[a] = n[a];
    return i[n.length] = r, t === 0 ? e.apply(null, i) : Uh(e, t - 1, i);
  };
}
var os = {
  1: function(e) {
    return [e];
  },
  2: function(e) {
    return function(t) {
      return [e, t];
    };
  },
  3: function(e) {
    return function(t) {
      return function(n) {
        return [e, t, n];
      };
    };
  },
  4: function(e) {
    return function(t) {
      return function(n) {
        return function(r) {
          return [e, t, n, r];
        };
      };
    };
  },
  5: function(e) {
    return function(t) {
      return function(n) {
        return function(r) {
          return function(i) {
            return [e, t, n, r, i];
          };
        };
      };
    };
  }
};
function jI(e) {
  return $I.has.call(os, e) || (os[e] = Uh(OI.tuple, e - 1, [])), os[e];
}
function PI(e) {
  return function() {
    for (var t = [], n = 0; n < arguments.length; n++)
      t[n] = arguments[n];
    for (var r = t.length, i = jI(r), a = e.map(t[0], i), o = 1; o < r; o++)
      a = e.ap(a, t[o]);
    return a;
  };
}
Be.sequenceT = PI;
function kI(e) {
  var t = e.length;
  switch (t) {
    case 1:
      return function(n) {
        var r;
        return r = {}, r[e[0]] = n, r;
      };
    case 2:
      return function(n) {
        return function(r) {
          var i;
          return i = {}, i[e[0]] = n, i[e[1]] = r, i;
        };
      };
    case 3:
      return function(n) {
        return function(r) {
          return function(i) {
            var a;
            return a = {}, a[e[0]] = n, a[e[1]] = r, a[e[2]] = i, a;
          };
        };
      };
    case 4:
      return function(n) {
        return function(r) {
          return function(i) {
            return function(a) {
              var o;
              return o = {}, o[e[0]] = n, o[e[1]] = r, o[e[2]] = i, o[e[3]] = a, o;
            };
          };
        };
      };
    case 5:
      return function(n) {
        return function(r) {
          return function(i) {
            return function(a) {
              return function(o) {
                var s;
                return s = {}, s[e[0]] = n, s[e[1]] = r, s[e[2]] = i, s[e[3]] = a, s[e[4]] = o, s;
              };
            };
          };
        };
      };
    default:
      return Uh(function() {
        for (var n = [], r = 0; r < arguments.length; r++)
          n[r] = arguments[r];
        for (var i = {}, a = 0; a < t; a++)
          i[e[a]] = n[a];
        return i;
      }, t - 1, []);
  }
}
function FI(e) {
  return function(t) {
    for (var n = Object.keys(t), r = n.length, i = kI(n), a = e.map(t[n[0]], i), o = 1; o < r; o++)
      a = e.ap(a, t[n[o]]);
    return a;
  };
}
Be.sequenceS = FI;
var Pe = {};
Object.defineProperty(Pe, "__esModule", { value: !0 });
Pe.asUnit = Pe.as = Pe.getFunctorComposition = Pe.let = Pe.bindTo = Pe.flap = Pe.map = void 0;
var BI = un;
function r1(e, t) {
  return function(n) {
    return function(r) {
      return e.map(r, function(i) {
        return t.map(i, n);
      });
    };
  };
}
Pe.map = r1;
function UI(e) {
  return function(t) {
    return function(n) {
      return e.map(n, function(r) {
        return r(t);
      });
    };
  };
}
Pe.flap = UI;
function HI(e) {
  return function(t) {
    return function(n) {
      return e.map(n, function(r) {
        var i;
        return i = {}, i[t] = r, i;
      });
    };
  };
}
Pe.bindTo = HI;
function GI(e) {
  return function(t, n) {
    return function(r) {
      return e.map(r, function(i) {
        var a;
        return Object.assign({}, i, (a = {}, a[t] = n(i), a));
      });
    };
  };
}
Pe.let = GI;
function VI(e, t) {
  var n = r1(e, t);
  return {
    map: function(r, i) {
      return (0, BI.pipe)(r, n(i));
    }
  };
}
Pe.getFunctorComposition = VI;
function i1(e) {
  return function(t, n) {
    return e.map(t, function() {
      return n;
    });
  };
}
Pe.as = i1;
function WI(e) {
  var t = i1(e);
  return function(n) {
    return t(n, void 0);
  };
}
Pe.asUnit = WI;
Object.defineProperty(ei, "__esModule", { value: !0 });
ei.getApplicativeComposition = ei.getApplicativeMonoid = void 0;
var a1 = Be, JI = un, zI = Pe;
function KI(e) {
  var t = (0, a1.getApplySemigroup)(e);
  return function(n) {
    return {
      concat: t(n).concat,
      empty: e.of(n.empty)
    };
  };
}
ei.getApplicativeMonoid = KI;
function XI(e, t) {
  var n = (0, zI.getFunctorComposition)(e, t).map, r = (0, a1.ap)(e, t);
  return {
    map: n,
    of: function(i) {
      return e.of(t.of(i));
    },
    ap: function(i, a) {
      return (0, JI.pipe)(i, r(a));
    }
  };
}
ei.getApplicativeComposition = XI;
var wn = {};
Object.defineProperty(wn, "__esModule", { value: !0 });
wn.bind = wn.tap = wn.chainFirst = void 0;
function YI(e) {
  var t = o1(e);
  return function(n) {
    return function(r) {
      return t(r, n);
    };
  };
}
wn.chainFirst = YI;
function o1(e) {
  return function(t, n) {
    return e.chain(t, function(r) {
      return e.map(n(r), function() {
        return r;
      });
    });
  };
}
wn.tap = o1;
function QI(e) {
  return function(t, n) {
    return function(r) {
      return e.chain(r, function(i) {
        return e.map(n(i), function(a) {
          var o;
          return Object.assign({}, i, (o = {}, o[t] = a, o));
        });
      });
    };
  };
}
wn.bind = QI;
var so = {};
Object.defineProperty(so, "__esModule", { value: !0 });
so.tailRec = void 0;
var ZI = function(e, t) {
  for (var n = t(e); n._tag === "Left"; )
    n = t(n.left);
  return n.right;
};
so.tailRec = ZI;
var xe = {}, eT = X && X.__createBinding || (Object.create ? function(e, t, n, r) {
  r === void 0 && (r = n);
  var i = Object.getOwnPropertyDescriptor(t, n);
  (!i || ("get" in i ? !t.__esModule : i.writable || i.configurable)) && (i = { enumerable: !0, get: function() {
    return t[n];
  } }), Object.defineProperty(e, r, i);
} : function(e, t, n, r) {
  r === void 0 && (r = n), e[r] = t[n];
}), tT = X && X.__setModuleDefault || (Object.create ? function(e, t) {
  Object.defineProperty(e, "default", { enumerable: !0, value: t });
} : function(e, t) {
  e.default = t;
}), nT = X && X.__importStar || function(e) {
  if (e && e.__esModule)
    return e;
  var t = {};
  if (e != null)
    for (var n in e)
      n !== "default" && Object.prototype.hasOwnProperty.call(e, n) && eT(t, e, n);
  return tT(t, e), t;
};
Object.defineProperty(xe, "__esModule", { value: !0 });
xe.tapEither = xe.filterOrElse = xe.chainFirstEitherK = xe.chainEitherK = xe.fromEitherK = xe.chainOptionK = xe.fromOptionK = xe.fromPredicate = xe.fromOption = void 0;
var rT = wn, s1 = un, dr = nT(Xi);
function u1(e) {
  return function(t) {
    return function(n) {
      return e.fromEither(dr.isNone(n) ? dr.left(t()) : dr.right(n.value));
    };
  };
}
xe.fromOption = u1;
function iT(e) {
  return function(t, n) {
    return function(r) {
      return e.fromEither(t(r) ? dr.right(r) : dr.left(n(r)));
    };
  };
}
xe.fromPredicate = iT;
function l1(e) {
  var t = u1(e);
  return function(n) {
    var r = t(n);
    return function(i) {
      return (0, s1.flow)(i, r);
    };
  };
}
xe.fromOptionK = l1;
function aT(e, t) {
  var n = l1(e);
  return function(r) {
    var i = n(r);
    return function(a) {
      return function(o) {
        return t.chain(o, i(a));
      };
    };
  };
}
xe.chainOptionK = aT;
function Hh(e) {
  return function(t) {
    return (0, s1.flow)(t, e.fromEither);
  };
}
xe.fromEitherK = Hh;
function oT(e, t) {
  var n = Hh(e);
  return function(r) {
    return function(i) {
      return t.chain(i, n(r));
    };
  };
}
xe.chainEitherK = oT;
function sT(e, t) {
  var n = f1(e, t);
  return function(r) {
    return function(i) {
      return n(i, r);
    };
  };
}
xe.chainFirstEitherK = sT;
function uT(e, t) {
  return function(n, r) {
    return function(i) {
      return t.chain(i, function(a) {
        return e.fromEither(n(a) ? dr.right(a) : dr.left(r(a)));
      });
    };
  };
}
xe.filterOrElse = uT;
function f1(e, t) {
  var n = Hh(e), r = (0, rT.tap)(t);
  return function(i, a) {
    return r(i, n(a));
  };
}
xe.tapEither = f1;
var c1 = {};
(function(e) {
  Object.defineProperty(e, "__esModule", { value: !0 }), e.right = e.left = e.flap = e.Functor = e.Bifunctor = e.URI = e.bimap = e.mapLeft = e.map = e.separated = void 0;
  var t = un, n = Pe, r = function(p, d) {
    return { left: p, right: d };
  };
  e.separated = r;
  var i = function(p, d) {
    return (0, t.pipe)(p, (0, e.map)(d));
  }, a = function(p, d) {
    return (0, t.pipe)(p, (0, e.mapLeft)(d));
  }, o = function(p, d, y) {
    return (0, t.pipe)(p, (0, e.bimap)(d, y));
  }, s = function(p) {
    return function(d) {
      return (0, e.separated)((0, e.left)(d), p((0, e.right)(d)));
    };
  };
  e.map = s;
  var u = function(p) {
    return function(d) {
      return (0, e.separated)(p((0, e.left)(d)), (0, e.right)(d));
    };
  };
  e.mapLeft = u;
  var l = function(p, d) {
    return function(y) {
      return (0, e.separated)(p((0, e.left)(y)), d((0, e.right)(y)));
    };
  };
  e.bimap = l, e.URI = "Separated", e.Bifunctor = {
    URI: e.URI,
    mapLeft: a,
    bimap: o
  }, e.Functor = {
    URI: e.URI,
    map: i
  }, e.flap = (0, n.flap)(e.Functor);
  var f = function(p) {
    return p.left;
  };
  e.left = f;
  var c = function(p) {
    return p.right;
  };
  e.right = c;
})(c1);
var Jn = {}, lT = X && X.__createBinding || (Object.create ? function(e, t, n, r) {
  r === void 0 && (r = n);
  var i = Object.getOwnPropertyDescriptor(t, n);
  (!i || ("get" in i ? !t.__esModule : i.writable || i.configurable)) && (i = { enumerable: !0, get: function() {
    return t[n];
  } }), Object.defineProperty(e, r, i);
} : function(e, t, n, r) {
  r === void 0 && (r = n), e[r] = t[n];
}), fT = X && X.__setModuleDefault || (Object.create ? function(e, t) {
  Object.defineProperty(e, "default", { enumerable: !0, value: t });
} : function(e, t) {
  e.default = t;
}), cT = X && X.__importStar || function(e) {
  if (e && e.__esModule)
    return e;
  var t = {};
  if (e != null)
    for (var n in e)
      n !== "default" && Object.prototype.hasOwnProperty.call(e, n) && lT(t, e, n);
  return fT(t, e), t;
};
Object.defineProperty(Jn, "__esModule", { value: !0 });
Jn.filterE = Jn.witherDefault = Jn.wiltDefault = void 0;
var lg = cT(Xi);
function dT(e, t) {
  return function(n) {
    var r = e.traverse(n);
    return function(i, a) {
      return n.map(r(i, a), t.separate);
    };
  };
}
Jn.wiltDefault = dT;
function hT(e, t) {
  return function(n) {
    var r = e.traverse(n);
    return function(i, a) {
      return n.map(r(i, a), t.compact);
    };
  };
}
Jn.witherDefault = hT;
function pT(e) {
  return function(t) {
    var n = e.wither(t);
    return function(r) {
      return function(i) {
        return n(i, function(a) {
          return t.map(r(a), function(o) {
            return o ? lg.some(a) : lg.none;
          });
        });
      };
    };
  };
}
Jn.filterE = pT;
(function(e) {
  var t = X && X.__createBinding || (Object.create ? function(w, S, x, U) {
    U === void 0 && (U = x);
    var Q = Object.getOwnPropertyDescriptor(S, x);
    (!Q || ("get" in Q ? !S.__esModule : Q.writable || Q.configurable)) && (Q = { enumerable: !0, get: function() {
      return S[x];
    } }), Object.defineProperty(w, U, Q);
  } : function(w, S, x, U) {
    U === void 0 && (U = x), w[U] = S[x];
  }), n = X && X.__setModuleDefault || (Object.create ? function(w, S) {
    Object.defineProperty(w, "default", { enumerable: !0, value: S });
  } : function(w, S) {
    w.default = S;
  }), r = X && X.__importStar || function(w) {
    if (w && w.__esModule)
      return w;
    var S = {};
    if (w != null)
      for (var x in w)
        x !== "default" && Object.prototype.hasOwnProperty.call(w, x) && t(S, w, x);
    return n(S, w), S;
  };
  Object.defineProperty(e, "__esModule", { value: !0 }), e.match = e.foldW = e.matchW = e.isRight = e.isLeft = e.fromOption = e.fromPredicate = e.FromEither = e.MonadThrow = e.throwError = e.ChainRec = e.Extend = e.extend = e.Alt = e.alt = e.altW = e.Bifunctor = e.mapLeft = e.bimap = e.Traversable = e.sequence = e.traverse = e.Foldable = e.reduceRight = e.foldMap = e.reduce = e.Monad = e.Chain = e.Applicative = e.Apply = e.ap = e.apW = e.Pointed = e.of = e.asUnit = e.as = e.Functor = e.map = e.getAltValidation = e.getApplicativeValidation = e.getWitherable = e.getFilterable = e.getCompactable = e.getSemigroup = e.getEq = e.getShow = e.URI = e.flatMap = e.right = e.left = void 0, e.chainFirstW = e.chainFirst = e.chain = e.chainW = e.sequenceArray = e.traverseArray = e.traverseArrayWithIndex = e.traverseReadonlyArrayWithIndex = e.traverseReadonlyNonEmptyArrayWithIndex = e.ApT = e.apSW = e.apS = e.bindW = e.bind = e.let = e.bindTo = e.Do = e.exists = e.elem = e.toError = e.toUnion = e.chainNullableK = e.fromNullableK = e.tryCatchK = e.tryCatch = e.fromNullable = e.orElse = e.orElseW = e.swap = e.filterOrElseW = e.filterOrElse = e.flatMapOption = e.flatMapNullable = e.liftOption = e.liftNullable = e.chainOptionKW = e.chainOptionK = e.fromOptionK = e.duplicate = e.flatten = e.flattenW = e.tap = e.apSecondW = e.apSecond = e.apFirstW = e.apFirst = e.flap = e.getOrElse = e.getOrElseW = e.fold = void 0, e.getValidation = e.getValidationMonoid = e.getValidationSemigroup = e.getApplyMonoid = e.getApplySemigroup = e.either = e.stringifyJSON = e.parseJSON = void 0;
  var i = ei, a = Be, o = r(wn), s = so, u = xe, l = un, f = Pe, c = r(Xi), p = c1, d = Jn;
  e.left = c.left, e.right = c.right, e.flatMap = (0, l.dual)(2, function(w, S) {
    return (0, e.isLeft)(w) ? w : S(w.right);
  });
  var y = function(w, S) {
    return (0, l.pipe)(w, (0, e.map)(S));
  }, v = function(w, S) {
    return (0, l.pipe)(w, (0, e.ap)(S));
  }, _ = function(w, S, x) {
    return (0, l.pipe)(w, (0, e.reduce)(S, x));
  }, m = function(w) {
    return function(S, x) {
      var U = (0, e.foldMap)(w);
      return (0, l.pipe)(S, U(x));
    };
  }, E = function(w, S, x) {
    return (0, l.pipe)(w, (0, e.reduceRight)(S, x));
  }, L = function(w) {
    var S = (0, e.traverse)(w);
    return function(x, U) {
      return (0, l.pipe)(x, S(U));
    };
  }, I = function(w, S, x) {
    return (0, l.pipe)(w, (0, e.bimap)(S, x));
  }, j = function(w, S) {
    return (0, l.pipe)(w, (0, e.mapLeft)(S));
  }, M = function(w, S) {
    return (0, l.pipe)(w, (0, e.alt)(S));
  }, O = function(w, S) {
    return (0, l.pipe)(w, (0, e.extend)(S));
  }, $ = function(w, S) {
    return (0, s.tailRec)(S(w), function(x) {
      return (0, e.isLeft)(x) ? (0, e.right)((0, e.left)(x.left)) : (0, e.isLeft)(x.right) ? (0, e.left)(S(x.right.left)) : (0, e.right)((0, e.right)(x.right.right));
    });
  };
  e.URI = "Either";
  var q = function(w, S) {
    return {
      show: function(x) {
        return (0, e.isLeft)(x) ? "left(".concat(w.show(x.left), ")") : "right(".concat(S.show(x.right), ")");
      }
    };
  };
  e.getShow = q;
  var N = function(w, S) {
    return {
      equals: function(x, U) {
        return x === U || ((0, e.isLeft)(x) ? (0, e.isLeft)(U) && w.equals(x.left, U.left) : (0, e.isRight)(U) && S.equals(x.right, U.right));
      }
    };
  };
  e.getEq = N;
  var P = function(w) {
    return {
      concat: function(S, x) {
        return (0, e.isLeft)(x) ? S : (0, e.isLeft)(S) ? x : (0, e.right)(w.concat(S.right, x.right));
      }
    };
  };
  e.getSemigroup = P;
  var H = function(w) {
    var S = (0, e.left)(w.empty);
    return {
      URI: e.URI,
      _E: void 0,
      compact: function(x) {
        return (0, e.isLeft)(x) ? x : x.right._tag === "None" ? S : (0, e.right)(x.right.value);
      },
      separate: function(x) {
        return (0, e.isLeft)(x) ? (0, p.separated)(x, x) : (0, e.isLeft)(x.right) ? (0, p.separated)((0, e.right)(x.right.left), S) : (0, p.separated)(S, (0, e.right)(x.right.right));
      }
    };
  };
  e.getCompactable = H;
  var G = function(w) {
    var S = (0, e.left)(w.empty), x = (0, e.getCompactable)(w), U = x.compact, Q = x.separate, oe = function(J, $t) {
      return (0, e.isLeft)(J) || $t(J.right) ? J : S;
    }, be = function(J, $t) {
      return (0, e.isLeft)(J) ? (0, p.separated)(J, J) : $t(J.right) ? (0, p.separated)(S, (0, e.right)(J.right)) : (0, p.separated)((0, e.right)(J.right), S);
    };
    return {
      URI: e.URI,
      _E: void 0,
      map: y,
      compact: U,
      separate: Q,
      filter: oe,
      filterMap: function(J, $t) {
        if ((0, e.isLeft)(J))
          return J;
        var Vt = $t(J.right);
        return Vt._tag === "None" ? S : (0, e.right)(Vt.value);
      },
      partition: be,
      partitionMap: function(J, $t) {
        if ((0, e.isLeft)(J))
          return (0, p.separated)(J, J);
        var Vt = $t(J.right);
        return (0, e.isLeft)(Vt) ? (0, p.separated)((0, e.right)(Vt.left), S) : (0, p.separated)(S, (0, e.right)(Vt.right));
      }
    };
  };
  e.getFilterable = G;
  var z = function(w) {
    var S = (0, e.getFilterable)(w), x = (0, e.getCompactable)(w);
    return {
      URI: e.URI,
      _E: void 0,
      map: y,
      compact: S.compact,
      separate: S.separate,
      filter: S.filter,
      filterMap: S.filterMap,
      partition: S.partition,
      partitionMap: S.partitionMap,
      traverse: L,
      sequence: e.sequence,
      reduce: _,
      foldMap: m,
      reduceRight: E,
      wither: (0, d.witherDefault)(e.Traversable, x),
      wilt: (0, d.wiltDefault)(e.Traversable, x)
    };
  };
  e.getWitherable = z;
  var W = function(w) {
    return {
      URI: e.URI,
      _E: void 0,
      map: y,
      ap: function(S, x) {
        return (0, e.isLeft)(S) ? (0, e.isLeft)(x) ? (0, e.left)(w.concat(S.left, x.left)) : S : (0, e.isLeft)(x) ? x : (0, e.right)(S.right(x.right));
      },
      of: e.of
    };
  };
  e.getApplicativeValidation = W;
  var se = function(w) {
    return {
      URI: e.URI,
      _E: void 0,
      map: y,
      alt: function(S, x) {
        if ((0, e.isRight)(S))
          return S;
        var U = x();
        return (0, e.isLeft)(U) ? (0, e.left)(w.concat(S.left, U.left)) : U;
      }
    };
  };
  e.getAltValidation = se;
  var ge = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? S : (0, e.right)(w(S.right));
    };
  };
  e.map = ge, e.Functor = {
    URI: e.URI,
    map: y
  }, e.as = (0, l.dual)(2, (0, f.as)(e.Functor)), e.asUnit = (0, f.asUnit)(e.Functor), e.of = e.right, e.Pointed = {
    URI: e.URI,
    of: e.of
  };
  var re = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? S : (0, e.isLeft)(w) ? w : (0, e.right)(S.right(w.right));
    };
  };
  e.apW = re, e.ap = e.apW, e.Apply = {
    URI: e.URI,
    map: y,
    ap: v
  }, e.Applicative = {
    URI: e.URI,
    map: y,
    ap: v,
    of: e.of
  }, e.Chain = {
    URI: e.URI,
    map: y,
    ap: v,
    chain: e.flatMap
  }, e.Monad = {
    URI: e.URI,
    map: y,
    ap: v,
    of: e.of,
    chain: e.flatMap
  };
  var ke = function(w, S) {
    return function(x) {
      return (0, e.isLeft)(x) ? w : S(w, x.right);
    };
  };
  e.reduce = ke;
  var B = function(w) {
    return function(S) {
      return function(x) {
        return (0, e.isLeft)(x) ? w.empty : S(x.right);
      };
    };
  };
  e.foldMap = B;
  var te = function(w, S) {
    return function(x) {
      return (0, e.isLeft)(x) ? w : S(x.right, w);
    };
  };
  e.reduceRight = te, e.Foldable = {
    URI: e.URI,
    reduce: _,
    foldMap: m,
    reduceRight: E
  };
  var ye = function(w) {
    return function(S) {
      return function(x) {
        return (0, e.isLeft)(x) ? w.of((0, e.left)(x.left)) : w.map(S(x.right), e.right);
      };
    };
  };
  e.traverse = ye;
  var gt = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? w.of((0, e.left)(S.left)) : w.map(S.right, e.right);
    };
  };
  e.sequence = gt, e.Traversable = {
    URI: e.URI,
    map: y,
    reduce: _,
    foldMap: m,
    reduceRight: E,
    traverse: L,
    sequence: e.sequence
  };
  var yt = function(w, S) {
    return function(x) {
      return (0, e.isLeft)(x) ? (0, e.left)(w(x.left)) : (0, e.right)(S(x.right));
    };
  };
  e.bimap = yt;
  var Ze = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? (0, e.left)(w(S.left)) : S;
    };
  };
  e.mapLeft = Ze, e.Bifunctor = {
    URI: e.URI,
    bimap: I,
    mapLeft: j
  };
  var Gt = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? w() : S;
    };
  };
  e.altW = Gt, e.alt = e.altW, e.Alt = {
    URI: e.URI,
    map: y,
    alt: M
  };
  var st = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? S : (0, e.right)(w(S));
    };
  };
  e.extend = st, e.Extend = {
    URI: e.URI,
    map: y,
    extend: O
  }, e.ChainRec = {
    URI: e.URI,
    map: y,
    ap: v,
    chain: e.flatMap,
    chainRec: $
  }, e.throwError = e.left, e.MonadThrow = {
    URI: e.URI,
    map: y,
    ap: v,
    of: e.of,
    chain: e.flatMap,
    throwError: e.throwError
  }, e.FromEither = {
    URI: e.URI,
    fromEither: l.identity
  }, e.fromPredicate = (0, u.fromPredicate)(e.FromEither), e.fromOption = /* @__PURE__ */ (0, u.fromOption)(e.FromEither), e.isLeft = c.isLeft, e.isRight = c.isRight;
  var Fe = function(w, S) {
    return function(x) {
      return (0, e.isLeft)(x) ? w(x.left) : S(x.right);
    };
  };
  e.matchW = Fe, e.foldW = e.matchW, e.match = e.matchW, e.fold = e.match;
  var He = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? w(S.left) : S.right;
    };
  };
  e.getOrElseW = He, e.getOrElse = e.getOrElseW, e.flap = (0, f.flap)(e.Functor), e.apFirst = (0, a.apFirst)(e.Apply), e.apFirstW = e.apFirst, e.apSecond = (0, a.apSecond)(e.Apply), e.apSecondW = e.apSecond, e.tap = (0, l.dual)(2, o.tap(e.Chain)), e.flattenW = /* @__PURE__ */ (0, e.flatMap)(l.identity), e.flatten = e.flattenW, e.duplicate = (0, e.extend)(l.identity), e.fromOptionK = /* @__PURE__ */ (0, u.fromOptionK)(e.FromEither), e.chainOptionK = (0, u.chainOptionK)(e.FromEither, e.Chain), e.chainOptionKW = e.chainOptionK;
  var ut = {
    fromEither: e.FromEither.fromEither
  };
  e.liftNullable = c.liftNullable(ut), e.liftOption = c.liftOption(ut);
  var vt = {
    flatMap: e.flatMap
  };
  e.flatMapNullable = c.flatMapNullable(ut, vt), e.flatMapOption = c.flatMapOption(ut, vt), e.filterOrElse = (0, u.filterOrElse)(e.FromEither, e.Chain), e.filterOrElseW = e.filterOrElse;
  var _t = function(w) {
    return (0, e.isLeft)(w) ? (0, e.right)(w.left) : (0, e.left)(w.right);
  };
  e.swap = _t;
  var C = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? w(S.left) : S;
    };
  };
  e.orElseW = C, e.orElse = e.orElseW;
  var D = function(w) {
    return function(S) {
      return S == null ? (0, e.left)(w) : (0, e.right)(S);
    };
  };
  e.fromNullable = D;
  var k = function(w, S) {
    try {
      return (0, e.right)(w());
    } catch (x) {
      return (0, e.left)(S(x));
    }
  };
  e.tryCatch = k;
  var V = function(w, S) {
    return function() {
      for (var x = [], U = 0; U < arguments.length; U++)
        x[U] = arguments[U];
      return (0, e.tryCatch)(function() {
        return w.apply(void 0, x);
      }, S);
    };
  };
  e.tryCatchK = V;
  var F = function(w) {
    var S = (0, e.fromNullable)(w);
    return function(x) {
      return (0, l.flow)(x, S);
    };
  };
  e.fromNullableK = F;
  var ne = function(w) {
    var S = (0, e.fromNullableK)(w);
    return function(x) {
      return (0, e.flatMap)(S(x));
    };
  };
  e.chainNullableK = ne, e.toUnion = (0, e.foldW)(l.identity, l.identity);
  function ie(w) {
    return w instanceof Error ? w : new Error(String(w));
  }
  e.toError = ie;
  function Ce(w) {
    return function(S, x) {
      if (x === void 0) {
        var U = Ce(w);
        return function(Q) {
          return U(S, Q);
        };
      }
      return (0, e.isLeft)(x) ? !1 : w.equals(S, x.right);
    };
  }
  e.elem = Ce;
  var De = function(w) {
    return function(S) {
      return (0, e.isLeft)(S) ? !1 : w(S.right);
    };
  };
  e.exists = De, e.Do = (0, e.of)(c.emptyRecord), e.bindTo = (0, f.bindTo)(e.Functor);
  var _e = /* @__PURE__ */ (0, f.let)(e.Functor);
  e.let = _e, e.bind = o.bind(e.Chain), e.bindW = e.bind, e.apS = (0, a.apS)(e.Apply), e.apSW = e.apS, e.ApT = (0, e.of)(c.emptyReadonlyArray);
  var de = function(w) {
    return function(S) {
      var x = w(0, c.head(S));
      if ((0, e.isLeft)(x))
        return x;
      for (var U = [x.right], Q = 1; Q < S.length; Q++) {
        var oe = w(Q, S[Q]);
        if ((0, e.isLeft)(oe))
          return oe;
        U.push(oe.right);
      }
      return (0, e.right)(U);
    };
  };
  e.traverseReadonlyNonEmptyArrayWithIndex = de;
  var Ot = function(w) {
    var S = (0, e.traverseReadonlyNonEmptyArrayWithIndex)(w);
    return function(x) {
      return c.isNonEmpty(x) ? S(x) : e.ApT;
    };
  };
  e.traverseReadonlyArrayWithIndex = Ot, e.traverseArrayWithIndex = e.traverseReadonlyArrayWithIndex;
  var R = function(w) {
    return (0, e.traverseReadonlyArrayWithIndex)(function(S, x) {
      return w(x);
    });
  };
  e.traverseArray = R, e.sequenceArray = /* @__PURE__ */ (0, e.traverseArray)(l.identity), e.chainW = e.flatMap, e.chain = e.flatMap, e.chainFirst = e.tap, e.chainFirstW = e.tap;
  function T(w, S) {
    return (0, e.tryCatch)(function() {
      return JSON.parse(w);
    }, S);
  }
  e.parseJSON = T;
  var b = function(w, S) {
    return (0, e.tryCatch)(function() {
      var x = JSON.stringify(w);
      if (typeof x != "string")
        throw new Error("Converting unsupported structure to JSON");
      return x;
    }, S);
  };
  e.stringifyJSON = b, e.either = {
    URI: e.URI,
    map: y,
    of: e.of,
    ap: v,
    chain: e.flatMap,
    reduce: _,
    foldMap: m,
    reduceRight: E,
    traverse: L,
    sequence: e.sequence,
    bimap: I,
    mapLeft: j,
    alt: M,
    extend: O,
    chainRec: $,
    throwError: e.throwError
  }, e.getApplySemigroup = /* @__PURE__ */ (0, a.getApplySemigroup)(e.Apply), e.getApplyMonoid = /* @__PURE__ */ (0, i.getApplicativeMonoid)(e.Applicative);
  var h = function(w, S) {
    return (0, a.getApplySemigroup)((0, e.getApplicativeValidation)(w))(S);
  };
  e.getValidationSemigroup = h;
  var g = function(w, S) {
    return (0, i.getApplicativeMonoid)((0, e.getApplicativeValidation)(w))(S);
  };
  e.getValidationMonoid = g;
  function A(w) {
    var S = (0, e.getApplicativeValidation)(w).ap, x = (0, e.getAltValidation)(w).alt;
    return {
      URI: e.URI,
      _E: void 0,
      map: y,
      of: e.of,
      chain: e.flatMap,
      bimap: I,
      mapLeft: j,
      reduce: _,
      foldMap: m,
      reduceRight: E,
      extend: O,
      traverse: L,
      sequence: e.sequence,
      chainRec: $,
      throwError: e.throwError,
      ap: S,
      alt: x
    };
  }
  e.getValidation = A;
})(Bh);
function gT(e) {
  var t = Object.create(Object.getPrototypeOf(e));
  return Object.assign(t, e), t;
}
function yT(e, t, n) {
  n === void 0 && (n = e.name);
  var r = gT(e);
  return r.validate = t, r.decode = function(i) {
    return t(i, Qw(r));
  }, r.name = n, r;
}
function vT(e, t, n) {
  return n === void 0 && (n = "fromNullable(" + e.name + ")"), yT(e, function(r, i) {
    return r == null ? pe(t) : e.validate(r, i);
  }, n);
}
const hr = new le("UrlFromString", (e) => e instanceof URL, (e, t) => un.pipe(Re.validate(e, t), Bh.chain((n) => {
  try {
    const r = new URL(n);
    return pe(r);
  } catch {
    return Le(e, t);
  }
})), String), Fa = new le("DateFromUnknown", (e) => e instanceof Date, (e, t) => {
  if (typeof e == "string" || typeof e == "number" || e instanceof Date) {
    const n = new Date(e);
    if (!isNaN(n.getTime()))
      return pe(n);
  }
  return Le(e, t, "Invalid date-like input");
}, (e) => e.toISOString());
function Ur(e) {
  return vT(mr(e), []);
}
function uo(e) {
  const [t, n, ...r] = e;
  if (!t)
    throw "Never use arrayOrOneOf with zero options!";
  if (!n) {
    const a = Hn(t);
    return tn([a, mr(a)]);
  }
  const i = tn([Hn(t), Hn(n), ...r.map((a) => Hn(a))]);
  return tn([mr(i), i]);
}
const _T = {
  "@context": n1(Hn("https://w3id.org/docmaps/context.jsonld"), "https://w3id.org/docmaps/context.jsonld")
}, bT = sn([
  rt({
    id: Re
  }),
  on({
    service: hr
  })
]), mT = sn([
  rt({}),
  on({
    id: Re,
    logo: hr,
    name: Re,
    homepage: hr,
    url: hr,
    account: bT
  })
]), wT = sn([
  rt({
    type: uo([
      "web-page",
      "web-content"
    ])
  }),
  on({
    id: Re,
    service: hr,
    url: hr
  })
]), ET = tn([
  rt({
    type: uo(["person"]),
    name: Re
  }),
  Mh
]), ST = sn([
  rt({
    actor: ET,
    role: Re
  }),
  on({
    id: Re,
    type: tn([Hn("pro:roleintime"), Hn("pro:RoleInTime")])
  })
]), AT = uo([
  "review",
  "preprint",
  "evaluation-summary",
  "review",
  "review-article",
  "journal-article",
  "editorial",
  "comment",
  "reply"
]), Wd = sn([
  rt({}),
  on({
    published: Fa,
    id: Re,
    doi: Re,
    type: AT,
    url: hr,
    content: mr(wT)
  })
]), RT = sn([
  rt({
    outputs: Ur(Wd),
    inputs: Ur(Wd),
    participants: Ur(ST)
  }),
  on({
    id: Re
  })
]), IT = Re, TT = sn([
  rt({
    item: Mh,
    status: IT
  }),
  on({
    happened: Fa
  })
]), xT = sn([
  rt({
    actions: Ur(RT),
    assertions: Ur(TT)
  }),
  on({
    inputs: Ur(Wd),
    id: Re,
    "next-step": tn([Re, ja]),
    "previous-step": tn([Re, ja])
  })
]), NT = sn([
  rt({
    ..._T,
    id: Re,
    type: uo(["docmap", "Docmap", "https://w3id.org/docmaps/v0/Docmap"]),
    publisher: mT,
    created: Fa
  }),
  on({
    steps: jh(Re, xT),
    "first-step": Re,
    updated: Fa
  })
]);
var d1 = {}, Gh = class h1 {
  /**
   * Creates a new IdentifierIssuer. A IdentifierIssuer issues unique
   * identifiers, keeping track of any previously issued identifiers.
   *
   * @param prefix the prefix to use ('<prefix><counter>').
   * @param existing an existing Map to use.
   * @param counter the counter to use.
   */
  constructor(t, n = /* @__PURE__ */ new Map(), r = 0) {
    this.prefix = t, this._existing = n, this.counter = r;
  }
  /**
   * Copies this IdentifierIssuer.
   *
   * @return a copy of this IdentifierIssuer.
   */
  clone() {
    const { prefix: t, _existing: n, counter: r } = this;
    return new h1(t, new Map(n), r);
  }
  /**
   * Gets the new identifier for the given old identifier, where if no old
   * identifier is given a new identifier will be generated.
   *
   * @param [old] the old identifier to get the new identifier for.
   *
   * @return the new identifier.
   */
  getId(t) {
    const n = t && this._existing.get(t);
    if (n)
      return n;
    const r = this.prefix + this.counter;
    return this.counter++, t && this._existing.set(t, r), r;
  }
  /**
   * Returns true if the given old identifer has already been assigned a new
   * identifier.
   *
   * @param old the old identifier to check.
   *
   * @return true if the old identifier has been assigned a new identifier,
   *   false if not.
   */
  hasId(t) {
    return this._existing.has(t);
  }
  /**
   * Returns all of the IDs that have been issued new IDs in the order in
   * which they were issued new IDs.
   *
   * @return the list of old IDs that has been issued new IDs in order.
   */
  getOldIds() {
    return [...this._existing.keys()];
  }
};
(function(e, t) {
  if (e.setImmediate)
    return;
  var n = 1, r = {}, i = !1, a = e.document, o;
  function s(E) {
    typeof E != "function" && (E = new Function("" + E));
    for (var L = new Array(arguments.length - 1), I = 0; I < L.length; I++)
      L[I] = arguments[I + 1];
    var j = { callback: E, args: L };
    return r[n] = j, o(n), n++;
  }
  function u(E) {
    delete r[E];
  }
  function l(E) {
    var L = E.callback, I = E.args;
    switch (I.length) {
      case 0:
        L();
        break;
      case 1:
        L(I[0]);
        break;
      case 2:
        L(I[0], I[1]);
        break;
      case 3:
        L(I[0], I[1], I[2]);
        break;
      default:
        L.apply(t, I);
        break;
    }
  }
  function f(E) {
    if (i)
      setTimeout(f, 0, E);
    else {
      var L = r[E];
      if (L) {
        i = !0;
        try {
          l(L);
        } finally {
          u(E), i = !1;
        }
      }
    }
  }
  function c() {
    o = function(E) {
      process.nextTick(function() {
        f(E);
      });
    };
  }
  function p() {
    if (e.postMessage && !e.importScripts) {
      var E = !0, L = e.onmessage;
      return e.onmessage = function() {
        E = !1;
      }, e.postMessage("", "*"), e.onmessage = L, E;
    }
  }
  function d() {
    var E = "setImmediate$" + Math.random() + "$", L = function(I) {
      I.source === e && typeof I.data == "string" && I.data.indexOf(E) === 0 && f(+I.data.slice(E.length));
    };
    e.addEventListener ? e.addEventListener("message", L, !1) : e.attachEvent("onmessage", L), o = function(I) {
      e.postMessage(E + I, "*");
    };
  }
  function y() {
    var E = new MessageChannel();
    E.port1.onmessage = function(L) {
      var I = L.data;
      f(I);
    }, o = function(L) {
      E.port2.postMessage(L);
    };
  }
  function v() {
    var E = a.documentElement;
    o = function(L) {
      var I = a.createElement("script");
      I.onreadystatechange = function() {
        f(L), I.onreadystatechange = null, E.removeChild(I), I = null;
      }, E.appendChild(I);
    };
  }
  function _() {
    o = function(E) {
      setTimeout(f, 0, E);
    };
  }
  var m = Object.getPrototypeOf && Object.getPrototypeOf(e);
  m = m && m.setTimeout ? m : e, {}.toString.call(e.process) === "[object process]" ? c() : p() ? d() : e.MessageChannel ? y() : a && "onreadystatechange" in a.createElement("script") ? v() : _(), m.setImmediate = s, m.clearImmediate = u;
})(typeof self > "u" ? X : self);
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
const ss = self.crypto || self.msCrypto;
var lo = class {
  /**
   * Creates a new MessageDigest.
   *
   * @param algorithm the algorithm to use.
   */
  constructor(t) {
    if (!(ss && ss.subtle))
      throw new Error("crypto.subtle not found.");
    if (t === "sha256")
      this.algorithm = { name: "SHA-256" };
    else if (t === "sha1")
      this.algorithm = { name: "SHA-1" };
    else
      throw new Error(`Unsupported algorithm "${t}".`);
    this._content = "";
  }
  update(t) {
    this._content += t;
  }
  async digest() {
    const t = new TextEncoder().encode(this._content), n = new Uint8Array(
      await ss.subtle.digest(this.algorithm, t)
    );
    let r = "";
    for (let i = 0; i < n.length; ++i)
      r += n[i].toString(16).padStart(2, "0");
    return r;
  }
};
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
var p1 = class {
  /**
   * A Permuter iterates over all possible permutations of the given array
   * of elements.
   *
   * @param list the array of elements to iterate over.
   */
  constructor(t) {
    this.current = t.sort(), this.done = !1, this.dir = /* @__PURE__ */ new Map();
    for (let n = 0; n < t.length; ++n)
      this.dir.set(t[n], !0);
  }
  /**
   * Returns true if there is another permutation.
   *
   * @return true if there is another permutation, false if not.
   */
  hasNext() {
    return !this.done;
  }
  /**
   * Gets the next permutation. Call hasNext() to ensure there is another one
   * first.
   *
   * @return the next permutation.
   */
  next() {
    const { current: t, dir: n } = this, r = t.slice();
    let i = null, a = 0;
    const o = t.length;
    for (let s = 0; s < o; ++s) {
      const u = t[s], l = n.get(u);
      (i === null || u > i) && (l && s > 0 && u > t[s - 1] || !l && s < o - 1 && u > t[s + 1]) && (i = u, a = s);
    }
    if (i === null)
      this.done = !0;
    else {
      const s = n.get(i) ? a - 1 : a + 1;
      t[a] = t[s], t[s] = i;
      for (const u of t)
        u > i && n.set(u, !n.get(u));
    }
    return r;
  }
};
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
const OT = "http://www.w3.org/1999/02/22-rdf-syntax-ns#", us = OT + "langString", ls = "http://www.w3.org/2001/XMLSchema#string", Lt = "NamedNode", rr = "BlankNode", wa = "Literal", fg = "DefaultGraph", Hr = {};
(() => {
  const e = "(?:<([^:]+:[^>]*)>)", n = "A-Za-zÀ-ÖØ-öø-˿Ͱ-ͽͿ-῿‌-‍⁰-↏Ⰰ-⿯、-퟿豈-﷏ﷰ-�" + "_", r = n + "0-9-·̀-ͯ‿-⁀", a = "(_:(?:[" + n + "0-9])(?:(?:[" + r + ".])*(?:[" + r + "]))?)", o = '"([^"\\\\]*(?:\\\\.[^"\\\\]*)*)"', s = "(?:\\^\\^" + e + ")", u = "(?:@([a-zA-Z]+(?:-[a-zA-Z0-9]+)*))", l = "(?:" + o + "(?:" + s + "|" + u + ")?)", f = "[ \\t]+", c = "[ \\t]*", p = "(?:" + e + "|" + a + ")" + f, d = e + f, y = "(?:" + e + "|" + a + "|" + l + ")" + c, v = "(?:\\.|(?:(?:" + e + "|" + a + ")" + c + "\\.))";
  Hr.eoln = /(?:\r\n)|(?:\n)|(?:\r)/g, Hr.empty = new RegExp("^" + c + "$"), Hr.quad = new RegExp(
    "^" + c + p + d + y + v + c + "$"
  );
})();
var Vh = class Ea {
  /**
   * Parses RDF in the form of N-Quads.
   *
   * @param input the N-Quads input to parse.
   *
   * @return an RDF dataset (an array of quads per http://rdf.js.org/).
   */
  static parse(t) {
    const n = [], r = {}, i = t.split(Hr.eoln);
    let a = 0;
    for (const o of i) {
      if (a++, Hr.empty.test(o))
        continue;
      const s = o.match(Hr.quad);
      if (s === null)
        throw new Error("N-Quads parse error on line " + a + ".");
      const u = { subject: null, predicate: null, object: null, graph: null };
      if (s[1] !== void 0 ? u.subject = { termType: Lt, value: s[1] } : u.subject = { termType: rr, value: s[2] }, u.predicate = { termType: Lt, value: s[3] }, s[4] !== void 0 ? u.object = { termType: Lt, value: s[4] } : s[5] !== void 0 ? u.object = { termType: rr, value: s[5] } : (u.object = {
        termType: wa,
        value: void 0,
        datatype: {
          termType: Lt
        }
      }, s[7] !== void 0 ? u.object.datatype.value = s[7] : s[8] !== void 0 ? (u.object.datatype.value = us, u.object.language = s[8]) : u.object.datatype.value = ls, u.object.value = DT(s[6])), s[9] !== void 0 ? u.graph = {
        termType: Lt,
        value: s[9]
      } : s[10] !== void 0 ? u.graph = {
        termType: rr,
        value: s[10]
      } : u.graph = {
        termType: fg,
        value: ""
      }, !(u.graph.value in r))
        r[u.graph.value] = [u], n.push(u);
      else {
        let l = !0;
        const f = r[u.graph.value];
        for (const c of f)
          if ($T(c, u)) {
            l = !1;
            break;
          }
        l && (f.push(u), n.push(u));
      }
    }
    return n;
  }
  /**
   * Converts an RDF dataset to N-Quads.
   *
   * @param dataset (array of quads) the RDF dataset to convert.
   *
   * @return the N-Quads string.
   */
  static serialize(t) {
    Array.isArray(t) || (t = Ea.legacyDatasetToQuads(t));
    const n = [];
    for (const r of t)
      n.push(Ea.serializeQuad(r));
    return n.sort().join("");
  }
  /**
   * Converts RDF quad components to an N-Quad string (a single quad).
   *
   * @param {Object} s - N-Quad subject component.
   * @param {Object} p - N-Quad predicate component.
   * @param {Object} o - N-Quad object component.
   * @param {Object} g - N-Quad graph component.
   *
   * @return {string} the N-Quad.
   */
  static serializeQuadComponents(t, n, r, i) {
    let a = "";
    return t.termType === Lt ? a += `<${t.value}>` : a += `${t.value}`, a += ` <${n.value}> `, r.termType === Lt ? a += `<${r.value}>` : r.termType === rr ? a += r.value : (a += `"${MT(r.value)}"`, r.datatype.value === us ? r.language && (a += `@${r.language}`) : r.datatype.value !== ls && (a += `^^<${r.datatype.value}>`)), i.termType === Lt ? a += ` <${i.value}>` : i.termType === rr && (a += ` ${i.value}`), a += ` .
`, a;
  }
  /**
   * Converts an RDF quad to an N-Quad string (a single quad).
   *
   * @param quad the RDF quad convert.
   *
   * @return the N-Quad string.
   */
  static serializeQuad(t) {
    return Ea.serializeQuadComponents(
      t.subject,
      t.predicate,
      t.object,
      t.graph
    );
  }
  /**
   * Converts a legacy-formatted dataset to an array of quads dataset per
   * http://rdf.js.org/.
   *
   * @param dataset the legacy dataset to convert.
   *
   * @return the array of quads dataset.
   */
  static legacyDatasetToQuads(t) {
    const n = [], r = {
      "blank node": rr,
      IRI: Lt,
      literal: wa
    };
    for (const i in t)
      t[i].forEach((o) => {
        const s = {};
        for (const u in o) {
          const l = o[u], f = {
            termType: r[l.type],
            value: l.value
          };
          f.termType === wa && (f.datatype = {
            termType: Lt
          }, "datatype" in l && (f.datatype.value = l.datatype), "language" in l ? ("datatype" in l || (f.datatype.value = us), f.language = l.language) : "datatype" in l || (f.datatype.value = ls)), s[u] = f;
        }
        i === "@default" ? s.graph = {
          termType: fg,
          value: ""
        } : s.graph = {
          termType: i.startsWith("_:") ? rr : Lt,
          value: i
        }, n.push(s);
      });
    return n;
  }
};
function $T(e, t) {
  return !(e.subject.termType === t.subject.termType && e.object.termType === t.object.termType) || !(e.subject.value === t.subject.value && e.predicate.value === t.predicate.value && e.object.value === t.object.value) ? !1 : e.object.termType !== wa ? !0 : e.object.datatype.termType === t.object.datatype.termType && e.object.language === t.object.language && e.object.datatype.value === t.object.datatype.value;
}
const LT = /["\\\n\r]/g;
function MT(e) {
  return e.replace(LT, function(t) {
    switch (t) {
      case '"':
        return '\\"';
      case "\\":
        return "\\\\";
      case `
`:
        return "\\n";
      case "\r":
        return "\\r";
    }
  });
}
const CT = /(?:\\([tbnrf"'\\]))|(?:\\u([0-9A-Fa-f]{4}))|(?:\\U([0-9A-Fa-f]{8}))/g;
function DT(e) {
  return e.replace(CT, function(t, n, r, i) {
    if (n)
      switch (n) {
        case "t":
          return "	";
        case "b":
          return "\b";
        case "n":
          return `
`;
        case "r":
          return "\r";
        case "f":
          return "\f";
        case '"':
          return '"';
        case "'":
          return "'";
        case "\\":
          return "\\";
      }
    if (r)
      return String.fromCharCode(parseInt(r, 16));
    if (i)
      throw new Error("Unsupported U escape");
  });
}
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
const cg = Gh, qT = lo, jT = p1, dg = Vh;
var g1 = class {
  constructor({
    createMessageDigest: t = () => new qT("sha256"),
    maxDeepIterations: n = 1 / 0
  } = {}) {
    this.name = "URDNA2015", this.blankNodeInfo = /* @__PURE__ */ new Map(), this.canonicalIssuer = new cg("_:c14n"), this.createMessageDigest = t, this.maxDeepIterations = n, this.quads = null, this.deepIterations = null;
  }
  // 4.4) Normalization Algorithm
  async main(t) {
    this.deepIterations = /* @__PURE__ */ new Map(), this.quads = t;
    for (const u of t)
      this._addBlankNodeQuadInfo({ quad: u, component: u.subject }), this._addBlankNodeQuadInfo({ quad: u, component: u.object }), this._addBlankNodeQuadInfo({ quad: u, component: u.graph });
    const n = /* @__PURE__ */ new Map(), r = [...this.blankNodeInfo.keys()];
    let i = 0;
    for (const u of r)
      ++i % 100 === 0 && await this._yield(), await this._hashAndTrackBlankNode({ id: u, hashToBlankNodes: n });
    const a = [...n.keys()].sort(), o = [];
    for (const u of a) {
      const l = n.get(u);
      if (l.length > 1) {
        o.push(l);
        continue;
      }
      const f = l[0];
      this.canonicalIssuer.getId(f);
    }
    for (const u of o) {
      const l = [];
      for (const f of u) {
        if (this.canonicalIssuer.hasId(f))
          continue;
        const c = new cg("_:b");
        c.getId(f);
        const p = await this.hashNDegreeQuads(f, c);
        l.push(p);
      }
      l.sort(PT);
      for (const f of l) {
        const c = f.issuer.getOldIds();
        for (const p of c)
          this.canonicalIssuer.getId(p);
      }
    }
    const s = [];
    for (const u of this.quads) {
      const l = dg.serializeQuadComponents(
        this._componentWithCanonicalId(u.subject),
        u.predicate,
        this._componentWithCanonicalId(u.object),
        this._componentWithCanonicalId(u.graph)
      );
      s.push(l);
    }
    return s.sort(), s.join("");
  }
  // 4.6) Hash First Degree Quads
  async hashFirstDegreeQuads(t) {
    const n = [], r = this.blankNodeInfo.get(t), i = r.quads;
    for (const o of i) {
      const s = {
        subject: null,
        predicate: o.predicate,
        object: null,
        graph: null
      };
      s.subject = this.modifyFirstDegreeComponent(
        t,
        o.subject,
        "subject"
      ), s.object = this.modifyFirstDegreeComponent(
        t,
        o.object,
        "object"
      ), s.graph = this.modifyFirstDegreeComponent(
        t,
        o.graph,
        "graph"
      ), n.push(dg.serializeQuad(s));
    }
    n.sort();
    const a = this.createMessageDigest();
    for (const o of n)
      a.update(o);
    return r.hash = await a.digest(), r.hash;
  }
  // 4.7) Hash Related Blank Node
  async hashRelatedBlankNode(t, n, r, i) {
    let a;
    this.canonicalIssuer.hasId(t) ? a = this.canonicalIssuer.getId(t) : r.hasId(t) ? a = r.getId(t) : a = this.blankNodeInfo.get(t).hash;
    const o = this.createMessageDigest();
    return o.update(i), i !== "g" && o.update(this.getRelatedPredicate(n)), o.update(a), o.digest();
  }
  // 4.8) Hash N-Degree Quads
  async hashNDegreeQuads(t, n) {
    const r = this.deepIterations.get(t) || 0;
    if (r > this.maxDeepIterations)
      throw new Error(
        `Maximum deep iterations (${this.maxDeepIterations}) exceeded.`
      );
    this.deepIterations.set(t, r + 1);
    const i = this.createMessageDigest(), a = await this.createHashToRelated(t, n), o = [...a.keys()].sort();
    for (const s of o) {
      i.update(s);
      let u = "", l;
      const f = new jT(a.get(s));
      let c = 0;
      for (; f.hasNext(); ) {
        const p = f.next();
        ++c % 3 === 0 && await this._yield();
        let d = n.clone(), y = "";
        const v = [];
        let _ = !1;
        for (const m of p)
          if (this.canonicalIssuer.hasId(m) ? y += this.canonicalIssuer.getId(m) : (d.hasId(m) || v.push(m), y += d.getId(m)), u.length !== 0 && y > u) {
            _ = !0;
            break;
          }
        if (!_) {
          for (const m of v) {
            const E = await this.hashNDegreeQuads(m, d);
            if (y += d.getId(m), y += `<${E.hash}>`, d = E.issuer, u.length !== 0 && y > u) {
              _ = !0;
              break;
            }
          }
          _ || (u.length === 0 || y < u) && (u = y, l = d);
        }
      }
      i.update(u), n = l;
    }
    return { hash: await i.digest(), issuer: n };
  }
  // helper for modifying component during Hash First Degree Quads
  modifyFirstDegreeComponent(t, n) {
    return n.termType !== "BlankNode" ? n : {
      termType: "BlankNode",
      value: n.value === t ? "_:a" : "_:z"
    };
  }
  // helper for getting a related predicate
  getRelatedPredicate(t) {
    return `<${t.predicate.value}>`;
  }
  // helper for creating hash to related blank nodes map
  async createHashToRelated(t, n) {
    const r = /* @__PURE__ */ new Map(), i = this.blankNodeInfo.get(t).quads;
    let a = 0;
    for (const o of i)
      ++a % 100 === 0 && await this._yield(), await Promise.all([
        this._addRelatedBlankNodeHash({
          quad: o,
          component: o.subject,
          position: "s",
          id: t,
          issuer: n,
          hashToRelated: r
        }),
        this._addRelatedBlankNodeHash({
          quad: o,
          component: o.object,
          position: "o",
          id: t,
          issuer: n,
          hashToRelated: r
        }),
        this._addRelatedBlankNodeHash({
          quad: o,
          component: o.graph,
          position: "g",
          id: t,
          issuer: n,
          hashToRelated: r
        })
      ]);
    return r;
  }
  async _hashAndTrackBlankNode({ id: t, hashToBlankNodes: n }) {
    const r = await this.hashFirstDegreeQuads(t), i = n.get(r);
    i ? i.push(t) : n.set(r, [t]);
  }
  _addBlankNodeQuadInfo({ quad: t, component: n }) {
    if (n.termType !== "BlankNode")
      return;
    const r = n.value, i = this.blankNodeInfo.get(r);
    i ? i.quads.add(t) : this.blankNodeInfo.set(r, { quads: /* @__PURE__ */ new Set([t]), hash: null });
  }
  async _addRelatedBlankNodeHash({ quad: t, component: n, position: r, id: i, issuer: a, hashToRelated: o }) {
    if (!(n.termType === "BlankNode" && n.value !== i))
      return;
    const s = n.value, u = await this.hashRelatedBlankNode(
      s,
      t,
      a,
      r
    ), l = o.get(u);
    l ? l.push(s) : o.set(u, [s]);
  }
  // canonical ids for 7.1
  _componentWithCanonicalId(t) {
    return t.termType === "BlankNode" && !t.value.startsWith(this.canonicalIssuer.prefix) ? {
      termType: "BlankNode",
      value: this.canonicalIssuer.getId(t.value)
    } : t;
  }
  async _yield() {
    return new Promise((t) => setImmediate(t));
  }
};
function PT(e, t) {
  return e.hash < t.hash ? -1 : e.hash > t.hash ? 1 : 0;
}
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
const kT = lo, FT = g1;
var BT = class extends FT {
  constructor() {
    super(), this.name = "URGNA2012", this.createMessageDigest = () => new kT("sha1");
  }
  // helper for modifying component during Hash First Degree Quads
  modifyFirstDegreeComponent(t, n, r) {
    return n.termType !== "BlankNode" ? n : r === "graph" ? {
      termType: "BlankNode",
      value: "_:g"
    } : {
      termType: "BlankNode",
      value: n.value === t ? "_:a" : "_:z"
    };
  }
  // helper for getting a related predicate
  getRelatedPredicate(t) {
    return t.predicate.value;
  }
  // helper for creating hash to related blank nodes map
  async createHashToRelated(t, n) {
    const r = /* @__PURE__ */ new Map(), i = this.blankNodeInfo.get(t).quads;
    let a = 0;
    for (const o of i) {
      let s, u;
      if (o.subject.termType === "BlankNode" && o.subject.value !== t)
        u = o.subject.value, s = "p";
      else if (o.object.termType === "BlankNode" && o.object.value !== t)
        u = o.object.value, s = "r";
      else
        continue;
      ++a % 100 === 0 && await this._yield();
      const l = await this.hashRelatedBlankNode(
        u,
        o,
        n,
        s
      ), f = r.get(l);
      f ? f.push(u) : r.set(l, [u]);
    }
    return r;
  }
};
/*!
 * Copyright (c) 2016-2022 Digital Bazaar, Inc. All rights reserved.
 */
const hg = Gh, UT = lo, HT = p1, pg = Vh;
var y1 = class {
  constructor({
    createMessageDigest: t = () => new UT("sha256"),
    maxDeepIterations: n = 1 / 0
  } = {}) {
    this.name = "URDNA2015", this.blankNodeInfo = /* @__PURE__ */ new Map(), this.canonicalIssuer = new hg("_:c14n"), this.createMessageDigest = t, this.maxDeepIterations = n, this.quads = null, this.deepIterations = null;
  }
  // 4.4) Normalization Algorithm
  main(t) {
    this.deepIterations = /* @__PURE__ */ new Map(), this.quads = t;
    for (const s of t)
      this._addBlankNodeQuadInfo({ quad: s, component: s.subject }), this._addBlankNodeQuadInfo({ quad: s, component: s.object }), this._addBlankNodeQuadInfo({ quad: s, component: s.graph });
    const n = /* @__PURE__ */ new Map(), r = [...this.blankNodeInfo.keys()];
    for (const s of r)
      this._hashAndTrackBlankNode({ id: s, hashToBlankNodes: n });
    const i = [...n.keys()].sort(), a = [];
    for (const s of i) {
      const u = n.get(s);
      if (u.length > 1) {
        a.push(u);
        continue;
      }
      const l = u[0];
      this.canonicalIssuer.getId(l);
    }
    for (const s of a) {
      const u = [];
      for (const l of s) {
        if (this.canonicalIssuer.hasId(l))
          continue;
        const f = new hg("_:b");
        f.getId(l);
        const c = this.hashNDegreeQuads(l, f);
        u.push(c);
      }
      u.sort(GT);
      for (const l of u) {
        const f = l.issuer.getOldIds();
        for (const c of f)
          this.canonicalIssuer.getId(c);
      }
    }
    const o = [];
    for (const s of this.quads) {
      const u = pg.serializeQuadComponents(
        this._componentWithCanonicalId({ component: s.subject }),
        s.predicate,
        this._componentWithCanonicalId({ component: s.object }),
        this._componentWithCanonicalId({ component: s.graph })
      );
      o.push(u);
    }
    return o.sort(), o.join("");
  }
  // 4.6) Hash First Degree Quads
  hashFirstDegreeQuads(t) {
    const n = [], r = this.blankNodeInfo.get(t), i = r.quads;
    for (const o of i) {
      const s = {
        subject: null,
        predicate: o.predicate,
        object: null,
        graph: null
      };
      s.subject = this.modifyFirstDegreeComponent(
        t,
        o.subject,
        "subject"
      ), s.object = this.modifyFirstDegreeComponent(
        t,
        o.object,
        "object"
      ), s.graph = this.modifyFirstDegreeComponent(
        t,
        o.graph,
        "graph"
      ), n.push(pg.serializeQuad(s));
    }
    n.sort();
    const a = this.createMessageDigest();
    for (const o of n)
      a.update(o);
    return r.hash = a.digest(), r.hash;
  }
  // 4.7) Hash Related Blank Node
  hashRelatedBlankNode(t, n, r, i) {
    let a;
    this.canonicalIssuer.hasId(t) ? a = this.canonicalIssuer.getId(t) : r.hasId(t) ? a = r.getId(t) : a = this.blankNodeInfo.get(t).hash;
    const o = this.createMessageDigest();
    return o.update(i), i !== "g" && o.update(this.getRelatedPredicate(n)), o.update(a), o.digest();
  }
  // 4.8) Hash N-Degree Quads
  hashNDegreeQuads(t, n) {
    const r = this.deepIterations.get(t) || 0;
    if (r > this.maxDeepIterations)
      throw new Error(
        `Maximum deep iterations (${this.maxDeepIterations}) exceeded.`
      );
    this.deepIterations.set(t, r + 1);
    const i = this.createMessageDigest(), a = this.createHashToRelated(t, n), o = [...a.keys()].sort();
    for (const s of o) {
      i.update(s);
      let u = "", l;
      const f = new HT(a.get(s));
      for (; f.hasNext(); ) {
        const c = f.next();
        let p = n.clone(), d = "";
        const y = [];
        let v = !1;
        for (const _ of c)
          if (this.canonicalIssuer.hasId(_) ? d += this.canonicalIssuer.getId(_) : (p.hasId(_) || y.push(_), d += p.getId(_)), u.length !== 0 && d > u) {
            v = !0;
            break;
          }
        if (!v) {
          for (const _ of y) {
            const m = this.hashNDegreeQuads(_, p);
            if (d += p.getId(_), d += `<${m.hash}>`, p = m.issuer, u.length !== 0 && d > u) {
              v = !0;
              break;
            }
          }
          v || (u.length === 0 || d < u) && (u = d, l = p);
        }
      }
      i.update(u), n = l;
    }
    return { hash: i.digest(), issuer: n };
  }
  // helper for modifying component during Hash First Degree Quads
  modifyFirstDegreeComponent(t, n) {
    return n.termType !== "BlankNode" ? n : {
      termType: "BlankNode",
      value: n.value === t ? "_:a" : "_:z"
    };
  }
  // helper for getting a related predicate
  getRelatedPredicate(t) {
    return `<${t.predicate.value}>`;
  }
  // helper for creating hash to related blank nodes map
  createHashToRelated(t, n) {
    const r = /* @__PURE__ */ new Map(), i = this.blankNodeInfo.get(t).quads;
    for (const a of i)
      this._addRelatedBlankNodeHash({
        quad: a,
        component: a.subject,
        position: "s",
        id: t,
        issuer: n,
        hashToRelated: r
      }), this._addRelatedBlankNodeHash({
        quad: a,
        component: a.object,
        position: "o",
        id: t,
        issuer: n,
        hashToRelated: r
      }), this._addRelatedBlankNodeHash({
        quad: a,
        component: a.graph,
        position: "g",
        id: t,
        issuer: n,
        hashToRelated: r
      });
    return r;
  }
  _hashAndTrackBlankNode({ id: t, hashToBlankNodes: n }) {
    const r = this.hashFirstDegreeQuads(t), i = n.get(r);
    i ? i.push(t) : n.set(r, [t]);
  }
  _addBlankNodeQuadInfo({ quad: t, component: n }) {
    if (n.termType !== "BlankNode")
      return;
    const r = n.value, i = this.blankNodeInfo.get(r);
    i ? i.quads.add(t) : this.blankNodeInfo.set(r, { quads: /* @__PURE__ */ new Set([t]), hash: null });
  }
  _addRelatedBlankNodeHash({ quad: t, component: n, position: r, id: i, issuer: a, hashToRelated: o }) {
    if (!(n.termType === "BlankNode" && n.value !== i))
      return;
    const s = n.value, u = this.hashRelatedBlankNode(s, t, a, r), l = o.get(u);
    l ? l.push(s) : o.set(u, [s]);
  }
  // canonical ids for 7.1
  _componentWithCanonicalId({ component: t }) {
    return t.termType === "BlankNode" && !t.value.startsWith(this.canonicalIssuer.prefix) ? {
      termType: "BlankNode",
      value: this.canonicalIssuer.getId(t.value)
    } : t;
  }
};
function GT(e, t) {
  return e.hash < t.hash ? -1 : e.hash > t.hash ? 1 : 0;
}
/*!
 * Copyright (c) 2016-2021 Digital Bazaar, Inc. All rights reserved.
 */
const VT = lo, WT = y1;
var JT = class extends WT {
  constructor() {
    super(), this.name = "URGNA2012", this.createMessageDigest = () => new VT("sha1");
  }
  // helper for modifying component during Hash First Degree Quads
  modifyFirstDegreeComponent(t, n, r) {
    return n.termType !== "BlankNode" ? n : r === "graph" ? {
      termType: "BlankNode",
      value: "_:g"
    } : {
      termType: "BlankNode",
      value: n.value === t ? "_:a" : "_:z"
    };
  }
  // helper for getting a related predicate
  getRelatedPredicate(t) {
    return t.predicate.value;
  }
  // helper for creating hash to related blank nodes map
  createHashToRelated(t, n) {
    const r = /* @__PURE__ */ new Map(), i = this.blankNodeInfo.get(t).quads;
    for (const a of i) {
      let o, s;
      if (a.subject.termType === "BlankNode" && a.subject.value !== t)
        s = a.subject.value, o = "p";
      else if (a.object.termType === "BlankNode" && a.object.value !== t)
        s = a.object.value, o = "r";
      else
        continue;
      const u = this.hashRelatedBlankNode(s, a, n, o), l = r.get(u);
      l ? l.push(s) : r.set(u, [s]);
    }
    return r;
  }
};
const zT = {}, KT = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  default: zT
}, Symbol.toStringTag, { value: "Module" })), XT = /* @__PURE__ */ e1(KT);
(function(e) {
  const t = g1, n = BT, r = y1, i = JT;
  let a;
  try {
    a = XT;
  } catch {
  }
  e.NQuads = Vh, e.IdentifierIssuer = Gh, e._rdfCanonizeNative = function(o) {
    return o && (a = o), a;
  }, e.canonize = async function(o, s) {
    if (Array.isArray(o) || (o = e.NQuads.legacyDatasetToQuads(o)), s.useNative) {
      if (!a)
        throw new Error("rdf-canonize-native not available");
      if (s.createMessageDigest)
        throw new Error(
          '"createMessageDigest" cannot be used with "useNative".'
        );
      return new Promise((u, l) => a.canonize(o, s, (f, c) => f ? l(f) : u(c)));
    }
    if (s.algorithm === "URDNA2015")
      return new t(s).main(o);
    if (s.algorithm === "URGNA2012") {
      if (s.createMessageDigest)
        throw new Error(
          '"createMessageDigest" cannot be used with "URGNA2012".'
        );
      return new n(s).main(o);
    }
    throw "algorithm" in s ? new Error(
      "Invalid RDF Dataset Canonicalization algorithm: " + s.algorithm
    ) : new Error("No RDF Dataset Canonicalization algorithm specified.");
  }, e._canonizeSync = function(o, s) {
    if (Array.isArray(o) || (o = e.NQuads.legacyDatasetToQuads(o)), s.useNative) {
      if (!a)
        throw new Error("rdf-canonize-native not available");
      if (s.createMessageDigest)
        throw new Error(
          '"createMessageDigest" cannot be used with "useNative".'
        );
      return a.canonizeSync(o, s);
    }
    if (s.algorithm === "URDNA2015")
      return new r(s).main(o);
    if (s.algorithm === "URGNA2012") {
      if (s.createMessageDigest)
        throw new Error(
          '"createMessageDigest" cannot be used with "URGNA2012".'
        );
      return new i(s).main(o);
    }
    throw "algorithm" in s ? new Error(
      "Invalid RDF Dataset Canonicalization algorithm: " + s.algorithm
    ) : new Error("No RDF Dataset Canonicalization algorithm specified.");
  };
})(d1);
var Wh = d1;
const It = {};
var pt = It;
It.isArray = Array.isArray;
It.isBoolean = (e) => typeof e == "boolean" || Object.prototype.toString.call(e) === "[object Boolean]";
It.isDouble = (e) => It.isNumber(e) && (String(e).indexOf(".") !== -1 || Math.abs(e) >= 1e21);
It.isEmptyObject = (e) => It.isObject(e) && Object.keys(e).length === 0;
It.isNumber = (e) => typeof e == "number" || Object.prototype.toString.call(e) === "[object Number]";
It.isNumeric = (e) => !isNaN(parseFloat(e)) && isFinite(e);
It.isObject = (e) => Object.prototype.toString.call(e) === "[object Object]";
It.isString = (e) => typeof e == "string" || Object.prototype.toString.call(e) === "[object String]";
It.isUndefined = (e) => typeof e > "u";
const wr = pt, An = {};
var Nn = An;
An.isSubject = (e) => wr.isObject(e) && !("@value" in e || "@set" in e || "@list" in e) ? Object.keys(e).length > 1 || !("@id" in e) : !1;
An.isSubjectReference = (e) => (
  // Note: A value is a subject reference if all of these hold true:
  // 1. It is an Object.
  // 2. It has a single key: @id.
  wr.isObject(e) && Object.keys(e).length === 1 && "@id" in e
);
An.isValue = (e) => (
  // Note: A value is a @value if all of these hold true:
  // 1. It is an Object.
  // 2. It has the @value property.
  wr.isObject(e) && "@value" in e
);
An.isList = (e) => (
  // Note: A value is a @list if all of these hold true:
  // 1. It is an Object.
  // 2. It has the @list property.
  wr.isObject(e) && "@list" in e
);
An.isGraph = (e) => wr.isObject(e) && "@graph" in e && Object.keys(e).filter((t) => t !== "@id" && t !== "@index").length === 1;
An.isSimpleGraph = (e) => An.isGraph(e) && !("@id" in e);
An.isBlankNode = (e) => {
  if (wr.isObject(e)) {
    if ("@id" in e) {
      const t = e["@id"];
      return !wr.isString(t) || t.indexOf("_:") === 0;
    }
    return Object.keys(e).length === 0 || !("@value" in e || "@set" in e || "@list" in e);
  }
  return !1;
};
var kt = class extends Error {
  /**
   * Creates a JSON-LD Error.
   *
   * @param msg the error message.
   * @param type the error type.
   * @param details the error details.
   */
  constructor(t = "An unspecified JSON-LD error occurred.", n = "jsonld.Error", r = {}) {
    super(t), this.name = n, this.message = t, this.details = r;
  }
};
const Vi = Nn, Ve = pt, v1 = Wh.IdentifierIssuer, YT = kt, QT = /^[a-zA-Z]{1,8}(-[a-zA-Z0-9]{1,8})*$/, ZT = /(?:<[^>]*?>|"[^"]*?"|[^,])+/g, ex = /\s*<([^>]*?)>\s*(?:;\s*(.*))?/, tx = /(.*?)=(?:(?:"([^"]*?)")|([^"]*?))\s*(?:(?:;\s*)|$)/g, nx = /^@[a-zA-Z]+$/, gg = {
  headers: {
    accept: "application/ld+json, application/json"
  }
}, ce = {};
var Tt = ce;
ce.IdentifierIssuer = v1;
ce.REGEX_BCP47 = QT;
ce.REGEX_KEYWORD = nx;
ce.clone = function(e) {
  if (e && typeof e == "object") {
    let t;
    if (Ve.isArray(e)) {
      t = [];
      for (let n = 0; n < e.length; ++n)
        t[n] = ce.clone(e[n]);
    } else if (e instanceof Map) {
      t = /* @__PURE__ */ new Map();
      for (const [n, r] of e)
        t.set(n, ce.clone(r));
    } else if (e instanceof Set) {
      t = /* @__PURE__ */ new Set();
      for (const n of e)
        t.add(ce.clone(n));
    } else if (Ve.isObject(e)) {
      t = {};
      for (const n in e)
        t[n] = ce.clone(e[n]);
    } else
      t = e.toString();
    return t;
  }
  return e;
};
ce.asArray = function(e) {
  return Array.isArray(e) ? e : [e];
};
ce.buildHeaders = (e = {}) => {
  if (Object.keys(e).some(
    (n) => n.toLowerCase() === "accept"
  ))
    throw new RangeError(
      'Accept header may not be specified; only "' + gg.headers.accept + '" is supported.'
    );
  return Object.assign({ Accept: gg.headers.accept }, e);
};
ce.parseLinkHeader = (e) => {
  const t = {}, n = e.match(ZT);
  for (let r = 0; r < n.length; ++r) {
    let i = n[r].match(ex);
    if (!i)
      continue;
    const a = { target: i[1] }, o = i[2];
    for (; i = tx.exec(o); )
      a[i[1]] = i[2] === void 0 ? i[3] : i[2];
    const s = a.rel || "";
    Array.isArray(t[s]) ? t[s].push(a) : t.hasOwnProperty(s) ? t[s] = [t[s], a] : t[s] = a;
  }
  return t;
};
ce.validateTypeValue = (e, t) => {
  if (!Ve.isString(e) && !(Ve.isArray(e) && e.every((n) => Ve.isString(n)))) {
    if (t && Ve.isObject(e))
      switch (Object.keys(e).length) {
        case 0:
          return;
        case 1:
          if ("@default" in e && ce.asArray(e["@default"]).every((n) => Ve.isString(n)))
            return;
      }
    throw new YT(
      'Invalid JSON-LD syntax; "@type" value must a string, an array of strings, an empty object, or a default object.',
      "jsonld.SyntaxError",
      { code: "invalid type value", value: e }
    );
  }
};
ce.hasProperty = (e, t) => {
  if (e.hasOwnProperty(t)) {
    const n = e[t];
    return !Ve.isArray(n) || n.length > 0;
  }
  return !1;
};
ce.hasValue = (e, t, n) => {
  if (ce.hasProperty(e, t)) {
    let r = e[t];
    const i = Vi.isList(r);
    if (Ve.isArray(r) || i) {
      i && (r = r["@list"]);
      for (let a = 0; a < r.length; ++a)
        if (ce.compareValues(n, r[a]))
          return !0;
    } else if (!Ve.isArray(n))
      return ce.compareValues(n, r);
  }
  return !1;
};
ce.addValue = (e, t, n, r) => {
  if (r = r || {}, "propertyIsArray" in r || (r.propertyIsArray = !1), "valueIsArray" in r || (r.valueIsArray = !1), "allowDuplicate" in r || (r.allowDuplicate = !0), "prependValue" in r || (r.prependValue = !1), r.valueIsArray)
    e[t] = n;
  else if (Ve.isArray(n)) {
    n.length === 0 && r.propertyIsArray && !e.hasOwnProperty(t) && (e[t] = []), r.prependValue && (n = n.concat(e[t]), e[t] = []);
    for (let i = 0; i < n.length; ++i)
      ce.addValue(e, t, n[i], r);
  } else if (e.hasOwnProperty(t)) {
    const i = !r.allowDuplicate && ce.hasValue(e, t, n);
    !Ve.isArray(e[t]) && (!i || r.propertyIsArray) && (e[t] = [e[t]]), i || (r.prependValue ? e[t].unshift(n) : e[t].push(n));
  } else
    e[t] = r.propertyIsArray ? [n] : n;
};
ce.getValues = (e, t) => [].concat(e[t] || []);
ce.removeProperty = (e, t) => {
  delete e[t];
};
ce.removeValue = (e, t, n, r) => {
  r = r || {}, "propertyIsArray" in r || (r.propertyIsArray = !1);
  const i = ce.getValues(e, t).filter(
    (a) => !ce.compareValues(a, n)
  );
  i.length === 0 ? ce.removeProperty(e, t) : i.length === 1 && !r.propertyIsArray ? e[t] = i[0] : e[t] = i;
};
ce.relabelBlankNodes = (e, t) => {
  t = t || {};
  const n = t.issuer || new v1("_:b");
  return Sa(n, e);
};
ce.compareValues = (e, t) => e === t || Vi.isValue(e) && Vi.isValue(t) && e["@value"] === t["@value"] && e["@type"] === t["@type"] && e["@language"] === t["@language"] && e["@index"] === t["@index"] ? !0 : Ve.isObject(e) && "@id" in e && Ve.isObject(t) && "@id" in t ? e["@id"] === t["@id"] : !1;
ce.compareShortestLeast = (e, t) => e.length < t.length ? -1 : t.length < e.length ? 1 : e === t ? 0 : e < t ? -1 : 1;
function Sa(e, t) {
  if (Ve.isArray(t))
    for (let n = 0; n < t.length; ++n)
      t[n] = Sa(e, t[n]);
  else if (Vi.isList(t))
    t["@list"] = Sa(e, t["@list"]);
  else if (Ve.isObject(t)) {
    Vi.isBlankNode(t) && (t["@id"] = e.getId(t["@id"]));
    const n = Object.keys(t).sort();
    for (let r = 0; r < n.length; ++r) {
      const i = n[r];
      i !== "@id" && (t[i] = Sa(e, t[i]));
    }
  }
  return t;
}
const Mt = "http://www.w3.org/1999/02/22-rdf-syntax-ns#", yi = "http://www.w3.org/2001/XMLSchema#";
var Jh = {
  // TODO: Deprecated and will be removed later. Use LINK_HEADER_CONTEXT.
  LINK_HEADER_REL: "http://www.w3.org/ns/json-ld#context",
  LINK_HEADER_CONTEXT: "http://www.w3.org/ns/json-ld#context",
  RDF: Mt,
  RDF_LIST: Mt + "List",
  RDF_FIRST: Mt + "first",
  RDF_REST: Mt + "rest",
  RDF_NIL: Mt + "nil",
  RDF_TYPE: Mt + "type",
  RDF_PLAIN_LITERAL: Mt + "PlainLiteral",
  RDF_XML_LITERAL: Mt + "XMLLiteral",
  RDF_JSON_LITERAL: Mt + "JSON",
  RDF_OBJECT: Mt + "object",
  RDF_LANGSTRING: Mt + "langString",
  XSD: yi,
  XSD_BOOLEAN: yi + "boolean",
  XSD_DOUBLE: yi + "double",
  XSD_INTEGER: yi + "integer",
  XSD_STRING: yi + "string"
}, fs, yg;
function _1() {
  return yg || (yg = 1, fs = class {
    /**
     * Creates a simple queue for requesting documents.
     */
    constructor() {
      this._requests = {};
    }
    wrapLoader(t) {
      const n = this;
      return n._loader = t, function() {
        return n.add.apply(n, arguments);
      };
    }
    async add(t) {
      let n = this._requests[t];
      if (n)
        return Promise.resolve(n);
      n = this._requests[t] = this._loader(t);
      try {
        return await n;
      } finally {
        delete this._requests[t];
      }
    }
  }), fs;
}
const fo = pt, We = {};
var Qn = We;
We.parsers = {
  simple: {
    // RFC 3986 basic parts
    keys: [
      "href",
      "scheme",
      "authority",
      "path",
      "query",
      "fragment"
    ],
    /* eslint-disable-next-line max-len */
    regex: /^(?:([^:\/?#]+):)?(?:\/\/([^\/?#]*))?([^?#]*)(?:\?([^#]*))?(?:#(.*))?/
  },
  full: {
    keys: [
      "href",
      "protocol",
      "scheme",
      "authority",
      "auth",
      "user",
      "password",
      "hostname",
      "port",
      "path",
      "directory",
      "file",
      "query",
      "fragment"
    ],
    /* eslint-disable-next-line max-len */
    regex: /^(([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?(?:(((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/
  }
};
We.parse = (e, t) => {
  const n = {}, r = We.parsers[t || "full"], i = r.regex.exec(e);
  let a = r.keys.length;
  for (; a--; )
    n[r.keys[a]] = i[a] === void 0 ? null : i[a];
  return (n.scheme === "https" && n.port === "443" || n.scheme === "http" && n.port === "80") && (n.href = n.href.replace(":" + n.port, ""), n.authority = n.authority.replace(":" + n.port, ""), n.port = null), n.normalizedPath = We.removeDotSegments(n.path), n;
};
We.prependBase = (e, t) => {
  if (e === null || We.isAbsolute(t))
    return t;
  (!e || fo.isString(e)) && (e = We.parse(e || ""));
  const n = We.parse(t), r = {
    protocol: e.protocol || ""
  };
  if (n.authority !== null)
    r.authority = n.authority, r.path = n.path, r.query = n.query;
  else if (r.authority = e.authority, n.path === "")
    r.path = e.path, n.query !== null ? r.query = n.query : r.query = e.query;
  else {
    if (n.path.indexOf("/") === 0)
      r.path = n.path;
    else {
      let a = e.path;
      a = a.substr(0, a.lastIndexOf("/") + 1), (a.length > 0 || e.authority) && a.substr(-1) !== "/" && (a += "/"), a += n.path, r.path = a;
    }
    r.query = n.query;
  }
  n.path !== "" && (r.path = We.removeDotSegments(r.path));
  let i = r.protocol;
  return r.authority !== null && (i += "//" + r.authority), i += r.path, r.query !== null && (i += "?" + r.query), n.fragment !== null && (i += "#" + n.fragment), i === "" && (i = "./"), i;
};
We.removeBase = (e, t) => {
  if (e === null)
    return t;
  (!e || fo.isString(e)) && (e = We.parse(e || ""));
  let n = "";
  if (e.href !== "" ? n += (e.protocol || "") + "//" + (e.authority || "") : t.indexOf("//") && (n += "//"), t.indexOf(n) !== 0)
    return t;
  const r = We.parse(t.substr(n.length)), i = e.normalizedPath.split("/"), a = r.normalizedPath.split("/"), o = r.fragment || r.query ? 0 : 1;
  for (; i.length > 0 && a.length > o && i[0] === a[0]; )
    i.shift(), a.shift();
  let s = "";
  if (i.length > 0) {
    i.pop();
    for (let u = 0; u < i.length; ++u)
      s += "../";
  }
  return s += a.join("/"), r.query !== null && (s += "?" + r.query), r.fragment !== null && (s += "#" + r.fragment), s === "" && (s = "./"), s;
};
We.removeDotSegments = (e) => {
  if (e.length === 0)
    return "";
  const t = e.split("/"), n = [];
  for (; t.length > 0; ) {
    const r = t.shift(), i = t.length === 0;
    if (r === ".") {
      i && n.push("");
      continue;
    }
    if (r === "..") {
      n.pop(), i && n.push("");
      continue;
    }
    n.push(r);
  }
  return e[0] === "/" && n.length > 0 && n[0] !== "" && n.unshift(""), n.length === 1 && n[0] === "" ? "/" : n.join("/");
};
const rx = /^([A-Za-z][A-Za-z0-9+-.]*|_):[^\s]*$/;
We.isAbsolute = (e) => fo.isString(e) && rx.test(e);
We.isRelative = (e) => fo.isString(e);
const { parseLinkHeader: ix, buildHeaders: ax } = Tt, { LINK_HEADER_CONTEXT: ox } = Jh, vi = kt, sx = _1(), { prependBase: ux } = Qn, lx = /(^|(\r\n))link:/i;
var fx = ({
  secure: e,
  headers: t = {},
  xhr: n
} = { headers: {} }) => {
  return t = ax(t), new sx().wrapLoader(i);
  async function i(a) {
    if (a.indexOf("http:") !== 0 && a.indexOf("https:") !== 0)
      throw new vi(
        'URL could not be dereferenced; only "http" and "https" URLs are supported.',
        "jsonld.InvalidUrl",
        { code: "loading document failed", url: a }
      );
    if (e && a.indexOf("https") !== 0)
      throw new vi(
        `URL could not be dereferenced; secure mode is enabled and the URL's scheme is not "https".`,
        "jsonld.InvalidUrl",
        { code: "loading document failed", url: a }
      );
    let o;
    try {
      o = await cx(n, a, t);
    } catch (c) {
      throw new vi(
        "URL could not be dereferenced, an error occurred.",
        "jsonld.LoadDocumentError",
        { code: "loading document failed", url: a, cause: c }
      );
    }
    if (o.status >= 400)
      throw new vi(
        "URL could not be dereferenced: " + o.statusText,
        "jsonld.LoadDocumentError",
        {
          code: "loading document failed",
          url: a,
          httpStatusCode: o.status
        }
      );
    let s = { contextUrl: null, documentUrl: a, document: o.response }, u = null;
    const l = o.getResponseHeader("Content-Type");
    let f;
    if (lx.test(o.getAllResponseHeaders()) && (f = o.getResponseHeader("Link")), f && l !== "application/ld+json") {
      const c = ix(f), p = c[ox];
      if (Array.isArray(p))
        throw new vi(
          "URL could not be dereferenced, it has more than one associated HTTP Link Header.",
          "jsonld.InvalidUrl",
          { code: "multiple context link headers", url: a }
        );
      p && (s.contextUrl = p.target), u = c.alternate, u && u.type == "application/ld+json" && !(l || "").match(/^application\/(\w*\+)?json$/) && (s = await i(ux(a, u.target)));
    }
    return s;
  }
};
function cx(e, t, n) {
  e = e || XMLHttpRequest;
  const r = new e();
  return new Promise((i, a) => {
    r.onload = () => i(r), r.onerror = (o) => a(o), r.open("GET", t, !0);
    for (const o in n)
      r.setRequestHeader(o, n[o]);
    r.send();
  });
}
const dx = fx, zh = {};
var hx = zh;
zh.setupDocumentLoaders = function(e) {
  typeof XMLHttpRequest < "u" && (e.documentLoaders.xhr = dx, e.useDocumentLoader("xhr"));
};
zh.setupGlobals = function(e) {
  typeof globalThis.JsonLdProcessor > "u" && Object.defineProperty(globalThis, "JsonLdProcessor", {
    writable: !0,
    enumerable: !1,
    configurable: !0,
    value: e.JsonLdProcessor
  });
};
var cs, vg;
function px() {
  return vg || (vg = 1, cs = function(e) {
    e.prototype[Symbol.iterator] = function* () {
      for (let t = this.head; t; t = t.next)
        yield t.value;
    };
  }), cs;
}
var gx = ue;
ue.Node = Er;
ue.create = ue;
function ue(e) {
  var t = this;
  if (t instanceof ue || (t = new ue()), t.tail = null, t.head = null, t.length = 0, e && typeof e.forEach == "function")
    e.forEach(function(i) {
      t.push(i);
    });
  else if (arguments.length > 0)
    for (var n = 0, r = arguments.length; n < r; n++)
      t.push(arguments[n]);
  return t;
}
ue.prototype.removeNode = function(e) {
  if (e.list !== this)
    throw new Error("removing node which does not belong to this list");
  var t = e.next, n = e.prev;
  return t && (t.prev = n), n && (n.next = t), e === this.head && (this.head = t), e === this.tail && (this.tail = n), e.list.length--, e.next = null, e.prev = null, e.list = null, t;
};
ue.prototype.unshiftNode = function(e) {
  if (e !== this.head) {
    e.list && e.list.removeNode(e);
    var t = this.head;
    e.list = this, e.next = t, t && (t.prev = e), this.head = e, this.tail || (this.tail = e), this.length++;
  }
};
ue.prototype.pushNode = function(e) {
  if (e !== this.tail) {
    e.list && e.list.removeNode(e);
    var t = this.tail;
    e.list = this, e.prev = t, t && (t.next = e), this.tail = e, this.head || (this.head = e), this.length++;
  }
};
ue.prototype.push = function() {
  for (var e = 0, t = arguments.length; e < t; e++)
    vx(this, arguments[e]);
  return this.length;
};
ue.prototype.unshift = function() {
  for (var e = 0, t = arguments.length; e < t; e++)
    _x(this, arguments[e]);
  return this.length;
};
ue.prototype.pop = function() {
  if (this.tail) {
    var e = this.tail.value;
    return this.tail = this.tail.prev, this.tail ? this.tail.next = null : this.head = null, this.length--, e;
  }
};
ue.prototype.shift = function() {
  if (this.head) {
    var e = this.head.value;
    return this.head = this.head.next, this.head ? this.head.prev = null : this.tail = null, this.length--, e;
  }
};
ue.prototype.forEach = function(e, t) {
  t = t || this;
  for (var n = this.head, r = 0; n !== null; r++)
    e.call(t, n.value, r, this), n = n.next;
};
ue.prototype.forEachReverse = function(e, t) {
  t = t || this;
  for (var n = this.tail, r = this.length - 1; n !== null; r--)
    e.call(t, n.value, r, this), n = n.prev;
};
ue.prototype.get = function(e) {
  for (var t = 0, n = this.head; n !== null && t < e; t++)
    n = n.next;
  if (t === e && n !== null)
    return n.value;
};
ue.prototype.getReverse = function(e) {
  for (var t = 0, n = this.tail; n !== null && t < e; t++)
    n = n.prev;
  if (t === e && n !== null)
    return n.value;
};
ue.prototype.map = function(e, t) {
  t = t || this;
  for (var n = new ue(), r = this.head; r !== null; )
    n.push(e.call(t, r.value, this)), r = r.next;
  return n;
};
ue.prototype.mapReverse = function(e, t) {
  t = t || this;
  for (var n = new ue(), r = this.tail; r !== null; )
    n.push(e.call(t, r.value, this)), r = r.prev;
  return n;
};
ue.prototype.reduce = function(e, t) {
  var n, r = this.head;
  if (arguments.length > 1)
    n = t;
  else if (this.head)
    r = this.head.next, n = this.head.value;
  else
    throw new TypeError("Reduce of empty list with no initial value");
  for (var i = 0; r !== null; i++)
    n = e(n, r.value, i), r = r.next;
  return n;
};
ue.prototype.reduceReverse = function(e, t) {
  var n, r = this.tail;
  if (arguments.length > 1)
    n = t;
  else if (this.tail)
    r = this.tail.prev, n = this.tail.value;
  else
    throw new TypeError("Reduce of empty list with no initial value");
  for (var i = this.length - 1; r !== null; i--)
    n = e(n, r.value, i), r = r.prev;
  return n;
};
ue.prototype.toArray = function() {
  for (var e = new Array(this.length), t = 0, n = this.head; n !== null; t++)
    e[t] = n.value, n = n.next;
  return e;
};
ue.prototype.toArrayReverse = function() {
  for (var e = new Array(this.length), t = 0, n = this.tail; n !== null; t++)
    e[t] = n.value, n = n.prev;
  return e;
};
ue.prototype.slice = function(e, t) {
  t = t || this.length, t < 0 && (t += this.length), e = e || 0, e < 0 && (e += this.length);
  var n = new ue();
  if (t < e || t < 0)
    return n;
  e < 0 && (e = 0), t > this.length && (t = this.length);
  for (var r = 0, i = this.head; i !== null && r < e; r++)
    i = i.next;
  for (; i !== null && r < t; r++, i = i.next)
    n.push(i.value);
  return n;
};
ue.prototype.sliceReverse = function(e, t) {
  t = t || this.length, t < 0 && (t += this.length), e = e || 0, e < 0 && (e += this.length);
  var n = new ue();
  if (t < e || t < 0)
    return n;
  e < 0 && (e = 0), t > this.length && (t = this.length);
  for (var r = this.length, i = this.tail; i !== null && r > t; r--)
    i = i.prev;
  for (; i !== null && r > e; r--, i = i.prev)
    n.push(i.value);
  return n;
};
ue.prototype.splice = function(e, t, ...n) {
  e > this.length && (e = this.length - 1), e < 0 && (e = this.length + e);
  for (var r = 0, i = this.head; i !== null && r < e; r++)
    i = i.next;
  for (var a = [], r = 0; i && r < t; r++)
    a.push(i.value), i = this.removeNode(i);
  i === null && (i = this.tail), i !== this.head && i !== this.tail && (i = i.prev);
  for (var r = 0; r < n.length; r++)
    i = yx(this, i, n[r]);
  return a;
};
ue.prototype.reverse = function() {
  for (var e = this.head, t = this.tail, n = e; n !== null; n = n.prev) {
    var r = n.prev;
    n.prev = n.next, n.next = r;
  }
  return this.head = t, this.tail = e, this;
};
function yx(e, t, n) {
  var r = t === e.head ? new Er(n, null, t, e) : new Er(n, t, t.next, e);
  return r.next === null && (e.tail = r), r.prev === null && (e.head = r), e.length++, r;
}
function vx(e, t) {
  e.tail = new Er(t, e.tail, null, e), e.head || (e.head = e.tail), e.length++;
}
function _x(e, t) {
  e.head = new Er(t, null, e.head, e), e.tail || (e.tail = e.head), e.length++;
}
function Er(e, t, n, r) {
  if (!(this instanceof Er))
    return new Er(e, t, n, r);
  this.list = r, this.value = e, t ? (t.next = this, this.prev = t) : this.prev = null, n ? (n.prev = this, this.next = n) : this.next = null;
}
try {
  px()(ue);
} catch {
}
const bx = gx, or = Symbol("max"), yn = Symbol("length"), Dr = Symbol("lengthCalculator"), Ci = Symbol("allowStale"), ur = Symbol("maxAge"), gn = Symbol("dispose"), _g = Symbol("noDisposeOnSet"), qe = Symbol("lruList"), Dt = Symbol("cache"), b1 = Symbol("updateAgeOnGet"), ds = () => 1;
class mx {
  constructor(t) {
    if (typeof t == "number" && (t = { max: t }), t || (t = {}), t.max && (typeof t.max != "number" || t.max < 0))
      throw new TypeError("max must be a non-negative number");
    this[or] = t.max || 1 / 0;
    const n = t.length || ds;
    if (this[Dr] = typeof n != "function" ? ds : n, this[Ci] = t.stale || !1, t.maxAge && typeof t.maxAge != "number")
      throw new TypeError("maxAge must be a number");
    this[ur] = t.maxAge || 0, this[gn] = t.dispose, this[_g] = t.noDisposeOnSet || !1, this[b1] = t.updateAgeOnGet || !1, this.reset();
  }
  // resize the cache when the max changes.
  set max(t) {
    if (typeof t != "number" || t < 0)
      throw new TypeError("max must be a non-negative number");
    this[or] = t || 1 / 0, _i(this);
  }
  get max() {
    return this[or];
  }
  set allowStale(t) {
    this[Ci] = !!t;
  }
  get allowStale() {
    return this[Ci];
  }
  set maxAge(t) {
    if (typeof t != "number")
      throw new TypeError("maxAge must be a non-negative number");
    this[ur] = t, _i(this);
  }
  get maxAge() {
    return this[ur];
  }
  // resize the cache when the lengthCalculator changes.
  set lengthCalculator(t) {
    typeof t != "function" && (t = ds), t !== this[Dr] && (this[Dr] = t, this[yn] = 0, this[qe].forEach((n) => {
      n.length = this[Dr](n.value, n.key), this[yn] += n.length;
    })), _i(this);
  }
  get lengthCalculator() {
    return this[Dr];
  }
  get length() {
    return this[yn];
  }
  get itemCount() {
    return this[qe].length;
  }
  rforEach(t, n) {
    n = n || this;
    for (let r = this[qe].tail; r !== null; ) {
      const i = r.prev;
      bg(this, t, r, n), r = i;
    }
  }
  forEach(t, n) {
    n = n || this;
    for (let r = this[qe].head; r !== null; ) {
      const i = r.next;
      bg(this, t, r, n), r = i;
    }
  }
  keys() {
    return this[qe].toArray().map((t) => t.key);
  }
  values() {
    return this[qe].toArray().map((t) => t.value);
  }
  reset() {
    this[gn] && this[qe] && this[qe].length && this[qe].forEach((t) => this[gn](t.key, t.value)), this[Dt] = /* @__PURE__ */ new Map(), this[qe] = new bx(), this[yn] = 0;
  }
  dump() {
    return this[qe].map((t) => Ba(this, t) ? !1 : {
      k: t.key,
      v: t.value,
      e: t.now + (t.maxAge || 0)
    }).toArray().filter((t) => t);
  }
  dumpLru() {
    return this[qe];
  }
  set(t, n, r) {
    if (r = r || this[ur], r && typeof r != "number")
      throw new TypeError("maxAge must be a number");
    const i = r ? Date.now() : 0, a = this[Dr](n, t);
    if (this[Dt].has(t)) {
      if (a > this[or])
        return Gr(this, this[Dt].get(t)), !1;
      const u = this[Dt].get(t).value;
      return this[gn] && (this[_g] || this[gn](t, u.value)), u.now = i, u.maxAge = r, u.value = n, this[yn] += a - u.length, u.length = a, this.get(t), _i(this), !0;
    }
    const o = new wx(t, n, a, i, r);
    return o.length > this[or] ? (this[gn] && this[gn](t, n), !1) : (this[yn] += o.length, this[qe].unshift(o), this[Dt].set(t, this[qe].head), _i(this), !0);
  }
  has(t) {
    if (!this[Dt].has(t))
      return !1;
    const n = this[Dt].get(t).value;
    return !Ba(this, n);
  }
  get(t) {
    return hs(this, t, !0);
  }
  peek(t) {
    return hs(this, t, !1);
  }
  pop() {
    const t = this[qe].tail;
    return t ? (Gr(this, t), t.value) : null;
  }
  del(t) {
    Gr(this, this[Dt].get(t));
  }
  load(t) {
    this.reset();
    const n = Date.now();
    for (let r = t.length - 1; r >= 0; r--) {
      const i = t[r], a = i.e || 0;
      if (a === 0)
        this.set(i.k, i.v);
      else {
        const o = a - n;
        o > 0 && this.set(i.k, i.v, o);
      }
    }
  }
  prune() {
    this[Dt].forEach((t, n) => hs(this, n, !1));
  }
}
const hs = (e, t, n) => {
  const r = e[Dt].get(t);
  if (r) {
    const i = r.value;
    if (Ba(e, i)) {
      if (Gr(e, r), !e[Ci])
        return;
    } else
      n && (e[b1] && (r.value.now = Date.now()), e[qe].unshiftNode(r));
    return i.value;
  }
}, Ba = (e, t) => {
  if (!t || !t.maxAge && !e[ur])
    return !1;
  const n = Date.now() - t.now;
  return t.maxAge ? n > t.maxAge : e[ur] && n > e[ur];
}, _i = (e) => {
  if (e[yn] > e[or])
    for (let t = e[qe].tail; e[yn] > e[or] && t !== null; ) {
      const n = t.prev;
      Gr(e, t), t = n;
    }
}, Gr = (e, t) => {
  if (t) {
    const n = t.value;
    e[gn] && e[gn](n.key, n.value), e[yn] -= n.length, e[Dt].delete(n.key), e[qe].removeNode(t);
  }
};
class wx {
  constructor(t, n, r, i, a) {
    this.key = t, this.value = n, this.length = r, this.now = i, this.maxAge = a || 0;
  }
}
const bg = (e, t, n, r) => {
  let i = n.value;
  Ba(e, i) && (Gr(e, n), e[Ci] || (i = void 0)), i && t.call(r, i.value, i.key, e);
};
var m1 = mx;
const Ex = m1, Sx = 10;
var Ax = class {
  /**
   * Creates a ResolvedContext.
   *
   * @param document the context document.
   */
  constructor({ document: t }) {
    this.document = t, this.cache = new Ex({ max: Sx });
  }
  getProcessed(t) {
    return this.cache.get(t);
  }
  setProcessed(t, n) {
    this.cache.set(t, n);
  }
};
const {
  isArray: Jd,
  isObject: Di,
  isString: Ua
} = pt, {
  asArray: Rx
} = Tt, { prependBase: zd } = Qn, Ei = kt, mg = Ax, wg = 10;
var Ix = class {
  /**
   * Creates a ContextResolver.
   *
   * @param sharedCache a shared LRU cache with `get` and `set` APIs.
   */
  constructor({ sharedCache: t }) {
    this.perOpCache = /* @__PURE__ */ new Map(), this.sharedCache = t;
  }
  async resolve({
    activeCtx: t,
    context: n,
    documentLoader: r,
    base: i,
    cycles: a = /* @__PURE__ */ new Set()
  }) {
    n && Di(n) && n["@context"] && (n = n["@context"]), n = Rx(n);
    const o = [];
    for (const s of n) {
      if (Ua(s)) {
        let f = this._get(s);
        f || (f = await this._resolveRemoteContext(
          { activeCtx: t, url: s, documentLoader: r, base: i, cycles: a }
        )), Jd(f) ? o.push(...f) : o.push(f);
        continue;
      }
      if (s === null) {
        o.push(new mg({ document: null }));
        continue;
      }
      Di(s) || Tx(n);
      const u = JSON.stringify(s);
      let l = this._get(u);
      l || (l = new mg({ document: s }), this._cacheResolvedContext({ key: u, resolved: l, tag: "static" })), o.push(l);
    }
    return o;
  }
  _get(t) {
    let n = this.perOpCache.get(t);
    if (!n) {
      const r = this.sharedCache.get(t);
      r && (n = r.get("static"), n && this.perOpCache.set(t, n));
    }
    return n;
  }
  _cacheResolvedContext({ key: t, resolved: n, tag: r }) {
    if (this.perOpCache.set(t, n), r !== void 0) {
      let i = this.sharedCache.get(t);
      i || (i = /* @__PURE__ */ new Map(), this.sharedCache.set(t, i)), i.set(r, n);
    }
    return n;
  }
  async _resolveRemoteContext({ activeCtx: t, url: n, documentLoader: r, base: i, cycles: a }) {
    n = zd(i, n);
    const { context: o, remoteDoc: s } = await this._fetchContext(
      { activeCtx: t, url: n, documentLoader: r, cycles: a }
    );
    i = s.documentUrl || n, Kd({ context: o, base: i });
    const u = await this.resolve(
      { activeCtx: t, context: o, documentLoader: r, base: i, cycles: a }
    );
    return this._cacheResolvedContext({ key: n, resolved: u, tag: s.tag }), u;
  }
  async _fetchContext({ activeCtx: t, url: n, documentLoader: r, cycles: i }) {
    if (i.size > wg)
      throw new Ei(
        "Maximum number of @context URLs exceeded.",
        "jsonld.ContextUrlError",
        {
          code: t.processingMode === "json-ld-1.0" ? "loading remote context failed" : "context overflow",
          max: wg
        }
      );
    if (i.has(n))
      throw new Ei(
        "Cyclical @context URLs detected.",
        "jsonld.ContextUrlError",
        {
          code: t.processingMode === "json-ld-1.0" ? "recursive context inclusion" : "context overflow",
          url: n
        }
      );
    i.add(n);
    let a, o;
    try {
      o = await r(n), a = o.document || null, Ua(a) && (a = JSON.parse(a));
    } catch (s) {
      throw new Ei(
        "Dereferencing a URL did not result in a valid JSON-LD object. Possible causes are an inaccessible URL perhaps due to a same-origin policy (ensure the server uses CORS if you are using client-side JavaScript), too many redirects, a non-JSON response, or more than one HTTP Link Header was provided for a remote context.",
        "jsonld.InvalidUrl",
        { code: "loading remote context failed", url: n, cause: s }
      );
    }
    if (!Di(a))
      throw new Ei(
        "Dereferencing a URL did not result in a JSON object. The response was valid JSON, but it was not a JSON object.",
        "jsonld.InvalidUrl",
        { code: "invalid remote context", url: n }
      );
    return "@context" in a ? a = { "@context": a["@context"] } : a = { "@context": {} }, o.contextUrl && (Jd(a["@context"]) || (a["@context"] = [a["@context"]]), a["@context"].push(o.contextUrl)), { context: a, remoteDoc: o };
  }
};
function Tx(e) {
  throw new Ei(
    "Invalid JSON-LD syntax; @context must be an object.",
    "jsonld.SyntaxError",
    {
      code: "invalid local context",
      context: e
    }
  );
}
function Kd({ context: e, base: t }) {
  if (!e)
    return;
  const n = e["@context"];
  if (Ua(n)) {
    e["@context"] = zd(t, n);
    return;
  }
  if (Jd(n)) {
    for (let r = 0; r < n.length; ++r) {
      const i = n[r];
      if (Ua(i)) {
        n[r] = zd(t, i);
        continue;
      }
      Di(i) && Kd({ context: { "@context": i }, base: t });
    }
    return;
  }
  if (Di(n))
    for (const r in n)
      Kd({ context: n[r], base: t });
}
var xx = Wh.NQuads;
const Kh = kt, {
  isArray: Nx
} = pt, {
  asArray: w1
} = Tt, dt = {};
var Yi = dt;
dt.defaultEventHandler = null;
dt.setupEventHandler = ({ options: e = {} }) => {
  const t = [].concat(
    e.safe ? dt.safeEventHandler : [],
    e.eventHandler ? w1(e.eventHandler) : [],
    dt.defaultEventHandler ? dt.defaultEventHandler : []
  );
  return t.length === 0 ? null : t;
};
dt.handleEvent = ({
  event: e,
  options: t
}) => {
  E1({ event: e, handlers: t.eventHandler });
};
function E1({ event: e, handlers: t }) {
  let n = !0;
  for (let r = 0; n && r < t.length; ++r) {
    n = !1;
    const i = t[r];
    if (Nx(i))
      n = E1({ event: e, handlers: i });
    else if (typeof i == "function")
      i({ event: e, next: () => {
        n = !0;
      } });
    else if (typeof i == "object")
      e.code in i ? i[e.code]({ event: e, next: () => {
        n = !0;
      } }) : n = !0;
    else
      throw new Kh(
        "Invalid event handler.",
        "jsonld.InvalidEventHandler",
        { event: e }
      );
  }
  return n;
}
const Ox = /* @__PURE__ */ new Set([
  "empty object",
  "free-floating scalar",
  "invalid @language value",
  "invalid property",
  // NOTE: spec edge case
  "null @id value",
  "null @value value",
  "object with only @id",
  "object with only @language",
  "object with only @list",
  "object with only @value",
  "relative @id reference",
  "relative @type reference",
  "relative @vocab reference",
  "reserved @id value",
  "reserved @reverse value",
  "reserved term",
  // toRDF
  "blank node predicate",
  "relative graph reference",
  "relative object reference",
  "relative predicate reference",
  "relative subject reference"
]);
dt.safeEventHandler = function({ event: t, next: n }) {
  if (t.level === "warning" && Ox.has(t.code))
    throw new Kh(
      "Safe mode validation error.",
      "jsonld.ValidationError",
      { event: t }
    );
  n();
};
dt.logEventHandler = function({ event: t, next: n }) {
  console.log(`EVENT: ${t.message}`, { event: t }), n();
};
dt.logWarningEventHandler = function({ event: t, next: n }) {
  t.level === "warning" && console.warn(`WARNING: ${t.message}`, { event: t }), n();
};
dt.unhandledEventHandler = function({ event: t }) {
  throw new Kh(
    "No handler for event.",
    "jsonld.UnhandledEvent",
    { event: t }
  );
};
dt.setDefaultEventHandler = function({ eventHandler: e } = {}) {
  dt.defaultEventHandler = e ? w1(e) : null;
};
const Eg = Tt, K = kt, {
  isArray: S1,
  isObject: Bn,
  isString: et,
  isUndefined: $x
} = pt, {
  isAbsolute: pr,
  isRelative: Lx,
  prependBase: qi
} = Qn, {
  handleEvent: Vr
} = Yi, {
  REGEX_BCP47: Mx,
  REGEX_KEYWORD: Aa,
  asArray: A1,
  compareShortestLeast: Cx
} = Tt, sa = /* @__PURE__ */ new Map(), Dx = 1e4, we = {};
var Rr = we;
we.process = async ({
  activeCtx: e,
  localCtx: t,
  options: n,
  propagate: r = !0,
  overrideProtected: i = !1,
  cycles: a = /* @__PURE__ */ new Set()
}) => {
  if (Bn(t) && "@context" in t && S1(t["@context"]) && (t = t["@context"]), A1(t).length === 0)
    return e;
  const s = [], u = [
    ({ event: p, next: d }) => {
      s.push(p), d();
    }
  ];
  n.eventHandler && u.push(n.eventHandler);
  const l = n;
  n = { ...n, eventHandler: u };
  const f = await n.contextResolver.resolve({
    activeCtx: e,
    context: t,
    documentLoader: n.documentLoader,
    base: n.base
  });
  Bn(f[0].document) && typeof f[0].document["@propagate"] == "boolean" && (r = f[0].document["@propagate"]);
  let c = e;
  !r && !c.previousContext && (c = c.clone(), c.previousContext = e);
  for (const p of f) {
    let { document: d } = p;
    if (e = c, d === null) {
      if (!i && Object.keys(e.protected).length !== 0)
        throw new K(
          "Tried to nullify a context with protected terms outside of a term definition.",
          "jsonld.SyntaxError",
          { code: "invalid context nullification" }
        );
      c = e = we.getInitialContext(n).clone();
      continue;
    }
    const y = p.getProcessed(e);
    if (y) {
      if (l.eventHandler)
        for (const _ of y.events)
          Vr({ event: _, options: l });
      c = e = y.context;
      continue;
    }
    if (Bn(d) && "@context" in d && (d = d["@context"]), !Bn(d))
      throw new K(
        "Invalid JSON-LD syntax; @context must be an object.",
        "jsonld.SyntaxError",
        { code: "invalid local context", context: d }
      );
    c = c.clone();
    const v = /* @__PURE__ */ new Map();
    if ("@version" in d) {
      if (d["@version"] !== 1.1)
        throw new K(
          "Unsupported JSON-LD version: " + d["@version"],
          "jsonld.UnsupportedVersion",
          { code: "invalid @version value", context: d }
        );
      if (e.processingMode && e.processingMode === "json-ld-1.0")
        throw new K(
          "@version: " + d["@version"] + " not compatible with " + e.processingMode,
          "jsonld.ProcessingModeConflict",
          { code: "processing mode conflict", context: d }
        );
      c.processingMode = "json-ld-1.1", c["@version"] = d["@version"], v.set("@version", !0);
    }
    if (c.processingMode = c.processingMode || e.processingMode, "@base" in d) {
      let _ = d["@base"];
      if (!(_ === null || pr(_)))
        if (Lx(_))
          _ = qi(c["@base"], _);
        else
          throw new K(
            'Invalid JSON-LD syntax; the value of "@base" in a @context must be an absolute IRI, a relative IRI, or null.',
            "jsonld.SyntaxError",
            { code: "invalid base IRI", context: d }
          );
      c["@base"] = _, v.set("@base", !0);
    }
    if ("@vocab" in d) {
      const _ = d["@vocab"];
      if (_ === null)
        delete c["@vocab"];
      else if (et(_)) {
        if (!pr(_) && we.processingMode(c, 1))
          throw new K(
            'Invalid JSON-LD syntax; the value of "@vocab" in a @context must be an absolute IRI.',
            "jsonld.SyntaxError",
            { code: "invalid vocab mapping", context: d }
          );
        {
          const m = Br(
            c,
            _,
            { vocab: !0, base: !0 },
            void 0,
            void 0,
            n
          );
          pr(m) || n.eventHandler && Vr({
            event: {
              type: ["JsonLdEvent"],
              code: "relative @vocab reference",
              level: "warning",
              message: "Relative @vocab reference found.",
              details: {
                vocab: m
              }
            },
            options: n
          }), c["@vocab"] = m;
        }
      } else
        throw new K(
          'Invalid JSON-LD syntax; the value of "@vocab" in a @context must be a string or null.',
          "jsonld.SyntaxError",
          { code: "invalid vocab mapping", context: d }
        );
      v.set("@vocab", !0);
    }
    if ("@language" in d) {
      const _ = d["@language"];
      if (_ === null)
        delete c["@language"];
      else if (et(_))
        _.match(Mx) || n.eventHandler && Vr({
          event: {
            type: ["JsonLdEvent"],
            code: "invalid @language value",
            level: "warning",
            message: "@language value must be valid BCP47.",
            details: {
              language: _
            }
          },
          options: n
        }), c["@language"] = _.toLowerCase();
      else
        throw new K(
          'Invalid JSON-LD syntax; the value of "@language" in a @context must be a string or null.',
          "jsonld.SyntaxError",
          { code: "invalid default language", context: d }
        );
      v.set("@language", !0);
    }
    if ("@direction" in d) {
      const _ = d["@direction"];
      if (e.processingMode === "json-ld-1.0")
        throw new K(
          "Invalid JSON-LD syntax; @direction not compatible with " + e.processingMode,
          "jsonld.SyntaxError",
          { code: "invalid context member", context: d }
        );
      if (_ === null)
        delete c["@direction"];
      else {
        if (_ !== "ltr" && _ !== "rtl")
          throw new K(
            'Invalid JSON-LD syntax; the value of "@direction" in a @context must be null, "ltr", or "rtl".',
            "jsonld.SyntaxError",
            { code: "invalid base direction", context: d }
          );
        c["@direction"] = _;
      }
      v.set("@direction", !0);
    }
    if ("@propagate" in d) {
      const _ = d["@propagate"];
      if (e.processingMode === "json-ld-1.0")
        throw new K(
          "Invalid JSON-LD syntax; @propagate not compatible with " + e.processingMode,
          "jsonld.SyntaxError",
          { code: "invalid context entry", context: d }
        );
      if (typeof _ != "boolean")
        throw new K(
          "Invalid JSON-LD syntax; @propagate value must be a boolean.",
          "jsonld.SyntaxError",
          { code: "invalid @propagate value", context: t }
        );
      v.set("@propagate", !0);
    }
    if ("@import" in d) {
      const _ = d["@import"];
      if (e.processingMode === "json-ld-1.0")
        throw new K(
          "Invalid JSON-LD syntax; @import not compatible with " + e.processingMode,
          "jsonld.SyntaxError",
          { code: "invalid context entry", context: d }
        );
      if (!et(_))
        throw new K(
          "Invalid JSON-LD syntax; @import must be a string.",
          "jsonld.SyntaxError",
          { code: "invalid @import value", context: t }
        );
      const m = await n.contextResolver.resolve({
        activeCtx: e,
        context: _,
        documentLoader: n.documentLoader,
        base: n.base
      });
      if (m.length !== 1)
        throw new K(
          "Invalid JSON-LD syntax; @import must reference a single context.",
          "jsonld.SyntaxError",
          { code: "invalid remote context", context: t }
        );
      const E = m[0].getProcessed(e);
      if (E)
        d = E;
      else {
        const L = m[0].document;
        if ("@import" in L)
          throw new K(
            "Invalid JSON-LD syntax: imported context must not include @import.",
            "jsonld.SyntaxError",
            { code: "invalid context entry", context: t }
          );
        for (const I in L)
          d.hasOwnProperty(I) || (d[I] = L[I]);
        m[0].setProcessed(e, d);
      }
      v.set("@import", !0);
    }
    v.set("@protected", d["@protected"] || !1);
    for (const _ in d)
      if (we.createTermDefinition({
        activeCtx: c,
        localCtx: d,
        term: _,
        defined: v,
        options: n,
        overrideProtected: i
      }), Bn(d[_]) && "@context" in d[_]) {
        const m = d[_]["@context"];
        let E = !0;
        if (et(m)) {
          const L = qi(n.base, m);
          a.has(L) ? E = !1 : a.add(L);
        }
        if (E)
          try {
            await we.process({
              activeCtx: c.clone(),
              localCtx: d[_]["@context"],
              overrideProtected: !0,
              options: n,
              cycles: a
            });
          } catch {
            throw new K(
              "Invalid JSON-LD syntax; invalid scoped context.",
              "jsonld.SyntaxError",
              {
                code: "invalid scoped context",
                context: d[_]["@context"],
                term: _
              }
            );
          }
      }
    p.setProcessed(e, {
      context: c,
      events: s
    });
  }
  return c;
};
we.createTermDefinition = ({
  activeCtx: e,
  localCtx: t,
  term: n,
  defined: r,
  options: i,
  overrideProtected: a = !1
}) => {
  if (r.has(n)) {
    if (r.get(n))
      return;
    throw new K(
      "Cyclical context definition detected.",
      "jsonld.CyclicalContext",
      { code: "cyclic IRI mapping", context: t, term: n }
    );
  }
  r.set(n, !1);
  let o;
  if (t.hasOwnProperty(n) && (o = t[n]), n === "@type" && Bn(o) && (o["@container"] || "@set") === "@set" && we.processingMode(e, 1.1)) {
    const d = ["@container", "@id", "@protected"], y = Object.keys(o);
    if (y.length === 0 || y.some((v) => !d.includes(v)))
      throw new K(
        "Invalid JSON-LD syntax; keywords cannot be overridden.",
        "jsonld.SyntaxError",
        { code: "keyword redefinition", context: t, term: n }
      );
  } else {
    if (we.isKeyword(n))
      throw new K(
        "Invalid JSON-LD syntax; keywords cannot be overridden.",
        "jsonld.SyntaxError",
        { code: "keyword redefinition", context: t, term: n }
      );
    if (n.match(Aa)) {
      i.eventHandler && Vr({
        event: {
          type: ["JsonLdEvent"],
          code: "reserved term",
          level: "warning",
          message: 'Terms beginning with "@" are reserved for future use and dropped.',
          details: {
            term: n
          }
        },
        options: i
      });
      return;
    } else if (n === "")
      throw new K(
        "Invalid JSON-LD syntax; a term cannot be an empty string.",
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
  }
  const s = e.mappings.get(n);
  e.mappings.has(n) && e.mappings.delete(n);
  let u = !1;
  if ((et(o) || o === null) && (u = !0, o = { "@id": o }), !Bn(o))
    throw new K(
      "Invalid JSON-LD syntax; @context term values must be strings or objects.",
      "jsonld.SyntaxError",
      { code: "invalid term definition", context: t }
    );
  const l = {};
  e.mappings.set(n, l), l.reverse = !1;
  const f = ["@container", "@id", "@language", "@reverse", "@type"];
  we.processingMode(e, 1.1) && f.push(
    "@context",
    "@direction",
    "@index",
    "@nest",
    "@prefix",
    "@protected"
  );
  for (const d in o)
    if (!f.includes(d))
      throw new K(
        "Invalid JSON-LD syntax; a term definition must not contain " + d,
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
  const c = n.indexOf(":");
  if (l._termHasColon = c > 0, "@reverse" in o) {
    if ("@id" in o)
      throw new K(
        "Invalid JSON-LD syntax; a @reverse term definition must not contain @id.",
        "jsonld.SyntaxError",
        { code: "invalid reverse property", context: t }
      );
    if ("@nest" in o)
      throw new K(
        "Invalid JSON-LD syntax; a @reverse term definition must not contain @nest.",
        "jsonld.SyntaxError",
        { code: "invalid reverse property", context: t }
      );
    const d = o["@reverse"];
    if (!et(d))
      throw new K(
        "Invalid JSON-LD syntax; a @context @reverse value must be a string.",
        "jsonld.SyntaxError",
        { code: "invalid IRI mapping", context: t }
      );
    if (d.match(Aa)) {
      i.eventHandler && Vr({
        event: {
          type: ["JsonLdEvent"],
          code: "reserved @reverse value",
          level: "warning",
          message: '@reverse values beginning with "@" are reserved for future use and dropped.',
          details: {
            reverse: d
          }
        },
        options: i
      }), s ? e.mappings.set(n, s) : e.mappings.delete(n);
      return;
    }
    const y = Br(
      e,
      d,
      { vocab: !0, base: !1 },
      t,
      r,
      i
    );
    if (!pr(y))
      throw new K(
        "Invalid JSON-LD syntax; a @context @reverse value must be an absolute IRI or a blank node identifier.",
        "jsonld.SyntaxError",
        { code: "invalid IRI mapping", context: t }
      );
    l["@id"] = y, l.reverse = !0;
  } else if ("@id" in o) {
    let d = o["@id"];
    if (d && !et(d))
      throw new K(
        "Invalid JSON-LD syntax; a @context @id value must be an array of strings or a string.",
        "jsonld.SyntaxError",
        { code: "invalid IRI mapping", context: t }
      );
    if (d === null)
      l["@id"] = null;
    else if (!we.isKeyword(d) && d.match(Aa)) {
      i.eventHandler && Vr({
        event: {
          type: ["JsonLdEvent"],
          code: "reserved @id value",
          level: "warning",
          message: '@id values beginning with "@" are reserved for future use and dropped.',
          details: {
            id: d
          }
        },
        options: i
      }), s ? e.mappings.set(n, s) : e.mappings.delete(n);
      return;
    } else if (d !== n) {
      if (d = Br(
        e,
        d,
        { vocab: !0, base: !1 },
        t,
        r,
        i
      ), !pr(d) && !we.isKeyword(d))
        throw new K(
          "Invalid JSON-LD syntax; a @context @id value must be an absolute IRI, a blank node identifier, or a keyword.",
          "jsonld.SyntaxError",
          { code: "invalid IRI mapping", context: t }
        );
      if (n.match(/(?::[^:])|\//)) {
        const y = new Map(r).set(n, !0);
        if (Br(
          e,
          n,
          { vocab: !0, base: !1 },
          t,
          y,
          i
        ) !== d)
          throw new K(
            "Invalid JSON-LD syntax; term in form of IRI must expand to definition.",
            "jsonld.SyntaxError",
            { code: "invalid IRI mapping", context: t }
          );
      }
      l["@id"] = d, l._prefix = u && !l._termHasColon && d.match(/[:\/\?#\[\]@]$/);
    }
  }
  if (!("@id" in l))
    if (l._termHasColon) {
      const d = n.substr(0, c);
      if (t.hasOwnProperty(d) && we.createTermDefinition({
        activeCtx: e,
        localCtx: t,
        term: d,
        defined: r,
        options: i
      }), e.mappings.has(d)) {
        const y = n.substr(c + 1);
        l["@id"] = e.mappings.get(d)["@id"] + y;
      } else
        l["@id"] = n;
    } else if (n === "@type")
      l["@id"] = n;
    else {
      if (!("@vocab" in e))
        throw new K(
          "Invalid JSON-LD syntax; @context terms must define an @id.",
          "jsonld.SyntaxError",
          { code: "invalid IRI mapping", context: t, term: n }
        );
      l["@id"] = e["@vocab"] + n;
    }
  if ((o["@protected"] === !0 || r.get("@protected") === !0 && o["@protected"] !== !1) && (e.protected[n] = !0, l.protected = !0), r.set(n, !0), "@type" in o) {
    let d = o["@type"];
    if (!et(d))
      throw new K(
        "Invalid JSON-LD syntax; an @context @type value must be a string.",
        "jsonld.SyntaxError",
        { code: "invalid type mapping", context: t }
      );
    if (d === "@json" || d === "@none") {
      if (we.processingMode(e, 1))
        throw new K(
          `Invalid JSON-LD syntax; an @context @type value must not be "${d}" in JSON-LD 1.0 mode.`,
          "jsonld.SyntaxError",
          { code: "invalid type mapping", context: t }
        );
    } else if (d !== "@id" && d !== "@vocab") {
      if (d = Br(
        e,
        d,
        { vocab: !0, base: !1 },
        t,
        r,
        i
      ), !pr(d))
        throw new K(
          "Invalid JSON-LD syntax; an @context @type value must be an absolute IRI.",
          "jsonld.SyntaxError",
          { code: "invalid type mapping", context: t }
        );
      if (d.indexOf("_:") === 0)
        throw new K(
          "Invalid JSON-LD syntax; an @context @type value must be an IRI, not a blank node identifier.",
          "jsonld.SyntaxError",
          { code: "invalid type mapping", context: t }
        );
    }
    l["@type"] = d;
  }
  if ("@container" in o) {
    const d = et(o["@container"]) ? [o["@container"]] : o["@container"] || [], y = ["@list", "@set", "@index", "@language"];
    let v = !0;
    const _ = d.includes("@set");
    if (we.processingMode(e, 1.1)) {
      if (y.push("@graph", "@id", "@type"), d.includes("@list")) {
        if (d.length !== 1)
          throw new K(
            "Invalid JSON-LD syntax; @context @container with @list must have no other values",
            "jsonld.SyntaxError",
            { code: "invalid container mapping", context: t }
          );
      } else if (d.includes("@graph")) {
        if (d.some((m) => m !== "@graph" && m !== "@id" && m !== "@index" && m !== "@set"))
          throw new K(
            "Invalid JSON-LD syntax; @context @container with @graph must have no other values other than @id, @index, and @set",
            "jsonld.SyntaxError",
            { code: "invalid container mapping", context: t }
          );
      } else
        v &= d.length <= (_ ? 2 : 1);
      if (d.includes("@type") && (l["@type"] = l["@type"] || "@id", !["@id", "@vocab"].includes(l["@type"])))
        throw new K(
          "Invalid JSON-LD syntax; container: @type requires @type to be @id or @vocab.",
          "jsonld.SyntaxError",
          { code: "invalid type mapping", context: t }
        );
    } else
      v &= !S1(o["@container"]), v &= d.length <= 1;
    if (v &= d.every((m) => y.includes(m)), v &= !(_ && d.includes("@list")), !v)
      throw new K(
        "Invalid JSON-LD syntax; @context @container value must be one of the following: " + y.join(", "),
        "jsonld.SyntaxError",
        { code: "invalid container mapping", context: t }
      );
    if (l.reverse && !d.every((m) => ["@index", "@set"].includes(m)))
      throw new K(
        "Invalid JSON-LD syntax; @context @container value for a @reverse type definition must be @index or @set.",
        "jsonld.SyntaxError",
        { code: "invalid reverse property", context: t }
      );
    l["@container"] = d;
  }
  if ("@index" in o) {
    if (!("@container" in o) || !l["@container"].includes("@index"))
      throw new K(
        `Invalid JSON-LD syntax; @index without @index in @container: "${o["@index"]}" on term "${n}".`,
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
    if (!et(o["@index"]) || o["@index"].indexOf("@") === 0)
      throw new K(
        `Invalid JSON-LD syntax; @index must expand to an IRI: "${o["@index"]}" on term "${n}".`,
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
    l["@index"] = o["@index"];
  }
  if ("@context" in o && (l["@context"] = o["@context"]), "@language" in o && !("@type" in o)) {
    let d = o["@language"];
    if (d !== null && !et(d))
      throw new K(
        "Invalid JSON-LD syntax; @context @language value must be a string or null.",
        "jsonld.SyntaxError",
        { code: "invalid language mapping", context: t }
      );
    d !== null && (d = d.toLowerCase()), l["@language"] = d;
  }
  if ("@prefix" in o) {
    if (n.match(/:|\//))
      throw new K(
        "Invalid JSON-LD syntax; @context @prefix used on a compact IRI term",
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
    if (we.isKeyword(l["@id"]))
      throw new K(
        "Invalid JSON-LD syntax; keywords may not be used as prefixes",
        "jsonld.SyntaxError",
        { code: "invalid term definition", context: t }
      );
    if (typeof o["@prefix"] == "boolean")
      l._prefix = o["@prefix"] === !0;
    else
      throw new K(
        "Invalid JSON-LD syntax; @context value for @prefix must be boolean",
        "jsonld.SyntaxError",
        { code: "invalid @prefix value", context: t }
      );
  }
  if ("@direction" in o) {
    const d = o["@direction"];
    if (d !== null && d !== "ltr" && d !== "rtl")
      throw new K(
        'Invalid JSON-LD syntax; @direction value must be null, "ltr", or "rtl".',
        "jsonld.SyntaxError",
        { code: "invalid base direction", context: t }
      );
    l["@direction"] = d;
  }
  if ("@nest" in o) {
    const d = o["@nest"];
    if (!et(d) || d !== "@nest" && d.indexOf("@") === 0)
      throw new K(
        "Invalid JSON-LD syntax; @context @nest value must be a string which is not a keyword other than @nest.",
        "jsonld.SyntaxError",
        { code: "invalid @nest value", context: t }
      );
    l["@nest"] = d;
  }
  // disallow aliasing @context and @preserve
  const p = l["@id"];
  if (p === "@context" || p === "@preserve")
    throw new K(
      "Invalid JSON-LD syntax; @context and @preserve cannot be aliased.",
      "jsonld.SyntaxError",
      { code: "invalid keyword alias", context: t }
    );
  if (s && s.protected && !a && (e.protected[n] = !0, l.protected = !0, !Xd(s, l)))
    throw new K(
      "Invalid JSON-LD syntax; tried to redefine a protected term.",
      "jsonld.SyntaxError",
      { code: "protected term redefinition", context: t, term: n }
    );
};
we.expandIri = (e, t, n, r) => Br(
  e,
  t,
  n,
  void 0,
  void 0,
  r
);
function Br(e, t, n, r, i, a) {
  if (t === null || !et(t) || we.isKeyword(t))
    return t;
  if (t.match(Aa))
    return null;
  if (r && r.hasOwnProperty(t) && i.get(t) !== !0 && we.createTermDefinition({
    activeCtx: e,
    localCtx: r,
    term: t,
    defined: i,
    options: a
  }), n = n || {}, n.vocab) {
    const s = e.mappings.get(t);
    if (s === null)
      return null;
    if (Bn(s) && "@id" in s)
      return s["@id"];
  }
  const o = t.indexOf(":");
  if (o > 0) {
    const s = t.substr(0, o), u = t.substr(o + 1);
    if (s === "_" || u.indexOf("//") === 0)
      return t;
    r && r.hasOwnProperty(s) && we.createTermDefinition({
      activeCtx: e,
      localCtx: r,
      term: s,
      defined: i,
      options: a
    });
    const l = e.mappings.get(s);
    if (l && l._prefix)
      return l["@id"] + u;
    if (pr(t))
      return t;
  }
  if (n.vocab && "@vocab" in e)
    t = e["@vocab"] + t;
  else if (n.base) {
    let s, u;
    "@base" in e ? e["@base"] ? (u = qi(a.base, e["@base"]), s = qi(u, t)) : (u = e["@base"], s = t) : (u = a.base, s = qi(a.base, t)), t = s;
  }
  return t;
}
we.getInitialContext = (e) => {
  const t = JSON.stringify({ processingMode: e.processingMode }), n = sa.get(t);
  if (n)
    return n;
  const r = {
    processingMode: e.processingMode,
    mappings: /* @__PURE__ */ new Map(),
    inverse: null,
    getInverse: i,
    clone: s,
    revertToPreviousContext: u,
    protected: {}
  };
  return sa.size === Dx && sa.clear(), sa.set(t, r), r;
  function i() {
    const l = this;
    if (l.inverse)
      return l.inverse;
    const f = l.inverse = {}, c = l.fastCurieMap = {}, p = {}, d = (l["@language"] || "@none").toLowerCase(), y = l["@direction"], v = l.mappings, _ = [...v.keys()].sort(Cx);
    for (const m of _) {
      const E = v.get(m);
      if (E === null)
        continue;
      let L = E["@container"] || "@none";
      if (L = [].concat(L).sort().join(""), E["@id"] === null)
        continue;
      const I = A1(E["@id"]);
      for (const j of I) {
        let M = f[j];
        const O = we.isKeyword(j);
        if (M)
          !O && !E._termHasColon && p[j].push(m);
        else if (f[j] = M = {}, !O && !E._termHasColon) {
          p[j] = [m];
          const $ = { iri: j, terms: p[j] };
          j[0] in c ? c[j[0]].push($) : c[j[0]] = [$];
        }
        if (M[L] || (M[L] = {
          "@language": {},
          "@type": {},
          "@any": {}
        }), M = M[L], o(m, M["@any"], "@none"), E.reverse)
          o(m, M["@type"], "@reverse");
        else if (E["@type"] === "@none")
          o(m, M["@any"], "@none"), o(m, M["@language"], "@none"), o(m, M["@type"], "@none");
        else if ("@type" in E)
          o(m, M["@type"], E["@type"]);
        else if ("@language" in E && "@direction" in E) {
          const $ = E["@language"], q = E["@direction"];
          $ && q ? o(
            m,
            M["@language"],
            `${$}_${q}`.toLowerCase()
          ) : $ ? o(m, M["@language"], $.toLowerCase()) : q ? o(m, M["@language"], `_${q}`) : o(m, M["@language"], "@null");
        } else
          "@language" in E ? o(
            m,
            M["@language"],
            (E["@language"] || "@null").toLowerCase()
          ) : "@direction" in E ? E["@direction"] ? o(
            m,
            M["@language"],
            `_${E["@direction"]}`
          ) : o(m, M["@language"], "@none") : y ? (o(m, M["@language"], `_${y}`), o(m, M["@language"], "@none"), o(m, M["@type"], "@none")) : (o(m, M["@language"], d), o(m, M["@language"], "@none"), o(m, M["@type"], "@none"));
      }
    }
    for (const m in c)
      a(c, m, 1);
    return f;
  }
  function a(l, f, c) {
    const p = l[f], d = l[f] = {};
    let y, v;
    for (const _ of p)
      y = _.iri, c >= y.length ? v = "" : v = y[c], v in d ? d[v].push(_) : d[v] = [_];
    for (const _ in d)
      _ !== "" && a(d, _, c + 1);
  }
  function o(l, f, c) {
    f.hasOwnProperty(c) || (f[c] = l);
  }
  function s() {
    const l = {};
    return l.mappings = Eg.clone(this.mappings), l.clone = this.clone, l.inverse = null, l.getInverse = this.getInverse, l.protected = Eg.clone(this.protected), this.previousContext && (l.previousContext = this.previousContext.clone()), l.revertToPreviousContext = this.revertToPreviousContext, "@base" in this && (l["@base"] = this["@base"]), "@language" in this && (l["@language"] = this["@language"]), "@vocab" in this && (l["@vocab"] = this["@vocab"]), l;
  }
  function u() {
    return this.previousContext ? this.previousContext.clone() : this;
  }
};
we.getContextValue = (e, t, n) => {
  if (t === null)
    return n === "@context" ? void 0 : null;
  if (e.mappings.has(t)) {
    const r = e.mappings.get(t);
    if ($x(n))
      return r;
    if (r.hasOwnProperty(n))
      return r[n];
  }
  if (n === "@language" && n in e || n === "@direction" && n in e)
    return e[n];
  if (n !== "@context")
    return null;
};
we.processingMode = (e, t) => t.toString() >= "1.1" ? !e.processingMode || e.processingMode >= "json-ld-" + t.toString() : e.processingMode === "json-ld-1.0";
we.isKeyword = (e) => {
  if (!et(e) || e[0] !== "@")
    return !1;
  switch (e) {
    case "@base":
    case "@container":
    case "@context":
    case "@default":
    case "@direction":
    case "@embed":
    case "@explicit":
    case "@graph":
    case "@id":
    case "@included":
    case "@index":
    case "@json":
    case "@language":
    case "@list":
    case "@nest":
    case "@none":
    case "@omitDefault":
    case "@prefix":
    case "@preserve":
    case "@protected":
    case "@requireAll":
    case "@reverse":
    case "@set":
    case "@type":
    case "@value":
    case "@version":
    case "@vocab":
      return !0;
  }
  return !1;
};
function Xd(e, t) {
  if (!(e && typeof e == "object") || !(t && typeof t == "object"))
    return e === t;
  const n = Array.isArray(e);
  if (n !== Array.isArray(t))
    return !1;
  if (n) {
    if (e.length !== t.length)
      return !1;
    for (let a = 0; a < e.length; ++a)
      if (!Xd(e[a], t[a]))
        return !1;
    return !0;
  }
  const r = Object.keys(e), i = Object.keys(t);
  if (r.length !== i.length)
    return !1;
  for (const a in e) {
    let o = e[a], s = t[a];
    if (a === "@container" && Array.isArray(o) && Array.isArray(s) && (o = o.slice().sort(), s = s.slice().sort()), !Xd(o, s))
      return !1;
  }
  return !0;
}
const Ee = kt, {
  isArray: At,
  isObject: bt,
  isEmptyObject: Yd,
  isString: Ge,
  isUndefined: Ha
} = pt, {
  isList: ps,
  isValue: Qd,
  isGraph: qx,
  isSubject: jx
} = Nn, {
  expandIri: Ne,
  getContextValue: Rt,
  isKeyword: Zd,
  process: ji,
  processingMode: Ra
} = Rr, {
  isAbsolute: Ia
} = Qn, {
  REGEX_BCP47: R1,
  REGEX_KEYWORD: Px,
  addValue: ft,
  asArray: Ct,
  getValues: kx,
  validateTypeValue: Fx
} = Tt, {
  handleEvent: Et
} = Yi, Gn = {};
var Bx = Gn;
Gn.expand = async ({
  activeCtx: e,
  activeProperty: t = null,
  element: n,
  options: r = {},
  insideList: i = !1,
  insideIndex: a = !1,
  typeScopedContext: o = null
}) => {
  if (n == null)
    return null;
  if (t === "@default" && (r = Object.assign({}, r, { isFrame: !1 })), !At(n) && !bt(n))
    return !i && (t === null || Ne(
      e,
      t,
      { vocab: !0 },
      r
    ) === "@graph") ? (r.eventHandler && Et({
      event: {
        type: ["JsonLdEvent"],
        code: "free-floating scalar",
        level: "warning",
        message: "Dropping free-floating scalar not in a list.",
        details: {
          value: n
          //activeProperty
          //insideList
        }
      },
      options: r
    }), null) : T1({ activeCtx: e, activeProperty: t, value: n, options: r });
  if (At(n)) {
    let y = [];
    const v = Rt(
      e,
      t,
      "@container"
    ) || [];
    i = i || v.includes("@list");
    for (let _ = 0; _ < n.length; ++_) {
      let m = await Gn.expand({
        activeCtx: e,
        activeProperty: t,
        element: n[_],
        options: r,
        insideIndex: a,
        typeScopedContext: o
      });
      i && At(m) && (m = { "@list": m }), m !== null && (At(m) ? y = y.concat(m) : y.push(m));
    }
    return y;
  }
  const s = Ne(
    e,
    t,
    { vocab: !0 },
    r
  ), u = Rt(e, t, "@context");
  o = o || (e.previousContext ? e : null);
  let l = Object.keys(n).sort(), f = !a;
  if (f && o && l.length <= 2 && !l.includes("@context"))
    for (const y of l) {
      const v = Ne(
        o,
        y,
        { vocab: !0 },
        r
      );
      if (v === "@value") {
        f = !1, e = o;
        break;
      }
      if (v === "@id" && l.length === 1) {
        f = !1;
        break;
      }
    }
  f && (e = e.revertToPreviousContext()), Ha(u) || (e = await ji({
    activeCtx: e,
    localCtx: u,
    propagate: !0,
    overrideProtected: !0,
    options: r
  })), "@context" in n && (e = await ji(
    { activeCtx: e, localCtx: n["@context"], options: r }
  )), o = e;
  let c = null;
  for (const y of l)
    if (Ne(e, y, { vocab: !0 }, r) === "@type") {
      c = c || y;
      const _ = n[y], m = Array.isArray(_) ? _.length > 1 ? _.slice().sort() : _ : [_];
      for (const E of m) {
        const L = Rt(o, E, "@context");
        Ha(L) || (e = await ji({
          activeCtx: e,
          localCtx: L,
          options: r,
          propagate: !1
        }));
      }
    }
  let p = {};
  await I1({
    activeCtx: e,
    activeProperty: t,
    expandedActiveProperty: s,
    element: n,
    expandedParent: p,
    options: r,
    insideList: i,
    typeKey: c,
    typeScopedContext: o
  }), l = Object.keys(p);
  let d = l.length;
  if ("@value" in p) {
    if ("@type" in p && ("@language" in p || "@direction" in p))
      throw new Ee(
        'Invalid JSON-LD syntax; an element containing "@value" may not contain both "@type" and either "@language" or "@direction".',
        "jsonld.SyntaxError",
        { code: "invalid value object", element: p }
      );
    let y = d - 1;
    if ("@type" in p && (y -= 1), "@index" in p && (y -= 1), "@language" in p && (y -= 1), "@direction" in p && (y -= 1), y !== 0)
      throw new Ee(
        'Invalid JSON-LD syntax; an element containing "@value" may only have an "@index" property and either "@type" or either or both "@language" or "@direction".',
        "jsonld.SyntaxError",
        { code: "invalid value object", element: p }
      );
    const v = p["@value"] === null ? [] : Ct(p["@value"]), _ = kx(p, "@type");
    if (!(Ra(e, 1.1) && _.includes("@json") && _.length === 1))
      if (v.length === 0)
        r.eventHandler && Et({
          event: {
            type: ["JsonLdEvent"],
            code: "null @value value",
            level: "warning",
            message: "Dropping null @value value.",
            details: {
              value: p
            }
          },
          options: r
        }), p = null;
      else {
        if (!v.every((m) => Ge(m) || Yd(m)) && "@language" in p)
          throw new Ee(
            "Invalid JSON-LD syntax; only strings may be language-tagged.",
            "jsonld.SyntaxError",
            { code: "invalid language-tagged value", element: p }
          );
        if (!_.every((m) => Ia(m) && !(Ge(m) && m.indexOf("_:") === 0) || Yd(m)))
          throw new Ee(
            'Invalid JSON-LD syntax; an element containing "@value" and "@type" must have an absolute IRI for the value of "@type".',
            "jsonld.SyntaxError",
            { code: "invalid typed value", element: p }
          );
      }
  } else if ("@type" in p && !At(p["@type"]))
    p["@type"] = [p["@type"]];
  else if ("@set" in p || "@list" in p) {
    if (d > 1 && !(d === 2 && "@index" in p))
      throw new Ee(
        'Invalid JSON-LD syntax; if an element has the property "@set" or "@list", then it can have at most one other property that is "@index".',
        "jsonld.SyntaxError",
        { code: "invalid set or list object", element: p }
      );
    "@set" in p && (p = p["@set"], l = Object.keys(p), d = l.length);
  } else
    d === 1 && "@language" in p && (r.eventHandler && Et({
      event: {
        type: ["JsonLdEvent"],
        code: "object with only @language",
        level: "warning",
        message: "Dropping object with only @language.",
        details: {
          value: p
        }
      },
      options: r
    }), p = null);
  if (bt(p) && !r.keepFreeFloatingNodes && !i && (t === null || s === "@graph") && (d === 0 || "@value" in p || "@list" in p || d === 1 && "@id" in p)) {
    if (r.eventHandler) {
      let y, v;
      d === 0 ? (y = "empty object", v = "Dropping empty object.") : "@value" in p ? (y = "object with only @value", v = "Dropping object with only @value.") : "@list" in p ? (y = "object with only @list", v = "Dropping object with only @list.") : d === 1 && "@id" in p && (y = "object with only @id", v = "Dropping object with only @id."), Et({
        event: {
          type: ["JsonLdEvent"],
          code: y,
          level: "warning",
          message: v,
          details: {
            value: p
          }
        },
        options: r
      });
    }
    p = null;
  }
  return p;
};
async function I1({
  activeCtx: e,
  activeProperty: t,
  expandedActiveProperty: n,
  element: r,
  expandedParent: i,
  options: a = {},
  insideList: o,
  typeKey: s,
  typeScopedContext: u
}) {
  const l = Object.keys(r).sort(), f = [];
  let c;
  const p = r[s] && Ne(
    e,
    At(r[s]) ? r[s][0] : r[s],
    { vocab: !0 },
    {
      ...a,
      typeExpansion: !0
    }
  ) === "@json";
  for (const d of l) {
    let y = r[d], v;
    if (d === "@context")
      continue;
    const _ = Ne(e, d, { vocab: !0 }, a);
    if (_ === null || !(Ia(_) || Zd(_))) {
      a.eventHandler && Et({
        event: {
          type: ["JsonLdEvent"],
          code: "invalid property",
          level: "warning",
          message: "Dropping property that did not expand into an absolute IRI or keyword.",
          details: {
            property: d,
            expandedProperty: _
          }
        },
        options: a
      });
      continue;
    }
    if (Zd(_)) {
      if (n === "@reverse")
        throw new Ee(
          "Invalid JSON-LD syntax; a keyword cannot be used as a @reverse property.",
          "jsonld.SyntaxError",
          { code: "invalid reverse property map", value: y }
        );
      if (_ in i && _ !== "@included" && _ !== "@type")
        throw new Ee(
          "Invalid JSON-LD syntax; colliding keywords detected.",
          "jsonld.SyntaxError",
          { code: "colliding keywords", keyword: _ }
        );
    }
    if (_ === "@id") {
      if (!Ge(y)) {
        if (!a.isFrame)
          throw new Ee(
            'Invalid JSON-LD syntax; "@id" value must a string.',
            "jsonld.SyntaxError",
            { code: "invalid @id value", value: y }
          );
        if (bt(y)) {
          if (!Yd(y))
            throw new Ee(
              'Invalid JSON-LD syntax; "@id" value an empty object or array of strings, if framing',
              "jsonld.SyntaxError",
              { code: "invalid @id value", value: y }
            );
        } else if (At(y)) {
          if (!y.every((I) => Ge(I)))
            throw new Ee(
              'Invalid JSON-LD syntax; "@id" value an empty object or array of strings, if framing',
              "jsonld.SyntaxError",
              { code: "invalid @id value", value: y }
            );
        } else
          throw new Ee(
            'Invalid JSON-LD syntax; "@id" value an empty object or array of strings, if framing',
            "jsonld.SyntaxError",
            { code: "invalid @id value", value: y }
          );
      }
      ft(
        i,
        "@id",
        Ct(y).map((I) => {
          if (Ge(I)) {
            const j = Ne(e, I, { base: !0 }, a);
            return a.eventHandler && (j === null ? Et(I === null ? {
              event: {
                type: ["JsonLdEvent"],
                code: "null @id value",
                level: "warning",
                message: "Null @id found.",
                details: {
                  id: I
                }
              },
              options: a
            } : {
              event: {
                type: ["JsonLdEvent"],
                code: "reserved @id value",
                level: "warning",
                message: "Reserved @id found.",
                details: {
                  id: I
                }
              },
              options: a
            }) : Ia(j) || Et({
              event: {
                type: ["JsonLdEvent"],
                code: "relative @id reference",
                level: "warning",
                message: "Relative @id reference found.",
                details: {
                  id: I,
                  expandedId: j
                }
              },
              options: a
            })), j;
          }
          return I;
        }),
        { propertyIsArray: a.isFrame }
      );
      continue;
    }
    if (_ === "@type") {
      bt(y) && (y = Object.fromEntries(Object.entries(y).map(([I, j]) => [
        Ne(u, I, { vocab: !0 }),
        Ct(j).map(
          (M) => Ne(
            u,
            M,
            { base: !0, vocab: !0 },
            { ...a, typeExpansion: !0 }
          )
        )
      ]))), Fx(y, a.isFrame), ft(
        i,
        "@type",
        Ct(y).map((I) => {
          if (Ge(I)) {
            const j = Ne(
              u,
              I,
              { base: !0, vocab: !0 },
              { ...a, typeExpansion: !0 }
            );
            return j !== "@json" && !Ia(j) && a.eventHandler && Et({
              event: {
                type: ["JsonLdEvent"],
                code: "relative @type reference",
                level: "warning",
                message: "Relative @type reference found.",
                details: {
                  type: I
                }
              },
              options: a
            }), j;
          }
          return I;
        }),
        { propertyIsArray: a.isFrame }
      );
      continue;
    }
    if (_ === "@included" && Ra(e, 1.1)) {
      const I = Ct(await Gn.expand({
        activeCtx: e,
        activeProperty: t,
        element: y,
        options: a
      }));
      if (!I.every((j) => jx(j)))
        throw new Ee(
          "Invalid JSON-LD syntax; values of @included must expand to node objects.",
          "jsonld.SyntaxError",
          { code: "invalid @included value", value: y }
        );
      ft(
        i,
        "@included",
        I,
        { propertyIsArray: !0 }
      );
      continue;
    }
    if (_ === "@graph" && !(bt(y) || At(y)))
      throw new Ee(
        'Invalid JSON-LD syntax; "@graph" value must not be an object or an array.',
        "jsonld.SyntaxError",
        { code: "invalid @graph value", value: y }
      );
    if (_ === "@value") {
      c = y, p && Ra(e, 1.1) ? i["@value"] = y : ft(
        i,
        "@value",
        y,
        { propertyIsArray: a.isFrame }
      );
      continue;
    }
    if (_ === "@language") {
      if (y === null)
        continue;
      if (!Ge(y) && !a.isFrame)
        throw new Ee(
          'Invalid JSON-LD syntax; "@language" value must be a string.',
          "jsonld.SyntaxError",
          { code: "invalid language-tagged string", value: y }
        );
      y = Ct(y).map((I) => Ge(I) ? I.toLowerCase() : I);
      for (const I of y)
        Ge(I) && !I.match(R1) && a.eventHandler && Et({
          event: {
            type: ["JsonLdEvent"],
            code: "invalid @language value",
            level: "warning",
            message: "@language value must be valid BCP47.",
            details: {
              language: I
            }
          },
          options: a
        });
      ft(
        i,
        "@language",
        y,
        { propertyIsArray: a.isFrame }
      );
      continue;
    }
    if (_ === "@direction") {
      if (!Ge(y) && !a.isFrame)
        throw new Ee(
          'Invalid JSON-LD syntax; "@direction" value must be a string.',
          "jsonld.SyntaxError",
          { code: "invalid base direction", value: y }
        );
      y = Ct(y);
      for (const I of y)
        if (Ge(I) && I !== "ltr" && I !== "rtl")
          throw new Ee(
            'Invalid JSON-LD syntax; "@direction" must be "ltr" or "rtl".',
            "jsonld.SyntaxError",
            { code: "invalid base direction", value: y }
          );
      ft(
        i,
        "@direction",
        y,
        { propertyIsArray: a.isFrame }
      );
      continue;
    }
    if (_ === "@index") {
      if (!Ge(y))
        throw new Ee(
          'Invalid JSON-LD syntax; "@index" value must be a string.',
          "jsonld.SyntaxError",
          { code: "invalid @index value", value: y }
        );
      ft(i, "@index", y);
      continue;
    }
    if (_ === "@reverse") {
      if (!bt(y))
        throw new Ee(
          'Invalid JSON-LD syntax; "@reverse" value must be an object.',
          "jsonld.SyntaxError",
          { code: "invalid @reverse value", value: y }
        );
      if (v = await Gn.expand({
        activeCtx: e,
        activeProperty: "@reverse",
        element: y,
        options: a
      }), "@reverse" in v)
        for (const j in v["@reverse"])
          ft(
            i,
            j,
            v["@reverse"][j],
            { propertyIsArray: !0 }
          );
      let I = i["@reverse"] || null;
      for (const j in v) {
        if (j === "@reverse")
          continue;
        I === null && (I = i["@reverse"] = {}), ft(I, j, [], { propertyIsArray: !0 });
        const M = v[j];
        for (let O = 0; O < M.length; ++O) {
          const $ = M[O];
          if (Qd($) || ps($))
            throw new Ee(
              'Invalid JSON-LD syntax; "@reverse" value must not be a @value or an @list.',
              "jsonld.SyntaxError",
              { code: "invalid reverse property value", value: v }
            );
          ft(I, j, $, { propertyIsArray: !0 });
        }
      }
      continue;
    }
    if (_ === "@nest") {
      f.push(d);
      continue;
    }
    let m = e;
    const E = Rt(e, d, "@context");
    Ha(E) || (m = await ji({
      activeCtx: e,
      localCtx: E,
      propagate: !0,
      overrideProtected: !0,
      options: a
    }));
    const L = Rt(m, d, "@container") || [];
    if (L.includes("@language") && bt(y)) {
      const I = Rt(m, d, "@direction");
      v = Ux(m, y, I, a);
    } else if (L.includes("@index") && bt(y)) {
      const I = L.includes("@graph"), j = Rt(m, d, "@index") || "@index", M = j !== "@index" && Ne(e, j, { vocab: !0 }, a);
      v = await gs({
        activeCtx: m,
        options: a,
        activeProperty: d,
        value: y,
        asGraph: I,
        indexKey: j,
        propertyIndex: M
      });
    } else if (L.includes("@id") && bt(y)) {
      const I = L.includes("@graph");
      v = await gs({
        activeCtx: m,
        options: a,
        activeProperty: d,
        value: y,
        asGraph: I,
        indexKey: "@id"
      });
    } else if (L.includes("@type") && bt(y))
      v = await gs({
        // since container is `@type`, revert type scoped context when expanding
        activeCtx: m.revertToPreviousContext(),
        options: a,
        activeProperty: d,
        value: y,
        asGraph: !1,
        indexKey: "@type"
      });
    else {
      const I = _ === "@list";
      if (I || _ === "@set") {
        let j = t;
        I && n === "@graph" && (j = null), v = await Gn.expand({
          activeCtx: m,
          activeProperty: j,
          element: y,
          options: a,
          insideList: I
        });
      } else
        Rt(e, d, "@type") === "@json" ? v = {
          "@type": "@json",
          "@value": y
        } : v = await Gn.expand({
          activeCtx: m,
          activeProperty: d,
          element: y,
          options: a,
          insideList: !1
        });
    }
    if (!(v === null && _ !== "@value")) {
      if (_ !== "@list" && !ps(v) && L.includes("@list") && (v = { "@list": Ct(v) }), L.includes("@graph") && !L.some((I) => I === "@id" || I === "@index") && (v = Ct(v).map((I) => ({ "@graph": Ct(I) }))), m.mappings.has(d) && m.mappings.get(d).reverse) {
        const I = i["@reverse"] = i["@reverse"] || {};
        v = Ct(v);
        for (let j = 0; j < v.length; ++j) {
          const M = v[j];
          if (Qd(M) || ps(M))
            throw new Ee(
              'Invalid JSON-LD syntax; "@reverse" value must not be a @value or an @list.',
              "jsonld.SyntaxError",
              { code: "invalid reverse property value", value: v }
            );
          ft(I, _, M, { propertyIsArray: !0 });
        }
        continue;
      }
      ft(i, _, v, {
        propertyIsArray: !0
      });
    }
  }
  if ("@value" in i && !(i["@type"] === "@json" && Ra(e, 1.1))) {
    if ((bt(c) || At(c)) && !a.isFrame)
      throw new Ee(
        'Invalid JSON-LD syntax; "@value" value must not be an object or an array.',
        "jsonld.SyntaxError",
        { code: "invalid value object value", value: c }
      );
  }
  for (const d of f) {
    const y = At(r[d]) ? r[d] : [r[d]];
    for (const v of y) {
      if (!bt(v) || Object.keys(v).some((_) => Ne(e, _, { vocab: !0 }, a) === "@value"))
        throw new Ee(
          "Invalid JSON-LD syntax; nested value must be a node object.",
          "jsonld.SyntaxError",
          { code: "invalid @nest value", value: v }
        );
      await I1({
        activeCtx: e,
        activeProperty: t,
        expandedActiveProperty: n,
        element: v,
        expandedParent: i,
        options: a,
        insideList: o,
        typeScopedContext: u,
        typeKey: s
      });
    }
  }
}
function T1({ activeCtx: e, activeProperty: t, value: n, options: r }) {
  if (n == null)
    return null;
  const i = Ne(
    e,
    t,
    { vocab: !0 },
    r
  );
  if (i === "@id")
    return Ne(e, n, { base: !0 }, r);
  if (i === "@type")
    return Ne(
      e,
      n,
      { vocab: !0, base: !0 },
      { ...r, typeExpansion: !0 }
    );
  const a = Rt(e, t, "@type");
  if ((a === "@id" || i === "@graph") && Ge(n)) {
    const s = Ne(e, n, { base: !0 }, r);
    return s === null && n.match(Px) && r.eventHandler && Et({
      event: {
        type: ["JsonLdEvent"],
        code: "reserved @id value",
        level: "warning",
        message: "Reserved @id found.",
        details: {
          id: t
        }
      },
      options: r
    }), { "@id": s };
  }
  if (a === "@vocab" && Ge(n))
    return {
      "@id": Ne(e, n, { vocab: !0, base: !0 }, r)
    };
  if (Zd(i))
    return n;
  const o = {};
  if (a && !["@id", "@vocab", "@none"].includes(a))
    o["@type"] = a;
  else if (Ge(n)) {
    const s = Rt(e, t, "@language");
    s !== null && (o["@language"] = s);
    const u = Rt(e, t, "@direction");
    u !== null && (o["@direction"] = u);
  }
  return ["boolean", "number", "string"].includes(typeof n) || (n = n.toString()), o["@value"] = n, o;
}
function Ux(e, t, n, r) {
  const i = [], a = Object.keys(t).sort();
  for (const o of a) {
    const s = Ne(e, o, { vocab: !0 }, r);
    let u = t[o];
    At(u) || (u = [u]);
    for (const l of u) {
      if (l === null)
        continue;
      if (!Ge(l))
        throw new Ee(
          "Invalid JSON-LD syntax; language map values must be strings.",
          "jsonld.SyntaxError",
          { code: "invalid language map value", languageMap: t }
        );
      const f = { "@value": l };
      s !== "@none" && (o.match(R1) || r.eventHandler && Et({
        event: {
          type: ["JsonLdEvent"],
          code: "invalid @language value",
          level: "warning",
          message: "@language value must be valid BCP47.",
          details: {
            language: o
          }
        },
        options: r
      }), f["@language"] = o.toLowerCase()), n && (f["@direction"] = n), i.push(f);
    }
  }
  return i;
}
async function gs({
  activeCtx: e,
  options: t,
  activeProperty: n,
  value: r,
  asGraph: i,
  indexKey: a,
  propertyIndex: o
}) {
  const s = [], u = Object.keys(r).sort(), l = a === "@type";
  for (let f of u) {
    if (l) {
      const d = Rt(e, f, "@context");
      Ha(d) || (e = await ji({
        activeCtx: e,
        localCtx: d,
        propagate: !1,
        options: t
      }));
    }
    let c = r[f];
    At(c) || (c = [c]), c = await Gn.expand({
      activeCtx: e,
      activeProperty: n,
      element: c,
      options: t,
      insideList: !1,
      insideIndex: !0
    });
    let p;
    o ? f === "@none" ? p = "@none" : p = T1(
      { activeCtx: e, activeProperty: a, value: f, options: t }
    ) : p = Ne(e, f, { vocab: !0 }, t), a === "@id" ? f = Ne(e, f, { base: !0 }, t) : l && (f = p);
    for (let d of c) {
      if (i && !qx(d) && (d = { "@graph": [d] }), a === "@type")
        p === "@none" || (d["@type"] ? d["@type"] = [f].concat(d["@type"]) : d["@type"] = [f]);
      else {
        if (Qd(d) && !["@language", "@type", "@index"].includes(a))
          throw new Ee(
            `Invalid JSON-LD syntax; Attempt to add illegal key to value object: "${a}".`,
            "jsonld.SyntaxError",
            { code: "invalid value object", value: d }
          );
        o ? p !== "@none" && ft(d, o, p, {
          propertyIsArray: !0,
          prependValue: !0
        }) : p !== "@none" && !(a in d) && (d[a] = f);
      }
      s.push(d);
    }
  }
  return s;
}
const { isKeyword: x1 } = Rr, zt = Nn, ys = pt, Yt = Tt, Hx = kt, Ke = {};
var co = Ke;
Ke.createMergedNodeMap = (e, t) => {
  t = t || {};
  const n = t.issuer || new Yt.IdentifierIssuer("_:b"), r = { "@default": {} };
  return Ke.createNodeMap(e, r, "@default", n), Ke.mergeNodeMaps(r);
};
Ke.createNodeMap = (e, t, n, r, i, a) => {
  if (ys.isArray(e)) {
    for (const l of e)
      Ke.createNodeMap(l, t, n, r, void 0, a);
    return;
  }
  if (!ys.isObject(e)) {
    a && a.push(e);
    return;
  }
  if (zt.isValue(e)) {
    if ("@type" in e) {
      let l = e["@type"];
      l.indexOf("_:") === 0 && (e["@type"] = l = r.getId(l));
    }
    a && a.push(e);
    return;
  } else if (a && zt.isList(e)) {
    const l = [];
    Ke.createNodeMap(e["@list"], t, n, r, i, l), a.push({ "@list": l });
    return;
  }
  if ("@type" in e) {
    const l = e["@type"];
    for (const f of l)
      f.indexOf("_:") === 0 && r.getId(f);
  }
  ys.isUndefined(i) && (i = zt.isBlankNode(e) ? r.getId(e["@id"]) : e["@id"]), a && a.push({ "@id": i });
  const o = t[n], s = o[i] = o[i] || {};
  s["@id"] = i;
  const u = Object.keys(e).sort();
  for (let l of u) {
    if (l === "@id")
      continue;
    if (l === "@reverse") {
      const c = { "@id": i }, p = e["@reverse"];
      for (const d in p) {
        const y = p[d];
        for (const v of y) {
          let _ = v["@id"];
          zt.isBlankNode(v) && (_ = r.getId(_)), Ke.createNodeMap(v, t, n, r, _), Yt.addValue(
            o[_],
            d,
            c,
            { propertyIsArray: !0, allowDuplicate: !1 }
          );
        }
      }
      continue;
    }
    if (l === "@graph") {
      i in t || (t[i] = {}), Ke.createNodeMap(e[l], t, i, r);
      continue;
    }
    if (l === "@included") {
      Ke.createNodeMap(e[l], t, n, r);
      continue;
    }
    if (l !== "@type" && x1(l)) {
      if (l === "@index" && l in s && (e[l] !== s[l] || e[l]["@id"] !== s[l]["@id"]))
        throw new Hx(
          "Invalid JSON-LD syntax; conflicting @index property detected.",
          "jsonld.SyntaxError",
          { code: "conflicting indexes", subject: s }
        );
      s[l] = e[l];
      continue;
    }
    const f = e[l];
    if (l.indexOf("_:") === 0 && (l = r.getId(l)), f.length === 0) {
      Yt.addValue(s, l, [], { propertyIsArray: !0 });
      continue;
    }
    for (let c of f)
      if (l === "@type" && (c = c.indexOf("_:") === 0 ? r.getId(c) : c), zt.isSubject(c) || zt.isSubjectReference(c)) {
        if ("@id" in c && !c["@id"])
          continue;
        const p = zt.isBlankNode(c) ? r.getId(c["@id"]) : c["@id"];
        Yt.addValue(
          s,
          l,
          { "@id": p },
          { propertyIsArray: !0, allowDuplicate: !1 }
        ), Ke.createNodeMap(c, t, n, r, p);
      } else if (zt.isValue(c))
        Yt.addValue(
          s,
          l,
          c,
          { propertyIsArray: !0, allowDuplicate: !1 }
        );
      else if (zt.isList(c)) {
        const p = [];
        Ke.createNodeMap(c["@list"], t, n, r, i, p), c = { "@list": p }, Yt.addValue(
          s,
          l,
          c,
          { propertyIsArray: !0, allowDuplicate: !1 }
        );
      } else
        Ke.createNodeMap(c, t, n, r, i), Yt.addValue(
          s,
          l,
          c,
          { propertyIsArray: !0, allowDuplicate: !1 }
        );
  }
};
Ke.mergeNodeMapGraphs = (e) => {
  const t = {};
  for (const n of Object.keys(e).sort())
    for (const r of Object.keys(e[n]).sort()) {
      const i = e[n][r];
      r in t || (t[r] = { "@id": r });
      const a = t[r];
      for (const o of Object.keys(i).sort())
        if (x1(o) && o !== "@type")
          a[o] = Yt.clone(i[o]);
        else
          for (const s of i[o])
            Yt.addValue(
              a,
              o,
              Yt.clone(s),
              { propertyIsArray: !0, allowDuplicate: !1 }
            );
    }
  return t;
};
Ke.mergeNodeMaps = (e) => {
  const t = e["@default"], n = Object.keys(e).sort();
  for (const r of n) {
    if (r === "@default")
      continue;
    const i = e[r];
    let a = t[r];
    a ? "@graph" in a || (a["@graph"] = []) : t[r] = a = {
      "@id": r,
      "@graph": []
    };
    const o = a["@graph"];
    for (const s of Object.keys(i).sort()) {
      const u = i[s];
      zt.isSubjectReference(u) || o.push(u);
    }
  }
  return t;
};
const {
  isSubjectReference: Gx
} = Nn, {
  createMergedNodeMap: Vx
} = co, N1 = {};
var Wx = N1;
N1.flatten = (e) => {
  const t = Vx(e), n = [], r = Object.keys(t).sort();
  for (let i = 0; i < r.length; ++i) {
    const a = t[r[i]];
    Gx(a) || n.push(a);
  }
  return n;
};
const Jx = kt, vs = Nn, Si = pt, {
  REGEX_BCP47: Sg,
  addValue: Ag
} = Tt, {
  handleEvent: Rg
} = Yi, {
  // RDF,
  RDF_LIST: zx,
  RDF_FIRST: _s,
  RDF_REST: bs,
  RDF_NIL: ms,
  RDF_TYPE: Kx,
  // RDF_PLAIN_LITERAL,
  // RDF_XML_LITERAL,
  RDF_JSON_LITERAL: Xx,
  // RDF_OBJECT,
  // RDF_LANGSTRING,
  // XSD,
  XSD_BOOLEAN: Ig,
  XSD_DOUBLE: Tg,
  XSD_INTEGER: xg,
  XSD_STRING: ws
} = Jh, O1 = {};
var Yx = O1;
O1.fromRDF = async (e, t) => {
  const n = {}, r = { "@default": n }, i = {}, {
    useRdfType: a = !1,
    useNativeTypes: o = !1,
    rdfDirection: s = null
  } = t;
  for (const f of e) {
    const c = f.graph.termType === "DefaultGraph" ? "@default" : f.graph.value;
    c in r || (r[c] = {}), c !== "@default" && !(c in n) && (n[c] = { "@id": c });
    const p = r[c], d = f.subject.value, y = f.predicate.value, v = f.object;
    d in p || (p[d] = { "@id": d });
    const _ = p[d], m = v.termType.endsWith("Node");
    if (m && !(v.value in p) && (p[v.value] = { "@id": v.value }), y === Kx && !a && m) {
      Ag(_, "@type", v.value, { propertyIsArray: !0 });
      continue;
    }
    const E = Qx(v, o, s, t);
    if (Ag(_, y, E, { propertyIsArray: !0 }), m)
      if (v.value === ms) {
        const L = p[v.value];
        "usages" in L || (L.usages = []), L.usages.push({
          node: _,
          property: y,
          value: E
        });
      } else
        v.value in i ? i[v.value] = !1 : i[v.value] = {
          node: _,
          property: y,
          value: E
        };
  }
  for (const f in r) {
    const c = r[f];
    if (!(ms in c))
      continue;
    const p = c[ms];
    if (p.usages) {
      for (let d of p.usages) {
        let y = d.node, v = d.property, _ = d.value;
        const m = [], E = [];
        let L = Object.keys(y).length;
        for (; v === bs && Si.isObject(i[y["@id"]]) && Si.isArray(y[_s]) && y[_s].length === 1 && Si.isArray(y[bs]) && y[bs].length === 1 && (L === 3 || L === 4 && Si.isArray(y["@type"]) && y["@type"].length === 1 && y["@type"][0] === zx) && (m.push(y[_s][0]), E.push(y["@id"]), d = i[y["@id"]], y = d.node, v = d.property, _ = d.value, L = Object.keys(y).length, !!vs.isBlankNode(y)); )
          ;
        delete _["@id"], _["@list"] = m.reverse();
        for (const I of E)
          delete c[I];
      }
      delete p.usages;
    }
  }
  const u = [], l = Object.keys(n).sort();
  for (const f of l) {
    const c = n[f];
    if (f in r) {
      const p = c["@graph"] = [], d = r[f], y = Object.keys(d).sort();
      for (const v of y) {
        const _ = d[v];
        vs.isSubjectReference(_) || p.push(_);
      }
    }
    vs.isSubjectReference(c) || u.push(c);
  }
  return u;
};
function Qx(e, t, n, r) {
  if (e.termType.endsWith("Node"))
    return { "@id": e.value };
  const i = { "@value": e.value };
  if (e.language)
    e.language.match(Sg) || r.eventHandler && Rg({
      event: {
        type: ["JsonLdEvent"],
        code: "invalid @language value",
        level: "warning",
        message: "@language value must be valid BCP47.",
        details: {
          language: e.language
        }
      },
      options: r
    }), i["@language"] = e.language;
  else {
    let a = e.datatype.value;
    if (a || (a = ws), a === Xx) {
      a = "@json";
      try {
        i["@value"] = JSON.parse(i["@value"]);
      } catch (o) {
        throw new Jx(
          "JSON literal could not be parsed.",
          "jsonld.InvalidJsonLiteral",
          { code: "invalid JSON literal", value: i["@value"], cause: o }
        );
      }
    }
    if (t) {
      if (a === Ig)
        i["@value"] === "true" ? i["@value"] = !0 : i["@value"] === "false" && (i["@value"] = !1);
      else if (Si.isNumeric(i["@value"]))
        if (a === xg) {
          const o = parseInt(i["@value"], 10);
          o.toFixed(0) === i["@value"] && (i["@value"] = o);
        } else
          a === Tg && (i["@value"] = parseFloat(i["@value"]));
      [Ig, xg, Tg, ws].includes(a) || (i["@type"] = a);
    } else if (n === "i18n-datatype" && a.startsWith("https://www.w3.org/ns/i18n#")) {
      const [, o, s] = a.split(/[#_]/);
      o.length > 0 && (i["@language"] = o, o.match(Sg) || r.eventHandler && Rg({
        event: {
          type: ["JsonLdEvent"],
          code: "invalid @language value",
          level: "warning",
          message: "@language value must be valid BCP47.",
          details: {
            language: o
          }
        },
        options: r
      })), i["@direction"] = s;
    } else
      a !== ws && (i["@type"] = a);
  }
  return i;
}
var Zx = function e(t) {
  return t === null || typeof t != "object" || t.toJSON != null ? JSON.stringify(t) : Array.isArray(t) ? "[" + t.reduce((n, r, i) => {
    const a = i === 0 ? "" : ",", o = r === void 0 || typeof r == "symbol" ? null : r;
    return n + a + e(o);
  }, "") + "]" : "{" + Object.keys(t).sort().reduce((n, r, i) => {
    if (t[r] === void 0 || typeof t[r] == "symbol")
      return n;
    const a = n.length === 0 ? "" : ",";
    return n + a + e(r) + ":" + e(t[r]);
  }, "") + "}";
};
const { createNodeMap: eN } = co, { isKeyword: tN } = Rr, Ng = Nn, nN = Zx, bi = pt, rN = Tt, {
  handleEvent: Pi
} = Yi, {
  // RDF,
  // RDF_LIST,
  RDF_FIRST: iN,
  RDF_REST: aN,
  RDF_NIL: oN,
  RDF_TYPE: sN,
  // RDF_PLAIN_LITERAL,
  // RDF_XML_LITERAL,
  RDF_JSON_LITERAL: uN,
  // RDF_OBJECT,
  RDF_LANGSTRING: lN,
  // XSD,
  XSD_BOOLEAN: fN,
  XSD_DOUBLE: Og,
  XSD_INTEGER: cN,
  XSD_STRING: dN
} = Jh, {
  isAbsolute: Ga
} = Qn, $1 = {};
var hN = $1;
$1.toRDF = (e, t) => {
  const n = new rN.IdentifierIssuer("_:b"), r = { "@default": {} };
  eN(e, r, "@default", n);
  const i = [], a = Object.keys(r).sort();
  for (const o of a) {
    let s;
    if (o === "@default")
      s = { termType: "DefaultGraph", value: "" };
    else if (Ga(o))
      o.startsWith("_:") ? s = { termType: "BlankNode" } : s = { termType: "NamedNode" }, s.value = o;
    else {
      t.eventHandler && Pi({
        event: {
          type: ["JsonLdEvent"],
          code: "relative graph reference",
          level: "warning",
          message: "Relative graph reference found.",
          details: {
            graph: o
          }
        },
        options: t
      });
      continue;
    }
    pN(i, r[o], s, n, t);
  }
  return i;
};
function pN(e, t, n, r, i) {
  const a = Object.keys(t).sort();
  for (const o of a) {
    const s = t[o], u = Object.keys(s).sort();
    for (let l of u) {
      const f = s[l];
      if (l === "@type")
        l = sN;
      else if (tN(l))
        continue;
      for (const c of f) {
        const p = {
          termType: o.startsWith("_:") ? "BlankNode" : "NamedNode",
          value: o
        };
        if (!Ga(o)) {
          i.eventHandler && Pi({
            event: {
              type: ["JsonLdEvent"],
              code: "relative subject reference",
              level: "warning",
              message: "Relative subject reference found.",
              details: {
                subject: o
              }
            },
            options: i
          });
          continue;
        }
        const d = {
          termType: l.startsWith("_:") ? "BlankNode" : "NamedNode",
          value: l
        };
        if (!Ga(l)) {
          i.eventHandler && Pi({
            event: {
              type: ["JsonLdEvent"],
              code: "relative predicate reference",
              level: "warning",
              message: "Relative predicate reference found.",
              details: {
                predicate: l
              }
            },
            options: i
          });
          continue;
        }
        if (d.termType === "BlankNode" && !i.produceGeneralizedRdf) {
          i.eventHandler && Pi({
            event: {
              type: ["JsonLdEvent"],
              code: "blank node predicate",
              level: "warning",
              message: "Dropping blank node predicate.",
              details: {
                // FIXME: add better issuer API to get reverse mapping
                property: r.getOldIds().find((v) => r.getId(v) === l)
              }
            },
            options: i
          });
          continue;
        }
        const y = eh(
          c,
          r,
          e,
          n,
          i.rdfDirection,
          i
        );
        y && e.push({
          subject: p,
          predicate: d,
          object: y,
          graph: n
        });
      }
    }
  }
}
function gN(e, t, n, r, i, a) {
  const o = { termType: "NamedNode", value: iN }, s = { termType: "NamedNode", value: aN }, u = { termType: "NamedNode", value: oN }, l = e.pop(), f = l ? { termType: "BlankNode", value: t.getId() } : u;
  let c = f;
  for (const p of e) {
    const d = eh(
      p,
      t,
      n,
      r,
      i,
      a
    ), y = { termType: "BlankNode", value: t.getId() };
    n.push({
      subject: c,
      predicate: o,
      object: d,
      graph: r
    }), n.push({
      subject: c,
      predicate: s,
      object: y,
      graph: r
    }), c = y;
  }
  if (l) {
    const p = eh(
      l,
      t,
      n,
      r,
      i,
      a
    );
    n.push({
      subject: c,
      predicate: o,
      object: p,
      graph: r
    }), n.push({
      subject: c,
      predicate: s,
      object: u,
      graph: r
    });
  }
  return f;
}
function eh(e, t, n, r, i, a) {
  const o = {};
  if (Ng.isValue(e)) {
    o.termType = "Literal", o.value = void 0, o.datatype = {
      termType: "NamedNode"
    };
    let s = e["@value"];
    const u = e["@type"] || null;
    if (u === "@json")
      o.value = nN(s), o.datatype.value = uN;
    else if (bi.isBoolean(s))
      o.value = s.toString(), o.datatype.value = u || fN;
    else if (bi.isDouble(s) || u === Og)
      bi.isDouble(s) || (s = parseFloat(s)), o.value = s.toExponential(15).replace(/(\d)0*e\+?/, "$1E"), o.datatype.value = u || Og;
    else if (bi.isNumber(s))
      o.value = s.toFixed(0), o.datatype.value = u || cN;
    else if (i === "i18n-datatype" && "@direction" in e) {
      const l = "https://www.w3.org/ns/i18n#" + (e["@language"] || "") + `_${e["@direction"]}`;
      o.datatype.value = l, o.value = s;
    } else
      "@language" in e ? (o.value = s, o.datatype.value = u || lN, o.language = e["@language"]) : (o.value = s, o.datatype.value = u || dN);
  } else if (Ng.isList(e)) {
    const s = gN(
      e["@list"],
      t,
      n,
      r,
      i,
      a
    );
    o.termType = s.termType, o.value = s.value;
  } else {
    const s = bi.isObject(e) ? e["@id"] : e;
    o.termType = s.startsWith("_:") ? "BlankNode" : "NamedNode", o.value = s;
  }
  return o.termType === "NamedNode" && !Ga(o.value) ? (a.eventHandler && Pi({
    event: {
      type: ["JsonLdEvent"],
      code: "relative object reference",
      level: "warning",
      message: "Relative object reference found.",
      details: {
        object: o.value
      }
    },
    options: a
  }), null) : o;
}
const { isKeyword: th } = Rr, wt = Nn, me = pt, je = Tt, $g = Qn, ki = kt, {
  createNodeMap: yN,
  mergeNodeMapGraphs: vN
} = co, St = {};
var _N = St;
St.frameMergedOrDefault = (e, t, n) => {
  const r = {
    options: n,
    embedded: !1,
    graph: "@default",
    graphMap: { "@default": {} },
    subjectStack: [],
    link: {},
    bnodeMap: {}
  }, i = new je.IdentifierIssuer("_:b");
  yN(e, r.graphMap, "@default", i), n.merged && (r.graphMap["@merged"] = vN(r.graphMap), r.graph = "@merged"), r.subjects = r.graphMap[r.graph];
  const a = [];
  St.frame(r, Object.keys(r.subjects).sort(), t, a), n.pruneBlankNodeIdentifiers && (n.bnodesToClear = Object.keys(r.bnodeMap).filter((o) => r.bnodeMap[o].length === 1));
  // remove @preserve from results
  return n.link = {}, Ta(a, n);
};
St.frame = (e, t, n, r, i = null) => {
  L1(n), n = n[0];
  const a = e.options, o = {
    embed: ua(n, a, "embed"),
    explicit: ua(n, a, "explicit"),
    requireAll: ua(n, a, "requireAll")
  };
  e.link.hasOwnProperty(e.graph) || (e.link[e.graph] = {});
  const s = e.link[e.graph], u = mN(e, t, n, o), l = Object.keys(u).sort();
  for (const f of l) {
    const c = u[f];
    if (i === null ? e.uniqueEmbeds = { [e.graph]: {} } : e.uniqueEmbeds[e.graph] = e.uniqueEmbeds[e.graph] || {}, o.embed === "@link" && f in s) {
      ir(r, i, s[f]);
      continue;
    }
    const p = { "@id": f };
    if (f.indexOf("_:") === 0 && je.addValue(e.bnodeMap, f, p, { propertyIsArray: !0 }), s[f] = p, (o.embed === "@first" || o.embed === "@last") && e.is11)
      throw new ki(
        "Invalid JSON-LD syntax; invalid value of @embed.",
        "jsonld.SyntaxError",
        { code: "invalid @embed value", frame: n }
      );
    if (!(!e.embedded && e.uniqueEmbeds[e.graph].hasOwnProperty(f))) {
      if (e.embedded && (o.embed === "@never" || bN(c, e.graph, e.subjectStack))) {
        ir(r, i, p);
        continue;
      }
      if (e.embedded && (o.embed == "@first" || o.embed == "@once") && e.uniqueEmbeds[e.graph].hasOwnProperty(f)) {
        ir(r, i, p);
        continue;
      }
      if (o.embed === "@last" && f in e.uniqueEmbeds[e.graph] && wN(e, f), e.uniqueEmbeds[e.graph][f] = { parent: r, property: i }, e.subjectStack.push({ subject: c, graph: e.graph }), f in e.graphMap) {
        let d = !1, y = null;
        "@graph" in n ? (y = n["@graph"][0], d = !(f === "@merged" || f === "@default"), me.isObject(y) || (y = {})) : (d = e.graph !== "@merged", y = {}), d && St.frame(
          { ...e, graph: f, embedded: !1 },
          Object.keys(e.graphMap[f]).sort(),
          [y],
          p,
          "@graph"
        );
      }
      "@included" in n && St.frame(
        { ...e, embedded: !1 },
        t,
        n["@included"],
        p,
        "@included"
      );
      for (const d of Object.keys(c).sort()) {
        if (th(d)) {
          if (p[d] = je.clone(c[d]), d === "@type")
            for (const y of c["@type"])
              y.indexOf("_:") === 0 && je.addValue(
                e.bnodeMap,
                y,
                p,
                { propertyIsArray: !0 }
              );
          continue;
        }
        if (!(o.explicit && !(d in n)))
          for (const y of c[d]) {
            const v = d in n ? n[d] : Lg(o);
            if (wt.isList(y)) {
              const _ = n[d] && n[d][0] && n[d][0]["@list"] ? n[d][0]["@list"] : Lg(o), m = { "@list": [] };
              ir(p, d, m);
              const E = y["@list"];
              for (const L of E)
                wt.isSubjectReference(L) ? St.frame(
                  { ...e, embedded: !0 },
                  [L["@id"]],
                  _,
                  m,
                  "@list"
                ) : ir(m, "@list", je.clone(L));
            } else
              wt.isSubjectReference(y) ? St.frame(
                { ...e, embedded: !0 },
                [y["@id"]],
                v,
                p,
                d
              ) : nh(v[0], y) && ir(p, d, je.clone(y));
          }
      }
      for (const d of Object.keys(n).sort()) {
        if (d === "@type") {
          if (!me.isObject(n[d][0]) || !("@default" in n[d][0]))
            continue;
        } else if (th(d))
          continue;
        const y = n[d][0] || {};
        if (!ua(y, a, "omitDefault") && !(d in p)) {
          let _ = "@null";
          "@default" in y && (_ = je.clone(y["@default"])), me.isArray(_) || (_ = [_]), p[d] = [{ "@preserve": _ }];
        }
      }
      for (const d of Object.keys(n["@reverse"] || {}).sort()) {
        const y = n["@reverse"][d];
        for (const v of Object.keys(e.subjects))
          je.getValues(e.subjects[v], d).some((m) => m["@id"] === f) && (p["@reverse"] = p["@reverse"] || {}, je.addValue(
            p["@reverse"],
            d,
            [],
            { propertyIsArray: !0 }
          ), St.frame(
            { ...e, embedded: !0 },
            [v],
            y,
            p["@reverse"][d],
            i
          ));
      }
      ir(r, i, p), e.subjectStack.pop();
    }
  }
};
St.cleanupNull = (e, t) => {
  if (me.isArray(e))
    return e.map((r) => St.cleanupNull(r, t)).filter((r) => r);
  if (e === "@null")
    return null;
  if (me.isObject(e)) {
    if ("@id" in e) {
      const n = e["@id"];
      if (t.link.hasOwnProperty(n)) {
        const r = t.link[n].indexOf(e);
        if (r !== -1)
          return t.link[n][r];
        t.link[n].push(e);
      } else
        t.link[n] = [e];
    }
    for (const n in e)
      e[n] = St.cleanupNull(e[n], t);
  }
  return e;
};
function Lg(e) {
  const t = {};
  for (const n in e)
    e[n] !== void 0 && (t["@" + n] = [e[n]]);
  return [t];
}
function bN(e, t, n) {
  for (let r = n.length - 1; r >= 0; --r) {
    const i = n[r];
    if (i.graph === t && i.subject["@id"] === e["@id"])
      return !0;
  }
  return !1;
}
function ua(e, t, n) {
  const r = "@" + n;
  let i = r in e ? e[r][0] : t[n];
  if (n === "embed") {
    if (i === !0)
      i = "@once";
    else if (i === !1)
      i = "@never";
    else if (i !== "@always" && i !== "@never" && i !== "@link" && i !== "@first" && i !== "@last" && i !== "@once")
      throw new ki(
        "Invalid JSON-LD syntax; invalid value of @embed.",
        "jsonld.SyntaxError",
        { code: "invalid @embed value", frame: e }
      );
  }
  return i;
}
function L1(e) {
  if (!me.isArray(e) || e.length !== 1 || !me.isObject(e[0]))
    throw new ki(
      "Invalid JSON-LD syntax; a JSON-LD frame must be a single object.",
      "jsonld.SyntaxError",
      { frame: e }
    );
  if ("@id" in e[0]) {
    for (const t of je.asArray(e[0]["@id"]))
      if (!(me.isObject(t) || $g.isAbsolute(t)) || me.isString(t) && t.indexOf("_:") === 0)
        throw new ki(
          "Invalid JSON-LD syntax; invalid @id in frame.",
          "jsonld.SyntaxError",
          { code: "invalid frame", frame: e }
        );
  }
  if ("@type" in e[0]) {
    for (const t of je.asArray(e[0]["@type"]))
      if (!(me.isObject(t) || $g.isAbsolute(t)) || me.isString(t) && t.indexOf("_:") === 0)
        throw new ki(
          "Invalid JSON-LD syntax; invalid @type in frame.",
          "jsonld.SyntaxError",
          { code: "invalid frame", frame: e }
        );
  }
}
function mN(e, t, n, r) {
  const i = {};
  for (const a of t) {
    const o = e.graphMap[e.graph][a];
    M1(e, o, n, r) && (i[a] = o);
  }
  return i;
}
function M1(e, t, n, r) {
  let i = !0, a = !1;
  for (const o in n) {
    let s = !1;
    const u = je.getValues(t, o), l = je.getValues(n, o).length === 0;
    if (o === "@id") {
      if (me.isEmptyObject(n["@id"][0] || {}) ? s = !0 : n["@id"].length >= 0 && (s = n["@id"].includes(u[0])), !r.requireAll)
        return s;
    } else if (o === "@type") {
      if (i = !1, l) {
        if (u.length > 0)
          return !1;
        s = !0;
      } else if (n["@type"].length === 1 && me.isEmptyObject(n["@type"][0]))
        s = u.length > 0;
      else
        for (const f of n["@type"])
          me.isObject(f) && "@default" in f ? s = !0 : s = s || u.some((c) => c === f);
      if (!r.requireAll)
        return s;
    } else {
      if (th(o))
        continue;
      {
        const f = je.getValues(n, o)[0];
        let c = !1;
        if (f && (L1([f]), c = "@default" in f), i = !1, u.length === 0 && c)
          continue;
        if (u.length > 0 && l)
          return !1;
        if (f === void 0) {
          if (u.length > 0)
            return !1;
          s = !0;
        } else if (wt.isList(f)) {
          const p = f["@list"][0];
          if (wt.isList(u[0])) {
            const d = u[0]["@list"];
            wt.isValue(p) ? s = d.some((y) => nh(p, y)) : (wt.isSubject(p) || wt.isSubjectReference(p)) && (s = d.some((y) => Mg(
              e,
              p,
              y,
              r
            )));
          }
        } else
          wt.isValue(f) ? s = u.some((p) => nh(f, p)) : wt.isSubjectReference(f) ? s = u.some((p) => Mg(e, f, p, r)) : me.isObject(f) ? s = u.length > 0 : s = !1;
      }
    }
    if (!s && r.requireAll)
      return !1;
    a = a || s;
  }
  return i || a;
}
function wN(e, t) {
  const n = e.uniqueEmbeds[e.graph], r = n[t], i = r.parent, a = r.property, o = { "@id": t };
  if (me.isArray(i)) {
    for (let u = 0; u < i.length; ++u)
      if (je.compareValues(i[u], o)) {
        i[u] = o;
        break;
      }
  } else {
    const u = me.isArray(i[a]);
    je.removeValue(i, a, o, { propertyIsArray: u }), je.addValue(i, a, o, { propertyIsArray: u });
  }
  const s = (u) => {
    const l = Object.keys(n);
    for (const f of l)
      f in n && me.isObject(n[f].parent) && n[f].parent["@id"] === u && (delete n[f], s(f));
  };
  s(t);
}
/**
 * Removes the @preserve keywords from expanded result of framing.
 *
 * @param input the framed, framed output.
 * @param options the framing options used.
 *
 * @return the resulting output.
 */
function Ta(e, t) {
  if (me.isArray(e))
    return e.map((n) => Ta(n, t));
  if (me.isObject(e)) {
    // remove @preserve
    if ("@preserve" in e)
      return e["@preserve"][0];
    if (wt.isValue(e))
      return e;
    if (wt.isList(e))
      return e["@list"] = Ta(e["@list"], t), e;
    if ("@id" in e) {
      const n = e["@id"];
      if (t.link.hasOwnProperty(n)) {
        const r = t.link[n].indexOf(e);
        if (r !== -1)
          return t.link[n][r];
        t.link[n].push(e);
      } else
        t.link[n] = [e];
    }
    for (const n in e) {
      if (n === "@id" && t.bnodesToClear.includes(e[n])) {
        delete e["@id"];
        continue;
      }
      e[n] = Ta(e[n], t);
    }
  }
  return e;
}
function ir(e, t, n) {
  me.isObject(e) ? je.addValue(e, t, n, { propertyIsArray: !0 }) : e.push(n);
}
function Mg(e, t, n, r) {
  if (!("@id" in n))
    return !1;
  const i = e.subjects[n["@id"]];
  return i && M1(e, i, t, r);
}
function nh(e, t) {
  const n = t["@value"], r = t["@type"], i = t["@language"], a = e["@value"] ? me.isArray(e["@value"]) ? e["@value"] : [e["@value"]] : [], o = e["@type"] ? me.isArray(e["@type"]) ? e["@type"] : [e["@type"]] : [], s = e["@language"] ? me.isArray(e["@language"]) ? e["@language"] : [e["@language"]] : [];
  return a.length === 0 && o.length === 0 && s.length === 0 ? !0 : !(!(a.includes(n) || me.isEmptyObject(a[0])) || !(!r && o.length === 0 || o.includes(r) || r && me.isEmptyObject(o[0])) || !(!i && s.length === 0 || s.includes(i) || i && me.isEmptyObject(s[0])));
}
const Xh = kt, {
  isArray: Dn,
  isObject: Vn,
  isString: C1,
  isUndefined: Es
} = pt, {
  isList: rh,
  isValue: vn,
  isGraph: D1,
  isSimpleGraph: Cg,
  isSubjectReference: Ss
} = Nn, {
  expandIri: q1,
  getContextValue: ze,
  isKeyword: j1,
  process: As,
  processingMode: EN
} = Rr, {
  removeBase: Dg,
  prependBase: SN
} = Qn, {
  REGEX_KEYWORD: AN,
  addValue: lt,
  asArray: la,
  compareShortestLeast: RN
} = Tt, Z = {};
var IN = Z;
Z.compact = async ({
  activeCtx: e,
  activeProperty: t = null,
  element: n,
  options: r = {}
}) => {
  if (Dn(n)) {
    let a = [];
    for (let o = 0; o < n.length; ++o) {
      const s = await Z.compact({
        activeCtx: e,
        activeProperty: t,
        element: n[o],
        options: r
      });
      s !== null && a.push(s);
    }
    return r.compactArrays && a.length === 1 && (ze(
      e,
      t,
      "@container"
    ) || []).length === 0 && (a = a[0]), a;
  }
  const i = ze(e, t, "@context");
  if (Es(i) || (e = await As({
    activeCtx: e,
    localCtx: i,
    propagate: !0,
    overrideProtected: !0,
    options: r
  })), Vn(n)) {
    if (r.link && "@id" in n && r.link.hasOwnProperty(n["@id"])) {
      const p = r.link[n["@id"]];
      for (let d = 0; d < p.length; ++d)
        if (p[d].expanded === n)
          return p[d].compacted;
    }
    if (vn(n) || Ss(n)) {
      const p = Z.compactValue({ activeCtx: e, activeProperty: t, value: n, options: r });
      return r.link && Ss(n) && (r.link.hasOwnProperty(n["@id"]) || (r.link[n["@id"]] = []), r.link[n["@id"]].push({ expanded: n, compacted: p })), p;
    }
    if (rh(n) && (ze(
      e,
      t,
      "@container"
    ) || []).includes("@list"))
      return Z.compact({
        activeCtx: e,
        activeProperty: t,
        element: n["@list"],
        options: r
      });
    const a = t === "@reverse", o = {}, s = e;
    !vn(n) && !Ss(n) && (e = e.revertToPreviousContext());
    const u = ze(s, t, "@context");
    Es(u) || (e = await As({
      activeCtx: e,
      localCtx: u,
      propagate: !0,
      overrideProtected: !0,
      options: r
    })), r.link && "@id" in n && (r.link.hasOwnProperty(n["@id"]) || (r.link[n["@id"]] = []), r.link[n["@id"]].push({ expanded: n, compacted: o }));
    let l = n["@type"] || [];
    l.length > 1 && (l = Array.from(l).sort());
    const f = e;
    for (const p of l) {
      const d = Z.compactIri(
        { activeCtx: f, iri: p, relativeTo: { vocab: !0 } }
      ), y = ze(s, d, "@context");
      Es(y) || (e = await As({
        activeCtx: e,
        localCtx: y,
        options: r,
        propagate: !1
      }));
    }
    const c = Object.keys(n).sort();
    for (const p of c) {
      const d = n[p];
      if (p === "@id") {
        let y = la(d).map(
          (_) => Z.compactIri({
            activeCtx: e,
            iri: _,
            relativeTo: { vocab: !1 },
            base: r.base
          })
        );
        y.length === 1 && (y = y[0]);
        const v = Z.compactIri(
          { activeCtx: e, iri: "@id", relativeTo: { vocab: !0 } }
        );
        o[v] = y;
        continue;
      }
      if (p === "@type") {
        let y = la(d).map(
          (L) => Z.compactIri({
            activeCtx: s,
            iri: L,
            relativeTo: { vocab: !0 }
          })
        );
        y.length === 1 && (y = y[0]);
        const v = Z.compactIri(
          { activeCtx: e, iri: "@type", relativeTo: { vocab: !0 } }
        ), E = (ze(
          e,
          v,
          "@container"
        ) || []).includes("@set") && EN(e, 1.1) || Dn(y) && d.length === 0;
        lt(o, v, y, { propertyIsArray: E });
        continue;
      }
      if (p === "@reverse") {
        const y = await Z.compact({
          activeCtx: e,
          activeProperty: "@reverse",
          element: d,
          options: r
        });
        for (const v in y)
          if (e.mappings.has(v) && e.mappings.get(v).reverse) {
            const _ = y[v], E = (ze(
              e,
              v,
              "@container"
            ) || []).includes("@set") || !r.compactArrays;
            lt(
              o,
              v,
              _,
              { propertyIsArray: E }
            ), delete y[v];
          }
        if (Object.keys(y).length > 0) {
          const v = Z.compactIri({
            activeCtx: e,
            iri: p,
            relativeTo: { vocab: !0 }
          });
          lt(o, v, y);
        }
        continue;
      }
      if (p === "@preserve") {
        const y = await Z.compact({
          activeCtx: e,
          activeProperty: t,
          element: d,
          options: r
        });
        Dn(y) && y.length === 0 || lt(o, p, y);
        continue;
      }
      if (p === "@index") {
        if ((ze(
          e,
          t,
          "@container"
        ) || []).includes("@index"))
          continue;
        const v = Z.compactIri({
          activeCtx: e,
          iri: p,
          relativeTo: { vocab: !0 }
        });
        lt(o, v, d);
        continue;
      }
      if (p !== "@graph" && p !== "@list" && p !== "@included" && j1(p)) {
        const y = Z.compactIri({
          activeCtx: e,
          iri: p,
          relativeTo: { vocab: !0 }
        });
        lt(o, y, d);
        continue;
      }
      if (!Dn(d))
        throw new Xh(
          "JSON-LD expansion error; expanded value must be an array.",
          "jsonld.SyntaxError"
        );
      if (d.length === 0) {
        const y = Z.compactIri({
          activeCtx: e,
          iri: p,
          value: d,
          relativeTo: { vocab: !0 },
          reverse: a
        }), v = e.mappings.has(y) ? e.mappings.get(y)["@nest"] : null;
        let _ = o;
        v && (qg(e, v, r), Vn(o[v]) || (o[v] = {}), _ = o[v]), lt(
          _,
          y,
          d,
          {
            propertyIsArray: !0
          }
        );
      }
      for (const y of d) {
        const v = Z.compactIri({
          activeCtx: e,
          iri: p,
          value: y,
          relativeTo: { vocab: !0 },
          reverse: a
        }), _ = e.mappings.has(v) ? e.mappings.get(v)["@nest"] : null;
        let m = o;
        _ && (qg(e, _, r), Vn(o[_]) || (o[_] = {}), m = o[_]);
        const E = ze(
          e,
          v,
          "@container"
        ) || [], L = D1(y), I = rh(y);
        let j;
        I ? j = y["@list"] : L && (j = y["@graph"]);
        let M = await Z.compact({
          activeCtx: e,
          activeProperty: v,
          element: I || L ? j : y,
          options: r
        });
        if (I)
          if (Dn(M) || (M = [M]), !E.includes("@list"))
            M = {
              [Z.compactIri({
                activeCtx: e,
                iri: "@list",
                relativeTo: { vocab: !0 }
              })]: M
            }, "@index" in y && (M[Z.compactIri({
              activeCtx: e,
              iri: "@index",
              relativeTo: { vocab: !0 }
            })] = y["@index"]);
          else {
            lt(m, v, M, {
              valueIsArray: !0,
              allowDuplicate: !0
            });
            continue;
          }
        if (L)
          if (E.includes("@graph") && (E.includes("@id") || E.includes("@index") && Cg(y))) {
            let O;
            m.hasOwnProperty(v) ? O = m[v] : m[v] = O = {};
            const $ = (E.includes("@id") ? y["@id"] : y["@index"]) || Z.compactIri({
              activeCtx: e,
              iri: "@none",
              relativeTo: { vocab: !0 }
            });
            lt(
              O,
              $,
              M,
              {
                propertyIsArray: !r.compactArrays || E.includes("@set")
              }
            );
          } else
            E.includes("@graph") && Cg(y) ? (Dn(M) && M.length > 1 && (M = { "@included": M }), lt(
              m,
              v,
              M,
              {
                propertyIsArray: !r.compactArrays || E.includes("@set")
              }
            )) : (Dn(M) && M.length === 1 && r.compactArrays && (M = M[0]), M = {
              [Z.compactIri({
                activeCtx: e,
                iri: "@graph",
                relativeTo: { vocab: !0 }
              })]: M
            }, "@id" in y && (M[Z.compactIri({
              activeCtx: e,
              iri: "@id",
              relativeTo: { vocab: !0 }
            })] = y["@id"]), "@index" in y && (M[Z.compactIri({
              activeCtx: e,
              iri: "@index",
              relativeTo: { vocab: !0 }
            })] = y["@index"]), lt(
              m,
              v,
              M,
              {
                propertyIsArray: !r.compactArrays || E.includes("@set")
              }
            ));
        else if (E.includes("@language") || E.includes("@index") || E.includes("@id") || E.includes("@type")) {
          let O;
          m.hasOwnProperty(v) ? O = m[v] : m[v] = O = {};
          let $;
          if (E.includes("@language"))
            vn(M) && (M = M["@value"]), $ = y["@language"];
          else if (E.includes("@index")) {
            const q = ze(
              e,
              v,
              "@index"
            ) || "@index", N = Z.compactIri(
              { activeCtx: e, iri: q, relativeTo: { vocab: !0 } }
            );
            if (q === "@index")
              $ = y["@index"], delete M[N];
            else {
              let P;
              if ([$, ...P] = la(M[q] || []), !C1($))
                $ = null;
              else
                switch (P.length) {
                  case 0:
                    delete M[q];
                    break;
                  case 1:
                    M[q] = P[0];
                    break;
                  default:
                    M[q] = P;
                    break;
                }
            }
          } else if (E.includes("@id")) {
            const q = Z.compactIri({
              activeCtx: e,
              iri: "@id",
              relativeTo: { vocab: !0 }
            });
            $ = M[q], delete M[q];
          } else if (E.includes("@type")) {
            const q = Z.compactIri({
              activeCtx: e,
              iri: "@type",
              relativeTo: { vocab: !0 }
            });
            let N;
            switch ([$, ...N] = la(M[q] || []), N.length) {
              case 0:
                delete M[q];
                break;
              case 1:
                M[q] = N[0];
                break;
              default:
                M[q] = N;
                break;
            }
            Object.keys(M).length === 1 && "@id" in y && (M = await Z.compact({
              activeCtx: e,
              activeProperty: v,
              element: { "@id": y["@id"] },
              options: r
            }));
          }
          $ || ($ = Z.compactIri({
            activeCtx: e,
            iri: "@none",
            relativeTo: { vocab: !0 }
          })), lt(
            O,
            $,
            M,
            {
              propertyIsArray: E.includes("@set")
            }
          );
        } else {
          const O = !r.compactArrays || E.includes("@set") || E.includes("@list") || Dn(M) && M.length === 0 || p === "@list" || p === "@graph";
          lt(
            m,
            v,
            M,
            { propertyIsArray: O }
          );
        }
      }
    }
    return o;
  }
  return n;
};
Z.compactIri = ({
  activeCtx: e,
  iri: t,
  value: n = null,
  relativeTo: r = { vocab: !1 },
  reverse: i = !1,
  base: a = null
}) => {
  if (t === null)
    return t;
  e.isPropertyTermScoped && e.previousContext && (e = e.previousContext);
  const o = e.getInverse();
  if (j1(t) && t in o && "@none" in o[t] && "@type" in o[t]["@none"] && "@none" in o[t]["@none"]["@type"])
    return o[t]["@none"]["@type"]["@none"];
  if (r.vocab && t in o) {
    const c = e["@language"] || "@none", p = [];
    Vn(n) && "@index" in n && !("@graph" in n) && p.push("@index", "@index@set"), Vn(n) && "@preserve" in n && (n = n["@preserve"][0]), D1(n) ? ("@index" in n && p.push(
      "@graph@index",
      "@graph@index@set",
      "@index",
      "@index@set"
    ), "@id" in n && p.push(
      "@graph@id",
      "@graph@id@set"
    ), p.push("@graph", "@graph@set", "@set"), "@index" in n || p.push(
      "@graph@index",
      "@graph@index@set",
      "@index",
      "@index@set"
    ), "@id" in n || p.push("@graph@id", "@graph@id@set")) : Vn(n) && !vn(n) && p.push("@id", "@id@set", "@type", "@set@type");
    let d = "@language", y = "@null";
    if (i)
      d = "@type", y = "@reverse", p.push("@set");
    else if (rh(n)) {
      "@index" in n || p.push("@list");
      const _ = n["@list"];
      if (_.length === 0)
        d = "@any", y = "@none";
      else {
        let m = _.length === 0 ? c : null, E = null;
        for (let L = 0; L < _.length; ++L) {
          const I = _[L];
          let j = "@none", M = "@none";
          if (vn(I))
            if ("@direction" in I) {
              const O = (I["@language"] || "").toLowerCase(), $ = I["@direction"];
              j = `${O}_${$}`;
            } else
              "@language" in I ? j = I["@language"].toLowerCase() : "@type" in I ? M = I["@type"] : j = "@null";
          else
            M = "@id";
          if (m === null ? m = j : j !== m && vn(I) && (m = "@none"), E === null ? E = M : M !== E && (E = "@none"), m === "@none" && E === "@none")
            break;
        }
        m = m || "@none", E = E || "@none", E !== "@none" ? (d = "@type", y = E) : y = m;
      }
    } else {
      if (vn(n))
        if ("@language" in n && !("@index" in n)) {
          p.push("@language", "@language@set"), y = n["@language"];
          const _ = n["@direction"];
          _ && (y = `${y}_${_}`);
        } else
          "@direction" in n && !("@index" in n) ? y = `_${n["@direction"]}` : "@type" in n && (d = "@type", y = n["@type"]);
      else
        d = "@type", y = "@id";
      p.push("@set");
    }
    p.push("@none"), Vn(n) && !("@index" in n) && p.push("@index", "@index@set"), vn(n) && Object.keys(n).length === 1 && p.push("@language", "@language@set");
    const v = TN(
      e,
      t,
      n,
      p,
      d,
      y
    );
    if (v !== null)
      return v;
  }
  if (r.vocab && "@vocab" in e) {
    const c = e["@vocab"];
    if (t.indexOf(c) === 0 && t !== c) {
      const p = t.substr(c.length);
      if (!e.mappings.has(p))
        return p;
    }
  }
  let s = null;
  const u = [];
  let l = e.fastCurieMap;
  const f = t.length - 1;
  for (let c = 0; c < f && t[c] in l; ++c)
    l = l[t[c]], "" in l && u.push(l[""][0]);
  for (let c = u.length - 1; c >= 0; --c) {
    const p = u[c], d = p.terms;
    for (const y of d) {
      const v = y + ":" + t.substr(p.iri.length);
      e.mappings.get(y)._prefix && (!e.mappings.has(v) || n === null && e.mappings.get(v)["@id"] === t) && (s === null || RN(v, s) < 0) && (s = v);
    }
  }
  if (s !== null)
    return s;
  for (const [c, p] of e.mappings)
    if (p && p._prefix && t.startsWith(c + ":"))
      throw new Xh(
        `Absolute IRI "${t}" confused with prefix "${c}".`,
        "jsonld.SyntaxError",
        { code: "IRI confused with prefix", context: e }
      );
  if (!r.vocab)
    if ("@base" in e)
      if (e["@base"]) {
        const c = Dg(SN(a, e["@base"]), t);
        return AN.test(c) ? `./${c}` : c;
      } else
        return t;
    else
      return Dg(a, t);
  return t;
};
Z.compactValue = ({ activeCtx: e, activeProperty: t, value: n, options: r }) => {
  if (vn(n)) {
    const s = ze(e, t, "@type"), u = ze(e, t, "@language"), l = ze(e, t, "@direction"), f = ze(e, t, "@container") || [], c = "@index" in n && !f.includes("@index");
    if (!c && s !== "@none" && (n["@type"] === s || "@language" in n && n["@language"] === u && "@direction" in n && n["@direction"] === l || "@language" in n && n["@language"] === u || "@direction" in n && n["@direction"] === l))
      return n["@value"];
    const p = Object.keys(n).length, d = p === 1 || p === 2 && "@index" in n && !c, y = "@language" in e, v = C1(n["@value"]), _ = e.mappings.has(t) && e.mappings.get(t)["@language"] === null;
    if (d && s !== "@none" && (!y || !v || _))
      return n["@value"];
    const m = {};
    return c && (m[Z.compactIri({
      activeCtx: e,
      iri: "@index",
      relativeTo: { vocab: !0 }
    })] = n["@index"]), "@type" in n ? m[Z.compactIri({
      activeCtx: e,
      iri: "@type",
      relativeTo: { vocab: !0 }
    })] = Z.compactIri(
      { activeCtx: e, iri: n["@type"], relativeTo: { vocab: !0 } }
    ) : "@language" in n && (m[Z.compactIri({
      activeCtx: e,
      iri: "@language",
      relativeTo: { vocab: !0 }
    })] = n["@language"]), "@direction" in n && (m[Z.compactIri({
      activeCtx: e,
      iri: "@direction",
      relativeTo: { vocab: !0 }
    })] = n["@direction"]), m[Z.compactIri({
      activeCtx: e,
      iri: "@value",
      relativeTo: { vocab: !0 }
    })] = n["@value"], m;
  }
  const i = q1(
    e,
    t,
    { vocab: !0 },
    r
  ), a = ze(e, t, "@type"), o = Z.compactIri({
    activeCtx: e,
    iri: n["@id"],
    relativeTo: { vocab: a === "@vocab" },
    base: r.base
  });
  return a === "@id" || a === "@vocab" || i === "@graph" ? o : {
    [Z.compactIri({
      activeCtx: e,
      iri: "@id",
      relativeTo: { vocab: !0 }
    })]: o
  };
};
function TN(e, t, n, r, i, a) {
  a === null && (a = "@null");
  const o = [];
  if ((a === "@id" || a === "@reverse") && Vn(n) && "@id" in n) {
    a === "@reverse" && o.push("@reverse");
    const u = Z.compactIri(
      { activeCtx: e, iri: n["@id"], relativeTo: { vocab: !0 } }
    );
    e.mappings.has(u) && e.mappings.get(u) && e.mappings.get(u)["@id"] === n["@id"] ? o.push.apply(o, ["@vocab", "@id"]) : o.push.apply(o, ["@id", "@vocab"]);
  } else {
    o.push(a);
    const u = o.find((l) => l.includes("_"));
    u && o.push(u.replace(/^[^_]+_/, "_"));
  }
  o.push("@none");
  const s = e.inverse[t];
  for (const u of r) {
    if (!(u in s))
      continue;
    const l = s[u][i];
    for (const f of o)
      if (f in l)
        return l[f];
  }
  return null;
}
function qg(e, t, n) {
  if (q1(e, t, { vocab: !0 }, n) !== "@nest")
    throw new Xh(
      "JSON-LD compact error; nested property must have an @nest value resolving to @nest.",
      "jsonld.SyntaxError",
      { code: "invalid @nest value" }
    );
}
var Rs, jg;
function xN() {
  return jg || (jg = 1, Rs = (e) => {
    class t {
      toString() {
        return "[object JsonLdProcessor]";
      }
    }
    return Object.defineProperty(t, "prototype", {
      writable: !1,
      enumerable: !1
    }), Object.defineProperty(t.prototype, "constructor", {
      writable: !0,
      enumerable: !1,
      configurable: !0,
      value: t
    }), t.compact = function(n, r) {
      return arguments.length < 2 ? Promise.reject(
        new TypeError("Could not compact, too few arguments.")
      ) : e.compact(n, r);
    }, t.expand = function(n) {
      return arguments.length < 1 ? Promise.reject(
        new TypeError("Could not expand, too few arguments.")
      ) : e.expand(n);
    }, t.flatten = function(n) {
      return arguments.length < 1 ? Promise.reject(
        new TypeError("Could not flatten, too few arguments.")
      ) : e.flatten(n);
    }, t;
  }), Rs;
}
/**
 * A JavaScript implementation of the JSON-LD API.
 *
 * @author Dave Longley
 *
 * @license BSD 3-Clause License
 * Copyright (c) 2011-2022 Digital Bazaar, Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * Redistributions of source code must retain the above copyright notice,
 * this list of conditions and the following disclaimer.
 *
 * Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * Neither the name of the Digital Bazaar, Inc. nor the names of its
 * contributors may be used to endorse or promote products derived from
 * this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS
 * IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED
 * TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
 * PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
 * HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED
 * TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 * PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
 * NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 * SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
const Pg = Wh, kg = hx, jn = Tt, dn = Ix, Is = jn.IdentifierIssuer, Wt = kt, NN = m1, fa = xx, { expand: ON } = Bx, { flatten: $N } = Wx, { fromRDF: LN } = Yx, { toRDF: MN } = hN, {
  frameMergedOrDefault: CN,
  cleanupNull: DN
} = _N, {
  isArray: qr,
  isObject: ar,
  isString: Jt
} = pt, {
  isSubjectReference: qN
} = Nn, {
  expandIri: jN,
  getInitialContext: ca,
  process: Fg,
  processingMode: Ts
} = Rr, {
  compact: PN,
  compactIri: kN
} = IN, {
  createNodeMap: FN,
  createMergedNodeMap: BN,
  mergeNodeMaps: UN
} = co, {
  logEventHandler: HN,
  logWarningEventHandler: GN,
  safeEventHandler: VN,
  setDefaultEventHandler: WN,
  setupEventHandler: JN,
  strictEventHandler: zN,
  unhandledEventHandler: KN
} = Yi, P1 = function(e) {
  const t = {}, r = new NN({ max: 100 });
  e.compact = async function(a, o, s) {
    if (arguments.length < 2)
      throw new TypeError("Could not compact, too few arguments.");
    if (o === null)
      throw new Wt(
        "The compaction context must not be null.",
        "jsonld.CompactError",
        { code: "invalid local context" }
      );
    if (a === null)
      return null;
    s = i(s, {
      base: Jt(a) ? a : "",
      compactArrays: !0,
      compactToRelative: !0,
      graph: !1,
      skipExpansion: !1,
      link: !1,
      issuer: new Is("_:b"),
      contextResolver: new dn(
        { sharedCache: r }
      )
    }), s.link && (s.skipExpansion = !0), s.compactToRelative || delete s.base;
    let u;
    s.skipExpansion ? u = a : u = await e.expand(a, s);
    const l = await e.processContext(
      ca(s),
      o,
      s
    );
    let f = await PN({
      activeCtx: l,
      element: u,
      options: s
    });
    s.compactArrays && !s.graph && qr(f) ? f.length === 1 ? f = f[0] : f.length === 0 && (f = {}) : s.graph && ar(f) && (f = [f]), ar(o) && "@context" in o && (o = o["@context"]), o = jn.clone(o), qr(o) || (o = [o]);
    const c = o;
    o = [];
    for (let d = 0; d < c.length; ++d)
      (!ar(c[d]) || Object.keys(c[d]).length > 0) && o.push(c[d]);
    const p = o.length > 0;
    if (o.length === 1 && (o = o[0]), qr(f)) {
      const d = kN({
        activeCtx: l,
        iri: "@graph",
        relativeTo: { vocab: !0 }
      }), y = f;
      f = {}, p && (f["@context"] = o), f[d] = y;
    } else if (ar(f) && p) {
      const d = f;
      f = { "@context": o };
      for (const y in d)
        f[y] = d[y];
    }
    return f;
  }, e.expand = async function(a, o) {
    if (arguments.length < 1)
      throw new TypeError("Could not expand, too few arguments.");
    o = i(o, {
      keepFreeFloatingNodes: !1,
      contextResolver: new dn(
        { sharedCache: r }
      )
    });
    const s = {}, u = [];
    if ("expandContext" in o) {
      const p = jn.clone(o.expandContext);
      ar(p) && "@context" in p ? s.expandContext = p : s.expandContext = { "@context": p }, u.push(s.expandContext);
    }
    let l;
    if (!Jt(a))
      s.input = jn.clone(a);
    else {
      const p = await e.get(a, o);
      l = p.documentUrl, s.input = p.document, p.contextUrl && (s.remoteContext = { "@context": p.contextUrl }, u.push(s.remoteContext));
    }
    "base" in o || (o.base = l || "");
    let f = ca(o);
    for (const p of u)
      f = await Fg({ activeCtx: f, localCtx: p, options: o });
    let c = await ON({
      activeCtx: f,
      element: s.input,
      options: o
    });
    return ar(c) && "@graph" in c && Object.keys(c).length === 1 ? c = c["@graph"] : c === null && (c = []), qr(c) || (c = [c]), c;
  }, e.flatten = async function(a, o, s) {
    if (arguments.length < 1)
      return new TypeError("Could not flatten, too few arguments.");
    typeof o == "function" ? o = null : o = o || null, s = i(s, {
      base: Jt(a) ? a : "",
      contextResolver: new dn(
        { sharedCache: r }
      )
    });
    const u = await e.expand(a, s), l = $N(u);
    return o === null ? l : (s.graph = !0, s.skipExpansion = !0, await e.compact(l, o, s));
  }, e.frame = async function(a, o, s) {
    if (arguments.length < 2)
      throw new TypeError("Could not frame, too few arguments.");
    if (s = i(s, {
      base: Jt(a) ? a : "",
      embed: "@once",
      explicit: !1,
      requireAll: !1,
      omitDefault: !1,
      bnodesToClear: [],
      contextResolver: new dn(
        { sharedCache: r }
      )
    }), Jt(o)) {
      const _ = await e.get(o, s);
      if (o = _.document, _.contextUrl) {
        let m = o["@context"];
        m ? qr(m) ? m.push(_.contextUrl) : m = [m, _.contextUrl] : m = _.contextUrl, o["@context"] = m;
      }
    }
    const u = o ? o["@context"] || {} : {}, l = await e.processContext(
      ca(s),
      u,
      s
    );
    s.hasOwnProperty("omitGraph") || (s.omitGraph = Ts(l, 1.1)), s.hasOwnProperty("pruneBlankNodeIdentifiers") || (s.pruneBlankNodeIdentifiers = Ts(l, 1.1));
    const f = await e.expand(a, s), c = { ...s };
    c.isFrame = !0, c.keepFreeFloatingNodes = !0;
    const p = await e.expand(o, c), d = Object.keys(o).map((_) => jN(l, _, { vocab: !0 }));
    c.merged = !d.includes("@graph"), c.is11 = Ts(l, 1.1);
    const y = CN(f, p, c);
    c.graph = !s.omitGraph, c.skipExpansion = !0, c.link = {}, c.framing = !0;
    let v = await e.compact(y, u, c);
    return c.link = {}, v = DN(v, c), v;
  }, e.link = async function(a, o, s) {
    const u = {};
    return o && (u["@context"] = o), u["@embed"] = "@link", e.frame(a, u, s);
  }, e.normalize = e.canonize = async function(a, o) {
    if (arguments.length < 1)
      throw new TypeError("Could not canonize, too few arguments.");
    if (o = i(o, {
      base: Jt(a) ? a : null,
      algorithm: "URDNA2015",
      skipExpansion: !1,
      safe: !0,
      contextResolver: new dn(
        { sharedCache: r }
      )
    }), "inputFormat" in o) {
      if (o.inputFormat !== "application/n-quads" && o.inputFormat !== "application/nquads")
        throw new Wt(
          "Unknown canonicalization input format.",
          "jsonld.CanonizeError"
        );
      const l = fa.parse(a);
      return Pg.canonize(l, o);
    }
    const s = { ...o };
    delete s.format, s.produceGeneralizedRdf = !1;
    const u = await e.toRDF(a, s);
    return Pg.canonize(u, o);
  }, e.fromRDF = async function(a, o) {
    if (arguments.length < 1)
      throw new TypeError("Could not convert from RDF, too few arguments.");
    o = i(o, {
      format: Jt(a) ? "application/n-quads" : void 0
    });
    const { format: s } = o;
    let { rdfParser: u } = o;
    if (s) {
      if (u = u || t[s], !u)
        throw new Wt(
          "Unknown input format.",
          "jsonld.UnknownFormat",
          { format: s }
        );
    } else
      u = () => a;
    const l = await u(a);
    return LN(l, o);
  }, e.toRDF = async function(a, o) {
    if (arguments.length < 1)
      throw new TypeError("Could not convert to RDF, too few arguments.");
    o = i(o, {
      base: Jt(a) ? a : "",
      skipExpansion: !1,
      contextResolver: new dn(
        { sharedCache: r }
      )
    });
    let s;
    o.skipExpansion ? s = a : s = await e.expand(a, o);
    const u = MN(s, o);
    if (o.format) {
      if (o.format === "application/n-quads" || o.format === "application/nquads")
        return fa.serialize(u);
      throw new Wt(
        "Unknown output format.",
        "jsonld.UnknownFormat",
        { format: o.format }
      );
    }
    return u;
  }, e.createNodeMap = async function(a, o) {
    if (arguments.length < 1)
      throw new TypeError("Could not create node map, too few arguments.");
    o = i(o, {
      base: Jt(a) ? a : "",
      contextResolver: new dn(
        { sharedCache: r }
      )
    });
    const s = await e.expand(a, o);
    return BN(s, o);
  }, e.merge = async function(a, o, s) {
    if (arguments.length < 1)
      throw new TypeError("Could not merge, too few arguments.");
    if (!qr(a))
      throw new TypeError('Could not merge, "docs" must be an array.');
    typeof o == "function" ? o = null : o = o || null, s = i(s, {
      contextResolver: new dn(
        { sharedCache: r }
      )
    });
    const u = await Promise.all(a.map((_) => {
      const m = { ...s };
      return e.expand(_, m);
    }));
    let l = !0;
    "mergeNodes" in s && (l = s.mergeNodes);
    const f = s.issuer || new Is("_:b"), c = { "@default": {} };
    for (let _ = 0; _ < u.length; ++_) {
      const m = jn.relabelBlankNodes(u[_], {
        issuer: new Is("_:b" + _ + "-")
      }), E = l || _ === 0 ? c : { "@default": {} };
      if (FN(m, E, "@default", f), E !== c)
        for (const L in E) {
          const I = E[L];
          if (!(L in c)) {
            c[L] = I;
            continue;
          }
          const j = c[L];
          for (const M in I)
            M in j || (j[M] = I[M]);
        }
    }
    const p = UN(c), d = [], y = Object.keys(p).sort();
    for (let _ = 0; _ < y.length; ++_) {
      const m = p[y[_]];
      qN(m) || d.push(m);
    }
    return o === null ? d : (s.graph = !0, s.skipExpansion = !0, await e.compact(d, o, s));
  }, Object.defineProperty(e, "documentLoader", {
    get: () => e._documentLoader,
    set: (a) => e._documentLoader = a
  }), e.documentLoader = async (a) => {
    throw new Wt(
      "Could not retrieve a JSON-LD document from the URL. URL dereferencing not implemented.",
      "jsonld.LoadDocumentError",
      { code: "loading document failed", url: a }
    );
  }, e.get = async function(a, o) {
    let s;
    typeof o.documentLoader == "function" ? s = o.documentLoader : s = e.documentLoader;
    const u = await s(a);
    try {
      if (!u.document)
        throw new Wt(
          "No remote document found at the given URL.",
          "jsonld.NullRemoteDocument"
        );
      Jt(u.document) && (u.document = JSON.parse(u.document));
    } catch (l) {
      throw new Wt(
        "Could not retrieve a JSON-LD document from the URL.",
        "jsonld.LoadDocumentError",
        {
          code: "loading document failed",
          cause: l,
          remoteDoc: u
        }
      );
    }
    return u;
  }, e.processContext = async function(a, o, s) {
    return s = i(s, {
      base: "",
      contextResolver: new dn(
        { sharedCache: r }
      )
    }), o === null ? ca(s) : (o = jn.clone(o), ar(o) && "@context" in o || (o = { "@context": o }), Fg({ activeCtx: a, localCtx: o, options: s }));
  }, e.getContextValue = Rr.getContextValue, e.documentLoaders = {}, e.useDocumentLoader = function(a) {
    if (!(a in e.documentLoaders))
      throw new Wt(
        'Unknown document loader type: "' + a + '"',
        "jsonld.UnknownDocumentLoader",
        { type: a }
      );
    e.documentLoader = e.documentLoaders[a].apply(
      e,
      Array.prototype.slice.call(arguments, 1)
    );
  }, e.registerRDFParser = function(a, o) {
    t[a] = o;
  }, e.unregisterRDFParser = function(a) {
    delete t[a];
  }, e.registerRDFParser("application/n-quads", fa.parse), e.registerRDFParser("application/nquads", fa.parse), e.url = Qn, e.logEventHandler = HN, e.logWarningEventHandler = GN, e.safeEventHandler = VN, e.setDefaultEventHandler = WN, e.strictEventHandler = zN, e.unhandledEventHandler = KN, e.util = jn, Object.assign(e, jn), e.promises = e, e.RequestQueue = _1(), e.JsonLdProcessor = xN()(e), kg.setupGlobals(e), kg.setupDocumentLoaders(e);
  function i(a, {
    documentLoader: o = e.documentLoader,
    ...s
  }) {
    if (a && "compactionMap" in a)
      throw new Wt(
        '"compactionMap" not supported.',
        "jsonld.OptionsError"
      );
    if (a && "expansionMap" in a)
      throw new Wt(
        '"expansionMap" not supported.',
        "jsonld.OptionsError"
      );
    return Object.assign(
      {},
      { documentLoader: o },
      s,
      a,
      { eventHandler: JN({ options: a }) }
    );
  }
  return e;
}, k1 = function() {
  return P1(function() {
    return k1();
  });
};
P1(k1);
var XN = { exports: {} }, F1 = { exports: {} }, On = {}, ho = {};
ho.byteLength = ZN;
ho.toByteArray = tO;
ho.fromByteArray = iO;
var Qt = [], mt = [], YN = typeof Uint8Array < "u" ? Uint8Array : Array, xs = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
for (var jr = 0, QN = xs.length; jr < QN; ++jr)
  Qt[jr] = xs[jr], mt[xs.charCodeAt(jr)] = jr;
mt["-".charCodeAt(0)] = 62;
mt["_".charCodeAt(0)] = 63;
function B1(e) {
  var t = e.length;
  if (t % 4 > 0)
    throw new Error("Invalid string. Length must be a multiple of 4");
  var n = e.indexOf("=");
  n === -1 && (n = t);
  var r = n === t ? 0 : 4 - n % 4;
  return [n, r];
}
function ZN(e) {
  var t = B1(e), n = t[0], r = t[1];
  return (n + r) * 3 / 4 - r;
}
function eO(e, t, n) {
  return (t + n) * 3 / 4 - n;
}
function tO(e) {
  var t, n = B1(e), r = n[0], i = n[1], a = new YN(eO(e, r, i)), o = 0, s = i > 0 ? r - 4 : r, u;
  for (u = 0; u < s; u += 4)
    t = mt[e.charCodeAt(u)] << 18 | mt[e.charCodeAt(u + 1)] << 12 | mt[e.charCodeAt(u + 2)] << 6 | mt[e.charCodeAt(u + 3)], a[o++] = t >> 16 & 255, a[o++] = t >> 8 & 255, a[o++] = t & 255;
  return i === 2 && (t = mt[e.charCodeAt(u)] << 2 | mt[e.charCodeAt(u + 1)] >> 4, a[o++] = t & 255), i === 1 && (t = mt[e.charCodeAt(u)] << 10 | mt[e.charCodeAt(u + 1)] << 4 | mt[e.charCodeAt(u + 2)] >> 2, a[o++] = t >> 8 & 255, a[o++] = t & 255), a;
}
function nO(e) {
  return Qt[e >> 18 & 63] + Qt[e >> 12 & 63] + Qt[e >> 6 & 63] + Qt[e & 63];
}
function rO(e, t, n) {
  for (var r, i = [], a = t; a < n; a += 3)
    r = (e[a] << 16 & 16711680) + (e[a + 1] << 8 & 65280) + (e[a + 2] & 255), i.push(nO(r));
  return i.join("");
}
function iO(e) {
  for (var t, n = e.length, r = n % 3, i = [], a = 16383, o = 0, s = n - r; o < s; o += a)
    i.push(rO(e, o, o + a > s ? s : o + a));
  return r === 1 ? (t = e[n - 1], i.push(
    Qt[t >> 2] + Qt[t << 4 & 63] + "=="
  )) : r === 2 && (t = (e[n - 2] << 8) + e[n - 1], i.push(
    Qt[t >> 10] + Qt[t >> 4 & 63] + Qt[t << 2 & 63] + "="
  )), i.join("");
}
var Yh = {};
/*! ieee754. BSD-3-Clause License. Feross Aboukhadijeh <https://feross.org/opensource> */
Yh.read = function(e, t, n, r, i) {
  var a, o, s = i * 8 - r - 1, u = (1 << s) - 1, l = u >> 1, f = -7, c = n ? i - 1 : 0, p = n ? -1 : 1, d = e[t + c];
  for (c += p, a = d & (1 << -f) - 1, d >>= -f, f += s; f > 0; a = a * 256 + e[t + c], c += p, f -= 8)
    ;
  for (o = a & (1 << -f) - 1, a >>= -f, f += r; f > 0; o = o * 256 + e[t + c], c += p, f -= 8)
    ;
  if (a === 0)
    a = 1 - l;
  else {
    if (a === u)
      return o ? NaN : (d ? -1 : 1) * (1 / 0);
    o = o + Math.pow(2, r), a = a - l;
  }
  return (d ? -1 : 1) * o * Math.pow(2, a - r);
};
Yh.write = function(e, t, n, r, i, a) {
  var o, s, u, l = a * 8 - i - 1, f = (1 << l) - 1, c = f >> 1, p = i === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0, d = r ? 0 : a - 1, y = r ? 1 : -1, v = t < 0 || t === 0 && 1 / t < 0 ? 1 : 0;
  for (t = Math.abs(t), isNaN(t) || t === 1 / 0 ? (s = isNaN(t) ? 1 : 0, o = f) : (o = Math.floor(Math.log(t) / Math.LN2), t * (u = Math.pow(2, -o)) < 1 && (o--, u *= 2), o + c >= 1 ? t += p / u : t += p * Math.pow(2, 1 - c), t * u >= 2 && (o++, u /= 2), o + c >= f ? (s = 0, o = f) : o + c >= 1 ? (s = (t * u - 1) * Math.pow(2, i), o = o + c) : (s = t * Math.pow(2, c - 1) * Math.pow(2, i), o = 0)); i >= 8; e[n + d] = s & 255, d += y, s /= 256, i -= 8)
    ;
  for (o = o << i | s, l += i; l > 0; e[n + d] = o & 255, d += y, o /= 256, l -= 8)
    ;
  e[n + d - y] |= v * 128;
};
/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <https://feross.org>
 * @license  MIT
 */
(function(e) {
  const t = ho, n = Yh, r = typeof Symbol == "function" && typeof Symbol.for == "function" ? Symbol.for("nodejs.util.inspect.custom") : null;
  e.Buffer = s, e.SlowBuffer = E, e.INSPECT_MAX_BYTES = 50;
  const i = 2147483647;
  e.kMaxLength = i, s.TYPED_ARRAY_SUPPORT = a(), !s.TYPED_ARRAY_SUPPORT && typeof console < "u" && typeof console.error == "function" && console.error(
    "This browser lacks typed array (Uint8Array) support which is required by `buffer` v5.x. Use `buffer` v4.x if you require old browser support."
  );
  function a() {
    try {
      const b = new Uint8Array(1), h = { foo: function() {
        return 42;
      } };
      return Object.setPrototypeOf(h, Uint8Array.prototype), Object.setPrototypeOf(b, h), b.foo() === 42;
    } catch {
      return !1;
    }
  }
  Object.defineProperty(s.prototype, "parent", {
    enumerable: !0,
    get: function() {
      if (s.isBuffer(this))
        return this.buffer;
    }
  }), Object.defineProperty(s.prototype, "offset", {
    enumerable: !0,
    get: function() {
      if (s.isBuffer(this))
        return this.byteOffset;
    }
  });
  function o(b) {
    if (b > i)
      throw new RangeError('The value "' + b + '" is invalid for option "size"');
    const h = new Uint8Array(b);
    return Object.setPrototypeOf(h, s.prototype), h;
  }
  function s(b, h, g) {
    if (typeof b == "number") {
      if (typeof h == "string")
        throw new TypeError(
          'The "string" argument must be of type string. Received type number'
        );
      return c(b);
    }
    return u(b, h, g);
  }
  s.poolSize = 8192;
  function u(b, h, g) {
    if (typeof b == "string")
      return p(b, h);
    if (ArrayBuffer.isView(b))
      return y(b);
    if (b == null)
      throw new TypeError(
        "The first argument must be one of type string, Buffer, ArrayBuffer, Array, or Array-like Object. Received type " + typeof b
      );
    if (_e(b, ArrayBuffer) || b && _e(b.buffer, ArrayBuffer) || typeof SharedArrayBuffer < "u" && (_e(b, SharedArrayBuffer) || b && _e(b.buffer, SharedArrayBuffer)))
      return v(b, h, g);
    if (typeof b == "number")
      throw new TypeError(
        'The "value" argument must not be of type number. Received type number'
      );
    const A = b.valueOf && b.valueOf();
    if (A != null && A !== b)
      return s.from(A, h, g);
    const w = _(b);
    if (w)
      return w;
    if (typeof Symbol < "u" && Symbol.toPrimitive != null && typeof b[Symbol.toPrimitive] == "function")
      return s.from(b[Symbol.toPrimitive]("string"), h, g);
    throw new TypeError(
      "The first argument must be one of type string, Buffer, ArrayBuffer, Array, or Array-like Object. Received type " + typeof b
    );
  }
  s.from = function(b, h, g) {
    return u(b, h, g);
  }, Object.setPrototypeOf(s.prototype, Uint8Array.prototype), Object.setPrototypeOf(s, Uint8Array);
  function l(b) {
    if (typeof b != "number")
      throw new TypeError('"size" argument must be of type number');
    if (b < 0)
      throw new RangeError('The value "' + b + '" is invalid for option "size"');
  }
  function f(b, h, g) {
    return l(b), b <= 0 ? o(b) : h !== void 0 ? typeof g == "string" ? o(b).fill(h, g) : o(b).fill(h) : o(b);
  }
  s.alloc = function(b, h, g) {
    return f(b, h, g);
  };
  function c(b) {
    return l(b), o(b < 0 ? 0 : m(b) | 0);
  }
  s.allocUnsafe = function(b) {
    return c(b);
  }, s.allocUnsafeSlow = function(b) {
    return c(b);
  };
  function p(b, h) {
    if ((typeof h != "string" || h === "") && (h = "utf8"), !s.isEncoding(h))
      throw new TypeError("Unknown encoding: " + h);
    const g = L(b, h) | 0;
    let A = o(g);
    const w = A.write(b, h);
    return w !== g && (A = A.slice(0, w)), A;
  }
  function d(b) {
    const h = b.length < 0 ? 0 : m(b.length) | 0, g = o(h);
    for (let A = 0; A < h; A += 1)
      g[A] = b[A] & 255;
    return g;
  }
  function y(b) {
    if (_e(b, Uint8Array)) {
      const h = new Uint8Array(b);
      return v(h.buffer, h.byteOffset, h.byteLength);
    }
    return d(b);
  }
  function v(b, h, g) {
    if (h < 0 || b.byteLength < h)
      throw new RangeError('"offset" is outside of buffer bounds');
    if (b.byteLength < h + (g || 0))
      throw new RangeError('"length" is outside of buffer bounds');
    let A;
    return h === void 0 && g === void 0 ? A = new Uint8Array(b) : g === void 0 ? A = new Uint8Array(b, h) : A = new Uint8Array(b, h, g), Object.setPrototypeOf(A, s.prototype), A;
  }
  function _(b) {
    if (s.isBuffer(b)) {
      const h = m(b.length) | 0, g = o(h);
      return g.length === 0 || b.copy(g, 0, 0, h), g;
    }
    if (b.length !== void 0)
      return typeof b.length != "number" || de(b.length) ? o(0) : d(b);
    if (b.type === "Buffer" && Array.isArray(b.data))
      return d(b.data);
  }
  function m(b) {
    if (b >= i)
      throw new RangeError("Attempt to allocate Buffer larger than maximum size: 0x" + i.toString(16) + " bytes");
    return b | 0;
  }
  function E(b) {
    return +b != b && (b = 0), s.alloc(+b);
  }
  s.isBuffer = function(h) {
    return h != null && h._isBuffer === !0 && h !== s.prototype;
  }, s.compare = function(h, g) {
    if (_e(h, Uint8Array) && (h = s.from(h, h.offset, h.byteLength)), _e(g, Uint8Array) && (g = s.from(g, g.offset, g.byteLength)), !s.isBuffer(h) || !s.isBuffer(g))
      throw new TypeError(
        'The "buf1", "buf2" arguments must be one of type Buffer or Uint8Array'
      );
    if (h === g)
      return 0;
    let A = h.length, w = g.length;
    for (let S = 0, x = Math.min(A, w); S < x; ++S)
      if (h[S] !== g[S]) {
        A = h[S], w = g[S];
        break;
      }
    return A < w ? -1 : w < A ? 1 : 0;
  }, s.isEncoding = function(h) {
    switch (String(h).toLowerCase()) {
      case "hex":
      case "utf8":
      case "utf-8":
      case "ascii":
      case "latin1":
      case "binary":
      case "base64":
      case "ucs2":
      case "ucs-2":
      case "utf16le":
      case "utf-16le":
        return !0;
      default:
        return !1;
    }
  }, s.concat = function(h, g) {
    if (!Array.isArray(h))
      throw new TypeError('"list" argument must be an Array of Buffers');
    if (h.length === 0)
      return s.alloc(0);
    let A;
    if (g === void 0)
      for (g = 0, A = 0; A < h.length; ++A)
        g += h[A].length;
    const w = s.allocUnsafe(g);
    let S = 0;
    for (A = 0; A < h.length; ++A) {
      let x = h[A];
      if (_e(x, Uint8Array))
        S + x.length > w.length ? (s.isBuffer(x) || (x = s.from(x)), x.copy(w, S)) : Uint8Array.prototype.set.call(
          w,
          x,
          S
        );
      else if (s.isBuffer(x))
        x.copy(w, S);
      else
        throw new TypeError('"list" argument must be an Array of Buffers');
      S += x.length;
    }
    return w;
  };
  function L(b, h) {
    if (s.isBuffer(b))
      return b.length;
    if (ArrayBuffer.isView(b) || _e(b, ArrayBuffer))
      return b.byteLength;
    if (typeof b != "string")
      throw new TypeError(
        'The "string" argument must be one of type string, Buffer, or ArrayBuffer. Received type ' + typeof b
      );
    const g = b.length, A = arguments.length > 2 && arguments[2] === !0;
    if (!A && g === 0)
      return 0;
    let w = !1;
    for (; ; )
      switch (h) {
        case "ascii":
        case "latin1":
        case "binary":
          return g;
        case "utf8":
        case "utf-8":
          return F(b).length;
        case "ucs2":
        case "ucs-2":
        case "utf16le":
        case "utf-16le":
          return g * 2;
        case "hex":
          return g >>> 1;
        case "base64":
          return Ce(b).length;
        default:
          if (w)
            return A ? -1 : F(b).length;
          h = ("" + h).toLowerCase(), w = !0;
      }
  }
  s.byteLength = L;
  function I(b, h, g) {
    let A = !1;
    if ((h === void 0 || h < 0) && (h = 0), h > this.length || ((g === void 0 || g > this.length) && (g = this.length), g <= 0) || (g >>>= 0, h >>>= 0, g <= h))
      return "";
    for (b || (b = "utf8"); ; )
      switch (b) {
        case "hex":
          return ke(this, h, g);
        case "utf8":
        case "utf-8":
          return z(this, h, g);
        case "ascii":
          return ge(this, h, g);
        case "latin1":
        case "binary":
          return re(this, h, g);
        case "base64":
          return G(this, h, g);
        case "ucs2":
        case "ucs-2":
        case "utf16le":
        case "utf-16le":
          return B(this, h, g);
        default:
          if (A)
            throw new TypeError("Unknown encoding: " + b);
          b = (b + "").toLowerCase(), A = !0;
      }
  }
  s.prototype._isBuffer = !0;
  function j(b, h, g) {
    const A = b[h];
    b[h] = b[g], b[g] = A;
  }
  s.prototype.swap16 = function() {
    const h = this.length;
    if (h % 2 !== 0)
      throw new RangeError("Buffer size must be a multiple of 16-bits");
    for (let g = 0; g < h; g += 2)
      j(this, g, g + 1);
    return this;
  }, s.prototype.swap32 = function() {
    const h = this.length;
    if (h % 4 !== 0)
      throw new RangeError("Buffer size must be a multiple of 32-bits");
    for (let g = 0; g < h; g += 4)
      j(this, g, g + 3), j(this, g + 1, g + 2);
    return this;
  }, s.prototype.swap64 = function() {
    const h = this.length;
    if (h % 8 !== 0)
      throw new RangeError("Buffer size must be a multiple of 64-bits");
    for (let g = 0; g < h; g += 8)
      j(this, g, g + 7), j(this, g + 1, g + 6), j(this, g + 2, g + 5), j(this, g + 3, g + 4);
    return this;
  }, s.prototype.toString = function() {
    const h = this.length;
    return h === 0 ? "" : arguments.length === 0 ? z(this, 0, h) : I.apply(this, arguments);
  }, s.prototype.toLocaleString = s.prototype.toString, s.prototype.equals = function(h) {
    if (!s.isBuffer(h))
      throw new TypeError("Argument must be a Buffer");
    return this === h ? !0 : s.compare(this, h) === 0;
  }, s.prototype.inspect = function() {
    let h = "";
    const g = e.INSPECT_MAX_BYTES;
    return h = this.toString("hex", 0, g).replace(/(.{2})/g, "$1 ").trim(), this.length > g && (h += " ... "), "<Buffer " + h + ">";
  }, r && (s.prototype[r] = s.prototype.inspect), s.prototype.compare = function(h, g, A, w, S) {
    if (_e(h, Uint8Array) && (h = s.from(h, h.offset, h.byteLength)), !s.isBuffer(h))
      throw new TypeError(
        'The "target" argument must be one of type Buffer or Uint8Array. Received type ' + typeof h
      );
    if (g === void 0 && (g = 0), A === void 0 && (A = h ? h.length : 0), w === void 0 && (w = 0), S === void 0 && (S = this.length), g < 0 || A > h.length || w < 0 || S > this.length)
      throw new RangeError("out of range index");
    if (w >= S && g >= A)
      return 0;
    if (w >= S)
      return -1;
    if (g >= A)
      return 1;
    if (g >>>= 0, A >>>= 0, w >>>= 0, S >>>= 0, this === h)
      return 0;
    let x = S - w, U = A - g;
    const Q = Math.min(x, U), oe = this.slice(w, S), be = h.slice(g, A);
    for (let J = 0; J < Q; ++J)
      if (oe[J] !== be[J]) {
        x = oe[J], U = be[J];
        break;
      }
    return x < U ? -1 : U < x ? 1 : 0;
  };
  function M(b, h, g, A, w) {
    if (b.length === 0)
      return -1;
    if (typeof g == "string" ? (A = g, g = 0) : g > 2147483647 ? g = 2147483647 : g < -2147483648 && (g = -2147483648), g = +g, de(g) && (g = w ? 0 : b.length - 1), g < 0 && (g = b.length + g), g >= b.length) {
      if (w)
        return -1;
      g = b.length - 1;
    } else if (g < 0)
      if (w)
        g = 0;
      else
        return -1;
    if (typeof h == "string" && (h = s.from(h, A)), s.isBuffer(h))
      return h.length === 0 ? -1 : O(b, h, g, A, w);
    if (typeof h == "number")
      return h = h & 255, typeof Uint8Array.prototype.indexOf == "function" ? w ? Uint8Array.prototype.indexOf.call(b, h, g) : Uint8Array.prototype.lastIndexOf.call(b, h, g) : O(b, [h], g, A, w);
    throw new TypeError("val must be string, number or Buffer");
  }
  function O(b, h, g, A, w) {
    let S = 1, x = b.length, U = h.length;
    if (A !== void 0 && (A = String(A).toLowerCase(), A === "ucs2" || A === "ucs-2" || A === "utf16le" || A === "utf-16le")) {
      if (b.length < 2 || h.length < 2)
        return -1;
      S = 2, x /= 2, U /= 2, g /= 2;
    }
    function Q(be, J) {
      return S === 1 ? be[J] : be.readUInt16BE(J * S);
    }
    let oe;
    if (w) {
      let be = -1;
      for (oe = g; oe < x; oe++)
        if (Q(b, oe) === Q(h, be === -1 ? 0 : oe - be)) {
          if (be === -1 && (be = oe), oe - be + 1 === U)
            return be * S;
        } else
          be !== -1 && (oe -= oe - be), be = -1;
    } else
      for (g + U > x && (g = x - U), oe = g; oe >= 0; oe--) {
        let be = !0;
        for (let J = 0; J < U; J++)
          if (Q(b, oe + J) !== Q(h, J)) {
            be = !1;
            break;
          }
        if (be)
          return oe;
      }
    return -1;
  }
  s.prototype.includes = function(h, g, A) {
    return this.indexOf(h, g, A) !== -1;
  }, s.prototype.indexOf = function(h, g, A) {
    return M(this, h, g, A, !0);
  }, s.prototype.lastIndexOf = function(h, g, A) {
    return M(this, h, g, A, !1);
  };
  function $(b, h, g, A) {
    g = Number(g) || 0;
    const w = b.length - g;
    A ? (A = Number(A), A > w && (A = w)) : A = w;
    const S = h.length;
    A > S / 2 && (A = S / 2);
    let x;
    for (x = 0; x < A; ++x) {
      const U = parseInt(h.substr(x * 2, 2), 16);
      if (de(U))
        return x;
      b[g + x] = U;
    }
    return x;
  }
  function q(b, h, g, A) {
    return De(F(h, b.length - g), b, g, A);
  }
  function N(b, h, g, A) {
    return De(ne(h), b, g, A);
  }
  function P(b, h, g, A) {
    return De(Ce(h), b, g, A);
  }
  function H(b, h, g, A) {
    return De(ie(h, b.length - g), b, g, A);
  }
  s.prototype.write = function(h, g, A, w) {
    if (g === void 0)
      w = "utf8", A = this.length, g = 0;
    else if (A === void 0 && typeof g == "string")
      w = g, A = this.length, g = 0;
    else if (isFinite(g))
      g = g >>> 0, isFinite(A) ? (A = A >>> 0, w === void 0 && (w = "utf8")) : (w = A, A = void 0);
    else
      throw new Error(
        "Buffer.write(string, encoding, offset[, length]) is no longer supported"
      );
    const S = this.length - g;
    if ((A === void 0 || A > S) && (A = S), h.length > 0 && (A < 0 || g < 0) || g > this.length)
      throw new RangeError("Attempt to write outside buffer bounds");
    w || (w = "utf8");
    let x = !1;
    for (; ; )
      switch (w) {
        case "hex":
          return $(this, h, g, A);
        case "utf8":
        case "utf-8":
          return q(this, h, g, A);
        case "ascii":
        case "latin1":
        case "binary":
          return N(this, h, g, A);
        case "base64":
          return P(this, h, g, A);
        case "ucs2":
        case "ucs-2":
        case "utf16le":
        case "utf-16le":
          return H(this, h, g, A);
        default:
          if (x)
            throw new TypeError("Unknown encoding: " + w);
          w = ("" + w).toLowerCase(), x = !0;
      }
  }, s.prototype.toJSON = function() {
    return {
      type: "Buffer",
      data: Array.prototype.slice.call(this._arr || this, 0)
    };
  };
  function G(b, h, g) {
    return h === 0 && g === b.length ? t.fromByteArray(b) : t.fromByteArray(b.slice(h, g));
  }
  function z(b, h, g) {
    g = Math.min(b.length, g);
    const A = [];
    let w = h;
    for (; w < g; ) {
      const S = b[w];
      let x = null, U = S > 239 ? 4 : S > 223 ? 3 : S > 191 ? 2 : 1;
      if (w + U <= g) {
        let Q, oe, be, J;
        switch (U) {
          case 1:
            S < 128 && (x = S);
            break;
          case 2:
            Q = b[w + 1], (Q & 192) === 128 && (J = (S & 31) << 6 | Q & 63, J > 127 && (x = J));
            break;
          case 3:
            Q = b[w + 1], oe = b[w + 2], (Q & 192) === 128 && (oe & 192) === 128 && (J = (S & 15) << 12 | (Q & 63) << 6 | oe & 63, J > 2047 && (J < 55296 || J > 57343) && (x = J));
            break;
          case 4:
            Q = b[w + 1], oe = b[w + 2], be = b[w + 3], (Q & 192) === 128 && (oe & 192) === 128 && (be & 192) === 128 && (J = (S & 15) << 18 | (Q & 63) << 12 | (oe & 63) << 6 | be & 63, J > 65535 && J < 1114112 && (x = J));
        }
      }
      x === null ? (x = 65533, U = 1) : x > 65535 && (x -= 65536, A.push(x >>> 10 & 1023 | 55296), x = 56320 | x & 1023), A.push(x), w += U;
    }
    return se(A);
  }
  const W = 4096;
  function se(b) {
    const h = b.length;
    if (h <= W)
      return String.fromCharCode.apply(String, b);
    let g = "", A = 0;
    for (; A < h; )
      g += String.fromCharCode.apply(
        String,
        b.slice(A, A += W)
      );
    return g;
  }
  function ge(b, h, g) {
    let A = "";
    g = Math.min(b.length, g);
    for (let w = h; w < g; ++w)
      A += String.fromCharCode(b[w] & 127);
    return A;
  }
  function re(b, h, g) {
    let A = "";
    g = Math.min(b.length, g);
    for (let w = h; w < g; ++w)
      A += String.fromCharCode(b[w]);
    return A;
  }
  function ke(b, h, g) {
    const A = b.length;
    (!h || h < 0) && (h = 0), (!g || g < 0 || g > A) && (g = A);
    let w = "";
    for (let S = h; S < g; ++S)
      w += Ot[b[S]];
    return w;
  }
  function B(b, h, g) {
    const A = b.slice(h, g);
    let w = "";
    for (let S = 0; S < A.length - 1; S += 2)
      w += String.fromCharCode(A[S] + A[S + 1] * 256);
    return w;
  }
  s.prototype.slice = function(h, g) {
    const A = this.length;
    h = ~~h, g = g === void 0 ? A : ~~g, h < 0 ? (h += A, h < 0 && (h = 0)) : h > A && (h = A), g < 0 ? (g += A, g < 0 && (g = 0)) : g > A && (g = A), g < h && (g = h);
    const w = this.subarray(h, g);
    return Object.setPrototypeOf(w, s.prototype), w;
  };
  function te(b, h, g) {
    if (b % 1 !== 0 || b < 0)
      throw new RangeError("offset is not uint");
    if (b + h > g)
      throw new RangeError("Trying to access beyond buffer length");
  }
  s.prototype.readUintLE = s.prototype.readUIntLE = function(h, g, A) {
    h = h >>> 0, g = g >>> 0, A || te(h, g, this.length);
    let w = this[h], S = 1, x = 0;
    for (; ++x < g && (S *= 256); )
      w += this[h + x] * S;
    return w;
  }, s.prototype.readUintBE = s.prototype.readUIntBE = function(h, g, A) {
    h = h >>> 0, g = g >>> 0, A || te(h, g, this.length);
    let w = this[h + --g], S = 1;
    for (; g > 0 && (S *= 256); )
      w += this[h + --g] * S;
    return w;
  }, s.prototype.readUint8 = s.prototype.readUInt8 = function(h, g) {
    return h = h >>> 0, g || te(h, 1, this.length), this[h];
  }, s.prototype.readUint16LE = s.prototype.readUInt16LE = function(h, g) {
    return h = h >>> 0, g || te(h, 2, this.length), this[h] | this[h + 1] << 8;
  }, s.prototype.readUint16BE = s.prototype.readUInt16BE = function(h, g) {
    return h = h >>> 0, g || te(h, 2, this.length), this[h] << 8 | this[h + 1];
  }, s.prototype.readUint32LE = s.prototype.readUInt32LE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), (this[h] | this[h + 1] << 8 | this[h + 2] << 16) + this[h + 3] * 16777216;
  }, s.prototype.readUint32BE = s.prototype.readUInt32BE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), this[h] * 16777216 + (this[h + 1] << 16 | this[h + 2] << 8 | this[h + 3]);
  }, s.prototype.readBigUInt64LE = R(function(h) {
    h = h >>> 0, C(h, "offset");
    const g = this[h], A = this[h + 7];
    (g === void 0 || A === void 0) && D(h, this.length - 8);
    const w = g + this[++h] * 2 ** 8 + this[++h] * 2 ** 16 + this[++h] * 2 ** 24, S = this[++h] + this[++h] * 2 ** 8 + this[++h] * 2 ** 16 + A * 2 ** 24;
    return BigInt(w) + (BigInt(S) << BigInt(32));
  }), s.prototype.readBigUInt64BE = R(function(h) {
    h = h >>> 0, C(h, "offset");
    const g = this[h], A = this[h + 7];
    (g === void 0 || A === void 0) && D(h, this.length - 8);
    const w = g * 2 ** 24 + this[++h] * 2 ** 16 + this[++h] * 2 ** 8 + this[++h], S = this[++h] * 2 ** 24 + this[++h] * 2 ** 16 + this[++h] * 2 ** 8 + A;
    return (BigInt(w) << BigInt(32)) + BigInt(S);
  }), s.prototype.readIntLE = function(h, g, A) {
    h = h >>> 0, g = g >>> 0, A || te(h, g, this.length);
    let w = this[h], S = 1, x = 0;
    for (; ++x < g && (S *= 256); )
      w += this[h + x] * S;
    return S *= 128, w >= S && (w -= Math.pow(2, 8 * g)), w;
  }, s.prototype.readIntBE = function(h, g, A) {
    h = h >>> 0, g = g >>> 0, A || te(h, g, this.length);
    let w = g, S = 1, x = this[h + --w];
    for (; w > 0 && (S *= 256); )
      x += this[h + --w] * S;
    return S *= 128, x >= S && (x -= Math.pow(2, 8 * g)), x;
  }, s.prototype.readInt8 = function(h, g) {
    return h = h >>> 0, g || te(h, 1, this.length), this[h] & 128 ? (255 - this[h] + 1) * -1 : this[h];
  }, s.prototype.readInt16LE = function(h, g) {
    h = h >>> 0, g || te(h, 2, this.length);
    const A = this[h] | this[h + 1] << 8;
    return A & 32768 ? A | 4294901760 : A;
  }, s.prototype.readInt16BE = function(h, g) {
    h = h >>> 0, g || te(h, 2, this.length);
    const A = this[h + 1] | this[h] << 8;
    return A & 32768 ? A | 4294901760 : A;
  }, s.prototype.readInt32LE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), this[h] | this[h + 1] << 8 | this[h + 2] << 16 | this[h + 3] << 24;
  }, s.prototype.readInt32BE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), this[h] << 24 | this[h + 1] << 16 | this[h + 2] << 8 | this[h + 3];
  }, s.prototype.readBigInt64LE = R(function(h) {
    h = h >>> 0, C(h, "offset");
    const g = this[h], A = this[h + 7];
    (g === void 0 || A === void 0) && D(h, this.length - 8);
    const w = this[h + 4] + this[h + 5] * 2 ** 8 + this[h + 6] * 2 ** 16 + (A << 24);
    return (BigInt(w) << BigInt(32)) + BigInt(g + this[++h] * 2 ** 8 + this[++h] * 2 ** 16 + this[++h] * 2 ** 24);
  }), s.prototype.readBigInt64BE = R(function(h) {
    h = h >>> 0, C(h, "offset");
    const g = this[h], A = this[h + 7];
    (g === void 0 || A === void 0) && D(h, this.length - 8);
    const w = (g << 24) + // Overflow
    this[++h] * 2 ** 16 + this[++h] * 2 ** 8 + this[++h];
    return (BigInt(w) << BigInt(32)) + BigInt(this[++h] * 2 ** 24 + this[++h] * 2 ** 16 + this[++h] * 2 ** 8 + A);
  }), s.prototype.readFloatLE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), n.read(this, h, !0, 23, 4);
  }, s.prototype.readFloatBE = function(h, g) {
    return h = h >>> 0, g || te(h, 4, this.length), n.read(this, h, !1, 23, 4);
  }, s.prototype.readDoubleLE = function(h, g) {
    return h = h >>> 0, g || te(h, 8, this.length), n.read(this, h, !0, 52, 8);
  }, s.prototype.readDoubleBE = function(h, g) {
    return h = h >>> 0, g || te(h, 8, this.length), n.read(this, h, !1, 52, 8);
  };
  function ye(b, h, g, A, w, S) {
    if (!s.isBuffer(b))
      throw new TypeError('"buffer" argument must be a Buffer instance');
    if (h > w || h < S)
      throw new RangeError('"value" argument is out of bounds');
    if (g + A > b.length)
      throw new RangeError("Index out of range");
  }
  s.prototype.writeUintLE = s.prototype.writeUIntLE = function(h, g, A, w) {
    if (h = +h, g = g >>> 0, A = A >>> 0, !w) {
      const U = Math.pow(2, 8 * A) - 1;
      ye(this, h, g, A, U, 0);
    }
    let S = 1, x = 0;
    for (this[g] = h & 255; ++x < A && (S *= 256); )
      this[g + x] = h / S & 255;
    return g + A;
  }, s.prototype.writeUintBE = s.prototype.writeUIntBE = function(h, g, A, w) {
    if (h = +h, g = g >>> 0, A = A >>> 0, !w) {
      const U = Math.pow(2, 8 * A) - 1;
      ye(this, h, g, A, U, 0);
    }
    let S = A - 1, x = 1;
    for (this[g + S] = h & 255; --S >= 0 && (x *= 256); )
      this[g + S] = h / x & 255;
    return g + A;
  }, s.prototype.writeUint8 = s.prototype.writeUInt8 = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 1, 255, 0), this[g] = h & 255, g + 1;
  }, s.prototype.writeUint16LE = s.prototype.writeUInt16LE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 2, 65535, 0), this[g] = h & 255, this[g + 1] = h >>> 8, g + 2;
  }, s.prototype.writeUint16BE = s.prototype.writeUInt16BE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 2, 65535, 0), this[g] = h >>> 8, this[g + 1] = h & 255, g + 2;
  }, s.prototype.writeUint32LE = s.prototype.writeUInt32LE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 4, 4294967295, 0), this[g + 3] = h >>> 24, this[g + 2] = h >>> 16, this[g + 1] = h >>> 8, this[g] = h & 255, g + 4;
  }, s.prototype.writeUint32BE = s.prototype.writeUInt32BE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 4, 4294967295, 0), this[g] = h >>> 24, this[g + 1] = h >>> 16, this[g + 2] = h >>> 8, this[g + 3] = h & 255, g + 4;
  };
  function gt(b, h, g, A, w) {
    _t(h, A, w, b, g, 7);
    let S = Number(h & BigInt(4294967295));
    b[g++] = S, S = S >> 8, b[g++] = S, S = S >> 8, b[g++] = S, S = S >> 8, b[g++] = S;
    let x = Number(h >> BigInt(32) & BigInt(4294967295));
    return b[g++] = x, x = x >> 8, b[g++] = x, x = x >> 8, b[g++] = x, x = x >> 8, b[g++] = x, g;
  }
  function yt(b, h, g, A, w) {
    _t(h, A, w, b, g, 7);
    let S = Number(h & BigInt(4294967295));
    b[g + 7] = S, S = S >> 8, b[g + 6] = S, S = S >> 8, b[g + 5] = S, S = S >> 8, b[g + 4] = S;
    let x = Number(h >> BigInt(32) & BigInt(4294967295));
    return b[g + 3] = x, x = x >> 8, b[g + 2] = x, x = x >> 8, b[g + 1] = x, x = x >> 8, b[g] = x, g + 8;
  }
  s.prototype.writeBigUInt64LE = R(function(h, g = 0) {
    return gt(this, h, g, BigInt(0), BigInt("0xffffffffffffffff"));
  }), s.prototype.writeBigUInt64BE = R(function(h, g = 0) {
    return yt(this, h, g, BigInt(0), BigInt("0xffffffffffffffff"));
  }), s.prototype.writeIntLE = function(h, g, A, w) {
    if (h = +h, g = g >>> 0, !w) {
      const Q = Math.pow(2, 8 * A - 1);
      ye(this, h, g, A, Q - 1, -Q);
    }
    let S = 0, x = 1, U = 0;
    for (this[g] = h & 255; ++S < A && (x *= 256); )
      h < 0 && U === 0 && this[g + S - 1] !== 0 && (U = 1), this[g + S] = (h / x >> 0) - U & 255;
    return g + A;
  }, s.prototype.writeIntBE = function(h, g, A, w) {
    if (h = +h, g = g >>> 0, !w) {
      const Q = Math.pow(2, 8 * A - 1);
      ye(this, h, g, A, Q - 1, -Q);
    }
    let S = A - 1, x = 1, U = 0;
    for (this[g + S] = h & 255; --S >= 0 && (x *= 256); )
      h < 0 && U === 0 && this[g + S + 1] !== 0 && (U = 1), this[g + S] = (h / x >> 0) - U & 255;
    return g + A;
  }, s.prototype.writeInt8 = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 1, 127, -128), h < 0 && (h = 255 + h + 1), this[g] = h & 255, g + 1;
  }, s.prototype.writeInt16LE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 2, 32767, -32768), this[g] = h & 255, this[g + 1] = h >>> 8, g + 2;
  }, s.prototype.writeInt16BE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 2, 32767, -32768), this[g] = h >>> 8, this[g + 1] = h & 255, g + 2;
  }, s.prototype.writeInt32LE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 4, 2147483647, -2147483648), this[g] = h & 255, this[g + 1] = h >>> 8, this[g + 2] = h >>> 16, this[g + 3] = h >>> 24, g + 4;
  }, s.prototype.writeInt32BE = function(h, g, A) {
    return h = +h, g = g >>> 0, A || ye(this, h, g, 4, 2147483647, -2147483648), h < 0 && (h = 4294967295 + h + 1), this[g] = h >>> 24, this[g + 1] = h >>> 16, this[g + 2] = h >>> 8, this[g + 3] = h & 255, g + 4;
  }, s.prototype.writeBigInt64LE = R(function(h, g = 0) {
    return gt(this, h, g, -BigInt("0x8000000000000000"), BigInt("0x7fffffffffffffff"));
  }), s.prototype.writeBigInt64BE = R(function(h, g = 0) {
    return yt(this, h, g, -BigInt("0x8000000000000000"), BigInt("0x7fffffffffffffff"));
  });
  function Ze(b, h, g, A, w, S) {
    if (g + A > b.length)
      throw new RangeError("Index out of range");
    if (g < 0)
      throw new RangeError("Index out of range");
  }
  function Gt(b, h, g, A, w) {
    return h = +h, g = g >>> 0, w || Ze(b, h, g, 4), n.write(b, h, g, A, 23, 4), g + 4;
  }
  s.prototype.writeFloatLE = function(h, g, A) {
    return Gt(this, h, g, !0, A);
  }, s.prototype.writeFloatBE = function(h, g, A) {
    return Gt(this, h, g, !1, A);
  };
  function st(b, h, g, A, w) {
    return h = +h, g = g >>> 0, w || Ze(b, h, g, 8), n.write(b, h, g, A, 52, 8), g + 8;
  }
  s.prototype.writeDoubleLE = function(h, g, A) {
    return st(this, h, g, !0, A);
  }, s.prototype.writeDoubleBE = function(h, g, A) {
    return st(this, h, g, !1, A);
  }, s.prototype.copy = function(h, g, A, w) {
    if (!s.isBuffer(h))
      throw new TypeError("argument should be a Buffer");
    if (A || (A = 0), !w && w !== 0 && (w = this.length), g >= h.length && (g = h.length), g || (g = 0), w > 0 && w < A && (w = A), w === A || h.length === 0 || this.length === 0)
      return 0;
    if (g < 0)
      throw new RangeError("targetStart out of bounds");
    if (A < 0 || A >= this.length)
      throw new RangeError("Index out of range");
    if (w < 0)
      throw new RangeError("sourceEnd out of bounds");
    w > this.length && (w = this.length), h.length - g < w - A && (w = h.length - g + A);
    const S = w - A;
    return this === h && typeof Uint8Array.prototype.copyWithin == "function" ? this.copyWithin(g, A, w) : Uint8Array.prototype.set.call(
      h,
      this.subarray(A, w),
      g
    ), S;
  }, s.prototype.fill = function(h, g, A, w) {
    if (typeof h == "string") {
      if (typeof g == "string" ? (w = g, g = 0, A = this.length) : typeof A == "string" && (w = A, A = this.length), w !== void 0 && typeof w != "string")
        throw new TypeError("encoding must be a string");
      if (typeof w == "string" && !s.isEncoding(w))
        throw new TypeError("Unknown encoding: " + w);
      if (h.length === 1) {
        const x = h.charCodeAt(0);
        (w === "utf8" && x < 128 || w === "latin1") && (h = x);
      }
    } else
      typeof h == "number" ? h = h & 255 : typeof h == "boolean" && (h = Number(h));
    if (g < 0 || this.length < g || this.length < A)
      throw new RangeError("Out of range index");
    if (A <= g)
      return this;
    g = g >>> 0, A = A === void 0 ? this.length : A >>> 0, h || (h = 0);
    let S;
    if (typeof h == "number")
      for (S = g; S < A; ++S)
        this[S] = h;
    else {
      const x = s.isBuffer(h) ? h : s.from(h, w), U = x.length;
      if (U === 0)
        throw new TypeError('The value "' + h + '" is invalid for argument "value"');
      for (S = 0; S < A - g; ++S)
        this[S + g] = x[S % U];
    }
    return this;
  };
  const Fe = {};
  function He(b, h, g) {
    Fe[b] = class extends g {
      constructor() {
        super(), Object.defineProperty(this, "message", {
          value: h.apply(this, arguments),
          writable: !0,
          configurable: !0
        }), this.name = `${this.name} [${b}]`, this.stack, delete this.name;
      }
      get code() {
        return b;
      }
      set code(w) {
        Object.defineProperty(this, "code", {
          configurable: !0,
          enumerable: !0,
          value: w,
          writable: !0
        });
      }
      toString() {
        return `${this.name} [${b}]: ${this.message}`;
      }
    };
  }
  He(
    "ERR_BUFFER_OUT_OF_BOUNDS",
    function(b) {
      return b ? `${b} is outside of buffer bounds` : "Attempt to access memory outside buffer bounds";
    },
    RangeError
  ), He(
    "ERR_INVALID_ARG_TYPE",
    function(b, h) {
      return `The "${b}" argument must be of type number. Received type ${typeof h}`;
    },
    TypeError
  ), He(
    "ERR_OUT_OF_RANGE",
    function(b, h, g) {
      let A = `The value of "${b}" is out of range.`, w = g;
      return Number.isInteger(g) && Math.abs(g) > 2 ** 32 ? w = ut(String(g)) : typeof g == "bigint" && (w = String(g), (g > BigInt(2) ** BigInt(32) || g < -(BigInt(2) ** BigInt(32))) && (w = ut(w)), w += "n"), A += ` It must be ${h}. Received ${w}`, A;
    },
    RangeError
  );
  function ut(b) {
    let h = "", g = b.length;
    const A = b[0] === "-" ? 1 : 0;
    for (; g >= A + 4; g -= 3)
      h = `_${b.slice(g - 3, g)}${h}`;
    return `${b.slice(0, g)}${h}`;
  }
  function vt(b, h, g) {
    C(h, "offset"), (b[h] === void 0 || b[h + g] === void 0) && D(h, b.length - (g + 1));
  }
  function _t(b, h, g, A, w, S) {
    if (b > g || b < h) {
      const x = typeof h == "bigint" ? "n" : "";
      let U;
      throw S > 3 ? h === 0 || h === BigInt(0) ? U = `>= 0${x} and < 2${x} ** ${(S + 1) * 8}${x}` : U = `>= -(2${x} ** ${(S + 1) * 8 - 1}${x}) and < 2 ** ${(S + 1) * 8 - 1}${x}` : U = `>= ${h}${x} and <= ${g}${x}`, new Fe.ERR_OUT_OF_RANGE("value", U, b);
    }
    vt(A, w, S);
  }
  function C(b, h) {
    if (typeof b != "number")
      throw new Fe.ERR_INVALID_ARG_TYPE(h, "number", b);
  }
  function D(b, h, g) {
    throw Math.floor(b) !== b ? (C(b, g), new Fe.ERR_OUT_OF_RANGE(g || "offset", "an integer", b)) : h < 0 ? new Fe.ERR_BUFFER_OUT_OF_BOUNDS() : new Fe.ERR_OUT_OF_RANGE(
      g || "offset",
      `>= ${g ? 1 : 0} and <= ${h}`,
      b
    );
  }
  const k = /[^+/0-9A-Za-z-_]/g;
  function V(b) {
    if (b = b.split("=")[0], b = b.trim().replace(k, ""), b.length < 2)
      return "";
    for (; b.length % 4 !== 0; )
      b = b + "=";
    return b;
  }
  function F(b, h) {
    h = h || 1 / 0;
    let g;
    const A = b.length;
    let w = null;
    const S = [];
    for (let x = 0; x < A; ++x) {
      if (g = b.charCodeAt(x), g > 55295 && g < 57344) {
        if (!w) {
          if (g > 56319) {
            (h -= 3) > -1 && S.push(239, 191, 189);
            continue;
          } else if (x + 1 === A) {
            (h -= 3) > -1 && S.push(239, 191, 189);
            continue;
          }
          w = g;
          continue;
        }
        if (g < 56320) {
          (h -= 3) > -1 && S.push(239, 191, 189), w = g;
          continue;
        }
        g = (w - 55296 << 10 | g - 56320) + 65536;
      } else
        w && (h -= 3) > -1 && S.push(239, 191, 189);
      if (w = null, g < 128) {
        if ((h -= 1) < 0)
          break;
        S.push(g);
      } else if (g < 2048) {
        if ((h -= 2) < 0)
          break;
        S.push(
          g >> 6 | 192,
          g & 63 | 128
        );
      } else if (g < 65536) {
        if ((h -= 3) < 0)
          break;
        S.push(
          g >> 12 | 224,
          g >> 6 & 63 | 128,
          g & 63 | 128
        );
      } else if (g < 1114112) {
        if ((h -= 4) < 0)
          break;
        S.push(
          g >> 18 | 240,
          g >> 12 & 63 | 128,
          g >> 6 & 63 | 128,
          g & 63 | 128
        );
      } else
        throw new Error("Invalid code point");
    }
    return S;
  }
  function ne(b) {
    const h = [];
    for (let g = 0; g < b.length; ++g)
      h.push(b.charCodeAt(g) & 255);
    return h;
  }
  function ie(b, h) {
    let g, A, w;
    const S = [];
    for (let x = 0; x < b.length && !((h -= 2) < 0); ++x)
      g = b.charCodeAt(x), A = g >> 8, w = g % 256, S.push(w), S.push(A);
    return S;
  }
  function Ce(b) {
    return t.toByteArray(V(b));
  }
  function De(b, h, g, A) {
    let w;
    for (w = 0; w < A && !(w + g >= h.length || w >= b.length); ++w)
      h[w + g] = b[w];
    return w;
  }
  function _e(b, h) {
    return b instanceof h || b != null && b.constructor != null && b.constructor.name != null && b.constructor.name === h.name;
  }
  function de(b) {
    return b !== b;
  }
  const Ot = function() {
    const b = "0123456789abcdef", h = new Array(256);
    for (let g = 0; g < 16; ++g) {
      const A = g * 16;
      for (let w = 0; w < 16; ++w)
        h[A + w] = b[g] + b[w];
    }
    return h;
  }();
  function R(b) {
    return typeof BigInt > "u" ? T : b;
  }
  function T() {
    throw new Error("BigInt not supported");
  }
})(On);
var Me = {
  ArrayIsArray(e) {
    return Array.isArray(e);
  },
  ArrayPrototypeIncludes(e, t) {
    return e.includes(t);
  },
  ArrayPrototypeIndexOf(e, t) {
    return e.indexOf(t);
  },
  ArrayPrototypeJoin(e, t) {
    return e.join(t);
  },
  ArrayPrototypeMap(e, t) {
    return e.map(t);
  },
  ArrayPrototypePop(e, t) {
    return e.pop(t);
  },
  ArrayPrototypePush(e, t) {
    return e.push(t);
  },
  ArrayPrototypeSlice(e, t, n) {
    return e.slice(t, n);
  },
  Error,
  FunctionPrototypeCall(e, t, ...n) {
    return e.call(t, ...n);
  },
  FunctionPrototypeSymbolHasInstance(e, t) {
    return Function.prototype[Symbol.hasInstance].call(e, t);
  },
  MathFloor: Math.floor,
  Number,
  NumberIsInteger: Number.isInteger,
  NumberIsNaN: Number.isNaN,
  NumberMAX_SAFE_INTEGER: Number.MAX_SAFE_INTEGER,
  NumberMIN_SAFE_INTEGER: Number.MIN_SAFE_INTEGER,
  NumberParseInt: Number.parseInt,
  ObjectDefineProperties(e, t) {
    return Object.defineProperties(e, t);
  },
  ObjectDefineProperty(e, t, n) {
    return Object.defineProperty(e, t, n);
  },
  ObjectGetOwnPropertyDescriptor(e, t) {
    return Object.getOwnPropertyDescriptor(e, t);
  },
  ObjectKeys(e) {
    return Object.keys(e);
  },
  ObjectSetPrototypeOf(e, t) {
    return Object.setPrototypeOf(e, t);
  },
  Promise,
  PromisePrototypeCatch(e, t) {
    return e.catch(t);
  },
  PromisePrototypeThen(e, t, n) {
    return e.then(t, n);
  },
  PromiseReject(e) {
    return Promise.reject(e);
  },
  ReflectApply: Reflect.apply,
  RegExpPrototypeTest(e, t) {
    return e.test(t);
  },
  SafeSet: Set,
  String,
  StringPrototypeSlice(e, t, n) {
    return e.slice(t, n);
  },
  StringPrototypeToLowerCase(e) {
    return e.toLowerCase();
  },
  StringPrototypeToUpperCase(e) {
    return e.toUpperCase();
  },
  StringPrototypeTrim(e) {
    return e.trim();
  },
  Symbol,
  SymbolAsyncIterator: Symbol.asyncIterator,
  SymbolHasInstance: Symbol.hasInstance,
  SymbolIterator: Symbol.iterator,
  TypedArrayPrototypeSet(e, t, n) {
    return e.set(t, n);
  },
  Uint8Array
}, U1 = { exports: {} };
(function(e) {
  const t = On, n = Object.getPrototypeOf(async function() {
  }).constructor, r = globalThis.Blob || t.Blob, i = typeof r < "u" ? function(s) {
    return s instanceof r;
  } : function(s) {
    return !1;
  };
  class a extends Error {
    constructor(s) {
      if (!Array.isArray(s))
        throw new TypeError(`Expected input to be an Array, got ${typeof s}`);
      let u = "";
      for (let l = 0; l < s.length; l++)
        u += `    ${s[l].stack}
`;
      super(u), this.name = "AggregateError", this.errors = s;
    }
  }
  e.exports = {
    AggregateError: a,
    kEmptyObject: Object.freeze({}),
    once(o) {
      let s = !1;
      return function(...u) {
        s || (s = !0, o.apply(this, u));
      };
    },
    createDeferredPromise: function() {
      let o, s;
      return {
        promise: new Promise((l, f) => {
          o = l, s = f;
        }),
        resolve: o,
        reject: s
      };
    },
    promisify(o) {
      return new Promise((s, u) => {
        o((l, ...f) => l ? u(l) : s(...f));
      });
    },
    debuglog() {
      return function() {
      };
    },
    format(o, ...s) {
      return o.replace(/%([sdifj])/g, function(...[u, l]) {
        const f = s.shift();
        return l === "f" ? f.toFixed(6) : l === "j" ? JSON.stringify(f) : l === "s" && typeof f == "object" ? `${f.constructor !== Object ? f.constructor.name : ""} {}`.trim() : f.toString();
      });
    },
    inspect(o) {
      switch (typeof o) {
        case "string":
          if (o.includes("'"))
            if (o.includes('"')) {
              if (!o.includes("`") && !o.includes("${"))
                return `\`${o}\``;
            } else
              return `"${o}"`;
          return `'${o}'`;
        case "number":
          return isNaN(o) ? "NaN" : Object.is(o, -0) ? String(o) : o;
        case "bigint":
          return `${String(o)}n`;
        case "boolean":
        case "undefined":
          return String(o);
        case "object":
          return "{}";
      }
    },
    types: {
      isAsyncFunction(o) {
        return o instanceof n;
      },
      isArrayBufferView(o) {
        return ArrayBuffer.isView(o);
      }
    },
    isBlob: i
  }, e.exports.promisify.custom = Symbol.for("nodejs.util.promisify.custom");
})(U1);
var $n = U1.exports, Qh = {}, mi = { exports: {} }, Bg;
function Zh() {
  if (Bg)
    return mi.exports;
  Bg = 1;
  const { AbortController: e, AbortSignal: t } = typeof self < "u" ? self : typeof window < "u" ? window : (
    /* otherwise */
    void 0
  );
  return mi.exports = e, mi.exports.AbortSignal = t, mi.exports.default = e, mi.exports;
}
const { format: aO, inspect: Va, AggregateError: oO } = $n, sO = globalThis.AggregateError || oO, uO = Symbol("kIsNodeError"), lO = [
  "string",
  "function",
  "number",
  "object",
  // Accept 'Function' and 'Object' as alternative to the lower cased version.
  "Function",
  "Object",
  "boolean",
  "bigint",
  "symbol"
], fO = /^([A-Z][a-z0-9]*)+$/, cO = "__node_internal_", po = {};
function gr(e, t) {
  if (!e)
    throw new po.ERR_INTERNAL_ASSERTION(t);
}
function Ug(e) {
  let t = "", n = e.length;
  const r = e[0] === "-" ? 1 : 0;
  for (; n >= r + 4; n -= 3)
    t = `_${e.slice(n - 3, n)}${t}`;
  return `${e.slice(0, n)}${t}`;
}
function dO(e, t, n) {
  if (typeof t == "function")
    return gr(
      t.length <= n.length,
      // Default options do not count.
      `Code: ${e}; The provided arguments length (${n.length}) does not match the required ones (${t.length}).`
    ), t(...n);
  const r = (t.match(/%[dfijoOs]/g) || []).length;
  return gr(
    r === n.length,
    `Code: ${e}; The provided arguments length (${n.length}) does not match the required ones (${r}).`
  ), n.length === 0 ? t : aO(t, ...n);
}
function Ue(e, t, n) {
  n || (n = Error);
  class r extends n {
    constructor(...a) {
      super(dO(e, t, a));
    }
    toString() {
      return `${this.name} [${e}]: ${this.message}`;
    }
  }
  Object.defineProperties(r.prototype, {
    name: {
      value: n.name,
      writable: !0,
      enumerable: !1,
      configurable: !0
    },
    toString: {
      value() {
        return `${this.name} [${e}]: ${this.message}`;
      },
      writable: !0,
      enumerable: !1,
      configurable: !0
    }
  }), r.prototype.code = e, r.prototype[uO] = !0, po[e] = r;
}
function Hg(e) {
  const t = cO + e.name;
  return Object.defineProperty(e, "name", {
    value: t
  }), e;
}
function hO(e, t) {
  if (e && t && e !== t) {
    if (Array.isArray(t.errors))
      return t.errors.push(e), t;
    const n = new sO([t, e], t.message);
    return n.code = t.code, n;
  }
  return e || t;
}
let pO = class extends Error {
  constructor(t = "The operation was aborted", n = void 0) {
    if (n !== void 0 && typeof n != "object")
      throw new po.ERR_INVALID_ARG_TYPE("options", "Object", n);
    super(t, n), this.code = "ABORT_ERR", this.name = "AbortError";
  }
};
Ue("ERR_ASSERTION", "%s", Error);
Ue(
  "ERR_INVALID_ARG_TYPE",
  (e, t, n) => {
    gr(typeof e == "string", "'name' must be a string"), Array.isArray(t) || (t = [t]);
    let r = "The ";
    e.endsWith(" argument") ? r += `${e} ` : r += `"${e}" ${e.includes(".") ? "property" : "argument"} `, r += "must be ";
    const i = [], a = [], o = [];
    for (const u of t)
      gr(typeof u == "string", "All expected entries have to be of type string"), lO.includes(u) ? i.push(u.toLowerCase()) : fO.test(u) ? a.push(u) : (gr(u !== "object", 'The value "object" should be written as "Object"'), o.push(u));
    if (a.length > 0) {
      const u = i.indexOf("object");
      u !== -1 && (i.splice(i, u, 1), a.push("Object"));
    }
    if (i.length > 0) {
      switch (i.length) {
        case 1:
          r += `of type ${i[0]}`;
          break;
        case 2:
          r += `one of type ${i[0]} or ${i[1]}`;
          break;
        default: {
          const u = i.pop();
          r += `one of type ${i.join(", ")}, or ${u}`;
        }
      }
      (a.length > 0 || o.length > 0) && (r += " or ");
    }
    if (a.length > 0) {
      switch (a.length) {
        case 1:
          r += `an instance of ${a[0]}`;
          break;
        case 2:
          r += `an instance of ${a[0]} or ${a[1]}`;
          break;
        default: {
          const u = a.pop();
          r += `an instance of ${a.join(", ")}, or ${u}`;
        }
      }
      o.length > 0 && (r += " or ");
    }
    switch (o.length) {
      case 0:
        break;
      case 1:
        o[0].toLowerCase() !== o[0] && (r += "an "), r += `${o[0]}`;
        break;
      case 2:
        r += `one of ${o[0]} or ${o[1]}`;
        break;
      default: {
        const u = o.pop();
        r += `one of ${o.join(", ")}, or ${u}`;
      }
    }
    if (n == null)
      r += `. Received ${n}`;
    else if (typeof n == "function" && n.name)
      r += `. Received function ${n.name}`;
    else if (typeof n == "object") {
      var s;
      if ((s = n.constructor) !== null && s !== void 0 && s.name)
        r += `. Received an instance of ${n.constructor.name}`;
      else {
        const u = Va(n, {
          depth: -1
        });
        r += `. Received ${u}`;
      }
    } else {
      let u = Va(n, {
        colors: !1
      });
      u.length > 25 && (u = `${u.slice(0, 25)}...`), r += `. Received type ${typeof n} (${u})`;
    }
    return r;
  },
  TypeError
);
Ue(
  "ERR_INVALID_ARG_VALUE",
  (e, t, n = "is invalid") => {
    let r = Va(t);
    return r.length > 128 && (r = r.slice(0, 128) + "..."), `The ${e.includes(".") ? "property" : "argument"} '${e}' ${n}. Received ${r}`;
  },
  TypeError
);
Ue(
  "ERR_INVALID_RETURN_VALUE",
  (e, t, n) => {
    var r;
    const i = n != null && (r = n.constructor) !== null && r !== void 0 && r.name ? `instance of ${n.constructor.name}` : `type ${typeof n}`;
    return `Expected ${e} to be returned from the "${t}" function but got ${i}.`;
  },
  TypeError
);
Ue(
  "ERR_MISSING_ARGS",
  (...e) => {
    gr(e.length > 0, "At least one arg needs to be specified");
    let t;
    const n = e.length;
    switch (e = (Array.isArray(e) ? e : [e]).map((r) => `"${r}"`).join(" or "), n) {
      case 1:
        t += `The ${e[0]} argument`;
        break;
      case 2:
        t += `The ${e[0]} and ${e[1]} arguments`;
        break;
      default:
        {
          const r = e.pop();
          t += `The ${e.join(", ")}, and ${r} arguments`;
        }
        break;
    }
    return `${t} must be specified`;
  },
  TypeError
);
Ue(
  "ERR_OUT_OF_RANGE",
  (e, t, n) => {
    gr(t, 'Missing "range" argument');
    let r;
    return Number.isInteger(n) && Math.abs(n) > 2 ** 32 ? r = Ug(String(n)) : typeof n == "bigint" ? (r = String(n), (n > 2n ** 32n || n < -(2n ** 32n)) && (r = Ug(r)), r += "n") : r = Va(n), `The value of "${e}" is out of range. It must be ${t}. Received ${r}`;
  },
  RangeError
);
Ue("ERR_MULTIPLE_CALLBACK", "Callback called multiple times", Error);
Ue("ERR_METHOD_NOT_IMPLEMENTED", "The %s method is not implemented", Error);
Ue("ERR_STREAM_ALREADY_FINISHED", "Cannot call %s after a stream was finished", Error);
Ue("ERR_STREAM_CANNOT_PIPE", "Cannot pipe, not readable", Error);
Ue("ERR_STREAM_DESTROYED", "Cannot call %s after a stream was destroyed", Error);
Ue("ERR_STREAM_NULL_VALUES", "May not write null values to stream", TypeError);
Ue("ERR_STREAM_PREMATURE_CLOSE", "Premature close", Error);
Ue("ERR_STREAM_PUSH_AFTER_EOF", "stream.push() after EOF", Error);
Ue("ERR_STREAM_UNSHIFT_AFTER_END_EVENT", "stream.unshift() after end event", Error);
Ue("ERR_STREAM_WRITE_AFTER_END", "write after end", Error);
Ue("ERR_UNKNOWN_ENCODING", "Unknown encoding: %s", TypeError);
var at = {
  AbortError: pO,
  aggregateTwoErrors: Hg(hO),
  hideStackFrames: Hg,
  codes: po
};
const {
  ArrayIsArray: H1,
  ArrayPrototypeIncludes: G1,
  ArrayPrototypeJoin: V1,
  ArrayPrototypeMap: gO,
  NumberIsInteger: ep,
  NumberIsNaN: yO,
  NumberMAX_SAFE_INTEGER: vO,
  NumberMIN_SAFE_INTEGER: _O,
  NumberParseInt: bO,
  ObjectPrototypeHasOwnProperty: mO,
  RegExpPrototypeExec: wO,
  String: EO,
  StringPrototypeToUpperCase: SO,
  StringPrototypeTrim: AO
} = Me, {
  hideStackFrames: Ft,
  codes: { ERR_SOCKET_BAD_PORT: RO, ERR_INVALID_ARG_TYPE: ot, ERR_INVALID_ARG_VALUE: go, ERR_OUT_OF_RANGE: Sr, ERR_UNKNOWN_SIGNAL: Gg }
} = at, { normalizeEncoding: IO } = $n, { isAsyncFunction: TO, isArrayBufferView: xO } = $n.types, Vg = {};
function NO(e) {
  return e === (e | 0);
}
function OO(e) {
  return e === e >>> 0;
}
const $O = /^[0-7]+$/, LO = "must be a 32-bit unsigned integer or an octal string";
function MO(e, t, n) {
  if (typeof e > "u" && (e = n), typeof e == "string") {
    if (wO($O, e) === null)
      throw new go(t, e, LO);
    e = bO(e, 8);
  }
  return W1(e, t), e;
}
const CO = Ft((e, t, n = _O, r = vO) => {
  if (typeof e != "number")
    throw new ot(t, "number", e);
  if (!ep(e))
    throw new Sr(t, "an integer", e);
  if (e < n || e > r)
    throw new Sr(t, `>= ${n} && <= ${r}`, e);
}), DO = Ft((e, t, n = -2147483648, r = 2147483647) => {
  if (typeof e != "number")
    throw new ot(t, "number", e);
  if (!ep(e))
    throw new Sr(t, "an integer", e);
  if (e < n || e > r)
    throw new Sr(t, `>= ${n} && <= ${r}`, e);
}), W1 = Ft((e, t, n = !1) => {
  if (typeof e != "number")
    throw new ot(t, "number", e);
  if (!ep(e))
    throw new Sr(t, "an integer", e);
  const r = n ? 1 : 0, i = 4294967295;
  if (e < r || e > i)
    throw new Sr(t, `>= ${r} && <= ${i}`, e);
});
function J1(e, t) {
  if (typeof e != "string")
    throw new ot(t, "string", e);
}
function qO(e, t, n = void 0, r) {
  if (typeof e != "number")
    throw new ot(t, "number", e);
  if (n != null && e < n || r != null && e > r || (n != null || r != null) && yO(e))
    throw new Sr(
      t,
      `${n != null ? `>= ${n}` : ""}${n != null && r != null ? " && " : ""}${r != null ? `<= ${r}` : ""}`,
      e
    );
}
const jO = Ft((e, t, n) => {
  if (!G1(n, e)) {
    const i = "must be one of: " + V1(
      gO(n, (a) => typeof a == "string" ? `'${a}'` : EO(a)),
      ", "
    );
    throw new go(t, e, i);
  }
});
function PO(e, t) {
  if (typeof e != "boolean")
    throw new ot(t, "boolean", e);
}
function Ns(e, t, n) {
  return e == null || !mO(e, t) ? n : e[t];
}
const kO = Ft((e, t, n = null) => {
  const r = Ns(n, "allowArray", !1), i = Ns(n, "allowFunction", !1);
  if (!Ns(n, "nullable", !1) && e === null || !r && H1(e) || typeof e != "object" && (!i || typeof e != "function"))
    throw new ot(t, "Object", e);
}), FO = Ft((e, t, n = 0) => {
  if (!H1(e))
    throw new ot(t, "Array", e);
  if (e.length < n) {
    const r = `must be longer than ${n}`;
    throw new go(t, e, r);
  }
});
function BO(e, t = "signal") {
  if (J1(e, t), Vg[e] === void 0)
    throw Vg[SO(e)] !== void 0 ? new Gg(e + " (signals must use all capital letters)") : new Gg(e);
}
const UO = Ft((e, t = "buffer") => {
  if (!xO(e))
    throw new ot(t, ["Buffer", "TypedArray", "DataView"], e);
});
function HO(e, t) {
  const n = IO(t), r = e.length;
  if (n === "hex" && r % 2 !== 0)
    throw new go("encoding", t, `is invalid for data of length ${r}`);
}
function GO(e, t = "Port", n = !0) {
  if (typeof e != "number" && typeof e != "string" || typeof e == "string" && AO(e).length === 0 || +e !== +e >>> 0 || e > 65535 || e === 0 && !n)
    throw new RO(t, e, n);
  return e | 0;
}
const VO = Ft((e, t) => {
  if (e !== void 0 && (e === null || typeof e != "object" || !("aborted" in e)))
    throw new ot(t, "AbortSignal", e);
}), WO = Ft((e, t) => {
  if (typeof e != "function")
    throw new ot(t, "Function", e);
}), JO = Ft((e, t) => {
  if (typeof e != "function" || TO(e))
    throw new ot(t, "Function", e);
}), zO = Ft((e, t) => {
  if (e !== void 0)
    throw new ot(t, "undefined", e);
});
function KO(e, t, n) {
  if (!G1(n, e))
    throw new ot(t, `('${V1(n, "|")}')`, e);
}
var yo = {
  isInt32: NO,
  isUint32: OO,
  parseFileMode: MO,
  validateArray: FO,
  validateBoolean: PO,
  validateBuffer: UO,
  validateEncoding: HO,
  validateFunction: WO,
  validateInt32: DO,
  validateInteger: CO,
  validateNumber: qO,
  validateObject: kO,
  validateOneOf: jO,
  validatePlainFunction: JO,
  validatePort: GO,
  validateSignalName: BO,
  validateString: J1,
  validateUint32: W1,
  validateUndefined: zO,
  validateUnion: KO,
  validateAbortSignal: VO
}, tp = { exports: {} }, z1 = { exports: {} }, Te = z1.exports = {}, Kt, Xt;
function ih() {
  throw new Error("setTimeout has not been defined");
}
function ah() {
  throw new Error("clearTimeout has not been defined");
}
(function() {
  try {
    typeof setTimeout == "function" ? Kt = setTimeout : Kt = ih;
  } catch {
    Kt = ih;
  }
  try {
    typeof clearTimeout == "function" ? Xt = clearTimeout : Xt = ah;
  } catch {
    Xt = ah;
  }
})();
function K1(e) {
  if (Kt === setTimeout)
    return setTimeout(e, 0);
  if ((Kt === ih || !Kt) && setTimeout)
    return Kt = setTimeout, setTimeout(e, 0);
  try {
    return Kt(e, 0);
  } catch {
    try {
      return Kt.call(null, e, 0);
    } catch {
      return Kt.call(this, e, 0);
    }
  }
}
function XO(e) {
  if (Xt === clearTimeout)
    return clearTimeout(e);
  if ((Xt === ah || !Xt) && clearTimeout)
    return Xt = clearTimeout, clearTimeout(e);
  try {
    return Xt(e);
  } catch {
    try {
      return Xt.call(null, e);
    } catch {
      return Xt.call(this, e);
    }
  }
}
var _n = [], Wr = !1, lr, xa = -1;
function YO() {
  !Wr || !lr || (Wr = !1, lr.length ? _n = lr.concat(_n) : xa = -1, _n.length && X1());
}
function X1() {
  if (!Wr) {
    var e = K1(YO);
    Wr = !0;
    for (var t = _n.length; t; ) {
      for (lr = _n, _n = []; ++xa < t; )
        lr && lr[xa].run();
      xa = -1, t = _n.length;
    }
    lr = null, Wr = !1, XO(e);
  }
}
Te.nextTick = function(e) {
  var t = new Array(arguments.length - 1);
  if (arguments.length > 1)
    for (var n = 1; n < arguments.length; n++)
      t[n - 1] = arguments[n];
  _n.push(new Y1(e, t)), _n.length === 1 && !Wr && K1(X1);
};
function Y1(e, t) {
  this.fun = e, this.array = t;
}
Y1.prototype.run = function() {
  this.fun.apply(null, this.array);
};
Te.title = "browser";
Te.browser = !0;
Te.env = {};
Te.argv = [];
Te.version = "";
Te.versions = {};
function Ln() {
}
Te.on = Ln;
Te.addListener = Ln;
Te.once = Ln;
Te.off = Ln;
Te.removeListener = Ln;
Te.removeAllListeners = Ln;
Te.emit = Ln;
Te.prependListener = Ln;
Te.prependOnceListener = Ln;
Te.listeners = function(e) {
  return [];
};
Te.binding = function(e) {
  throw new Error("process.binding is not supported");
};
Te.cwd = function() {
  return "/";
};
Te.chdir = function(e) {
  throw new Error("process.chdir is not supported");
};
Te.umask = function() {
  return 0;
};
var Ir = z1.exports;
const { Symbol: vo, SymbolAsyncIterator: Wg, SymbolIterator: Jg } = Me, Q1 = vo("kDestroyed"), Z1 = vo("kIsErrored"), oh = vo("kIsReadable"), eE = vo("kIsDisturbed");
function _o(e, t = !1) {
  var n;
  return !!(e && typeof e.pipe == "function" && typeof e.on == "function" && (!t || typeof e.pause == "function" && typeof e.resume == "function") && (!e._writableState || ((n = e._readableState) === null || n === void 0 ? void 0 : n.readable) !== !1) && // Duplex
  (!e._writableState || e._readableState));
}
function bo(e) {
  var t;
  return !!(e && typeof e.write == "function" && typeof e.on == "function" && (!e._readableState || ((t = e._writableState) === null || t === void 0 ? void 0 : t.writable) !== !1));
}
function QO(e) {
  return !!(e && typeof e.pipe == "function" && e._readableState && typeof e.on == "function" && typeof e.write == "function");
}
function Tr(e) {
  return e && (e._readableState || e._writableState || typeof e.write == "function" && typeof e.on == "function" || typeof e.pipe == "function" && typeof e.on == "function");
}
function ZO(e, t) {
  return e == null ? !1 : t === !0 ? typeof e[Wg] == "function" : t === !1 ? typeof e[Jg] == "function" : typeof e[Wg] == "function" || typeof e[Jg] == "function";
}
function mo(e) {
  if (!Tr(e))
    return null;
  const t = e._writableState, n = e._readableState, r = t || n;
  return !!(e.destroyed || e[Q1] || r != null && r.destroyed);
}
function tE(e) {
  if (!bo(e))
    return null;
  if (e.writableEnded === !0)
    return !0;
  const t = e._writableState;
  return t != null && t.errored ? !1 : typeof (t == null ? void 0 : t.ended) != "boolean" ? null : t.ended;
}
function e2(e, t) {
  if (!bo(e))
    return null;
  if (e.writableFinished === !0)
    return !0;
  const n = e._writableState;
  return n != null && n.errored ? !1 : typeof (n == null ? void 0 : n.finished) != "boolean" ? null : !!(n.finished || t === !1 && n.ended === !0 && n.length === 0);
}
function t2(e) {
  if (!_o(e))
    return null;
  if (e.readableEnded === !0)
    return !0;
  const t = e._readableState;
  return !t || t.errored ? !1 : typeof (t == null ? void 0 : t.ended) != "boolean" ? null : t.ended;
}
function nE(e, t) {
  if (!_o(e))
    return null;
  const n = e._readableState;
  return n != null && n.errored ? !1 : typeof (n == null ? void 0 : n.endEmitted) != "boolean" ? null : !!(n.endEmitted || t === !1 && n.ended === !0 && n.length === 0);
}
function rE(e) {
  return e && e[oh] != null ? e[oh] : typeof (e == null ? void 0 : e.readable) != "boolean" ? null : mo(e) ? !1 : _o(e) && e.readable && !nE(e);
}
function iE(e) {
  return typeof (e == null ? void 0 : e.writable) != "boolean" ? null : mo(e) ? !1 : bo(e) && e.writable && !tE(e);
}
function n2(e, t) {
  return Tr(e) ? mo(e) ? !0 : !((t == null ? void 0 : t.readable) !== !1 && rE(e) || (t == null ? void 0 : t.writable) !== !1 && iE(e)) : null;
}
function r2(e) {
  var t, n;
  return Tr(e) ? e.writableErrored ? e.writableErrored : (t = (n = e._writableState) === null || n === void 0 ? void 0 : n.errored) !== null && t !== void 0 ? t : null : null;
}
function i2(e) {
  var t, n;
  return Tr(e) ? e.readableErrored ? e.readableErrored : (t = (n = e._readableState) === null || n === void 0 ? void 0 : n.errored) !== null && t !== void 0 ? t : null : null;
}
function a2(e) {
  if (!Tr(e))
    return null;
  if (typeof e.closed == "boolean")
    return e.closed;
  const t = e._writableState, n = e._readableState;
  return typeof (t == null ? void 0 : t.closed) == "boolean" || typeof (n == null ? void 0 : n.closed) == "boolean" ? (t == null ? void 0 : t.closed) || (n == null ? void 0 : n.closed) : typeof e._closed == "boolean" && aE(e) ? e._closed : null;
}
function aE(e) {
  return typeof e._closed == "boolean" && typeof e._defaultKeepAlive == "boolean" && typeof e._removedConnection == "boolean" && typeof e._removedContLen == "boolean";
}
function oE(e) {
  return typeof e._sent100 == "boolean" && aE(e);
}
function o2(e) {
  var t;
  return typeof e._consuming == "boolean" && typeof e._dumped == "boolean" && ((t = e.req) === null || t === void 0 ? void 0 : t.upgradeOrConnect) === void 0;
}
function s2(e) {
  if (!Tr(e))
    return null;
  const t = e._writableState, n = e._readableState, r = t || n;
  return !r && oE(e) || !!(r && r.autoDestroy && r.emitClose && r.closed === !1);
}
function u2(e) {
  var t;
  return !!(e && ((t = e[eE]) !== null && t !== void 0 ? t : e.readableDidRead || e.readableAborted));
}
function l2(e) {
  var t, n, r, i, a, o, s, u, l, f;
  return !!(e && ((t = (n = (r = (i = (a = (o = e[Z1]) !== null && o !== void 0 ? o : e.readableErrored) !== null && a !== void 0 ? a : e.writableErrored) !== null && i !== void 0 ? i : (s = e._readableState) === null || s === void 0 ? void 0 : s.errorEmitted) !== null && r !== void 0 ? r : (u = e._writableState) === null || u === void 0 ? void 0 : u.errorEmitted) !== null && n !== void 0 ? n : (l = e._readableState) === null || l === void 0 ? void 0 : l.errored) !== null && t !== void 0 ? t : !((f = e._writableState) === null || f === void 0) && f.errored));
}
var xr = {
  kDestroyed: Q1,
  isDisturbed: u2,
  kIsDisturbed: eE,
  isErrored: l2,
  kIsErrored: Z1,
  isReadable: rE,
  kIsReadable: oh,
  isClosed: a2,
  isDestroyed: mo,
  isDuplexNodeStream: QO,
  isFinished: n2,
  isIterable: ZO,
  isReadableNodeStream: _o,
  isReadableEnded: t2,
  isReadableFinished: nE,
  isReadableErrored: i2,
  isNodeStream: Tr,
  isWritable: iE,
  isWritableNodeStream: bo,
  isWritableEnded: tE,
  isWritableFinished: e2,
  isWritableErrored: r2,
  isServerRequest: o2,
  isServerResponse: oE,
  willEmitClose: s2
};
const Pr = Ir, { AbortError: f2, codes: c2 } = at, { ERR_INVALID_ARG_TYPE: d2, ERR_STREAM_PREMATURE_CLOSE: zg } = c2, { kEmptyObject: Kg, once: Xg } = $n, { validateAbortSignal: h2, validateFunction: p2, validateObject: g2 } = yo, { Promise: y2 } = Me, {
  isClosed: v2,
  isReadable: Yg,
  isReadableNodeStream: Os,
  isReadableFinished: Qg,
  isReadableErrored: _2,
  isWritable: Zg,
  isWritableNodeStream: ey,
  isWritableFinished: ty,
  isWritableErrored: b2,
  isNodeStream: m2,
  willEmitClose: w2
} = xr;
function E2(e) {
  return e.setHeader && typeof e.abort == "function";
}
const S2 = () => {
};
function sE(e, t, n) {
  var r, i;
  arguments.length === 2 ? (n = t, t = Kg) : t == null ? t = Kg : g2(t, "options"), p2(n, "callback"), h2(t.signal, "options.signal"), n = Xg(n);
  const a = (r = t.readable) !== null && r !== void 0 ? r : Os(e), o = (i = t.writable) !== null && i !== void 0 ? i : ey(e);
  if (!m2(e))
    throw new d2("stream", "Stream", e);
  const s = e._writableState, u = e._readableState, l = () => {
    e.writable || p();
  };
  let f = w2(e) && Os(e) === a && ey(e) === o, c = ty(e, !1);
  const p = () => {
    c = !0, e.destroyed && (f = !1), !(f && (!e.readable || a)) && (!a || d) && n.call(e);
  };
  let d = Qg(e, !1);
  const y = () => {
    d = !0, e.destroyed && (f = !1), !(f && (!e.writable || o)) && (!o || c) && n.call(e);
  }, v = (I) => {
    n.call(e, I);
  };
  let _ = v2(e);
  const m = () => {
    _ = !0;
    const I = b2(e) || _2(e);
    if (I && typeof I != "boolean")
      return n.call(e, I);
    if (a && !d && Os(e, !0) && !Qg(e, !1))
      return n.call(e, new zg());
    if (o && !c && !ty(e, !1))
      return n.call(e, new zg());
    n.call(e);
  }, E = () => {
    e.req.on("finish", p);
  };
  E2(e) ? (e.on("complete", p), f || e.on("abort", m), e.req ? E() : e.on("request", E)) : o && !s && (e.on("end", l), e.on("close", l)), !f && typeof e.aborted == "boolean" && e.on("aborted", m), e.on("end", y), e.on("finish", p), t.error !== !1 && e.on("error", v), e.on("close", m), _ ? Pr.nextTick(m) : s != null && s.errorEmitted || u != null && u.errorEmitted ? f || Pr.nextTick(m) : (!a && (!f || Yg(e)) && (c || Zg(e) === !1) || !o && (!f || Zg(e)) && (d || Yg(e) === !1) || u && e.req && e.aborted) && Pr.nextTick(m);
  const L = () => {
    n = S2, e.removeListener("aborted", m), e.removeListener("complete", p), e.removeListener("abort", m), e.removeListener("request", E), e.req && e.req.removeListener("finish", p), e.removeListener("end", l), e.removeListener("close", l), e.removeListener("finish", p), e.removeListener("end", y), e.removeListener("error", v), e.removeListener("close", m);
  };
  if (t.signal && !_) {
    const I = () => {
      const j = n;
      L(), j.call(
        e,
        new f2(void 0, {
          cause: t.signal.reason
        })
      );
    };
    if (t.signal.aborted)
      Pr.nextTick(I);
    else {
      const j = n;
      n = Xg((...M) => {
        t.signal.removeEventListener("abort", I), j.apply(e, M);
      }), t.signal.addEventListener("abort", I);
    }
  }
  return L;
}
function A2(e, t) {
  return new y2((n, r) => {
    sE(e, t, (i) => {
      i ? r(i) : n();
    });
  });
}
tp.exports = sE;
tp.exports.finished = A2;
var Nr = tp.exports;
const uE = globalThis.AbortController || Zh().AbortController, {
  codes: { ERR_INVALID_ARG_TYPE: Qi, ERR_MISSING_ARGS: R2, ERR_OUT_OF_RANGE: I2 },
  AbortError: rn
} = at, { validateAbortSignal: ai, validateInteger: T2, validateObject: oi } = yo, x2 = Me.Symbol("kWeak"), { finished: N2 } = Nr, {
  ArrayPrototypePush: O2,
  MathFloor: $2,
  Number: L2,
  NumberIsNaN: M2,
  Promise: ny,
  PromiseReject: ry,
  PromisePrototypeThen: C2,
  Symbol: lE
} = Me, Wa = lE("kEmpty"), iy = lE("kEof");
function wo(e, t) {
  if (typeof e != "function")
    throw new Qi("fn", ["Function", "AsyncFunction"], e);
  t != null && oi(t, "options"), (t == null ? void 0 : t.signal) != null && ai(t.signal, "options.signal");
  let n = 1;
  return (t == null ? void 0 : t.concurrency) != null && (n = $2(t.concurrency)), T2(n, "concurrency", 1), (async function* () {
    var i, a;
    const o = new uE(), s = this, u = [], l = o.signal, f = {
      signal: l
    }, c = () => o.abort();
    t != null && (i = t.signal) !== null && i !== void 0 && i.aborted && c(), t == null || (a = t.signal) === null || a === void 0 || a.addEventListener("abort", c);
    let p, d, y = !1;
    function v() {
      y = !0;
    }
    async function _() {
      try {
        for await (let L of s) {
          var m;
          if (y)
            return;
          if (l.aborted)
            throw new rn();
          try {
            L = e(L, f);
          } catch (I) {
            L = ry(I);
          }
          L !== Wa && (typeof ((m = L) === null || m === void 0 ? void 0 : m.catch) == "function" && L.catch(v), u.push(L), p && (p(), p = null), !y && u.length && u.length >= n && await new ny((I) => {
            d = I;
          }));
        }
        u.push(iy);
      } catch (L) {
        const I = ry(L);
        C2(I, void 0, v), u.push(I);
      } finally {
        var E;
        y = !0, p && (p(), p = null), t == null || (E = t.signal) === null || E === void 0 || E.removeEventListener("abort", c);
      }
    }
    _();
    try {
      for (; ; ) {
        for (; u.length > 0; ) {
          const m = await u[0];
          if (m === iy)
            return;
          if (l.aborted)
            throw new rn();
          m !== Wa && (yield m), u.shift(), d && (d(), d = null);
        }
        await new ny((m) => {
          p = m;
        });
      }
    } finally {
      o.abort(), y = !0, d && (d(), d = null);
    }
  }).call(this);
}
function D2(e = void 0) {
  return e != null && oi(e, "options"), (e == null ? void 0 : e.signal) != null && ai(e.signal, "options.signal"), (async function* () {
    let n = 0;
    for await (const i of this) {
      var r;
      if (e != null && (r = e.signal) !== null && r !== void 0 && r.aborted)
        throw new rn({
          cause: e.signal.reason
        });
      yield [n++, i];
    }
  }).call(this);
}
async function fE(e, t = void 0) {
  for await (const n of np.call(this, e, t))
    return !0;
  return !1;
}
async function q2(e, t = void 0) {
  if (typeof e != "function")
    throw new Qi("fn", ["Function", "AsyncFunction"], e);
  return !await fE.call(
    this,
    async (...n) => !await e(...n),
    t
  );
}
async function j2(e, t) {
  for await (const n of np.call(this, e, t))
    return n;
}
async function P2(e, t) {
  if (typeof e != "function")
    throw new Qi("fn", ["Function", "AsyncFunction"], e);
  async function n(r, i) {
    return await e(r, i), Wa;
  }
  for await (const r of wo.call(this, n, t))
    ;
}
function np(e, t) {
  if (typeof e != "function")
    throw new Qi("fn", ["Function", "AsyncFunction"], e);
  async function n(r, i) {
    return await e(r, i) ? r : Wa;
  }
  return wo.call(this, n, t);
}
class k2 extends R2 {
  constructor() {
    super("reduce"), this.message = "Reduce of an empty stream requires an initial value";
  }
}
async function F2(e, t, n) {
  var r;
  if (typeof e != "function")
    throw new Qi("reducer", ["Function", "AsyncFunction"], e);
  n != null && oi(n, "options"), (n == null ? void 0 : n.signal) != null && ai(n.signal, "options.signal");
  let i = arguments.length > 1;
  if (n != null && (r = n.signal) !== null && r !== void 0 && r.aborted) {
    const l = new rn(void 0, {
      cause: n.signal.reason
    });
    throw this.once("error", () => {
    }), await N2(this.destroy(l)), l;
  }
  const a = new uE(), o = a.signal;
  if (n != null && n.signal) {
    const l = {
      once: !0,
      [x2]: this
    };
    n.signal.addEventListener("abort", () => a.abort(), l);
  }
  let s = !1;
  try {
    for await (const l of this) {
      var u;
      if (s = !0, n != null && (u = n.signal) !== null && u !== void 0 && u.aborted)
        throw new rn();
      i ? t = await e(t, l, {
        signal: o
      }) : (t = l, i = !0);
    }
    if (!s && !i)
      throw new k2();
  } finally {
    a.abort();
  }
  return t;
}
async function B2(e) {
  e != null && oi(e, "options"), (e == null ? void 0 : e.signal) != null && ai(e.signal, "options.signal");
  const t = [];
  for await (const r of this) {
    var n;
    if (e != null && (n = e.signal) !== null && n !== void 0 && n.aborted)
      throw new rn(void 0, {
        cause: e.signal.reason
      });
    O2(t, r);
  }
  return t;
}
function U2(e, t) {
  const n = wo.call(this, e, t);
  return (async function* () {
    for await (const i of n)
      yield* i;
  }).call(this);
}
function cE(e) {
  if (e = L2(e), M2(e))
    return 0;
  if (e < 0)
    throw new I2("number", ">= 0", e);
  return e;
}
function H2(e, t = void 0) {
  return t != null && oi(t, "options"), (t == null ? void 0 : t.signal) != null && ai(t.signal, "options.signal"), e = cE(e), (async function* () {
    var r;
    if (t != null && (r = t.signal) !== null && r !== void 0 && r.aborted)
      throw new rn();
    for await (const a of this) {
      var i;
      if (t != null && (i = t.signal) !== null && i !== void 0 && i.aborted)
        throw new rn();
      e-- <= 0 && (yield a);
    }
  }).call(this);
}
function G2(e, t = void 0) {
  return t != null && oi(t, "options"), (t == null ? void 0 : t.signal) != null && ai(t.signal, "options.signal"), e = cE(e), (async function* () {
    var r;
    if (t != null && (r = t.signal) !== null && r !== void 0 && r.aborted)
      throw new rn();
    for await (const a of this) {
      var i;
      if (t != null && (i = t.signal) !== null && i !== void 0 && i.aborted)
        throw new rn();
      if (e-- > 0)
        yield a;
      else
        return;
    }
  }).call(this);
}
Qh.streamReturningOperators = {
  asIndexedPairs: D2,
  drop: H2,
  filter: np,
  flatMap: U2,
  map: wo,
  take: G2
};
Qh.promiseReturningOperators = {
  every: q2,
  forEach: P2,
  reduce: F2,
  toArray: B2,
  some: fE,
  find: j2
};
const Kn = Ir, {
  aggregateTwoErrors: V2,
  codes: { ERR_MULTIPLE_CALLBACK: W2 },
  AbortError: J2
} = at, { Symbol: dE } = Me, { kDestroyed: z2, isDestroyed: K2, isFinished: X2, isServerRequest: Y2 } = xr, hE = dE("kDestroy"), sh = dE("kConstruct");
function pE(e, t, n) {
  e && (e.stack, t && !t.errored && (t.errored = e), n && !n.errored && (n.errored = e));
}
function Q2(e, t) {
  const n = this._readableState, r = this._writableState, i = r || n;
  return r && r.destroyed || n && n.destroyed ? (typeof t == "function" && t(), this) : (pE(e, r, n), r && (r.destroyed = !0), n && (n.destroyed = !0), i.constructed ? ay(this, e, t) : this.once(hE, function(a) {
    ay(this, V2(a, e), t);
  }), this);
}
function ay(e, t, n) {
  let r = !1;
  function i(a) {
    if (r)
      return;
    r = !0;
    const o = e._readableState, s = e._writableState;
    pE(a, s, o), s && (s.closed = !0), o && (o.closed = !0), typeof n == "function" && n(a), a ? Kn.nextTick(Z2, e, a) : Kn.nextTick(gE, e);
  }
  try {
    e._destroy(t || null, i);
  } catch (a) {
    i(a);
  }
}
function Z2(e, t) {
  uh(e, t), gE(e);
}
function gE(e) {
  const t = e._readableState, n = e._writableState;
  n && (n.closeEmitted = !0), t && (t.closeEmitted = !0), (n && n.emitClose || t && t.emitClose) && e.emit("close");
}
function uh(e, t) {
  const n = e._readableState, r = e._writableState;
  r && r.errorEmitted || n && n.errorEmitted || (r && (r.errorEmitted = !0), n && (n.errorEmitted = !0), e.emit("error", t));
}
function e$() {
  const e = this._readableState, t = this._writableState;
  e && (e.constructed = !0, e.closed = !1, e.closeEmitted = !1, e.destroyed = !1, e.errored = null, e.errorEmitted = !1, e.reading = !1, e.ended = e.readable === !1, e.endEmitted = e.readable === !1), t && (t.constructed = !0, t.destroyed = !1, t.closed = !1, t.closeEmitted = !1, t.errored = null, t.errorEmitted = !1, t.finalCalled = !1, t.prefinished = !1, t.ended = t.writable === !1, t.ending = t.writable === !1, t.finished = t.writable === !1);
}
function lh(e, t, n) {
  const r = e._readableState, i = e._writableState;
  if (i && i.destroyed || r && r.destroyed)
    return this;
  r && r.autoDestroy || i && i.autoDestroy ? e.destroy(t) : t && (t.stack, i && !i.errored && (i.errored = t), r && !r.errored && (r.errored = t), n ? Kn.nextTick(uh, e, t) : uh(e, t));
}
function t$(e, t) {
  if (typeof e._construct != "function")
    return;
  const n = e._readableState, r = e._writableState;
  n && (n.constructed = !1), r && (r.constructed = !1), e.once(sh, t), !(e.listenerCount(sh) > 1) && Kn.nextTick(n$, e);
}
function n$(e) {
  let t = !1;
  function n(r) {
    if (t) {
      lh(e, r ?? new W2());
      return;
    }
    t = !0;
    const i = e._readableState, a = e._writableState, o = a || i;
    i && (i.constructed = !0), a && (a.constructed = !0), o.destroyed ? e.emit(hE, r) : r ? lh(e, r, !0) : Kn.nextTick(r$, e);
  }
  try {
    e._construct(n);
  } catch (r) {
    n(r);
  }
}
function r$(e) {
  e.emit(sh);
}
function oy(e) {
  return e && e.setHeader && typeof e.abort == "function";
}
function yE(e) {
  e.emit("close");
}
function i$(e, t) {
  e.emit("error", t), Kn.nextTick(yE, e);
}
function a$(e, t) {
  !e || K2(e) || (!t && !X2(e) && (t = new J2()), Y2(e) ? (e.socket = null, e.destroy(t)) : oy(e) ? e.abort() : oy(e.req) ? e.req.abort() : typeof e.destroy == "function" ? e.destroy(t) : typeof e.close == "function" ? e.close() : t ? Kn.nextTick(i$, e, t) : Kn.nextTick(yE, e), e.destroyed || (e[z2] = !0));
}
var si = {
  construct: t$,
  destroyer: a$,
  destroy: Q2,
  undestroy: e$,
  errorOrDestroy: lh
}, rp = { exports: {} }, Jr = typeof Reflect == "object" ? Reflect : null, sy = Jr && typeof Jr.apply == "function" ? Jr.apply : function(t, n, r) {
  return Function.prototype.apply.call(t, n, r);
}, Na;
Jr && typeof Jr.ownKeys == "function" ? Na = Jr.ownKeys : Object.getOwnPropertySymbols ? Na = function(t) {
  return Object.getOwnPropertyNames(t).concat(Object.getOwnPropertySymbols(t));
} : Na = function(t) {
  return Object.getOwnPropertyNames(t);
};
function o$(e) {
  console && console.warn && console.warn(e);
}
var vE = Number.isNaN || function(t) {
  return t !== t;
};
function ve() {
  ve.init.call(this);
}
rp.exports = ve;
rp.exports.once = f$;
ve.EventEmitter = ve;
ve.prototype._events = void 0;
ve.prototype._eventsCount = 0;
ve.prototype._maxListeners = void 0;
var uy = 10;
function Eo(e) {
  if (typeof e != "function")
    throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof e);
}
Object.defineProperty(ve, "defaultMaxListeners", {
  enumerable: !0,
  get: function() {
    return uy;
  },
  set: function(e) {
    if (typeof e != "number" || e < 0 || vE(e))
      throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + e + ".");
    uy = e;
  }
});
ve.init = function() {
  (this._events === void 0 || this._events === Object.getPrototypeOf(this)._events) && (this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0), this._maxListeners = this._maxListeners || void 0;
};
ve.prototype.setMaxListeners = function(t) {
  if (typeof t != "number" || t < 0 || vE(t))
    throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + t + ".");
  return this._maxListeners = t, this;
};
function _E(e) {
  return e._maxListeners === void 0 ? ve.defaultMaxListeners : e._maxListeners;
}
ve.prototype.getMaxListeners = function() {
  return _E(this);
};
ve.prototype.emit = function(t) {
  for (var n = [], r = 1; r < arguments.length; r++)
    n.push(arguments[r]);
  var i = t === "error", a = this._events;
  if (a !== void 0)
    i = i && a.error === void 0;
  else if (!i)
    return !1;
  if (i) {
    var o;
    if (n.length > 0 && (o = n[0]), o instanceof Error)
      throw o;
    var s = new Error("Unhandled error." + (o ? " (" + o.message + ")" : ""));
    throw s.context = o, s;
  }
  var u = a[t];
  if (u === void 0)
    return !1;
  if (typeof u == "function")
    sy(u, this, n);
  else
    for (var l = u.length, f = SE(u, l), r = 0; r < l; ++r)
      sy(f[r], this, n);
  return !0;
};
function bE(e, t, n, r) {
  var i, a, o;
  if (Eo(n), a = e._events, a === void 0 ? (a = e._events = /* @__PURE__ */ Object.create(null), e._eventsCount = 0) : (a.newListener !== void 0 && (e.emit(
    "newListener",
    t,
    n.listener ? n.listener : n
  ), a = e._events), o = a[t]), o === void 0)
    o = a[t] = n, ++e._eventsCount;
  else if (typeof o == "function" ? o = a[t] = r ? [n, o] : [o, n] : r ? o.unshift(n) : o.push(n), i = _E(e), i > 0 && o.length > i && !o.warned) {
    o.warned = !0;
    var s = new Error("Possible EventEmitter memory leak detected. " + o.length + " " + String(t) + " listeners added. Use emitter.setMaxListeners() to increase limit");
    s.name = "MaxListenersExceededWarning", s.emitter = e, s.type = t, s.count = o.length, o$(s);
  }
  return e;
}
ve.prototype.addListener = function(t, n) {
  return bE(this, t, n, !1);
};
ve.prototype.on = ve.prototype.addListener;
ve.prototype.prependListener = function(t, n) {
  return bE(this, t, n, !0);
};
function s$() {
  if (!this.fired)
    return this.target.removeListener(this.type, this.wrapFn), this.fired = !0, arguments.length === 0 ? this.listener.call(this.target) : this.listener.apply(this.target, arguments);
}
function mE(e, t, n) {
  var r = { fired: !1, wrapFn: void 0, target: e, type: t, listener: n }, i = s$.bind(r);
  return i.listener = n, r.wrapFn = i, i;
}
ve.prototype.once = function(t, n) {
  return Eo(n), this.on(t, mE(this, t, n)), this;
};
ve.prototype.prependOnceListener = function(t, n) {
  return Eo(n), this.prependListener(t, mE(this, t, n)), this;
};
ve.prototype.removeListener = function(t, n) {
  var r, i, a, o, s;
  if (Eo(n), i = this._events, i === void 0)
    return this;
  if (r = i[t], r === void 0)
    return this;
  if (r === n || r.listener === n)
    --this._eventsCount === 0 ? this._events = /* @__PURE__ */ Object.create(null) : (delete i[t], i.removeListener && this.emit("removeListener", t, r.listener || n));
  else if (typeof r != "function") {
    for (a = -1, o = r.length - 1; o >= 0; o--)
      if (r[o] === n || r[o].listener === n) {
        s = r[o].listener, a = o;
        break;
      }
    if (a < 0)
      return this;
    a === 0 ? r.shift() : u$(r, a), r.length === 1 && (i[t] = r[0]), i.removeListener !== void 0 && this.emit("removeListener", t, s || n);
  }
  return this;
};
ve.prototype.off = ve.prototype.removeListener;
ve.prototype.removeAllListeners = function(t) {
  var n, r, i;
  if (r = this._events, r === void 0)
    return this;
  if (r.removeListener === void 0)
    return arguments.length === 0 ? (this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0) : r[t] !== void 0 && (--this._eventsCount === 0 ? this._events = /* @__PURE__ */ Object.create(null) : delete r[t]), this;
  if (arguments.length === 0) {
    var a = Object.keys(r), o;
    for (i = 0; i < a.length; ++i)
      o = a[i], o !== "removeListener" && this.removeAllListeners(o);
    return this.removeAllListeners("removeListener"), this._events = /* @__PURE__ */ Object.create(null), this._eventsCount = 0, this;
  }
  if (n = r[t], typeof n == "function")
    this.removeListener(t, n);
  else if (n !== void 0)
    for (i = n.length - 1; i >= 0; i--)
      this.removeListener(t, n[i]);
  return this;
};
function wE(e, t, n) {
  var r = e._events;
  if (r === void 0)
    return [];
  var i = r[t];
  return i === void 0 ? [] : typeof i == "function" ? n ? [i.listener || i] : [i] : n ? l$(i) : SE(i, i.length);
}
ve.prototype.listeners = function(t) {
  return wE(this, t, !0);
};
ve.prototype.rawListeners = function(t) {
  return wE(this, t, !1);
};
ve.listenerCount = function(e, t) {
  return typeof e.listenerCount == "function" ? e.listenerCount(t) : EE.call(e, t);
};
ve.prototype.listenerCount = EE;
function EE(e) {
  var t = this._events;
  if (t !== void 0) {
    var n = t[e];
    if (typeof n == "function")
      return 1;
    if (n !== void 0)
      return n.length;
  }
  return 0;
}
ve.prototype.eventNames = function() {
  return this._eventsCount > 0 ? Na(this._events) : [];
};
function SE(e, t) {
  for (var n = new Array(t), r = 0; r < t; ++r)
    n[r] = e[r];
  return n;
}
function u$(e, t) {
  for (; t + 1 < e.length; t++)
    e[t] = e[t + 1];
  e.pop();
}
function l$(e) {
  for (var t = new Array(e.length), n = 0; n < t.length; ++n)
    t[n] = e[n].listener || e[n];
  return t;
}
function f$(e, t) {
  return new Promise(function(n, r) {
    function i(o) {
      e.removeListener(t, a), r(o);
    }
    function a() {
      typeof e.removeListener == "function" && e.removeListener("error", i), n([].slice.call(arguments));
    }
    AE(e, t, a, { once: !0 }), t !== "error" && c$(e, i, { once: !0 });
  });
}
function c$(e, t, n) {
  typeof e.on == "function" && AE(e, "error", t, n);
}
function AE(e, t, n, r) {
  if (typeof e.on == "function")
    r.once ? e.once(t, n) : e.on(t, n);
  else if (typeof e.addEventListener == "function")
    e.addEventListener(t, function i(a) {
      r.once && e.removeEventListener(t, i), n(a);
    });
  else
    throw new TypeError('The "emitter" argument must be of type EventEmitter. Received type ' + typeof e);
}
var ip = rp.exports;
const { ArrayIsArray: d$, ObjectSetPrototypeOf: RE } = Me, { EventEmitter: So } = ip;
function Ao(e) {
  So.call(this, e);
}
RE(Ao.prototype, So.prototype);
RE(Ao, So);
Ao.prototype.pipe = function(e, t) {
  const n = this;
  function r(f) {
    e.writable && e.write(f) === !1 && n.pause && n.pause();
  }
  n.on("data", r);
  function i() {
    n.readable && n.resume && n.resume();
  }
  e.on("drain", i), !e._isStdio && (!t || t.end !== !1) && (n.on("end", o), n.on("close", s));
  let a = !1;
  function o() {
    a || (a = !0, e.end());
  }
  function s() {
    a || (a = !0, typeof e.destroy == "function" && e.destroy());
  }
  function u(f) {
    l(), So.listenerCount(this, "error") === 0 && this.emit("error", f);
  }
  fh(n, "error", u), fh(e, "error", u);
  function l() {
    n.removeListener("data", r), e.removeListener("drain", i), n.removeListener("end", o), n.removeListener("close", s), n.removeListener("error", u), e.removeListener("error", u), n.removeListener("end", l), n.removeListener("close", l), e.removeListener("close", l);
  }
  return n.on("end", l), n.on("close", l), e.on("close", l), e.emit("pipe", n), e;
};
function fh(e, t, n) {
  if (typeof e.prependListener == "function")
    return e.prependListener(t, n);
  !e._events || !e._events[t] ? e.on(t, n) : d$(e._events[t]) ? e._events[t].unshift(n) : e._events[t] = [n, e._events[t]];
}
var ap = {
  Stream: Ao,
  prependListener: fh
}, IE = { exports: {} };
(function(e) {
  const { AbortError: t, codes: n } = at, r = Nr, { ERR_INVALID_ARG_TYPE: i } = n, a = (s, u) => {
    if (typeof s != "object" || !("aborted" in s))
      throw new i(u, "AbortSignal", s);
  };
  function o(s) {
    return !!(s && typeof s.pipe == "function");
  }
  e.exports.addAbortSignal = function(u, l) {
    if (a(u, "signal"), !o(l))
      throw new i("stream", "stream.Stream", l);
    return e.exports.addAbortSignalNoValidate(u, l);
  }, e.exports.addAbortSignalNoValidate = function(s, u) {
    if (typeof s != "object" || !("aborted" in s))
      return u;
    const l = () => {
      u.destroy(
        new t(void 0, {
          cause: s.reason
        })
      );
    };
    return s.aborted ? l() : (s.addEventListener("abort", l), r(u, () => s.removeEventListener("abort", l))), u;
  };
})(IE);
var op = IE.exports;
const { StringPrototypeSlice: ly, SymbolIterator: h$, TypedArrayPrototypeSet: da, Uint8Array: p$ } = Me, { Buffer: $s } = On, { inspect: g$ } = $n;
var y$ = class {
  constructor() {
    this.head = null, this.tail = null, this.length = 0;
  }
  push(t) {
    const n = {
      data: t,
      next: null
    };
    this.length > 0 ? this.tail.next = n : this.head = n, this.tail = n, ++this.length;
  }
  unshift(t) {
    const n = {
      data: t,
      next: this.head
    };
    this.length === 0 && (this.tail = n), this.head = n, ++this.length;
  }
  shift() {
    if (this.length === 0)
      return;
    const t = this.head.data;
    return this.length === 1 ? this.head = this.tail = null : this.head = this.head.next, --this.length, t;
  }
  clear() {
    this.head = this.tail = null, this.length = 0;
  }
  join(t) {
    if (this.length === 0)
      return "";
    let n = this.head, r = "" + n.data;
    for (; (n = n.next) !== null; )
      r += t + n.data;
    return r;
  }
  concat(t) {
    if (this.length === 0)
      return $s.alloc(0);
    const n = $s.allocUnsafe(t >>> 0);
    let r = this.head, i = 0;
    for (; r; )
      da(n, r.data, i), i += r.data.length, r = r.next;
    return n;
  }
  // Consumes a specified amount of bytes or characters from the buffered data.
  consume(t, n) {
    const r = this.head.data;
    if (t < r.length) {
      const i = r.slice(0, t);
      return this.head.data = r.slice(t), i;
    }
    return t === r.length ? this.shift() : n ? this._getString(t) : this._getBuffer(t);
  }
  first() {
    return this.head.data;
  }
  *[h$]() {
    for (let t = this.head; t; t = t.next)
      yield t.data;
  }
  // Consumes a specified amount of characters from the buffered data.
  _getString(t) {
    let n = "", r = this.head, i = 0;
    do {
      const a = r.data;
      if (t > a.length)
        n += a, t -= a.length;
      else {
        t === a.length ? (n += a, ++i, r.next ? this.head = r.next : this.head = this.tail = null) : (n += ly(a, 0, t), this.head = r, r.data = ly(a, t));
        break;
      }
      ++i;
    } while ((r = r.next) !== null);
    return this.length -= i, n;
  }
  // Consumes a specified amount of bytes from the buffered data.
  _getBuffer(t) {
    const n = $s.allocUnsafe(t), r = t;
    let i = this.head, a = 0;
    do {
      const o = i.data;
      if (t > o.length)
        da(n, o, r - t), t -= o.length;
      else {
        t === o.length ? (da(n, o, r - t), ++a, i.next ? this.head = i.next : this.head = this.tail = null) : (da(n, new p$(o.buffer, o.byteOffset, t), r - t), this.head = i, i.data = o.slice(t));
        break;
      }
      ++a;
    } while ((i = i.next) !== null);
    return this.length -= a, n;
  }
  // Make sure the linked list only shows the minimal necessary information.
  [Symbol.for("nodejs.util.inspect.custom")](t, n) {
    return g$(this, {
      ...n,
      // Only inspect one level.
      depth: 0,
      // It should not recurse.
      customInspect: !1
    });
  }
};
const { MathFloor: v$, NumberIsInteger: _$ } = Me, { ERR_INVALID_ARG_VALUE: b$ } = at.codes;
function m$(e, t, n) {
  return e.highWaterMark != null ? e.highWaterMark : t ? e[n] : null;
}
function TE(e) {
  return e ? 16 : 16 * 1024;
}
function w$(e, t, n, r) {
  const i = m$(t, r, n);
  if (i != null) {
    if (!_$(i) || i < 0) {
      const a = r ? `options.${n}` : "options.highWaterMark";
      throw new b$(a, i);
    }
    return v$(i);
  }
  return TE(e.objectMode);
}
var sp = {
  getHighWaterMark: w$,
  getDefaultHighWaterMark: TE
}, xE = {}, ch = { exports: {} };
/*! safe-buffer. MIT License. Feross Aboukhadijeh <https://feross.org/opensource> */
(function(e, t) {
  var n = On, r = n.Buffer;
  function i(o, s) {
    for (var u in o)
      s[u] = o[u];
  }
  r.from && r.alloc && r.allocUnsafe && r.allocUnsafeSlow ? e.exports = n : (i(n, t), t.Buffer = a);
  function a(o, s, u) {
    return r(o, s, u);
  }
  a.prototype = Object.create(r.prototype), i(r, a), a.from = function(o, s, u) {
    if (typeof o == "number")
      throw new TypeError("Argument must not be a number");
    return r(o, s, u);
  }, a.alloc = function(o, s, u) {
    if (typeof o != "number")
      throw new TypeError("Argument must be a number");
    var l = r(o);
    return s !== void 0 ? typeof u == "string" ? l.fill(s, u) : l.fill(s) : l.fill(0), l;
  }, a.allocUnsafe = function(o) {
    if (typeof o != "number")
      throw new TypeError("Argument must be a number");
    return r(o);
  }, a.allocUnsafeSlow = function(o) {
    if (typeof o != "number")
      throw new TypeError("Argument must be a number");
    return n.SlowBuffer(o);
  };
})(ch, ch.exports);
var E$ = ch.exports, up = E$.Buffer, fy = up.isEncoding || function(e) {
  switch (e = "" + e, e && e.toLowerCase()) {
    case "hex":
    case "utf8":
    case "utf-8":
    case "ascii":
    case "binary":
    case "base64":
    case "ucs2":
    case "ucs-2":
    case "utf16le":
    case "utf-16le":
    case "raw":
      return !0;
    default:
      return !1;
  }
};
function S$(e) {
  if (!e)
    return "utf8";
  for (var t; ; )
    switch (e) {
      case "utf8":
      case "utf-8":
        return "utf8";
      case "ucs2":
      case "ucs-2":
      case "utf16le":
      case "utf-16le":
        return "utf16le";
      case "latin1":
      case "binary":
        return "latin1";
      case "base64":
      case "ascii":
      case "hex":
        return e;
      default:
        if (t)
          return;
        e = ("" + e).toLowerCase(), t = !0;
    }
}
function A$(e) {
  var t = S$(e);
  if (typeof t != "string" && (up.isEncoding === fy || !fy(e)))
    throw new Error("Unknown encoding: " + e);
  return t || e;
}
xE.StringDecoder = Zi;
function Zi(e) {
  this.encoding = A$(e);
  var t;
  switch (this.encoding) {
    case "utf16le":
      this.text = O$, this.end = $$, t = 4;
      break;
    case "utf8":
      this.fillLast = T$, t = 4;
      break;
    case "base64":
      this.text = L$, this.end = M$, t = 3;
      break;
    default:
      this.write = C$, this.end = D$;
      return;
  }
  this.lastNeed = 0, this.lastTotal = 0, this.lastChar = up.allocUnsafe(t);
}
Zi.prototype.write = function(e) {
  if (e.length === 0)
    return "";
  var t, n;
  if (this.lastNeed) {
    if (t = this.fillLast(e), t === void 0)
      return "";
    n = this.lastNeed, this.lastNeed = 0;
  } else
    n = 0;
  return n < e.length ? t ? t + this.text(e, n) : this.text(e, n) : t || "";
};
Zi.prototype.end = N$;
Zi.prototype.text = x$;
Zi.prototype.fillLast = function(e) {
  if (this.lastNeed <= e.length)
    return e.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, this.lastNeed), this.lastChar.toString(this.encoding, 0, this.lastTotal);
  e.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, e.length), this.lastNeed -= e.length;
};
function Ls(e) {
  return e <= 127 ? 0 : e >> 5 === 6 ? 2 : e >> 4 === 14 ? 3 : e >> 3 === 30 ? 4 : e >> 6 === 2 ? -1 : -2;
}
function R$(e, t, n) {
  var r = t.length - 1;
  if (r < n)
    return 0;
  var i = Ls(t[r]);
  return i >= 0 ? (i > 0 && (e.lastNeed = i - 1), i) : --r < n || i === -2 ? 0 : (i = Ls(t[r]), i >= 0 ? (i > 0 && (e.lastNeed = i - 2), i) : --r < n || i === -2 ? 0 : (i = Ls(t[r]), i >= 0 ? (i > 0 && (i === 2 ? i = 0 : e.lastNeed = i - 3), i) : 0));
}
function I$(e, t, n) {
  if ((t[0] & 192) !== 128)
    return e.lastNeed = 0, "�";
  if (e.lastNeed > 1 && t.length > 1) {
    if ((t[1] & 192) !== 128)
      return e.lastNeed = 1, "�";
    if (e.lastNeed > 2 && t.length > 2 && (t[2] & 192) !== 128)
      return e.lastNeed = 2, "�";
  }
}
function T$(e) {
  var t = this.lastTotal - this.lastNeed, n = I$(this, e);
  if (n !== void 0)
    return n;
  if (this.lastNeed <= e.length)
    return e.copy(this.lastChar, t, 0, this.lastNeed), this.lastChar.toString(this.encoding, 0, this.lastTotal);
  e.copy(this.lastChar, t, 0, e.length), this.lastNeed -= e.length;
}
function x$(e, t) {
  var n = R$(this, e, t);
  if (!this.lastNeed)
    return e.toString("utf8", t);
  this.lastTotal = n;
  var r = e.length - (n - this.lastNeed);
  return e.copy(this.lastChar, 0, r), e.toString("utf8", t, r);
}
function N$(e) {
  var t = e && e.length ? this.write(e) : "";
  return this.lastNeed ? t + "�" : t;
}
function O$(e, t) {
  if ((e.length - t) % 2 === 0) {
    var n = e.toString("utf16le", t);
    if (n) {
      var r = n.charCodeAt(n.length - 1);
      if (r >= 55296 && r <= 56319)
        return this.lastNeed = 2, this.lastTotal = 4, this.lastChar[0] = e[e.length - 2], this.lastChar[1] = e[e.length - 1], n.slice(0, -1);
    }
    return n;
  }
  return this.lastNeed = 1, this.lastTotal = 2, this.lastChar[0] = e[e.length - 1], e.toString("utf16le", t, e.length - 1);
}
function $$(e) {
  var t = e && e.length ? this.write(e) : "";
  if (this.lastNeed) {
    var n = this.lastTotal - this.lastNeed;
    return t + this.lastChar.toString("utf16le", 0, n);
  }
  return t;
}
function L$(e, t) {
  var n = (e.length - t) % 3;
  return n === 0 ? e.toString("base64", t) : (this.lastNeed = 3 - n, this.lastTotal = 3, n === 1 ? this.lastChar[0] = e[e.length - 1] : (this.lastChar[0] = e[e.length - 2], this.lastChar[1] = e[e.length - 1]), e.toString("base64", t, e.length - n));
}
function M$(e) {
  var t = e && e.length ? this.write(e) : "";
  return this.lastNeed ? t + this.lastChar.toString("base64", 0, 3 - this.lastNeed) : t;
}
function C$(e) {
  return e.toString(this.encoding);
}
function D$(e) {
  return e && e.length ? this.write(e) : "";
}
const cy = Ir, { PromisePrototypeThen: q$, SymbolAsyncIterator: dy, SymbolIterator: hy } = Me, { Buffer: j$ } = On, { ERR_INVALID_ARG_TYPE: P$, ERR_STREAM_NULL_VALUES: k$ } = at.codes;
function F$(e, t, n) {
  let r;
  if (typeof t == "string" || t instanceof j$)
    return new e({
      objectMode: !0,
      ...n,
      read() {
        this.push(t), this.push(null);
      }
    });
  let i;
  if (t && t[dy])
    i = !0, r = t[dy]();
  else if (t && t[hy])
    i = !1, r = t[hy]();
  else
    throw new P$("iterable", ["Iterable"], t);
  const a = new e({
    objectMode: !0,
    highWaterMark: 1,
    // TODO(ronag): What options should be allowed?
    ...n
  });
  let o = !1;
  a._read = function() {
    o || (o = !0, u());
  }, a._destroy = function(l, f) {
    q$(
      s(l),
      () => cy.nextTick(f, l),
      // nextTick is here in case cb throws
      (c) => cy.nextTick(f, c || l)
    );
  };
  async function s(l) {
    const f = l != null, c = typeof r.throw == "function";
    if (f && c) {
      const { value: p, done: d } = await r.throw(l);
      if (await p, d)
        return;
    }
    if (typeof r.return == "function") {
      const { value: p } = await r.return();
      await p;
    }
  }
  async function u() {
    for (; ; ) {
      try {
        const { value: l, done: f } = i ? await r.next() : r.next();
        if (f)
          a.push(null);
        else {
          const c = l && typeof l.then == "function" ? await l : l;
          if (c === null)
            throw o = !1, new k$();
          if (a.push(c))
            continue;
          o = !1;
        }
      } catch (l) {
        a.destroy(l);
      }
      break;
    }
  }
  return a;
}
var NE = F$, Ms, py;
function Ro() {
  if (py)
    return Ms;
  py = 1;
  const e = Ir, {
    ArrayPrototypeIndexOf: t,
    NumberIsInteger: n,
    NumberIsNaN: r,
    NumberParseInt: i,
    ObjectDefineProperties: a,
    ObjectKeys: o,
    ObjectSetPrototypeOf: s,
    Promise: u,
    SafeSet: l,
    SymbolAsyncIterator: f,
    Symbol: c
  } = Me;
  Ms = B, B.ReadableState = ke;
  const { EventEmitter: p } = ip, { Stream: d, prependListener: y } = ap, { Buffer: v } = On, { addAbortSignal: _ } = op, m = Nr;
  let E = $n.debuglog("stream", (R) => {
    E = R;
  });
  const L = y$, I = si, { getHighWaterMark: j, getDefaultHighWaterMark: M } = sp, {
    aggregateTwoErrors: O,
    codes: {
      ERR_INVALID_ARG_TYPE: $,
      ERR_METHOD_NOT_IMPLEMENTED: q,
      ERR_OUT_OF_RANGE: N,
      ERR_STREAM_PUSH_AFTER_EOF: P,
      ERR_STREAM_UNSHIFT_AFTER_END_EVENT: H
    }
  } = at, { validateObject: G } = yo, z = c("kPaused"), { StringDecoder: W } = xE, se = NE;
  s(B.prototype, d.prototype), s(B, d);
  const ge = () => {
  }, { errorOrDestroy: re } = I;
  function ke(R, T, b) {
    typeof b != "boolean" && (b = T instanceof Rn()), this.objectMode = !!(R && R.objectMode), b && (this.objectMode = this.objectMode || !!(R && R.readableObjectMode)), this.highWaterMark = R ? j(this, R, "readableHighWaterMark", b) : M(!1), this.buffer = new L(), this.length = 0, this.pipes = [], this.flowing = null, this.ended = !1, this.endEmitted = !1, this.reading = !1, this.constructed = !0, this.sync = !0, this.needReadable = !1, this.emittedReadable = !1, this.readableListening = !1, this.resumeScheduled = !1, this[z] = null, this.errorEmitted = !1, this.emitClose = !R || R.emitClose !== !1, this.autoDestroy = !R || R.autoDestroy !== !1, this.destroyed = !1, this.errored = null, this.closed = !1, this.closeEmitted = !1, this.defaultEncoding = R && R.defaultEncoding || "utf8", this.awaitDrainWriters = null, this.multiAwaitDrain = !1, this.readingMore = !1, this.dataEmitted = !1, this.decoder = null, this.encoding = null, R && R.encoding && (this.decoder = new W(R.encoding), this.encoding = R.encoding);
  }
  function B(R) {
    if (!(this instanceof B))
      return new B(R);
    const T = this instanceof Rn();
    this._readableState = new ke(R, this, T), R && (typeof R.read == "function" && (this._read = R.read), typeof R.destroy == "function" && (this._destroy = R.destroy), typeof R.construct == "function" && (this._construct = R.construct), R.signal && !T && _(R.signal, this)), d.call(this, R), I.construct(this, () => {
      this._readableState.needReadable && He(this, this._readableState);
    });
  }
  B.prototype.destroy = I.destroy, B.prototype._undestroy = I.undestroy, B.prototype._destroy = function(R, T) {
    T(R);
  }, B.prototype[p.captureRejectionSymbol] = function(R) {
    this.destroy(R);
  }, B.prototype.push = function(R, T) {
    return te(this, R, T, !1);
  }, B.prototype.unshift = function(R, T) {
    return te(this, R, T, !0);
  };
  function te(R, T, b, h) {
    E("readableAddChunk", T);
    const g = R._readableState;
    let A;
    if (g.objectMode || (typeof T == "string" ? (b = b || g.defaultEncoding, g.encoding !== b && (h && g.encoding ? T = v.from(T, b).toString(g.encoding) : (T = v.from(T, b), b = ""))) : T instanceof v ? b = "" : d._isUint8Array(T) ? (T = d._uint8ArrayToBuffer(T), b = "") : T != null && (A = new $("chunk", ["string", "Buffer", "Uint8Array"], T))), A)
      re(R, A);
    else if (T === null)
      g.reading = !1, Gt(R, g);
    else if (g.objectMode || T && T.length > 0)
      if (h)
        if (g.endEmitted)
          re(R, new H());
        else {
          if (g.destroyed || g.errored)
            return !1;
          ye(R, g, T, !0);
        }
      else if (g.ended)
        re(R, new P());
      else {
        if (g.destroyed || g.errored)
          return !1;
        g.reading = !1, g.decoder && !b ? (T = g.decoder.write(T), g.objectMode || T.length !== 0 ? ye(R, g, T, !1) : He(R, g)) : ye(R, g, T, !1);
      }
    else
      h || (g.reading = !1, He(R, g));
    return !g.ended && (g.length < g.highWaterMark || g.length === 0);
  }
  function ye(R, T, b, h) {
    T.flowing && T.length === 0 && !T.sync && R.listenerCount("data") > 0 ? (T.multiAwaitDrain ? T.awaitDrainWriters.clear() : T.awaitDrainWriters = null, T.dataEmitted = !0, R.emit("data", b)) : (T.length += T.objectMode ? 1 : b.length, h ? T.buffer.unshift(b) : T.buffer.push(b), T.needReadable && st(R)), He(R, T);
  }
  B.prototype.isPaused = function() {
    const R = this._readableState;
    return R[z] === !0 || R.flowing === !1;
  }, B.prototype.setEncoding = function(R) {
    const T = new W(R);
    this._readableState.decoder = T, this._readableState.encoding = this._readableState.decoder.encoding;
    const b = this._readableState.buffer;
    let h = "";
    for (const g of b)
      h += T.write(g);
    return b.clear(), h !== "" && b.push(h), this._readableState.length = h.length, this;
  };
  const gt = 1073741824;
  function yt(R) {
    if (R > gt)
      throw new N("size", "<= 1GiB", R);
    return R--, R |= R >>> 1, R |= R >>> 2, R |= R >>> 4, R |= R >>> 8, R |= R >>> 16, R++, R;
  }
  function Ze(R, T) {
    return R <= 0 || T.length === 0 && T.ended ? 0 : T.objectMode ? 1 : r(R) ? T.flowing && T.length ? T.buffer.first().length : T.length : R <= T.length ? R : T.ended ? T.length : 0;
  }
  B.prototype.read = function(R) {
    E("read", R), R === void 0 ? R = NaN : n(R) || (R = i(R, 10));
    const T = this._readableState, b = R;
    if (R > T.highWaterMark && (T.highWaterMark = yt(R)), R !== 0 && (T.emittedReadable = !1), R === 0 && T.needReadable && ((T.highWaterMark !== 0 ? T.length >= T.highWaterMark : T.length > 0) || T.ended))
      return E("read: emitReadable", T.length, T.ended), T.length === 0 && T.ended ? Ce(this) : st(this), null;
    if (R = Ze(R, T), R === 0 && T.ended)
      return T.length === 0 && Ce(this), null;
    let h = T.needReadable;
    if (E("need readable", h), (T.length === 0 || T.length - R < T.highWaterMark) && (h = !0, E("length less than watermark", h)), T.ended || T.reading || T.destroyed || T.errored || !T.constructed)
      h = !1, E("reading, ended or constructing", h);
    else if (h) {
      E("do read"), T.reading = !0, T.sync = !0, T.length === 0 && (T.needReadable = !0);
      try {
        this._read(T.highWaterMark);
      } catch (A) {
        re(this, A);
      }
      T.sync = !1, T.reading || (R = Ze(b, T));
    }
    let g;
    return R > 0 ? g = ie(R, T) : g = null, g === null ? (T.needReadable = T.length <= T.highWaterMark, R = 0) : (T.length -= R, T.multiAwaitDrain ? T.awaitDrainWriters.clear() : T.awaitDrainWriters = null), T.length === 0 && (T.ended || (T.needReadable = !0), b !== R && T.ended && Ce(this)), g !== null && !T.errorEmitted && !T.closeEmitted && (T.dataEmitted = !0, this.emit("data", g)), g;
  };
  function Gt(R, T) {
    if (E("onEofChunk"), !T.ended) {
      if (T.decoder) {
        const b = T.decoder.end();
        b && b.length && (T.buffer.push(b), T.length += T.objectMode ? 1 : b.length);
      }
      T.ended = !0, T.sync ? st(R) : (T.needReadable = !1, T.emittedReadable = !0, Fe(R));
    }
  }
  function st(R) {
    const T = R._readableState;
    E("emitReadable", T.needReadable, T.emittedReadable), T.needReadable = !1, T.emittedReadable || (E("emitReadable", T.flowing), T.emittedReadable = !0, e.nextTick(Fe, R));
  }
  function Fe(R) {
    const T = R._readableState;
    E("emitReadable_", T.destroyed, T.length, T.ended), !T.destroyed && !T.errored && (T.length || T.ended) && (R.emit("readable"), T.emittedReadable = !1), T.needReadable = !T.flowing && !T.ended && T.length <= T.highWaterMark, V(R);
  }
  function He(R, T) {
    !T.readingMore && T.constructed && (T.readingMore = !0, e.nextTick(ut, R, T));
  }
  function ut(R, T) {
    for (; !T.reading && !T.ended && (T.length < T.highWaterMark || T.flowing && T.length === 0); ) {
      const b = T.length;
      if (E("maybeReadMore read 0"), R.read(0), b === T.length)
        break;
    }
    T.readingMore = !1;
  }
  B.prototype._read = function(R) {
    throw new q("_read()");
  }, B.prototype.pipe = function(R, T) {
    const b = this, h = this._readableState;
    h.pipes.length === 1 && (h.multiAwaitDrain || (h.multiAwaitDrain = !0, h.awaitDrainWriters = new l(h.awaitDrainWriters ? [h.awaitDrainWriters] : []))), h.pipes.push(R), E("pipe count=%d opts=%j", h.pipes.length, T);
    const A = (!T || T.end !== !1) && R !== e.stdout && R !== e.stderr ? S : pi;
    h.endEmitted ? e.nextTick(A) : b.once("end", A), R.on("unpipe", w);
    function w(er, cn) {
      E("onunpipe"), er === b && cn && cn.hasUnpiped === !1 && (cn.hasUnpiped = !0, Q());
    }
    function S() {
      E("onend"), R.end();
    }
    let x, U = !1;
    function Q() {
      E("cleanup"), R.removeListener("close", $t), R.removeListener("finish", Vt), x && R.removeListener("drain", x), R.removeListener("error", J), R.removeListener("unpipe", w), b.removeListener("end", S), b.removeListener("end", pi), b.removeListener("data", be), U = !0, x && h.awaitDrainWriters && (!R._writableState || R._writableState.needDrain) && x();
    }
    function oe() {
      U || (h.pipes.length === 1 && h.pipes[0] === R ? (E("false write response, pause", 0), h.awaitDrainWriters = R, h.multiAwaitDrain = !1) : h.pipes.length > 1 && h.pipes.includes(R) && (E("false write response, pause", h.awaitDrainWriters.size), h.awaitDrainWriters.add(R)), b.pause()), x || (x = vt(b, R), R.on("drain", x));
    }
    b.on("data", be);
    function be(er) {
      E("ondata");
      const cn = R.write(er);
      E("dest.write", cn), cn === !1 && oe();
    }
    function J(er) {
      if (E("onerror", er), pi(), R.removeListener("error", J), R.listenerCount("error") === 0) {
        const cn = R._writableState || R._readableState;
        cn && !cn.errorEmitted ? re(R, er) : R.emit("error", er);
      }
    }
    y(R, "error", J);
    function $t() {
      R.removeListener("finish", Vt), pi();
    }
    R.once("close", $t);
    function Vt() {
      E("onfinish"), R.removeListener("close", $t), pi();
    }
    R.once("finish", Vt);
    function pi() {
      E("unpipe"), b.unpipe(R);
    }
    return R.emit("pipe", b), R.writableNeedDrain === !0 ? h.flowing && oe() : h.flowing || (E("pipe resume"), b.resume()), R;
  };
  function vt(R, T) {
    return function() {
      const h = R._readableState;
      h.awaitDrainWriters === T ? (E("pipeOnDrain", 1), h.awaitDrainWriters = null) : h.multiAwaitDrain && (E("pipeOnDrain", h.awaitDrainWriters.size), h.awaitDrainWriters.delete(T)), (!h.awaitDrainWriters || h.awaitDrainWriters.size === 0) && R.listenerCount("data") && R.resume();
    };
  }
  B.prototype.unpipe = function(R) {
    const T = this._readableState, b = {
      hasUnpiped: !1
    };
    if (T.pipes.length === 0)
      return this;
    if (!R) {
      const g = T.pipes;
      T.pipes = [], this.pause();
      for (let A = 0; A < g.length; A++)
        g[A].emit("unpipe", this, {
          hasUnpiped: !1
        });
      return this;
    }
    const h = t(T.pipes, R);
    return h === -1 ? this : (T.pipes.splice(h, 1), T.pipes.length === 0 && this.pause(), R.emit("unpipe", this, b), this);
  }, B.prototype.on = function(R, T) {
    const b = d.prototype.on.call(this, R, T), h = this._readableState;
    return R === "data" ? (h.readableListening = this.listenerCount("readable") > 0, h.flowing !== !1 && this.resume()) : R === "readable" && !h.endEmitted && !h.readableListening && (h.readableListening = h.needReadable = !0, h.flowing = !1, h.emittedReadable = !1, E("on readable", h.length, h.reading), h.length ? st(this) : h.reading || e.nextTick(C, this)), b;
  }, B.prototype.addListener = B.prototype.on, B.prototype.removeListener = function(R, T) {
    const b = d.prototype.removeListener.call(this, R, T);
    return R === "readable" && e.nextTick(_t, this), b;
  }, B.prototype.off = B.prototype.removeListener, B.prototype.removeAllListeners = function(R) {
    const T = d.prototype.removeAllListeners.apply(this, arguments);
    return (R === "readable" || R === void 0) && e.nextTick(_t, this), T;
  };
  function _t(R) {
    const T = R._readableState;
    T.readableListening = R.listenerCount("readable") > 0, T.resumeScheduled && T[z] === !1 ? T.flowing = !0 : R.listenerCount("data") > 0 ? R.resume() : T.readableListening || (T.flowing = null);
  }
  function C(R) {
    E("readable nexttick read 0"), R.read(0);
  }
  B.prototype.resume = function() {
    const R = this._readableState;
    return R.flowing || (E("resume"), R.flowing = !R.readableListening, D(this, R)), R[z] = !1, this;
  };
  function D(R, T) {
    T.resumeScheduled || (T.resumeScheduled = !0, e.nextTick(k, R, T));
  }
  function k(R, T) {
    E("resume", T.reading), T.reading || R.read(0), T.resumeScheduled = !1, R.emit("resume"), V(R), T.flowing && !T.reading && R.read(0);
  }
  B.prototype.pause = function() {
    return E("call pause flowing=%j", this._readableState.flowing), this._readableState.flowing !== !1 && (E("pause"), this._readableState.flowing = !1, this.emit("pause")), this._readableState[z] = !0, this;
  };
  function V(R) {
    const T = R._readableState;
    for (E("flow", T.flowing); T.flowing && R.read() !== null; )
      ;
  }
  B.prototype.wrap = function(R) {
    let T = !1;
    R.on("data", (h) => {
      !this.push(h) && R.pause && (T = !0, R.pause());
    }), R.on("end", () => {
      this.push(null);
    }), R.on("error", (h) => {
      re(this, h);
    }), R.on("close", () => {
      this.destroy();
    }), R.on("destroy", () => {
      this.destroy();
    }), this._read = () => {
      T && R.resume && (T = !1, R.resume());
    };
    const b = o(R);
    for (let h = 1; h < b.length; h++) {
      const g = b[h];
      this[g] === void 0 && typeof R[g] == "function" && (this[g] = R[g].bind(R));
    }
    return this;
  }, B.prototype[f] = function() {
    return F(this);
  }, B.prototype.iterator = function(R) {
    return R !== void 0 && G(R, "options"), F(this, R);
  };
  function F(R, T) {
    typeof R.read != "function" && (R = B.wrap(R, {
      objectMode: !0
    }));
    const b = ne(R, T);
    return b.stream = R, b;
  }
  async function* ne(R, T) {
    let b = ge;
    function h(w) {
      this === R ? (b(), b = ge) : b = w;
    }
    R.on("readable", h);
    let g;
    const A = m(
      R,
      {
        writable: !1
      },
      (w) => {
        g = w ? O(g, w) : null, b(), b = ge;
      }
    );
    try {
      for (; ; ) {
        const w = R.destroyed ? null : R.read();
        if (w !== null)
          yield w;
        else {
          if (g)
            throw g;
          if (g === null)
            return;
          await new u(h);
        }
      }
    } catch (w) {
      throw g = O(g, w), g;
    } finally {
      (g || (T == null ? void 0 : T.destroyOnReturn) !== !1) && (g === void 0 || R._readableState.autoDestroy) ? I.destroyer(R, null) : (R.off("readable", h), A());
    }
  }
  a(B.prototype, {
    readable: {
      __proto__: null,
      get() {
        const R = this._readableState;
        return !!R && R.readable !== !1 && !R.destroyed && !R.errorEmitted && !R.endEmitted;
      },
      set(R) {
        this._readableState && (this._readableState.readable = !!R);
      }
    },
    readableDidRead: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return this._readableState.dataEmitted;
      }
    },
    readableAborted: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return !!(this._readableState.readable !== !1 && (this._readableState.destroyed || this._readableState.errored) && !this._readableState.endEmitted);
      }
    },
    readableHighWaterMark: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return this._readableState.highWaterMark;
      }
    },
    readableBuffer: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return this._readableState && this._readableState.buffer;
      }
    },
    readableFlowing: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return this._readableState.flowing;
      },
      set: function(R) {
        this._readableState && (this._readableState.flowing = R);
      }
    },
    readableLength: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState.length;
      }
    },
    readableObjectMode: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState ? this._readableState.objectMode : !1;
      }
    },
    readableEncoding: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState ? this._readableState.encoding : null;
      }
    },
    errored: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState ? this._readableState.errored : null;
      }
    },
    closed: {
      __proto__: null,
      get() {
        return this._readableState ? this._readableState.closed : !1;
      }
    },
    destroyed: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState ? this._readableState.destroyed : !1;
      },
      set(R) {
        this._readableState && (this._readableState.destroyed = R);
      }
    },
    readableEnded: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._readableState ? this._readableState.endEmitted : !1;
      }
    }
  }), a(ke.prototype, {
    // Legacy getter for `pipesCount`.
    pipesCount: {
      __proto__: null,
      get() {
        return this.pipes.length;
      }
    },
    // Legacy property for `paused`.
    paused: {
      __proto__: null,
      get() {
        return this[z] !== !1;
      },
      set(R) {
        this[z] = !!R;
      }
    }
  }), B._fromList = ie;
  function ie(R, T) {
    if (T.length === 0)
      return null;
    let b;
    return T.objectMode ? b = T.buffer.shift() : !R || R >= T.length ? (T.decoder ? b = T.buffer.join("") : T.buffer.length === 1 ? b = T.buffer.first() : b = T.buffer.concat(T.length), T.buffer.clear()) : b = T.buffer.consume(R, T.decoder), b;
  }
  function Ce(R) {
    const T = R._readableState;
    E("endReadable", T.endEmitted), T.endEmitted || (T.ended = !0, e.nextTick(De, T, R));
  }
  function De(R, T) {
    if (E("endReadableNT", R.endEmitted, R.length), !R.errored && !R.closeEmitted && !R.endEmitted && R.length === 0) {
      if (R.endEmitted = !0, T.emit("end"), T.writable && T.allowHalfOpen === !1)
        e.nextTick(_e, T);
      else if (R.autoDestroy) {
        const b = T._writableState;
        (!b || b.autoDestroy && // We don't expect the writable to ever 'finish'
        // if writable is explicitly set to false.
        (b.finished || b.writable === !1)) && T.destroy();
      }
    }
  }
  function _e(R) {
    R.writable && !R.writableEnded && !R.destroyed && R.end();
  }
  B.from = function(R, T) {
    return se(B, R, T);
  };
  let de;
  function Ot() {
    return de === void 0 && (de = {}), de;
  }
  return B.fromWeb = function(R, T) {
    return Ot().newStreamReadableFromReadableStream(R, T);
  }, B.toWeb = function(R, T) {
    return Ot().newReadableStreamFromStreamReadable(R, T);
  }, B.wrap = function(R, T) {
    var b, h;
    return new B({
      objectMode: (b = (h = R.readableObjectMode) !== null && h !== void 0 ? h : R.objectMode) !== null && b !== void 0 ? b : !0,
      ...T,
      destroy(g, A) {
        I.destroyer(R, g), A(g);
      }
    }).wrap(R);
  }, Ms;
}
var Cs, gy;
function OE() {
  if (gy)
    return Cs;
  gy = 1;
  const e = Ir, {
    ArrayPrototypeSlice: t,
    Error: n,
    FunctionPrototypeSymbolHasInstance: r,
    ObjectDefineProperty: i,
    ObjectDefineProperties: a,
    ObjectSetPrototypeOf: o,
    StringPrototypeToLowerCase: s,
    Symbol: u,
    SymbolHasInstance: l
  } = Me;
  Cs = W, W.WritableState = G;
  const { EventEmitter: f } = ip, c = ap.Stream, { Buffer: p } = On, d = si, { addAbortSignal: y } = op, { getHighWaterMark: v, getDefaultHighWaterMark: _ } = sp, {
    ERR_INVALID_ARG_TYPE: m,
    ERR_METHOD_NOT_IMPLEMENTED: E,
    ERR_MULTIPLE_CALLBACK: L,
    ERR_STREAM_CANNOT_PIPE: I,
    ERR_STREAM_DESTROYED: j,
    ERR_STREAM_ALREADY_FINISHED: M,
    ERR_STREAM_NULL_VALUES: O,
    ERR_STREAM_WRITE_AFTER_END: $,
    ERR_UNKNOWN_ENCODING: q
  } = at.codes, { errorOrDestroy: N } = d;
  o(W.prototype, c.prototype), o(W, c);
  function P() {
  }
  const H = u("kOnFinished");
  function G(C, D, k) {
    typeof k != "boolean" && (k = D instanceof Rn()), this.objectMode = !!(C && C.objectMode), k && (this.objectMode = this.objectMode || !!(C && C.writableObjectMode)), this.highWaterMark = C ? v(this, C, "writableHighWaterMark", k) : _(!1), this.finalCalled = !1, this.needDrain = !1, this.ending = !1, this.ended = !1, this.finished = !1, this.destroyed = !1;
    const V = !!(C && C.decodeStrings === !1);
    this.decodeStrings = !V, this.defaultEncoding = C && C.defaultEncoding || "utf8", this.length = 0, this.writing = !1, this.corked = 0, this.sync = !0, this.bufferProcessing = !1, this.onwrite = B.bind(void 0, D), this.writecb = null, this.writelen = 0, this.afterWriteTickInfo = null, z(this), this.pendingcb = 0, this.constructed = !0, this.prefinished = !1, this.errorEmitted = !1, this.emitClose = !C || C.emitClose !== !1, this.autoDestroy = !C || C.autoDestroy !== !1, this.errored = null, this.closed = !1, this.closeEmitted = !1, this[H] = [];
  }
  function z(C) {
    C.buffered = [], C.bufferedIndex = 0, C.allBuffers = !0, C.allNoop = !0;
  }
  G.prototype.getBuffer = function() {
    return t(this.buffered, this.bufferedIndex);
  }, i(G.prototype, "bufferedRequestCount", {
    __proto__: null,
    get() {
      return this.buffered.length - this.bufferedIndex;
    }
  });
  function W(C) {
    const D = this instanceof Rn();
    if (!D && !r(W, this))
      return new W(C);
    this._writableState = new G(C, this, D), C && (typeof C.write == "function" && (this._write = C.write), typeof C.writev == "function" && (this._writev = C.writev), typeof C.destroy == "function" && (this._destroy = C.destroy), typeof C.final == "function" && (this._final = C.final), typeof C.construct == "function" && (this._construct = C.construct), C.signal && y(C.signal, this)), c.call(this, C), d.construct(this, () => {
      const k = this._writableState;
      k.writing || yt(this, k), Fe(this, k);
    });
  }
  i(W, l, {
    __proto__: null,
    value: function(C) {
      return r(this, C) ? !0 : this !== W ? !1 : C && C._writableState instanceof G;
    }
  }), W.prototype.pipe = function() {
    N(this, new I());
  };
  function se(C, D, k, V) {
    const F = C._writableState;
    if (typeof k == "function")
      V = k, k = F.defaultEncoding;
    else {
      if (!k)
        k = F.defaultEncoding;
      else if (k !== "buffer" && !p.isEncoding(k))
        throw new q(k);
      typeof V != "function" && (V = P);
    }
    if (D === null)
      throw new O();
    if (!F.objectMode)
      if (typeof D == "string")
        F.decodeStrings !== !1 && (D = p.from(D, k), k = "buffer");
      else if (D instanceof p)
        k = "buffer";
      else if (c._isUint8Array(D))
        D = c._uint8ArrayToBuffer(D), k = "buffer";
      else
        throw new m("chunk", ["string", "Buffer", "Uint8Array"], D);
    let ne;
    return F.ending ? ne = new $() : F.destroyed && (ne = new j("write")), ne ? (e.nextTick(V, ne), N(C, ne, !0), ne) : (F.pendingcb++, ge(C, F, D, k, V));
  }
  W.prototype.write = function(C, D, k) {
    return se(this, C, D, k) === !0;
  }, W.prototype.cork = function() {
    this._writableState.corked++;
  }, W.prototype.uncork = function() {
    const C = this._writableState;
    C.corked && (C.corked--, C.writing || yt(this, C));
  }, W.prototype.setDefaultEncoding = function(D) {
    if (typeof D == "string" && (D = s(D)), !p.isEncoding(D))
      throw new q(D);
    return this._writableState.defaultEncoding = D, this;
  };
  function ge(C, D, k, V, F) {
    const ne = D.objectMode ? 1 : k.length;
    D.length += ne;
    const ie = D.length < D.highWaterMark;
    return ie || (D.needDrain = !0), D.writing || D.corked || D.errored || !D.constructed ? (D.buffered.push({
      chunk: k,
      encoding: V,
      callback: F
    }), D.allBuffers && V !== "buffer" && (D.allBuffers = !1), D.allNoop && F !== P && (D.allNoop = !1)) : (D.writelen = ne, D.writecb = F, D.writing = !0, D.sync = !0, C._write(k, V, D.onwrite), D.sync = !1), ie && !D.errored && !D.destroyed;
  }
  function re(C, D, k, V, F, ne, ie) {
    D.writelen = V, D.writecb = ie, D.writing = !0, D.sync = !0, D.destroyed ? D.onwrite(new j("write")) : k ? C._writev(F, D.onwrite) : C._write(F, ne, D.onwrite), D.sync = !1;
  }
  function ke(C, D, k, V) {
    --D.pendingcb, V(k), gt(D), N(C, k);
  }
  function B(C, D) {
    const k = C._writableState, V = k.sync, F = k.writecb;
    if (typeof F != "function") {
      N(C, new L());
      return;
    }
    k.writing = !1, k.writecb = null, k.length -= k.writelen, k.writelen = 0, D ? (D.stack, k.errored || (k.errored = D), C._readableState && !C._readableState.errored && (C._readableState.errored = D), V ? e.nextTick(ke, C, k, D, F) : ke(C, k, D, F)) : (k.buffered.length > k.bufferedIndex && yt(C, k), V ? k.afterWriteTickInfo !== null && k.afterWriteTickInfo.cb === F ? k.afterWriteTickInfo.count++ : (k.afterWriteTickInfo = {
      count: 1,
      cb: F,
      stream: C,
      state: k
    }, e.nextTick(te, k.afterWriteTickInfo)) : ye(C, k, 1, F));
  }
  function te({ stream: C, state: D, count: k, cb: V }) {
    return D.afterWriteTickInfo = null, ye(C, D, k, V);
  }
  function ye(C, D, k, V) {
    for (!D.ending && !C.destroyed && D.length === 0 && D.needDrain && (D.needDrain = !1, C.emit("drain")); k-- > 0; )
      D.pendingcb--, V();
    D.destroyed && gt(D), Fe(C, D);
  }
  function gt(C) {
    if (C.writing)
      return;
    for (let F = C.bufferedIndex; F < C.buffered.length; ++F) {
      var D;
      const { chunk: ne, callback: ie } = C.buffered[F], Ce = C.objectMode ? 1 : ne.length;
      C.length -= Ce, ie(
        (D = C.errored) !== null && D !== void 0 ? D : new j("write")
      );
    }
    const k = C[H].splice(0);
    for (let F = 0; F < k.length; F++) {
      var V;
      k[F](
        (V = C.errored) !== null && V !== void 0 ? V : new j("end")
      );
    }
    z(C);
  }
  function yt(C, D) {
    if (D.corked || D.bufferProcessing || D.destroyed || !D.constructed)
      return;
    const { buffered: k, bufferedIndex: V, objectMode: F } = D, ne = k.length - V;
    if (!ne)
      return;
    let ie = V;
    if (D.bufferProcessing = !0, ne > 1 && C._writev) {
      D.pendingcb -= ne - 1;
      const Ce = D.allNoop ? P : (_e) => {
        for (let de = ie; de < k.length; ++de)
          k[de].callback(_e);
      }, De = D.allNoop && ie === 0 ? k : t(k, ie);
      De.allBuffers = D.allBuffers, re(C, D, !0, D.length, De, "", Ce), z(D);
    } else {
      do {
        const { chunk: Ce, encoding: De, callback: _e } = k[ie];
        k[ie++] = null;
        const de = F ? 1 : Ce.length;
        re(C, D, !1, de, Ce, De, _e);
      } while (ie < k.length && !D.writing);
      ie === k.length ? z(D) : ie > 256 ? (k.splice(0, ie), D.bufferedIndex = 0) : D.bufferedIndex = ie;
    }
    D.bufferProcessing = !1;
  }
  W.prototype._write = function(C, D, k) {
    if (this._writev)
      this._writev(
        [
          {
            chunk: C,
            encoding: D
          }
        ],
        k
      );
    else
      throw new E("_write()");
  }, W.prototype._writev = null, W.prototype.end = function(C, D, k) {
    const V = this._writableState;
    typeof C == "function" ? (k = C, C = null, D = null) : typeof D == "function" && (k = D, D = null);
    let F;
    if (C != null) {
      const ne = se(this, C, D);
      ne instanceof n && (F = ne);
    }
    return V.corked && (V.corked = 1, this.uncork()), F || (!V.errored && !V.ending ? (V.ending = !0, Fe(this, V, !0), V.ended = !0) : V.finished ? F = new M("end") : V.destroyed && (F = new j("end"))), typeof k == "function" && (F || V.finished ? e.nextTick(k, F) : V[H].push(k)), this;
  };
  function Ze(C) {
    return C.ending && !C.destroyed && C.constructed && C.length === 0 && !C.errored && C.buffered.length === 0 && !C.finished && !C.writing && !C.errorEmitted && !C.closeEmitted;
  }
  function Gt(C, D) {
    let k = !1;
    function V(F) {
      if (k) {
        N(C, F ?? L());
        return;
      }
      if (k = !0, D.pendingcb--, F) {
        const ne = D[H].splice(0);
        for (let ie = 0; ie < ne.length; ie++)
          ne[ie](F);
        N(C, F, D.sync);
      } else
        Ze(D) && (D.prefinished = !0, C.emit("prefinish"), D.pendingcb++, e.nextTick(He, C, D));
    }
    D.sync = !0, D.pendingcb++;
    try {
      C._final(V);
    } catch (F) {
      V(F);
    }
    D.sync = !1;
  }
  function st(C, D) {
    !D.prefinished && !D.finalCalled && (typeof C._final == "function" && !D.destroyed ? (D.finalCalled = !0, Gt(C, D)) : (D.prefinished = !0, C.emit("prefinish")));
  }
  function Fe(C, D, k) {
    Ze(D) && (st(C, D), D.pendingcb === 0 && (k ? (D.pendingcb++, e.nextTick(
      (V, F) => {
        Ze(F) ? He(V, F) : F.pendingcb--;
      },
      C,
      D
    )) : Ze(D) && (D.pendingcb++, He(C, D))));
  }
  function He(C, D) {
    D.pendingcb--, D.finished = !0;
    const k = D[H].splice(0);
    for (let V = 0; V < k.length; V++)
      k[V]();
    if (C.emit("finish"), D.autoDestroy) {
      const V = C._readableState;
      (!V || V.autoDestroy && // We don't expect the readable to ever 'end'
      // if readable is explicitly set to false.
      (V.endEmitted || V.readable === !1)) && C.destroy();
    }
  }
  a(W.prototype, {
    closed: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.closed : !1;
      }
    },
    destroyed: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.destroyed : !1;
      },
      set(C) {
        this._writableState && (this._writableState.destroyed = C);
      }
    },
    writable: {
      __proto__: null,
      get() {
        const C = this._writableState;
        return !!C && C.writable !== !1 && !C.destroyed && !C.errored && !C.ending && !C.ended;
      },
      set(C) {
        this._writableState && (this._writableState.writable = !!C);
      }
    },
    writableFinished: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.finished : !1;
      }
    },
    writableObjectMode: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.objectMode : !1;
      }
    },
    writableBuffer: {
      __proto__: null,
      get() {
        return this._writableState && this._writableState.getBuffer();
      }
    },
    writableEnded: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.ending : !1;
      }
    },
    writableNeedDrain: {
      __proto__: null,
      get() {
        const C = this._writableState;
        return C ? !C.destroyed && !C.ending && C.needDrain : !1;
      }
    },
    writableHighWaterMark: {
      __proto__: null,
      get() {
        return this._writableState && this._writableState.highWaterMark;
      }
    },
    writableCorked: {
      __proto__: null,
      get() {
        return this._writableState ? this._writableState.corked : 0;
      }
    },
    writableLength: {
      __proto__: null,
      get() {
        return this._writableState && this._writableState.length;
      }
    },
    errored: {
      __proto__: null,
      enumerable: !1,
      get() {
        return this._writableState ? this._writableState.errored : null;
      }
    },
    writableAborted: {
      __proto__: null,
      enumerable: !1,
      get: function() {
        return !!(this._writableState.writable !== !1 && (this._writableState.destroyed || this._writableState.errored) && !this._writableState.finished);
      }
    }
  });
  const ut = d.destroy;
  W.prototype.destroy = function(C, D) {
    const k = this._writableState;
    return !k.destroyed && (k.bufferedIndex < k.buffered.length || k[H].length) && e.nextTick(gt, k), ut.call(this, C, D), this;
  }, W.prototype._undestroy = d.undestroy, W.prototype._destroy = function(C, D) {
    D(C);
  }, W.prototype[f.captureRejectionSymbol] = function(C) {
    this.destroy(C);
  };
  let vt;
  function _t() {
    return vt === void 0 && (vt = {}), vt;
  }
  return W.fromWeb = function(C, D) {
    return _t().newStreamWritableFromWritableStream(C, D);
  }, W.toWeb = function(C) {
    return _t().newWritableStreamFromStreamWritable(C);
  }, Cs;
}
var Ds, yy;
function B$() {
  if (yy)
    return Ds;
  yy = 1;
  const e = Ir, t = On, {
    isReadable: n,
    isWritable: r,
    isIterable: i,
    isNodeStream: a,
    isReadableNodeStream: o,
    isWritableNodeStream: s,
    isDuplexNodeStream: u
  } = xr, l = Nr, {
    AbortError: f,
    codes: { ERR_INVALID_ARG_TYPE: c, ERR_INVALID_RETURN_VALUE: p }
  } = at, { destroyer: d } = si, y = Rn(), v = Ro(), { createDeferredPromise: _ } = $n, m = NE, E = globalThis.Blob || t.Blob, L = typeof E < "u" ? function(N) {
    return N instanceof E;
  } : function(N) {
    return !1;
  }, I = globalThis.AbortController || Zh().AbortController, { FunctionPrototypeCall: j } = Me;
  class M extends y {
    constructor(N) {
      super(N), (N == null ? void 0 : N.readable) === !1 && (this._readableState.readable = !1, this._readableState.ended = !0, this._readableState.endEmitted = !0), (N == null ? void 0 : N.writable) === !1 && (this._writableState.writable = !1, this._writableState.ending = !0, this._writableState.ended = !0, this._writableState.finished = !0);
    }
  }
  Ds = function q(N, P) {
    if (u(N))
      return N;
    if (o(N))
      return $({
        readable: N
      });
    if (s(N))
      return $({
        writable: N
      });
    if (a(N))
      return $({
        writable: !1,
        readable: !1
      });
    if (typeof N == "function") {
      const { value: G, write: z, final: W, destroy: se } = O(N);
      if (i(G))
        return m(M, G, {
          // TODO (ronag): highWaterMark?
          objectMode: !0,
          write: z,
          final: W,
          destroy: se
        });
      const ge = G == null ? void 0 : G.then;
      if (typeof ge == "function") {
        let re;
        const ke = j(
          ge,
          G,
          (B) => {
            if (B != null)
              throw new p("nully", "body", B);
          },
          (B) => {
            d(re, B);
          }
        );
        return re = new M({
          // TODO (ronag): highWaterMark?
          objectMode: !0,
          readable: !1,
          write: z,
          final(B) {
            W(async () => {
              try {
                await ke, e.nextTick(B, null);
              } catch (te) {
                e.nextTick(B, te);
              }
            });
          },
          destroy: se
        });
      }
      throw new p("Iterable, AsyncIterable or AsyncFunction", P, G);
    }
    if (L(N))
      return q(N.arrayBuffer());
    if (i(N))
      return m(M, N, {
        // TODO (ronag): highWaterMark?
        objectMode: !0,
        writable: !1
      });
    if (typeof (N == null ? void 0 : N.writable) == "object" || typeof (N == null ? void 0 : N.readable) == "object") {
      const G = N != null && N.readable ? o(N == null ? void 0 : N.readable) ? N == null ? void 0 : N.readable : q(N.readable) : void 0, z = N != null && N.writable ? s(N == null ? void 0 : N.writable) ? N == null ? void 0 : N.writable : q(N.writable) : void 0;
      return $({
        readable: G,
        writable: z
      });
    }
    const H = N == null ? void 0 : N.then;
    if (typeof H == "function") {
      let G;
      return j(
        H,
        N,
        (z) => {
          z != null && G.push(z), G.push(null);
        },
        (z) => {
          d(G, z);
        }
      ), G = new M({
        objectMode: !0,
        writable: !1,
        read() {
        }
      });
    }
    throw new c(
      P,
      [
        "Blob",
        "ReadableStream",
        "WritableStream",
        "Stream",
        "Iterable",
        "AsyncIterable",
        "Function",
        "{ readable, writable } pair",
        "Promise"
      ],
      N
    );
  };
  function O(q) {
    let { promise: N, resolve: P } = _();
    const H = new I(), G = H.signal;
    return {
      value: q(
        async function* () {
          for (; ; ) {
            const W = N;
            N = null;
            const { chunk: se, done: ge, cb: re } = await W;
            if (e.nextTick(re), ge)
              return;
            if (G.aborted)
              throw new f(void 0, {
                cause: G.reason
              });
            ({ promise: N, resolve: P } = _()), yield se;
          }
        }(),
        {
          signal: G
        }
      ),
      write(W, se, ge) {
        const re = P;
        P = null, re({
          chunk: W,
          done: !1,
          cb: ge
        });
      },
      final(W) {
        const se = P;
        P = null, se({
          done: !0,
          cb: W
        });
      },
      destroy(W, se) {
        H.abort(), se(W);
      }
    };
  }
  function $(q) {
    const N = q.readable && typeof q.readable.read != "function" ? v.wrap(q.readable) : q.readable, P = q.writable;
    let H = !!n(N), G = !!r(P), z, W, se, ge, re;
    function ke(B) {
      const te = ge;
      ge = null, te ? te(B) : B ? re.destroy(B) : !H && !G && re.destroy();
    }
    return re = new M({
      // TODO (ronag): highWaterMark?
      readableObjectMode: !!(N != null && N.readableObjectMode),
      writableObjectMode: !!(P != null && P.writableObjectMode),
      readable: H,
      writable: G
    }), G && (l(P, (B) => {
      G = !1, B && d(N, B), ke(B);
    }), re._write = function(B, te, ye) {
      P.write(B, te) ? ye() : z = ye;
    }, re._final = function(B) {
      P.end(), W = B;
    }, P.on("drain", function() {
      if (z) {
        const B = z;
        z = null, B();
      }
    }), P.on("finish", function() {
      if (W) {
        const B = W;
        W = null, B();
      }
    })), H && (l(N, (B) => {
      H = !1, B && d(N, B), ke(B);
    }), N.on("readable", function() {
      if (se) {
        const B = se;
        se = null, B();
      }
    }), N.on("end", function() {
      re.push(null);
    }), re._read = function() {
      for (; ; ) {
        const B = N.read();
        if (B === null) {
          se = re._read;
          return;
        }
        if (!re.push(B))
          return;
      }
    }), re._destroy = function(B, te) {
      !B && ge !== null && (B = new f()), se = null, z = null, W = null, ge === null ? te(B) : (ge = te, d(P, B), d(N, B));
    }, re;
  }
  return Ds;
}
var qs, vy;
function Rn() {
  if (vy)
    return qs;
  vy = 1;
  const {
    ObjectDefineProperties: e,
    ObjectGetOwnPropertyDescriptor: t,
    ObjectKeys: n,
    ObjectSetPrototypeOf: r
  } = Me;
  qs = o;
  const i = Ro(), a = OE();
  r(o.prototype, i.prototype), r(o, i);
  {
    const f = n(a.prototype);
    for (let c = 0; c < f.length; c++) {
      const p = f[c];
      o.prototype[p] || (o.prototype[p] = a.prototype[p]);
    }
  }
  function o(f) {
    if (!(this instanceof o))
      return new o(f);
    i.call(this, f), a.call(this, f), f ? (this.allowHalfOpen = f.allowHalfOpen !== !1, f.readable === !1 && (this._readableState.readable = !1, this._readableState.ended = !0, this._readableState.endEmitted = !0), f.writable === !1 && (this._writableState.writable = !1, this._writableState.ending = !0, this._writableState.ended = !0, this._writableState.finished = !0)) : this.allowHalfOpen = !0;
  }
  e(o.prototype, {
    writable: {
      __proto__: null,
      ...t(a.prototype, "writable")
    },
    writableHighWaterMark: {
      __proto__: null,
      ...t(a.prototype, "writableHighWaterMark")
    },
    writableObjectMode: {
      __proto__: null,
      ...t(a.prototype, "writableObjectMode")
    },
    writableBuffer: {
      __proto__: null,
      ...t(a.prototype, "writableBuffer")
    },
    writableLength: {
      __proto__: null,
      ...t(a.prototype, "writableLength")
    },
    writableFinished: {
      __proto__: null,
      ...t(a.prototype, "writableFinished")
    },
    writableCorked: {
      __proto__: null,
      ...t(a.prototype, "writableCorked")
    },
    writableEnded: {
      __proto__: null,
      ...t(a.prototype, "writableEnded")
    },
    writableNeedDrain: {
      __proto__: null,
      ...t(a.prototype, "writableNeedDrain")
    },
    destroyed: {
      __proto__: null,
      get() {
        return this._readableState === void 0 || this._writableState === void 0 ? !1 : this._readableState.destroyed && this._writableState.destroyed;
      },
      set(f) {
        this._readableState && this._writableState && (this._readableState.destroyed = f, this._writableState.destroyed = f);
      }
    }
  });
  let s;
  function u() {
    return s === void 0 && (s = {}), s;
  }
  o.fromWeb = function(f, c) {
    return u().newStreamDuplexFromReadableWritablePair(f, c);
  }, o.toWeb = function(f) {
    return u().newReadableWritablePairFromDuplex(f);
  };
  let l;
  return o.from = function(f) {
    return l || (l = B$()), l(f, "body");
  }, qs;
}
const { ObjectSetPrototypeOf: $E, Symbol: U$ } = Me;
var LE = In;
const { ERR_METHOD_NOT_IMPLEMENTED: H$ } = at.codes, lp = Rn(), { getHighWaterMark: G$ } = sp;
$E(In.prototype, lp.prototype);
$E(In, lp);
const Fi = U$("kCallback");
function In(e) {
  if (!(this instanceof In))
    return new In(e);
  const t = e ? G$(this, e, "readableHighWaterMark", !0) : null;
  t === 0 && (e = {
    ...e,
    highWaterMark: null,
    readableHighWaterMark: t,
    // TODO (ronag): 0 is not optimal since we have
    // a "bug" where we check needDrain before calling _write and not after.
    // Refs: https://github.com/nodejs/node/pull/32887
    // Refs: https://github.com/nodejs/node/pull/35941
    writableHighWaterMark: e.writableHighWaterMark || 0
  }), lp.call(this, e), this._readableState.sync = !1, this[Fi] = null, e && (typeof e.transform == "function" && (this._transform = e.transform), typeof e.flush == "function" && (this._flush = e.flush)), this.on("prefinish", V$);
}
function dh(e) {
  typeof this._flush == "function" && !this.destroyed ? this._flush((t, n) => {
    if (t) {
      e ? e(t) : this.destroy(t);
      return;
    }
    n != null && this.push(n), this.push(null), e && e();
  }) : (this.push(null), e && e());
}
function V$() {
  this._final !== dh && dh.call(this);
}
In.prototype._final = dh;
In.prototype._transform = function(e, t, n) {
  throw new H$("_transform()");
};
In.prototype._write = function(e, t, n) {
  const r = this._readableState, i = this._writableState, a = r.length;
  this._transform(e, t, (o, s) => {
    if (o) {
      n(o);
      return;
    }
    s != null && this.push(s), i.ended || // Backwards compat.
    a === r.length || // Backwards compat.
    r.length < r.highWaterMark ? n() : this[Fi] = n;
  });
};
In.prototype._read = function() {
  if (this[Fi]) {
    const e = this[Fi];
    this[Fi] = null, e();
  }
};
const { ObjectSetPrototypeOf: ME } = Me;
var CE = ti;
const fp = LE;
ME(ti.prototype, fp.prototype);
ME(ti, fp);
function ti(e) {
  if (!(this instanceof ti))
    return new ti(e);
  fp.call(this, e);
}
ti.prototype._transform = function(e, t, n) {
  n(null, e);
};
const ha = Ir, { ArrayIsArray: W$, Promise: J$, SymbolAsyncIterator: z$ } = Me, Ja = Nr, { once: K$ } = $n, X$ = si, _y = Rn(), {
  aggregateTwoErrors: Y$,
  codes: {
    ERR_INVALID_ARG_TYPE: DE,
    ERR_INVALID_RETURN_VALUE: js,
    ERR_MISSING_ARGS: Q$,
    ERR_STREAM_DESTROYED: Z$,
    ERR_STREAM_PREMATURE_CLOSE: eL
  },
  AbortError: tL
} = at, { validateFunction: nL, validateAbortSignal: rL } = yo, { isIterable: Fr, isReadable: Ps, isReadableNodeStream: hh, isNodeStream: by } = xr, iL = globalThis.AbortController || Zh().AbortController;
let ks, Fs;
function my(e, t, n) {
  let r = !1;
  e.on("close", () => {
    r = !0;
  });
  const i = Ja(
    e,
    {
      readable: t,
      writable: n
    },
    (a) => {
      r = !a;
    }
  );
  return {
    destroy: (a) => {
      r || (r = !0, X$.destroyer(e, a || new Z$("pipe")));
    },
    cleanup: i
  };
}
function aL(e) {
  return nL(e[e.length - 1], "streams[stream.length - 1]"), e.pop();
}
function oL(e) {
  if (Fr(e))
    return e;
  if (hh(e))
    return sL(e);
  throw new DE("val", ["Readable", "Iterable", "AsyncIterable"], e);
}
async function* sL(e) {
  Fs || (Fs = Ro()), yield* Fs.prototype[z$].call(e);
}
async function wy(e, t, n, { end: r }) {
  let i, a = null;
  const o = (l) => {
    if (l && (i = l), a) {
      const f = a;
      a = null, f();
    }
  }, s = () => new J$((l, f) => {
    i ? f(i) : a = () => {
      i ? f(i) : l();
    };
  });
  t.on("drain", o);
  const u = Ja(
    t,
    {
      readable: !1
    },
    o
  );
  try {
    t.writableNeedDrain && await s();
    for await (const l of e)
      t.write(l) || await s();
    r && t.end(), await s(), n();
  } catch (l) {
    n(i !== l ? Y$(i, l) : l);
  } finally {
    u(), t.off("drain", o);
  }
}
function uL(...e) {
  return qE(e, K$(aL(e)));
}
function qE(e, t, n) {
  if (e.length === 1 && W$(e[0]) && (e = e[0]), e.length < 2)
    throw new Q$("streams");
  const r = new iL(), i = r.signal, a = n == null ? void 0 : n.signal, o = [];
  rL(a, "options.signal");
  function s() {
    d(new tL());
  }
  a == null || a.addEventListener("abort", s);
  let u, l;
  const f = [];
  let c = 0;
  function p(_) {
    d(_, --c === 0);
  }
  function d(_, m) {
    if (_ && (!u || u.code === "ERR_STREAM_PREMATURE_CLOSE") && (u = _), !(!u && !m)) {
      for (; f.length; )
        f.shift()(u);
      a == null || a.removeEventListener("abort", s), r.abort(), m && (u || o.forEach((E) => E()), ha.nextTick(t, u, l));
    }
  }
  let y;
  for (let _ = 0; _ < e.length; _++) {
    const m = e[_], E = _ < e.length - 1, L = _ > 0, I = E || (n == null ? void 0 : n.end) !== !1, j = _ === e.length - 1;
    if (by(m)) {
      let M = function(O) {
        O && O.name !== "AbortError" && O.code !== "ERR_STREAM_PREMATURE_CLOSE" && p(O);
      };
      if (I) {
        const { destroy: O, cleanup: $ } = my(m, E, L);
        f.push(O), Ps(m) && j && o.push($);
      }
      m.on("error", M), Ps(m) && j && o.push(() => {
        m.removeListener("error", M);
      });
    }
    if (_ === 0)
      if (typeof m == "function") {
        if (y = m({
          signal: i
        }), !Fr(y))
          throw new js("Iterable, AsyncIterable or Stream", "source", y);
      } else
        Fr(m) || hh(m) ? y = m : y = _y.from(m);
    else if (typeof m == "function")
      if (y = oL(y), y = m(y, {
        signal: i
      }), E) {
        if (!Fr(y, !0))
          throw new js("AsyncIterable", `transform[${_ - 1}]`, y);
      } else {
        var v;
        ks || (ks = CE);
        const M = new ks({
          objectMode: !0
        }), O = (v = y) === null || v === void 0 ? void 0 : v.then;
        if (typeof O == "function")
          c++, O.call(
            y,
            (N) => {
              l = N, N != null && M.write(N), I && M.end(), ha.nextTick(p);
            },
            (N) => {
              M.destroy(N), ha.nextTick(p, N);
            }
          );
        else if (Fr(y, !0))
          c++, wy(y, M, p, {
            end: I
          });
        else
          throw new js("AsyncIterable or Promise", "destination", y);
        y = M;
        const { destroy: $, cleanup: q } = my(y, !1, !0);
        f.push($), j && o.push(q);
      }
    else if (by(m)) {
      if (hh(y)) {
        c += 2;
        const M = lL(y, m, p, {
          end: I
        });
        Ps(m) && j && o.push(M);
      } else if (Fr(y))
        c++, wy(y, m, p, {
          end: I
        });
      else
        throw new DE("val", ["Readable", "Iterable", "AsyncIterable"], y);
      y = m;
    } else
      y = _y.from(m);
  }
  return (i != null && i.aborted || a != null && a.aborted) && ha.nextTick(s), y;
}
function lL(e, t, n, { end: r }) {
  let i = !1;
  return t.on("close", () => {
    i || n(new eL());
  }), e.pipe(t, {
    end: r
  }), r ? e.once("end", () => {
    i = !0, t.end();
  }) : n(), Ja(
    e,
    {
      readable: !0,
      writable: !1
    },
    (a) => {
      const o = e._readableState;
      a && a.code === "ERR_STREAM_PREMATURE_CLOSE" && o && o.ended && !o.errored && !o.errorEmitted ? e.once("end", n).once("error", n) : n(a);
    }
  ), Ja(
    t,
    {
      readable: !1,
      writable: !0
    },
    n
  );
}
var cp = {
  pipelineImpl: qE,
  pipeline: uL
};
const { pipeline: fL } = cp, pa = Rn(), { destroyer: cL } = si, { isNodeStream: dL, isReadable: Ey, isWritable: Sy } = xr, {
  AbortError: hL,
  codes: { ERR_INVALID_ARG_VALUE: Ay, ERR_MISSING_ARGS: pL }
} = at;
var gL = function(...t) {
  if (t.length === 0)
    throw new pL("streams");
  if (t.length === 1)
    return pa.from(t[0]);
  const n = [...t];
  if (typeof t[0] == "function" && (t[0] = pa.from(t[0])), typeof t[t.length - 1] == "function") {
    const d = t.length - 1;
    t[d] = pa.from(t[d]);
  }
  for (let d = 0; d < t.length; ++d)
    if (dL(t[d])) {
      if (d < t.length - 1 && !Ey(t[d]))
        throw new Ay(`streams[${d}]`, n[d], "must be readable");
      if (d > 0 && !Sy(t[d]))
        throw new Ay(`streams[${d}]`, n[d], "must be writable");
    }
  let r, i, a, o, s;
  function u(d) {
    const y = o;
    o = null, y ? y(d) : d ? s.destroy(d) : !p && !c && s.destroy();
  }
  const l = t[0], f = fL(t, u), c = !!Sy(l), p = !!Ey(f);
  return s = new pa({
    // TODO (ronag): highWaterMark?
    writableObjectMode: !!(l != null && l.writableObjectMode),
    readableObjectMode: !!(f != null && f.writableObjectMode),
    writable: c,
    readable: p
  }), c && (s._write = function(d, y, v) {
    l.write(d, y) ? v() : r = v;
  }, s._final = function(d) {
    l.end(), i = d;
  }, l.on("drain", function() {
    if (r) {
      const d = r;
      r = null, d();
    }
  }), f.on("finish", function() {
    if (i) {
      const d = i;
      i = null, d();
    }
  })), p && (f.on("readable", function() {
    if (a) {
      const d = a;
      a = null, d();
    }
  }), f.on("end", function() {
    s.push(null);
  }), s._read = function() {
    for (; ; ) {
      const d = f.read();
      if (d === null) {
        a = s._read;
        return;
      }
      if (!s.push(d))
        return;
    }
  }), s._destroy = function(d, y) {
    !d && o !== null && (d = new hL()), a = null, r = null, i = null, o === null ? y(d) : (o = y, cL(f, d));
  }, s;
};
const { ArrayPrototypePop: yL, Promise: vL } = Me, { isIterable: _L, isNodeStream: bL } = xr, { pipelineImpl: mL } = cp, { finished: wL } = Nr;
function EL(...e) {
  return new vL((t, n) => {
    let r, i;
    const a = e[e.length - 1];
    if (a && typeof a == "object" && !bL(a) && !_L(a)) {
      const o = yL(e);
      r = o.signal, i = o.end;
    }
    mL(
      e,
      (o, s) => {
        o ? n(o) : t(s);
      },
      {
        signal: r,
        end: i
      }
    );
  });
}
var jE = {
  finished: wL,
  pipeline: EL
};
const { Buffer: SL } = On, { ObjectDefineProperty: En, ObjectKeys: PE, ReflectApply: kE } = Me, {
  promisify: { custom: FE }
} = $n, { streamReturningOperators: Ry, promiseReturningOperators: Iy } = Qh, {
  codes: { ERR_ILLEGAL_CONSTRUCTOR: BE }
} = at, AL = gL, { pipeline: UE } = cp, { destroyer: RL } = si, HE = Nr, dp = jE, hp = xr, Ie = F1.exports = ap.Stream;
Ie.isDisturbed = hp.isDisturbed;
Ie.isErrored = hp.isErrored;
Ie.isReadable = hp.isReadable;
Ie.Readable = Ro();
for (const e of PE(Ry)) {
  let n = function(...r) {
    if (new.target)
      throw BE();
    return Ie.Readable.from(kE(t, this, r));
  };
  const t = Ry[e];
  En(n, "name", {
    __proto__: null,
    value: t.name
  }), En(n, "length", {
    __proto__: null,
    value: t.length
  }), En(Ie.Readable.prototype, e, {
    __proto__: null,
    value: n,
    enumerable: !1,
    configurable: !0,
    writable: !0
  });
}
for (const e of PE(Iy)) {
  let n = function(...r) {
    if (new.target)
      throw BE();
    return kE(t, this, r);
  };
  const t = Iy[e];
  En(n, "name", {
    __proto__: null,
    value: t.name
  }), En(n, "length", {
    __proto__: null,
    value: t.length
  }), En(Ie.Readable.prototype, e, {
    __proto__: null,
    value: n,
    enumerable: !1,
    configurable: !0,
    writable: !0
  });
}
Ie.Writable = OE();
Ie.Duplex = Rn();
Ie.Transform = LE;
Ie.PassThrough = CE;
Ie.pipeline = UE;
const { addAbortSignal: IL } = op;
Ie.addAbortSignal = IL;
Ie.finished = HE;
Ie.destroy = RL;
Ie.compose = AL;
En(Ie, "promises", {
  __proto__: null,
  configurable: !0,
  enumerable: !0,
  get() {
    return dp;
  }
});
En(UE, FE, {
  __proto__: null,
  enumerable: !0,
  get() {
    return dp.pipeline;
  }
});
En(HE, FE, {
  __proto__: null,
  enumerable: !0,
  get() {
    return dp.finished;
  }
});
Ie.Stream = Ie;
Ie._isUint8Array = function(t) {
  return t instanceof Uint8Array;
};
Ie._uint8ArrayToBuffer = function(t) {
  return SL.from(t.buffer, t.byteOffset, t.byteLength);
};
var TL = F1.exports;
(function(e) {
  const t = TL, n = jE, r = t.Readable.destroy;
  e.exports = t.Readable, e.exports._uint8ArrayToBuffer = t._uint8ArrayToBuffer, e.exports._isUint8Array = t._isUint8Array, e.exports.isDisturbed = t.isDisturbed, e.exports.isErrored = t.isErrored, e.exports.isReadable = t.isReadable, e.exports.Readable = t.Readable, e.exports.Writable = t.Writable, e.exports.Duplex = t.Duplex, e.exports.Transform = t.Transform, e.exports.PassThrough = t.PassThrough, e.exports.addAbortSignal = t.addAbortSignal, e.exports.finished = t.finished, e.exports.destroy = t.destroy, e.exports.destroy = r, e.exports.pipeline = t.pipeline, e.exports.compose = t.compose, Object.defineProperty(t, "promises", {
    configurable: !0,
    enumerable: !0,
    get() {
      return n;
    }
  }), e.exports.Stream = t.Stream, e.exports.default = e.exports;
})(XN);
rt({
  type: tn([Re, mr(Re)])
});
const Io = 500, ni = 475, pp = "d3-canvas", GE = 50, gp = 37.5, VE = 100, xL = {
  doi: null,
  id: null,
  published: null,
  url: null,
  content: null,
  actors: null
};
function NL(e) {
  return e in xL;
}
function ph(e) {
  const { nodeId: t, type: n, doi: r, id: i, published: a, url: o, content: s, actors: u } = e;
  return {
    nodeId: t,
    type: n,
    ...r && { doi: r },
    ...i && { id: i },
    ...a && { published: a },
    ...o && { url: o },
    ...s && { content: s },
    ...u && { actors: u }
  };
}
function Ty(e, t) {
  return {
    ...e && ph(e),
    ...ph(t)
  };
}
const Zt = {
  review: {
    shortLabel: "R",
    longLabel: "Review",
    backgroundColor: "#1E2F48",
    textColor: "#D4E5FF"
  },
  preprint: {
    shortLabel: "P",
    longLabel: "Preprint",
    backgroundColor: "#077A12",
    textColor: "#CBFFD0"
  },
  "evaluation-summary": {
    shortLabel: "ES",
    longLabel: "Evaluation Summary",
    backgroundColor: "#936308",
    textColor: "#FFF1D8"
  },
  "review-article": {
    shortLabel: "RA",
    longLabel: "Review Article",
    backgroundColor: "#099CEE",
    textColor: "#E7F6FF"
  },
  "journal-article": {
    shortLabel: "JA",
    longLabel: "Journal Article",
    backgroundColor: "#880052",
    textColor: "#FFE3F4"
  },
  editorial: {
    shortLabel: "ED",
    longLabel: "Editorial",
    backgroundColor: "#2A8781",
    textColor: "#E8FFFE"
  },
  comment: {
    shortLabel: "CO",
    longLabel: "Comment",
    backgroundColor: "#B66248",
    textColor: "#FFF0EB"
  },
  reply: {
    shortLabel: "RE",
    longLabel: "Reply",
    backgroundColor: "#79109E",
    textColor: "#F9E9FF"
  },
  "??": {
    shortLabel: "",
    longLabel: "Type unknown",
    backgroundColor: "#CDCDCD",
    textColor: "#043945",
    detailViewBackgroundColor: "#777",
    detailViewTextColor: "#CDCDCD",
    dottedBorder: !0
  }
}, OL = Object.keys(Zt);
function WE(e) {
  const t = LL(e);
  return CL(t);
}
const $L = async ([
  e,
  t
]) => {
  const r = await gw({
    baseUrl: e,
    baseHeaders: {}
  }).getDocmapForDoi({ query: { subject: t } });
  if (r.status !== 200)
    throw new Error(
      `Failed to fetch docmap. ${r.body ? "Server response was " + JSON.stringify(r.body) : ""}`
    );
  const i = r.body;
  return WE(i);
};
function LL(e) {
  const t = un.pipe(e, NT.decode);
  if (Bh.isLeft(t))
    throw new TypeError(
      `Could not parse Docmap: ${JSON.stringify(e)}, found error: ${JSON.stringify(
        t.left
      )}`
    );
  const n = t.right;
  return ML(n);
}
function ML(e) {
  const t = e.steps;
  let n = e["first-step"];
  const r = /* @__PURE__ */ new Set(), i = [];
  for (; n && t && n in t && !r.has(n); ) {
    r.add(n);
    const a = t[n];
    i.push(a), n = a["next-step"];
  }
  return i;
}
function CL(e) {
  let t = {}, n = [];
  for (const i of e) {
    const { newNodesById: a, newEdges: o } = DL(i, t);
    t = a, n = [...n, ...o];
  }
  return { nodes: Object.values(t), edges: n };
}
function DL(e, t) {
  var a;
  const n = { ...t };
  let r = [];
  const i = ((a = e.inputs) == null ? void 0 : a.map((o) => {
    const s = xy(o), u = Ny(o, s, []);
    return n[s] = Ty(n[s], u), s;
  })) ?? [];
  for (const o of e.actions)
    for (const s of o.outputs) {
      const u = xy(s), l = Ny(s, u, o.participants);
      n[u] = Ty(n[u], l);
      const f = i.map(
        (c) => ({
          sourceId: c,
          targetId: u
        })
      );
      r = [...r, ...f];
    }
  return { newNodesById: n, newEdges: r };
}
function xy(e) {
  return e.doi || e.id || `n${Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)}`;
}
function Ny(e, t, n) {
  const { doi: r, id: i, type: a, url: o } = e, s = qL(e.published), u = PL(e.content), l = kL(n);
  return {
    nodeId: t,
    type: jL(a),
    url: o == null ? void 0 : o.toString(),
    doi: r,
    id: i,
    published: s,
    content: u,
    actors: l
  };
}
function qL(e) {
  return e instanceof Date ? FL(e) : e;
}
function jL(e) {
  const t = (Array.isArray(e) ? e[0] : e) ?? "??";
  return OL.includes(t) ? t : "??";
}
function PL(e) {
  return e == null ? void 0 : e.map((t) => {
    var n;
    return (n = t.url) == null ? void 0 : n.toString();
  }).filter((t) => t !== void 0);
}
function kL(e) {
  return e.map((t) => t.actor).filter((t) => {
    const n = t;
    return n && !!(n != null && n.name);
  }).map((t) => t.name).join(", ");
}
function FL(e) {
  const t = e.getFullYear(), n = e.getMonth() + 1, r = e.getDate();
  let i = n.toString();
  n < 10 && (i = "0" + n);
  let a = r.toString();
  return r < 10 && (a = "0" + r.toString()), t + "-" + i + "-" + a;
}
var BL = { value: () => {
} };
function yp() {
  for (var e = 0, t = arguments.length, n = {}, r; e < t; ++e) {
    if (!(r = arguments[e] + "") || r in n || /[\s.]/.test(r))
      throw new Error("illegal type: " + r);
    n[r] = [];
  }
  return new Oa(n);
}
function Oa(e) {
  this._ = e;
}
function UL(e, t) {
  return e.trim().split(/^|\s+/).map(function(n) {
    var r = "", i = n.indexOf(".");
    if (i >= 0 && (r = n.slice(i + 1), n = n.slice(0, i)), n && !t.hasOwnProperty(n))
      throw new Error("unknown type: " + n);
    return { type: n, name: r };
  });
}
Oa.prototype = yp.prototype = {
  constructor: Oa,
  on: function(e, t) {
    var n = this._, r = UL(e + "", n), i, a = -1, o = r.length;
    if (arguments.length < 2) {
      for (; ++a < o; )
        if ((i = (e = r[a]).type) && (i = HL(n[i], e.name)))
          return i;
      return;
    }
    if (t != null && typeof t != "function")
      throw new Error("invalid callback: " + t);
    for (; ++a < o; )
      if (i = (e = r[a]).type)
        n[i] = Oy(n[i], e.name, t);
      else if (t == null)
        for (i in n)
          n[i] = Oy(n[i], e.name, null);
    return this;
  },
  copy: function() {
    var e = {}, t = this._;
    for (var n in t)
      e[n] = t[n].slice();
    return new Oa(e);
  },
  call: function(e, t) {
    if ((i = arguments.length - 2) > 0)
      for (var n = new Array(i), r = 0, i, a; r < i; ++r)
        n[r] = arguments[r + 2];
    if (!this._.hasOwnProperty(e))
      throw new Error("unknown type: " + e);
    for (a = this._[e], r = 0, i = a.length; r < i; ++r)
      a[r].value.apply(t, n);
  },
  apply: function(e, t, n) {
    if (!this._.hasOwnProperty(e))
      throw new Error("unknown type: " + e);
    for (var r = this._[e], i = 0, a = r.length; i < a; ++i)
      r[i].value.apply(t, n);
  }
};
function HL(e, t) {
  for (var n = 0, r = e.length, i; n < r; ++n)
    if ((i = e[n]).name === t)
      return i.value;
}
function Oy(e, t, n) {
  for (var r = 0, i = e.length; r < i; ++r)
    if (e[r].name === t) {
      e[r] = BL, e = e.slice(0, r).concat(e.slice(r + 1));
      break;
    }
  return n != null && e.push({ name: t, value: n }), e;
}
var gh = "http://www.w3.org/1999/xhtml";
const $y = {
  svg: "http://www.w3.org/2000/svg",
  xhtml: gh,
  xlink: "http://www.w3.org/1999/xlink",
  xml: "http://www.w3.org/XML/1998/namespace",
  xmlns: "http://www.w3.org/2000/xmlns/"
};
function To(e) {
  var t = e += "", n = t.indexOf(":");
  return n >= 0 && (t = e.slice(0, n)) !== "xmlns" && (e = e.slice(n + 1)), $y.hasOwnProperty(t) ? { space: $y[t], local: e } : e;
}
function GL(e) {
  return function() {
    var t = this.ownerDocument, n = this.namespaceURI;
    return n === gh && t.documentElement.namespaceURI === gh ? t.createElement(e) : t.createElementNS(n, e);
  };
}
function VL(e) {
  return function() {
    return this.ownerDocument.createElementNS(e.space, e.local);
  };
}
function JE(e) {
  var t = To(e);
  return (t.local ? VL : GL)(t);
}
function WL() {
}
function vp(e) {
  return e == null ? WL : function() {
    return this.querySelector(e);
  };
}
function JL(e) {
  typeof e != "function" && (e = vp(e));
  for (var t = this._groups, n = t.length, r = new Array(n), i = 0; i < n; ++i)
    for (var a = t[i], o = a.length, s = r[i] = new Array(o), u, l, f = 0; f < o; ++f)
      (u = a[f]) && (l = e.call(u, u.__data__, f, a)) && ("__data__" in u && (l.__data__ = u.__data__), s[f] = l);
  return new ht(r, this._parents);
}
function zL(e) {
  return e == null ? [] : Array.isArray(e) ? e : Array.from(e);
}
function KL() {
  return [];
}
function zE(e) {
  return e == null ? KL : function() {
    return this.querySelectorAll(e);
  };
}
function XL(e) {
  return function() {
    return zL(e.apply(this, arguments));
  };
}
function YL(e) {
  typeof e == "function" ? e = XL(e) : e = zE(e);
  for (var t = this._groups, n = t.length, r = [], i = [], a = 0; a < n; ++a)
    for (var o = t[a], s = o.length, u, l = 0; l < s; ++l)
      (u = o[l]) && (r.push(e.call(u, u.__data__, l, o)), i.push(u));
  return new ht(r, i);
}
function KE(e) {
  return function() {
    return this.matches(e);
  };
}
function XE(e) {
  return function(t) {
    return t.matches(e);
  };
}
var QL = Array.prototype.find;
function ZL(e) {
  return function() {
    return QL.call(this.children, e);
  };
}
function eM() {
  return this.firstElementChild;
}
function tM(e) {
  return this.select(e == null ? eM : ZL(typeof e == "function" ? e : XE(e)));
}
var nM = Array.prototype.filter;
function rM() {
  return Array.from(this.children);
}
function iM(e) {
  return function() {
    return nM.call(this.children, e);
  };
}
function aM(e) {
  return this.selectAll(e == null ? rM : iM(typeof e == "function" ? e : XE(e)));
}
function oM(e) {
  typeof e != "function" && (e = KE(e));
  for (var t = this._groups, n = t.length, r = new Array(n), i = 0; i < n; ++i)
    for (var a = t[i], o = a.length, s = r[i] = [], u, l = 0; l < o; ++l)
      (u = a[l]) && e.call(u, u.__data__, l, a) && s.push(u);
  return new ht(r, this._parents);
}
function YE(e) {
  return new Array(e.length);
}
function sM() {
  return new ht(this._enter || this._groups.map(YE), this._parents);
}
function za(e, t) {
  this.ownerDocument = e.ownerDocument, this.namespaceURI = e.namespaceURI, this._next = null, this._parent = e, this.__data__ = t;
}
za.prototype = {
  constructor: za,
  appendChild: function(e) {
    return this._parent.insertBefore(e, this._next);
  },
  insertBefore: function(e, t) {
    return this._parent.insertBefore(e, t);
  },
  querySelector: function(e) {
    return this._parent.querySelector(e);
  },
  querySelectorAll: function(e) {
    return this._parent.querySelectorAll(e);
  }
};
function uM(e) {
  return function() {
    return e;
  };
}
function lM(e, t, n, r, i, a) {
  for (var o = 0, s, u = t.length, l = a.length; o < l; ++o)
    (s = t[o]) ? (s.__data__ = a[o], r[o] = s) : n[o] = new za(e, a[o]);
  for (; o < u; ++o)
    (s = t[o]) && (i[o] = s);
}
function fM(e, t, n, r, i, a, o) {
  var s, u, l = /* @__PURE__ */ new Map(), f = t.length, c = a.length, p = new Array(f), d;
  for (s = 0; s < f; ++s)
    (u = t[s]) && (p[s] = d = o.call(u, u.__data__, s, t) + "", l.has(d) ? i[s] = u : l.set(d, u));
  for (s = 0; s < c; ++s)
    d = o.call(e, a[s], s, a) + "", (u = l.get(d)) ? (r[s] = u, u.__data__ = a[s], l.delete(d)) : n[s] = new za(e, a[s]);
  for (s = 0; s < f; ++s)
    (u = t[s]) && l.get(p[s]) === u && (i[s] = u);
}
function cM(e) {
  return e.__data__;
}
function dM(e, t) {
  if (!arguments.length)
    return Array.from(this, cM);
  var n = t ? fM : lM, r = this._parents, i = this._groups;
  typeof e != "function" && (e = uM(e));
  for (var a = i.length, o = new Array(a), s = new Array(a), u = new Array(a), l = 0; l < a; ++l) {
    var f = r[l], c = i[l], p = c.length, d = hM(e.call(f, f && f.__data__, l, r)), y = d.length, v = s[l] = new Array(y), _ = o[l] = new Array(y), m = u[l] = new Array(p);
    n(f, c, v, _, m, d, t);
    for (var E = 0, L = 0, I, j; E < y; ++E)
      if (I = v[E]) {
        for (E >= L && (L = E + 1); !(j = _[L]) && ++L < y; )
          ;
        I._next = j || null;
      }
  }
  return o = new ht(o, r), o._enter = s, o._exit = u, o;
}
function hM(e) {
  return typeof e == "object" && "length" in e ? e : Array.from(e);
}
function pM() {
  return new ht(this._exit || this._groups.map(YE), this._parents);
}
function gM(e, t, n) {
  var r = this.enter(), i = this, a = this.exit();
  return typeof e == "function" ? (r = e(r), r && (r = r.selection())) : r = r.append(e + ""), t != null && (i = t(i), i && (i = i.selection())), n == null ? a.remove() : n(a), r && i ? r.merge(i).order() : i;
}
function yM(e) {
  for (var t = e.selection ? e.selection() : e, n = this._groups, r = t._groups, i = n.length, a = r.length, o = Math.min(i, a), s = new Array(i), u = 0; u < o; ++u)
    for (var l = n[u], f = r[u], c = l.length, p = s[u] = new Array(c), d, y = 0; y < c; ++y)
      (d = l[y] || f[y]) && (p[y] = d);
  for (; u < i; ++u)
    s[u] = n[u];
  return new ht(s, this._parents);
}
function vM() {
  for (var e = this._groups, t = -1, n = e.length; ++t < n; )
    for (var r = e[t], i = r.length - 1, a = r[i], o; --i >= 0; )
      (o = r[i]) && (a && o.compareDocumentPosition(a) ^ 4 && a.parentNode.insertBefore(o, a), a = o);
  return this;
}
function _M(e) {
  e || (e = bM);
  function t(c, p) {
    return c && p ? e(c.__data__, p.__data__) : !c - !p;
  }
  for (var n = this._groups, r = n.length, i = new Array(r), a = 0; a < r; ++a) {
    for (var o = n[a], s = o.length, u = i[a] = new Array(s), l, f = 0; f < s; ++f)
      (l = o[f]) && (u[f] = l);
    u.sort(t);
  }
  return new ht(i, this._parents).order();
}
function bM(e, t) {
  return e < t ? -1 : e > t ? 1 : e >= t ? 0 : NaN;
}
function mM() {
  var e = arguments[0];
  return arguments[0] = this, e.apply(null, arguments), this;
}
function wM() {
  return Array.from(this);
}
function EM() {
  for (var e = this._groups, t = 0, n = e.length; t < n; ++t)
    for (var r = e[t], i = 0, a = r.length; i < a; ++i) {
      var o = r[i];
      if (o)
        return o;
    }
  return null;
}
function SM() {
  let e = 0;
  for (const t of this)
    ++e;
  return e;
}
function AM() {
  return !this.node();
}
function RM(e) {
  for (var t = this._groups, n = 0, r = t.length; n < r; ++n)
    for (var i = t[n], a = 0, o = i.length, s; a < o; ++a)
      (s = i[a]) && e.call(s, s.__data__, a, i);
  return this;
}
function IM(e) {
  return function() {
    this.removeAttribute(e);
  };
}
function TM(e) {
  return function() {
    this.removeAttributeNS(e.space, e.local);
  };
}
function xM(e, t) {
  return function() {
    this.setAttribute(e, t);
  };
}
function NM(e, t) {
  return function() {
    this.setAttributeNS(e.space, e.local, t);
  };
}
function OM(e, t) {
  return function() {
    var n = t.apply(this, arguments);
    n == null ? this.removeAttribute(e) : this.setAttribute(e, n);
  };
}
function $M(e, t) {
  return function() {
    var n = t.apply(this, arguments);
    n == null ? this.removeAttributeNS(e.space, e.local) : this.setAttributeNS(e.space, e.local, n);
  };
}
function LM(e, t) {
  var n = To(e);
  if (arguments.length < 2) {
    var r = this.node();
    return n.local ? r.getAttributeNS(n.space, n.local) : r.getAttribute(n);
  }
  return this.each((t == null ? n.local ? TM : IM : typeof t == "function" ? n.local ? $M : OM : n.local ? NM : xM)(n, t));
}
function QE(e) {
  return e.ownerDocument && e.ownerDocument.defaultView || e.document && e || e.defaultView;
}
function MM(e) {
  return function() {
    this.style.removeProperty(e);
  };
}
function CM(e, t, n) {
  return function() {
    this.style.setProperty(e, t, n);
  };
}
function DM(e, t, n) {
  return function() {
    var r = t.apply(this, arguments);
    r == null ? this.style.removeProperty(e) : this.style.setProperty(e, r, n);
  };
}
function qM(e, t, n) {
  return arguments.length > 1 ? this.each((t == null ? MM : typeof t == "function" ? DM : CM)(e, t, n ?? "")) : ri(this.node(), e);
}
function ri(e, t) {
  return e.style.getPropertyValue(t) || QE(e).getComputedStyle(e, null).getPropertyValue(t);
}
function jM(e) {
  return function() {
    delete this[e];
  };
}
function PM(e, t) {
  return function() {
    this[e] = t;
  };
}
function kM(e, t) {
  return function() {
    var n = t.apply(this, arguments);
    n == null ? delete this[e] : this[e] = n;
  };
}
function FM(e, t) {
  return arguments.length > 1 ? this.each((t == null ? jM : typeof t == "function" ? kM : PM)(e, t)) : this.node()[e];
}
function ZE(e) {
  return e.trim().split(/^|\s+/);
}
function _p(e) {
  return e.classList || new eS(e);
}
function eS(e) {
  this._node = e, this._names = ZE(e.getAttribute("class") || "");
}
eS.prototype = {
  add: function(e) {
    var t = this._names.indexOf(e);
    t < 0 && (this._names.push(e), this._node.setAttribute("class", this._names.join(" ")));
  },
  remove: function(e) {
    var t = this._names.indexOf(e);
    t >= 0 && (this._names.splice(t, 1), this._node.setAttribute("class", this._names.join(" ")));
  },
  contains: function(e) {
    return this._names.indexOf(e) >= 0;
  }
};
function tS(e, t) {
  for (var n = _p(e), r = -1, i = t.length; ++r < i; )
    n.add(t[r]);
}
function nS(e, t) {
  for (var n = _p(e), r = -1, i = t.length; ++r < i; )
    n.remove(t[r]);
}
function BM(e) {
  return function() {
    tS(this, e);
  };
}
function UM(e) {
  return function() {
    nS(this, e);
  };
}
function HM(e, t) {
  return function() {
    (t.apply(this, arguments) ? tS : nS)(this, e);
  };
}
function GM(e, t) {
  var n = ZE(e + "");
  if (arguments.length < 2) {
    for (var r = _p(this.node()), i = -1, a = n.length; ++i < a; )
      if (!r.contains(n[i]))
        return !1;
    return !0;
  }
  return this.each((typeof t == "function" ? HM : t ? BM : UM)(n, t));
}
function VM() {
  this.textContent = "";
}
function WM(e) {
  return function() {
    this.textContent = e;
  };
}
function JM(e) {
  return function() {
    var t = e.apply(this, arguments);
    this.textContent = t ?? "";
  };
}
function zM(e) {
  return arguments.length ? this.each(e == null ? VM : (typeof e == "function" ? JM : WM)(e)) : this.node().textContent;
}
function KM() {
  this.innerHTML = "";
}
function XM(e) {
  return function() {
    this.innerHTML = e;
  };
}
function YM(e) {
  return function() {
    var t = e.apply(this, arguments);
    this.innerHTML = t ?? "";
  };
}
function QM(e) {
  return arguments.length ? this.each(e == null ? KM : (typeof e == "function" ? YM : XM)(e)) : this.node().innerHTML;
}
function ZM() {
  this.nextSibling && this.parentNode.appendChild(this);
}
function eC() {
  return this.each(ZM);
}
function tC() {
  this.previousSibling && this.parentNode.insertBefore(this, this.parentNode.firstChild);
}
function nC() {
  return this.each(tC);
}
function rC(e) {
  var t = typeof e == "function" ? e : JE(e);
  return this.select(function() {
    return this.appendChild(t.apply(this, arguments));
  });
}
function iC() {
  return null;
}
function aC(e, t) {
  var n = typeof e == "function" ? e : JE(e), r = t == null ? iC : typeof t == "function" ? t : vp(t);
  return this.select(function() {
    return this.insertBefore(n.apply(this, arguments), r.apply(this, arguments) || null);
  });
}
function oC() {
  var e = this.parentNode;
  e && e.removeChild(this);
}
function sC() {
  return this.each(oC);
}
function uC() {
  var e = this.cloneNode(!1), t = this.parentNode;
  return t ? t.insertBefore(e, this.nextSibling) : e;
}
function lC() {
  var e = this.cloneNode(!0), t = this.parentNode;
  return t ? t.insertBefore(e, this.nextSibling) : e;
}
function fC(e) {
  return this.select(e ? lC : uC);
}
function cC(e) {
  return arguments.length ? this.property("__data__", e) : this.node().__data__;
}
function dC(e) {
  return function(t) {
    e.call(this, t, this.__data__);
  };
}
function hC(e) {
  return e.trim().split(/^|\s+/).map(function(t) {
    var n = "", r = t.indexOf(".");
    return r >= 0 && (n = t.slice(r + 1), t = t.slice(0, r)), { type: t, name: n };
  });
}
function pC(e) {
  return function() {
    var t = this.__on;
    if (t) {
      for (var n = 0, r = -1, i = t.length, a; n < i; ++n)
        a = t[n], (!e.type || a.type === e.type) && a.name === e.name ? this.removeEventListener(a.type, a.listener, a.options) : t[++r] = a;
      ++r ? t.length = r : delete this.__on;
    }
  };
}
function gC(e, t, n) {
  return function() {
    var r = this.__on, i, a = dC(t);
    if (r) {
      for (var o = 0, s = r.length; o < s; ++o)
        if ((i = r[o]).type === e.type && i.name === e.name) {
          this.removeEventListener(i.type, i.listener, i.options), this.addEventListener(i.type, i.listener = a, i.options = n), i.value = t;
          return;
        }
    }
    this.addEventListener(e.type, a, n), i = { type: e.type, name: e.name, value: t, listener: a, options: n }, r ? r.push(i) : this.__on = [i];
  };
}
function yC(e, t, n) {
  var r = hC(e + ""), i, a = r.length, o;
  if (arguments.length < 2) {
    var s = this.node().__on;
    if (s) {
      for (var u = 0, l = s.length, f; u < l; ++u)
        for (i = 0, f = s[u]; i < a; ++i)
          if ((o = r[i]).type === f.type && o.name === f.name)
            return f.value;
    }
    return;
  }
  for (s = t ? gC : pC, i = 0; i < a; ++i)
    this.each(s(r[i], t, n));
  return this;
}
function rS(e, t, n) {
  var r = QE(e), i = r.CustomEvent;
  typeof i == "function" ? i = new i(t, n) : (i = r.document.createEvent("Event"), n ? (i.initEvent(t, n.bubbles, n.cancelable), i.detail = n.detail) : i.initEvent(t, !1, !1)), e.dispatchEvent(i);
}
function vC(e, t) {
  return function() {
    return rS(this, e, t);
  };
}
function _C(e, t) {
  return function() {
    return rS(this, e, t.apply(this, arguments));
  };
}
function bC(e, t) {
  return this.each((typeof t == "function" ? _C : vC)(e, t));
}
function* mC() {
  for (var e = this._groups, t = 0, n = e.length; t < n; ++t)
    for (var r = e[t], i = 0, a = r.length, o; i < a; ++i)
      (o = r[i]) && (yield o);
}
var iS = [null];
function ht(e, t) {
  this._groups = e, this._parents = t;
}
function ea() {
  return new ht([[document.documentElement]], iS);
}
function wC() {
  return this;
}
ht.prototype = ea.prototype = {
  constructor: ht,
  select: JL,
  selectAll: YL,
  selectChild: tM,
  selectChildren: aM,
  filter: oM,
  data: dM,
  enter: sM,
  exit: pM,
  join: gM,
  merge: yM,
  selection: wC,
  order: vM,
  sort: _M,
  call: mM,
  nodes: wM,
  node: EM,
  size: SM,
  empty: AM,
  each: RM,
  attr: LM,
  style: qM,
  property: FM,
  classed: GM,
  text: zM,
  html: QM,
  raise: eC,
  lower: nC,
  append: rC,
  insert: aC,
  remove: sC,
  clone: fC,
  datum: cC,
  on: yC,
  dispatch: bC,
  [Symbol.iterator]: mC
};
function xo(e) {
  return typeof e == "string" ? new ht([[document.querySelector(e)]], [document.documentElement]) : new ht([[e]], iS);
}
function bp(e, t, n) {
  e.prototype = t.prototype = n, n.constructor = e;
}
function aS(e, t) {
  var n = Object.create(e.prototype);
  for (var r in t)
    n[r] = t[r];
  return n;
}
function ta() {
}
var Wi = 0.7, Ka = 1 / Wi, zr = "\\s*([+-]?\\d+)\\s*", Ji = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)\\s*", nn = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)%\\s*", EC = /^#([0-9a-f]{3,8})$/, SC = new RegExp(`^rgb\\(${zr},${zr},${zr}\\)$`), AC = new RegExp(`^rgb\\(${nn},${nn},${nn}\\)$`), RC = new RegExp(`^rgba\\(${zr},${zr},${zr},${Ji}\\)$`), IC = new RegExp(`^rgba\\(${nn},${nn},${nn},${Ji}\\)$`), TC = new RegExp(`^hsl\\(${Ji},${nn},${nn}\\)$`), xC = new RegExp(`^hsla\\(${Ji},${nn},${nn},${Ji}\\)$`), Ly = {
  aliceblue: 15792383,
  antiquewhite: 16444375,
  aqua: 65535,
  aquamarine: 8388564,
  azure: 15794175,
  beige: 16119260,
  bisque: 16770244,
  black: 0,
  blanchedalmond: 16772045,
  blue: 255,
  blueviolet: 9055202,
  brown: 10824234,
  burlywood: 14596231,
  cadetblue: 6266528,
  chartreuse: 8388352,
  chocolate: 13789470,
  coral: 16744272,
  cornflowerblue: 6591981,
  cornsilk: 16775388,
  crimson: 14423100,
  cyan: 65535,
  darkblue: 139,
  darkcyan: 35723,
  darkgoldenrod: 12092939,
  darkgray: 11119017,
  darkgreen: 25600,
  darkgrey: 11119017,
  darkkhaki: 12433259,
  darkmagenta: 9109643,
  darkolivegreen: 5597999,
  darkorange: 16747520,
  darkorchid: 10040012,
  darkred: 9109504,
  darksalmon: 15308410,
  darkseagreen: 9419919,
  darkslateblue: 4734347,
  darkslategray: 3100495,
  darkslategrey: 3100495,
  darkturquoise: 52945,
  darkviolet: 9699539,
  deeppink: 16716947,
  deepskyblue: 49151,
  dimgray: 6908265,
  dimgrey: 6908265,
  dodgerblue: 2003199,
  firebrick: 11674146,
  floralwhite: 16775920,
  forestgreen: 2263842,
  fuchsia: 16711935,
  gainsboro: 14474460,
  ghostwhite: 16316671,
  gold: 16766720,
  goldenrod: 14329120,
  gray: 8421504,
  green: 32768,
  greenyellow: 11403055,
  grey: 8421504,
  honeydew: 15794160,
  hotpink: 16738740,
  indianred: 13458524,
  indigo: 4915330,
  ivory: 16777200,
  khaki: 15787660,
  lavender: 15132410,
  lavenderblush: 16773365,
  lawngreen: 8190976,
  lemonchiffon: 16775885,
  lightblue: 11393254,
  lightcoral: 15761536,
  lightcyan: 14745599,
  lightgoldenrodyellow: 16448210,
  lightgray: 13882323,
  lightgreen: 9498256,
  lightgrey: 13882323,
  lightpink: 16758465,
  lightsalmon: 16752762,
  lightseagreen: 2142890,
  lightskyblue: 8900346,
  lightslategray: 7833753,
  lightslategrey: 7833753,
  lightsteelblue: 11584734,
  lightyellow: 16777184,
  lime: 65280,
  limegreen: 3329330,
  linen: 16445670,
  magenta: 16711935,
  maroon: 8388608,
  mediumaquamarine: 6737322,
  mediumblue: 205,
  mediumorchid: 12211667,
  mediumpurple: 9662683,
  mediumseagreen: 3978097,
  mediumslateblue: 8087790,
  mediumspringgreen: 64154,
  mediumturquoise: 4772300,
  mediumvioletred: 13047173,
  midnightblue: 1644912,
  mintcream: 16121850,
  mistyrose: 16770273,
  moccasin: 16770229,
  navajowhite: 16768685,
  navy: 128,
  oldlace: 16643558,
  olive: 8421376,
  olivedrab: 7048739,
  orange: 16753920,
  orangered: 16729344,
  orchid: 14315734,
  palegoldenrod: 15657130,
  palegreen: 10025880,
  paleturquoise: 11529966,
  palevioletred: 14381203,
  papayawhip: 16773077,
  peachpuff: 16767673,
  peru: 13468991,
  pink: 16761035,
  plum: 14524637,
  powderblue: 11591910,
  purple: 8388736,
  rebeccapurple: 6697881,
  red: 16711680,
  rosybrown: 12357519,
  royalblue: 4286945,
  saddlebrown: 9127187,
  salmon: 16416882,
  sandybrown: 16032864,
  seagreen: 3050327,
  seashell: 16774638,
  sienna: 10506797,
  silver: 12632256,
  skyblue: 8900331,
  slateblue: 6970061,
  slategray: 7372944,
  slategrey: 7372944,
  snow: 16775930,
  springgreen: 65407,
  steelblue: 4620980,
  tan: 13808780,
  teal: 32896,
  thistle: 14204888,
  tomato: 16737095,
  turquoise: 4251856,
  violet: 15631086,
  wheat: 16113331,
  white: 16777215,
  whitesmoke: 16119285,
  yellow: 16776960,
  yellowgreen: 10145074
};
bp(ta, zi, {
  copy(e) {
    return Object.assign(new this.constructor(), this, e);
  },
  displayable() {
    return this.rgb().displayable();
  },
  hex: My,
  // Deprecated! Use color.formatHex.
  formatHex: My,
  formatHex8: NC,
  formatHsl: OC,
  formatRgb: Cy,
  toString: Cy
});
function My() {
  return this.rgb().formatHex();
}
function NC() {
  return this.rgb().formatHex8();
}
function OC() {
  return oS(this).formatHsl();
}
function Cy() {
  return this.rgb().formatRgb();
}
function zi(e) {
  var t, n;
  return e = (e + "").trim().toLowerCase(), (t = EC.exec(e)) ? (n = t[1].length, t = parseInt(t[1], 16), n === 6 ? Dy(t) : n === 3 ? new nt(t >> 8 & 15 | t >> 4 & 240, t >> 4 & 15 | t & 240, (t & 15) << 4 | t & 15, 1) : n === 8 ? ga(t >> 24 & 255, t >> 16 & 255, t >> 8 & 255, (t & 255) / 255) : n === 4 ? ga(t >> 12 & 15 | t >> 8 & 240, t >> 8 & 15 | t >> 4 & 240, t >> 4 & 15 | t & 240, ((t & 15) << 4 | t & 15) / 255) : null) : (t = SC.exec(e)) ? new nt(t[1], t[2], t[3], 1) : (t = AC.exec(e)) ? new nt(t[1] * 255 / 100, t[2] * 255 / 100, t[3] * 255 / 100, 1) : (t = RC.exec(e)) ? ga(t[1], t[2], t[3], t[4]) : (t = IC.exec(e)) ? ga(t[1] * 255 / 100, t[2] * 255 / 100, t[3] * 255 / 100, t[4]) : (t = TC.exec(e)) ? Py(t[1], t[2] / 100, t[3] / 100, 1) : (t = xC.exec(e)) ? Py(t[1], t[2] / 100, t[3] / 100, t[4]) : Ly.hasOwnProperty(e) ? Dy(Ly[e]) : e === "transparent" ? new nt(NaN, NaN, NaN, 0) : null;
}
function Dy(e) {
  return new nt(e >> 16 & 255, e >> 8 & 255, e & 255, 1);
}
function ga(e, t, n, r) {
  return r <= 0 && (e = t = n = NaN), new nt(e, t, n, r);
}
function $C(e) {
  return e instanceof ta || (e = zi(e)), e ? (e = e.rgb(), new nt(e.r, e.g, e.b, e.opacity)) : new nt();
}
function yh(e, t, n, r) {
  return arguments.length === 1 ? $C(e) : new nt(e, t, n, r ?? 1);
}
function nt(e, t, n, r) {
  this.r = +e, this.g = +t, this.b = +n, this.opacity = +r;
}
bp(nt, yh, aS(ta, {
  brighter(e) {
    return e = e == null ? Ka : Math.pow(Ka, e), new nt(this.r * e, this.g * e, this.b * e, this.opacity);
  },
  darker(e) {
    return e = e == null ? Wi : Math.pow(Wi, e), new nt(this.r * e, this.g * e, this.b * e, this.opacity);
  },
  rgb() {
    return this;
  },
  clamp() {
    return new nt(yr(this.r), yr(this.g), yr(this.b), Xa(this.opacity));
  },
  displayable() {
    return -0.5 <= this.r && this.r < 255.5 && -0.5 <= this.g && this.g < 255.5 && -0.5 <= this.b && this.b < 255.5 && 0 <= this.opacity && this.opacity <= 1;
  },
  hex: qy,
  // Deprecated! Use color.formatHex.
  formatHex: qy,
  formatHex8: LC,
  formatRgb: jy,
  toString: jy
}));
function qy() {
  return `#${fr(this.r)}${fr(this.g)}${fr(this.b)}`;
}
function LC() {
  return `#${fr(this.r)}${fr(this.g)}${fr(this.b)}${fr((isNaN(this.opacity) ? 1 : this.opacity) * 255)}`;
}
function jy() {
  const e = Xa(this.opacity);
  return `${e === 1 ? "rgb(" : "rgba("}${yr(this.r)}, ${yr(this.g)}, ${yr(this.b)}${e === 1 ? ")" : `, ${e})`}`;
}
function Xa(e) {
  return isNaN(e) ? 1 : Math.max(0, Math.min(1, e));
}
function yr(e) {
  return Math.max(0, Math.min(255, Math.round(e) || 0));
}
function fr(e) {
  return e = yr(e), (e < 16 ? "0" : "") + e.toString(16);
}
function Py(e, t, n, r) {
  return r <= 0 ? e = t = n = NaN : n <= 0 || n >= 1 ? e = t = NaN : t <= 0 && (e = NaN), new qt(e, t, n, r);
}
function oS(e) {
  if (e instanceof qt)
    return new qt(e.h, e.s, e.l, e.opacity);
  if (e instanceof ta || (e = zi(e)), !e)
    return new qt();
  if (e instanceof qt)
    return e;
  e = e.rgb();
  var t = e.r / 255, n = e.g / 255, r = e.b / 255, i = Math.min(t, n, r), a = Math.max(t, n, r), o = NaN, s = a - i, u = (a + i) / 2;
  return s ? (t === a ? o = (n - r) / s + (n < r) * 6 : n === a ? o = (r - t) / s + 2 : o = (t - n) / s + 4, s /= u < 0.5 ? a + i : 2 - a - i, o *= 60) : s = u > 0 && u < 1 ? 0 : o, new qt(o, s, u, e.opacity);
}
function MC(e, t, n, r) {
  return arguments.length === 1 ? oS(e) : new qt(e, t, n, r ?? 1);
}
function qt(e, t, n, r) {
  this.h = +e, this.s = +t, this.l = +n, this.opacity = +r;
}
bp(qt, MC, aS(ta, {
  brighter(e) {
    return e = e == null ? Ka : Math.pow(Ka, e), new qt(this.h, this.s, this.l * e, this.opacity);
  },
  darker(e) {
    return e = e == null ? Wi : Math.pow(Wi, e), new qt(this.h, this.s, this.l * e, this.opacity);
  },
  rgb() {
    var e = this.h % 360 + (this.h < 0) * 360, t = isNaN(e) || isNaN(this.s) ? 0 : this.s, n = this.l, r = n + (n < 0.5 ? n : 1 - n) * t, i = 2 * n - r;
    return new nt(
      Bs(e >= 240 ? e - 240 : e + 120, i, r),
      Bs(e, i, r),
      Bs(e < 120 ? e + 240 : e - 120, i, r),
      this.opacity
    );
  },
  clamp() {
    return new qt(ky(this.h), ya(this.s), ya(this.l), Xa(this.opacity));
  },
  displayable() {
    return (0 <= this.s && this.s <= 1 || isNaN(this.s)) && 0 <= this.l && this.l <= 1 && 0 <= this.opacity && this.opacity <= 1;
  },
  formatHsl() {
    const e = Xa(this.opacity);
    return `${e === 1 ? "hsl(" : "hsla("}${ky(this.h)}, ${ya(this.s) * 100}%, ${ya(this.l) * 100}%${e === 1 ? ")" : `, ${e})`}`;
  }
}));
function ky(e) {
  return e = (e || 0) % 360, e < 0 ? e + 360 : e;
}
function ya(e) {
  return Math.max(0, Math.min(1, e || 0));
}
function Bs(e, t, n) {
  return (e < 60 ? t + (n - t) * e / 60 : e < 180 ? n : e < 240 ? t + (n - t) * (240 - e) / 60 : t) * 255;
}
const sS = (e) => () => e;
function CC(e, t) {
  return function(n) {
    return e + n * t;
  };
}
function DC(e, t, n) {
  return e = Math.pow(e, n), t = Math.pow(t, n) - e, n = 1 / n, function(r) {
    return Math.pow(e + r * t, n);
  };
}
function qC(e) {
  return (e = +e) == 1 ? uS : function(t, n) {
    return n - t ? DC(t, n, e) : sS(isNaN(t) ? n : t);
  };
}
function uS(e, t) {
  var n = t - e;
  return n ? CC(e, n) : sS(isNaN(e) ? t : e);
}
const Fy = function e(t) {
  var n = qC(t);
  function r(i, a) {
    var o = n((i = yh(i)).r, (a = yh(a)).r), s = n(i.g, a.g), u = n(i.b, a.b), l = uS(i.opacity, a.opacity);
    return function(f) {
      return i.r = o(f), i.g = s(f), i.b = u(f), i.opacity = l(f), i + "";
    };
  }
  return r.gamma = e, r;
}(1);
function Pn(e, t) {
  return e = +e, t = +t, function(n) {
    return e * (1 - n) + t * n;
  };
}
var vh = /[-+]?(?:\d+\.?\d*|\.?\d+)(?:[eE][-+]?\d+)?/g, Us = new RegExp(vh.source, "g");
function jC(e) {
  return function() {
    return e;
  };
}
function PC(e) {
  return function(t) {
    return e(t) + "";
  };
}
function kC(e, t) {
  var n = vh.lastIndex = Us.lastIndex = 0, r, i, a, o = -1, s = [], u = [];
  for (e = e + "", t = t + ""; (r = vh.exec(e)) && (i = Us.exec(t)); )
    (a = i.index) > n && (a = t.slice(n, a), s[o] ? s[o] += a : s[++o] = a), (r = r[0]) === (i = i[0]) ? s[o] ? s[o] += i : s[++o] = i : (s[++o] = null, u.push({ i: o, x: Pn(r, i) })), n = Us.lastIndex;
  return n < t.length && (a = t.slice(n), s[o] ? s[o] += a : s[++o] = a), s.length < 2 ? u[0] ? PC(u[0].x) : jC(t) : (t = u.length, function(l) {
    for (var f = 0, c; f < t; ++f)
      s[(c = u[f]).i] = c.x(l);
    return s.join("");
  });
}
var By = 180 / Math.PI, _h = {
  translateX: 0,
  translateY: 0,
  rotate: 0,
  skewX: 0,
  scaleX: 1,
  scaleY: 1
};
function lS(e, t, n, r, i, a) {
  var o, s, u;
  return (o = Math.sqrt(e * e + t * t)) && (e /= o, t /= o), (u = e * n + t * r) && (n -= e * u, r -= t * u), (s = Math.sqrt(n * n + r * r)) && (n /= s, r /= s, u /= s), e * r < t * n && (e = -e, t = -t, u = -u, o = -o), {
    translateX: i,
    translateY: a,
    rotate: Math.atan2(t, e) * By,
    skewX: Math.atan(u) * By,
    scaleX: o,
    scaleY: s
  };
}
var va;
function FC(e) {
  const t = new (typeof DOMMatrix == "function" ? DOMMatrix : WebKitCSSMatrix)(e + "");
  return t.isIdentity ? _h : lS(t.a, t.b, t.c, t.d, t.e, t.f);
}
function BC(e) {
  return e == null || (va || (va = document.createElementNS("http://www.w3.org/2000/svg", "g")), va.setAttribute("transform", e), !(e = va.transform.baseVal.consolidate())) ? _h : (e = e.matrix, lS(e.a, e.b, e.c, e.d, e.e, e.f));
}
function fS(e, t, n, r) {
  function i(l) {
    return l.length ? l.pop() + " " : "";
  }
  function a(l, f, c, p, d, y) {
    if (l !== c || f !== p) {
      var v = d.push("translate(", null, t, null, n);
      y.push({ i: v - 4, x: Pn(l, c) }, { i: v - 2, x: Pn(f, p) });
    } else
      (c || p) && d.push("translate(" + c + t + p + n);
  }
  function o(l, f, c, p) {
    l !== f ? (l - f > 180 ? f += 360 : f - l > 180 && (l += 360), p.push({ i: c.push(i(c) + "rotate(", null, r) - 2, x: Pn(l, f) })) : f && c.push(i(c) + "rotate(" + f + r);
  }
  function s(l, f, c, p) {
    l !== f ? p.push({ i: c.push(i(c) + "skewX(", null, r) - 2, x: Pn(l, f) }) : f && c.push(i(c) + "skewX(" + f + r);
  }
  function u(l, f, c, p, d, y) {
    if (l !== c || f !== p) {
      var v = d.push(i(d) + "scale(", null, ",", null, ")");
      y.push({ i: v - 4, x: Pn(l, c) }, { i: v - 2, x: Pn(f, p) });
    } else
      (c !== 1 || p !== 1) && d.push(i(d) + "scale(" + c + "," + p + ")");
  }
  return function(l, f) {
    var c = [], p = [];
    return l = e(l), f = e(f), a(l.translateX, l.translateY, f.translateX, f.translateY, c, p), o(l.rotate, f.rotate, c, p), s(l.skewX, f.skewX, c, p), u(l.scaleX, l.scaleY, f.scaleX, f.scaleY, c, p), l = f = null, function(d) {
      for (var y = -1, v = p.length, _; ++y < v; )
        c[(_ = p[y]).i] = _.x(d);
      return c.join("");
    };
  };
}
var UC = fS(FC, "px, ", "px)", "deg)"), HC = fS(BC, ", ", ")", ")"), ii = 0, Ai = 0, wi = 0, cS = 1e3, Ya, Ri, Qa = 0, Ar = 0, No = 0, Ki = typeof performance == "object" && performance.now ? performance : Date, dS = typeof window == "object" && window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : function(e) {
  setTimeout(e, 17);
};
function mp() {
  return Ar || (dS(GC), Ar = Ki.now() + No);
}
function GC() {
  Ar = 0;
}
function Za() {
  this._call = this._time = this._next = null;
}
Za.prototype = wp.prototype = {
  constructor: Za,
  restart: function(e, t, n) {
    if (typeof e != "function")
      throw new TypeError("callback is not a function");
    n = (n == null ? mp() : +n) + (t == null ? 0 : +t), !this._next && Ri !== this && (Ri ? Ri._next = this : Ya = this, Ri = this), this._call = e, this._time = n, bh();
  },
  stop: function() {
    this._call && (this._call = null, this._time = 1 / 0, bh());
  }
};
function wp(e, t, n) {
  var r = new Za();
  return r.restart(e, t, n), r;
}
function VC() {
  mp(), ++ii;
  for (var e = Ya, t; e; )
    (t = Ar - e._time) >= 0 && e._call.call(void 0, t), e = e._next;
  --ii;
}
function Uy() {
  Ar = (Qa = Ki.now()) + No, ii = Ai = 0;
  try {
    VC();
  } finally {
    ii = 0, JC(), Ar = 0;
  }
}
function WC() {
  var e = Ki.now(), t = e - Qa;
  t > cS && (No -= t, Qa = e);
}
function JC() {
  for (var e, t = Ya, n, r = 1 / 0; t; )
    t._call ? (r > t._time && (r = t._time), e = t, t = t._next) : (n = t._next, t._next = null, t = e ? e._next = n : Ya = n);
  Ri = e, bh(r);
}
function bh(e) {
  if (!ii) {
    Ai && (Ai = clearTimeout(Ai));
    var t = e - Ar;
    t > 24 ? (e < 1 / 0 && (Ai = setTimeout(Uy, e - Ki.now() - No)), wi && (wi = clearInterval(wi))) : (wi || (Qa = Ki.now(), wi = setInterval(WC, cS)), ii = 1, dS(Uy));
  }
}
function Hy(e, t, n) {
  var r = new Za();
  return t = t == null ? 0 : +t, r.restart((i) => {
    r.stop(), e(i + t);
  }, t, n), r;
}
var zC = yp("start", "end", "cancel", "interrupt"), KC = [], hS = 0, Gy = 1, mh = 2, $a = 3, Vy = 4, wh = 5, La = 6;
function Oo(e, t, n, r, i, a) {
  var o = e.__transition;
  if (!o)
    e.__transition = {};
  else if (n in o)
    return;
  XC(e, n, {
    name: t,
    index: r,
    // For context during callback.
    group: i,
    // For context during callback.
    on: zC,
    tween: KC,
    time: a.time,
    delay: a.delay,
    duration: a.duration,
    ease: a.ease,
    timer: null,
    state: hS
  });
}
function Ep(e, t) {
  var n = Bt(e, t);
  if (n.state > hS)
    throw new Error("too late; already scheduled");
  return n;
}
function ln(e, t) {
  var n = Bt(e, t);
  if (n.state > $a)
    throw new Error("too late; already running");
  return n;
}
function Bt(e, t) {
  var n = e.__transition;
  if (!n || !(n = n[t]))
    throw new Error("transition not found");
  return n;
}
function XC(e, t, n) {
  var r = e.__transition, i;
  r[t] = n, n.timer = wp(a, 0, n.time);
  function a(l) {
    n.state = Gy, n.timer.restart(o, n.delay, n.time), n.delay <= l && o(l - n.delay);
  }
  function o(l) {
    var f, c, p, d;
    if (n.state !== Gy)
      return u();
    for (f in r)
      if (d = r[f], d.name === n.name) {
        if (d.state === $a)
          return Hy(o);
        d.state === Vy ? (d.state = La, d.timer.stop(), d.on.call("interrupt", e, e.__data__, d.index, d.group), delete r[f]) : +f < t && (d.state = La, d.timer.stop(), d.on.call("cancel", e, e.__data__, d.index, d.group), delete r[f]);
      }
    if (Hy(function() {
      n.state === $a && (n.state = Vy, n.timer.restart(s, n.delay, n.time), s(l));
    }), n.state = mh, n.on.call("start", e, e.__data__, n.index, n.group), n.state === mh) {
      for (n.state = $a, i = new Array(p = n.tween.length), f = 0, c = -1; f < p; ++f)
        (d = n.tween[f].value.call(e, e.__data__, n.index, n.group)) && (i[++c] = d);
      i.length = c + 1;
    }
  }
  function s(l) {
    for (var f = l < n.duration ? n.ease.call(null, l / n.duration) : (n.timer.restart(u), n.state = wh, 1), c = -1, p = i.length; ++c < p; )
      i[c].call(e, f);
    n.state === wh && (n.on.call("end", e, e.__data__, n.index, n.group), u());
  }
  function u() {
    n.state = La, n.timer.stop(), delete r[t];
    for (var l in r)
      return;
    delete e.__transition;
  }
}
function YC(e, t) {
  var n = e.__transition, r, i, a = !0, o;
  if (n) {
    t = t == null ? null : t + "";
    for (o in n) {
      if ((r = n[o]).name !== t) {
        a = !1;
        continue;
      }
      i = r.state > mh && r.state < wh, r.state = La, r.timer.stop(), r.on.call(i ? "interrupt" : "cancel", e, e.__data__, r.index, r.group), delete n[o];
    }
    a && delete e.__transition;
  }
}
function QC(e) {
  return this.each(function() {
    YC(this, e);
  });
}
function ZC(e, t) {
  var n, r;
  return function() {
    var i = ln(this, e), a = i.tween;
    if (a !== n) {
      r = n = a;
      for (var o = 0, s = r.length; o < s; ++o)
        if (r[o].name === t) {
          r = r.slice(), r.splice(o, 1);
          break;
        }
    }
    i.tween = r;
  };
}
function eD(e, t, n) {
  var r, i;
  if (typeof n != "function")
    throw new Error();
  return function() {
    var a = ln(this, e), o = a.tween;
    if (o !== r) {
      i = (r = o).slice();
      for (var s = { name: t, value: n }, u = 0, l = i.length; u < l; ++u)
        if (i[u].name === t) {
          i[u] = s;
          break;
        }
      u === l && i.push(s);
    }
    a.tween = i;
  };
}
function tD(e, t) {
  var n = this._id;
  if (e += "", arguments.length < 2) {
    for (var r = Bt(this.node(), n).tween, i = 0, a = r.length, o; i < a; ++i)
      if ((o = r[i]).name === e)
        return o.value;
    return null;
  }
  return this.each((t == null ? ZC : eD)(n, e, t));
}
function Sp(e, t, n) {
  var r = e._id;
  return e.each(function() {
    var i = ln(this, r);
    (i.value || (i.value = {}))[t] = n.apply(this, arguments);
  }), function(i) {
    return Bt(i, r).value[t];
  };
}
function pS(e, t) {
  var n;
  return (typeof t == "number" ? Pn : t instanceof zi ? Fy : (n = zi(t)) ? (t = n, Fy) : kC)(e, t);
}
function nD(e) {
  return function() {
    this.removeAttribute(e);
  };
}
function rD(e) {
  return function() {
    this.removeAttributeNS(e.space, e.local);
  };
}
function iD(e, t, n) {
  var r, i = n + "", a;
  return function() {
    var o = this.getAttribute(e);
    return o === i ? null : o === r ? a : a = t(r = o, n);
  };
}
function aD(e, t, n) {
  var r, i = n + "", a;
  return function() {
    var o = this.getAttributeNS(e.space, e.local);
    return o === i ? null : o === r ? a : a = t(r = o, n);
  };
}
function oD(e, t, n) {
  var r, i, a;
  return function() {
    var o, s = n(this), u;
    return s == null ? void this.removeAttribute(e) : (o = this.getAttribute(e), u = s + "", o === u ? null : o === r && u === i ? a : (i = u, a = t(r = o, s)));
  };
}
function sD(e, t, n) {
  var r, i, a;
  return function() {
    var o, s = n(this), u;
    return s == null ? void this.removeAttributeNS(e.space, e.local) : (o = this.getAttributeNS(e.space, e.local), u = s + "", o === u ? null : o === r && u === i ? a : (i = u, a = t(r = o, s)));
  };
}
function uD(e, t) {
  var n = To(e), r = n === "transform" ? HC : pS;
  return this.attrTween(e, typeof t == "function" ? (n.local ? sD : oD)(n, r, Sp(this, "attr." + e, t)) : t == null ? (n.local ? rD : nD)(n) : (n.local ? aD : iD)(n, r, t));
}
function lD(e, t) {
  return function(n) {
    this.setAttribute(e, t.call(this, n));
  };
}
function fD(e, t) {
  return function(n) {
    this.setAttributeNS(e.space, e.local, t.call(this, n));
  };
}
function cD(e, t) {
  var n, r;
  function i() {
    var a = t.apply(this, arguments);
    return a !== r && (n = (r = a) && fD(e, a)), n;
  }
  return i._value = t, i;
}
function dD(e, t) {
  var n, r;
  function i() {
    var a = t.apply(this, arguments);
    return a !== r && (n = (r = a) && lD(e, a)), n;
  }
  return i._value = t, i;
}
function hD(e, t) {
  var n = "attr." + e;
  if (arguments.length < 2)
    return (n = this.tween(n)) && n._value;
  if (t == null)
    return this.tween(n, null);
  if (typeof t != "function")
    throw new Error();
  var r = To(e);
  return this.tween(n, (r.local ? cD : dD)(r, t));
}
function pD(e, t) {
  return function() {
    Ep(this, e).delay = +t.apply(this, arguments);
  };
}
function gD(e, t) {
  return t = +t, function() {
    Ep(this, e).delay = t;
  };
}
function yD(e) {
  var t = this._id;
  return arguments.length ? this.each((typeof e == "function" ? pD : gD)(t, e)) : Bt(this.node(), t).delay;
}
function vD(e, t) {
  return function() {
    ln(this, e).duration = +t.apply(this, arguments);
  };
}
function _D(e, t) {
  return t = +t, function() {
    ln(this, e).duration = t;
  };
}
function bD(e) {
  var t = this._id;
  return arguments.length ? this.each((typeof e == "function" ? vD : _D)(t, e)) : Bt(this.node(), t).duration;
}
function mD(e, t) {
  if (typeof t != "function")
    throw new Error();
  return function() {
    ln(this, e).ease = t;
  };
}
function wD(e) {
  var t = this._id;
  return arguments.length ? this.each(mD(t, e)) : Bt(this.node(), t).ease;
}
function ED(e, t) {
  return function() {
    var n = t.apply(this, arguments);
    if (typeof n != "function")
      throw new Error();
    ln(this, e).ease = n;
  };
}
function SD(e) {
  if (typeof e != "function")
    throw new Error();
  return this.each(ED(this._id, e));
}
function AD(e) {
  typeof e != "function" && (e = KE(e));
  for (var t = this._groups, n = t.length, r = new Array(n), i = 0; i < n; ++i)
    for (var a = t[i], o = a.length, s = r[i] = [], u, l = 0; l < o; ++l)
      (u = a[l]) && e.call(u, u.__data__, l, a) && s.push(u);
  return new Tn(r, this._parents, this._name, this._id);
}
function RD(e) {
  if (e._id !== this._id)
    throw new Error();
  for (var t = this._groups, n = e._groups, r = t.length, i = n.length, a = Math.min(r, i), o = new Array(r), s = 0; s < a; ++s)
    for (var u = t[s], l = n[s], f = u.length, c = o[s] = new Array(f), p, d = 0; d < f; ++d)
      (p = u[d] || l[d]) && (c[d] = p);
  for (; s < r; ++s)
    o[s] = t[s];
  return new Tn(o, this._parents, this._name, this._id);
}
function ID(e) {
  return (e + "").trim().split(/^|\s+/).every(function(t) {
    var n = t.indexOf(".");
    return n >= 0 && (t = t.slice(0, n)), !t || t === "start";
  });
}
function TD(e, t, n) {
  var r, i, a = ID(t) ? Ep : ln;
  return function() {
    var o = a(this, e), s = o.on;
    s !== r && (i = (r = s).copy()).on(t, n), o.on = i;
  };
}
function xD(e, t) {
  var n = this._id;
  return arguments.length < 2 ? Bt(this.node(), n).on.on(e) : this.each(TD(n, e, t));
}
function ND(e) {
  return function() {
    var t = this.parentNode;
    for (var n in this.__transition)
      if (+n !== e)
        return;
    t && t.removeChild(this);
  };
}
function OD() {
  return this.on("end.remove", ND(this._id));
}
function $D(e) {
  var t = this._name, n = this._id;
  typeof e != "function" && (e = vp(e));
  for (var r = this._groups, i = r.length, a = new Array(i), o = 0; o < i; ++o)
    for (var s = r[o], u = s.length, l = a[o] = new Array(u), f, c, p = 0; p < u; ++p)
      (f = s[p]) && (c = e.call(f, f.__data__, p, s)) && ("__data__" in f && (c.__data__ = f.__data__), l[p] = c, Oo(l[p], t, n, p, l, Bt(f, n)));
  return new Tn(a, this._parents, t, n);
}
function LD(e) {
  var t = this._name, n = this._id;
  typeof e != "function" && (e = zE(e));
  for (var r = this._groups, i = r.length, a = [], o = [], s = 0; s < i; ++s)
    for (var u = r[s], l = u.length, f, c = 0; c < l; ++c)
      if (f = u[c]) {
        for (var p = e.call(f, f.__data__, c, u), d, y = Bt(f, n), v = 0, _ = p.length; v < _; ++v)
          (d = p[v]) && Oo(d, t, n, v, p, y);
        a.push(p), o.push(f);
      }
  return new Tn(a, o, t, n);
}
var MD = ea.prototype.constructor;
function CD() {
  return new MD(this._groups, this._parents);
}
function DD(e, t) {
  var n, r, i;
  return function() {
    var a = ri(this, e), o = (this.style.removeProperty(e), ri(this, e));
    return a === o ? null : a === n && o === r ? i : i = t(n = a, r = o);
  };
}
function gS(e) {
  return function() {
    this.style.removeProperty(e);
  };
}
function qD(e, t, n) {
  var r, i = n + "", a;
  return function() {
    var o = ri(this, e);
    return o === i ? null : o === r ? a : a = t(r = o, n);
  };
}
function jD(e, t, n) {
  var r, i, a;
  return function() {
    var o = ri(this, e), s = n(this), u = s + "";
    return s == null && (u = s = (this.style.removeProperty(e), ri(this, e))), o === u ? null : o === r && u === i ? a : (i = u, a = t(r = o, s));
  };
}
function PD(e, t) {
  var n, r, i, a = "style." + t, o = "end." + a, s;
  return function() {
    var u = ln(this, e), l = u.on, f = u.value[a] == null ? s || (s = gS(t)) : void 0;
    (l !== n || i !== f) && (r = (n = l).copy()).on(o, i = f), u.on = r;
  };
}
function kD(e, t, n) {
  var r = (e += "") == "transform" ? UC : pS;
  return t == null ? this.styleTween(e, DD(e, r)).on("end.style." + e, gS(e)) : typeof t == "function" ? this.styleTween(e, jD(e, r, Sp(this, "style." + e, t))).each(PD(this._id, e)) : this.styleTween(e, qD(e, r, t), n).on("end.style." + e, null);
}
function FD(e, t, n) {
  return function(r) {
    this.style.setProperty(e, t.call(this, r), n);
  };
}
function BD(e, t, n) {
  var r, i;
  function a() {
    var o = t.apply(this, arguments);
    return o !== i && (r = (i = o) && FD(e, o, n)), r;
  }
  return a._value = t, a;
}
function UD(e, t, n) {
  var r = "style." + (e += "");
  if (arguments.length < 2)
    return (r = this.tween(r)) && r._value;
  if (t == null)
    return this.tween(r, null);
  if (typeof t != "function")
    throw new Error();
  return this.tween(r, BD(e, t, n ?? ""));
}
function HD(e) {
  return function() {
    this.textContent = e;
  };
}
function GD(e) {
  return function() {
    var t = e(this);
    this.textContent = t ?? "";
  };
}
function VD(e) {
  return this.tween("text", typeof e == "function" ? GD(Sp(this, "text", e)) : HD(e == null ? "" : e + ""));
}
function WD(e) {
  return function(t) {
    this.textContent = e.call(this, t);
  };
}
function JD(e) {
  var t, n;
  function r() {
    var i = e.apply(this, arguments);
    return i !== n && (t = (n = i) && WD(i)), t;
  }
  return r._value = e, r;
}
function zD(e) {
  var t = "text";
  if (arguments.length < 1)
    return (t = this.tween(t)) && t._value;
  if (e == null)
    return this.tween(t, null);
  if (typeof e != "function")
    throw new Error();
  return this.tween(t, JD(e));
}
function KD() {
  for (var e = this._name, t = this._id, n = yS(), r = this._groups, i = r.length, a = 0; a < i; ++a)
    for (var o = r[a], s = o.length, u, l = 0; l < s; ++l)
      if (u = o[l]) {
        var f = Bt(u, t);
        Oo(u, e, n, l, o, {
          time: f.time + f.delay + f.duration,
          delay: 0,
          duration: f.duration,
          ease: f.ease
        });
      }
  return new Tn(r, this._parents, e, n);
}
function XD() {
  var e, t, n = this, r = n._id, i = n.size();
  return new Promise(function(a, o) {
    var s = { value: o }, u = { value: function() {
      --i === 0 && a();
    } };
    n.each(function() {
      var l = ln(this, r), f = l.on;
      f !== e && (t = (e = f).copy(), t._.cancel.push(s), t._.interrupt.push(s), t._.end.push(u)), l.on = t;
    }), i === 0 && a();
  });
}
var YD = 0;
function Tn(e, t, n, r) {
  this._groups = e, this._parents = t, this._name = n, this._id = r;
}
function yS() {
  return ++YD;
}
var hn = ea.prototype;
Tn.prototype = {
  constructor: Tn,
  select: $D,
  selectAll: LD,
  selectChild: hn.selectChild,
  selectChildren: hn.selectChildren,
  filter: AD,
  merge: RD,
  selection: CD,
  transition: KD,
  call: hn.call,
  nodes: hn.nodes,
  node: hn.node,
  size: hn.size,
  empty: hn.empty,
  each: hn.each,
  on: xD,
  attr: uD,
  attrTween: hD,
  style: kD,
  styleTween: UD,
  text: VD,
  textTween: zD,
  remove: OD,
  tween: tD,
  delay: yD,
  duration: bD,
  ease: wD,
  easeVarying: SD,
  end: XD,
  [Symbol.iterator]: hn[Symbol.iterator]
};
function QD(e) {
  return ((e *= 2) <= 1 ? e * e * e : (e -= 2) * e * e + 2) / 2;
}
var ZD = {
  time: null,
  // Set on use.
  delay: 0,
  duration: 250,
  ease: QD
};
function eq(e, t) {
  for (var n; !(n = e.__transition) || !(n = n[t]); )
    if (!(e = e.parentNode))
      throw new Error(`transition ${t} not found`);
  return n;
}
function tq(e) {
  var t, n;
  e instanceof Tn ? (t = e._id, e = e._name) : (t = yS(), (n = ZD).time = mp(), e = e == null ? null : e + "");
  for (var r = this._groups, i = r.length, a = 0; a < i; ++a)
    for (var o = r[a], s = o.length, u, l = 0; l < s; ++l)
      (u = o[l]) && Oo(u, e, t, l, o, n || eq(u, t));
  return new Tn(r, this._parents, e, t);
}
ea.prototype.interrupt = QC;
ea.prototype.transition = tq;
function nq(e, t) {
  var n, r = 1;
  e == null && (e = 0), t == null && (t = 0);
  function i() {
    var a, o = n.length, s, u = 0, l = 0;
    for (a = 0; a < o; ++a)
      s = n[a], u += s.x, l += s.y;
    for (u = (u / o - e) * r, l = (l / o - t) * r, a = 0; a < o; ++a)
      s = n[a], s.x -= u, s.y -= l;
  }
  return i.initialize = function(a) {
    n = a;
  }, i.x = function(a) {
    return arguments.length ? (e = +a, i) : e;
  }, i.y = function(a) {
    return arguments.length ? (t = +a, i) : t;
  }, i.strength = function(a) {
    return arguments.length ? (r = +a, i) : r;
  }, i;
}
function rq(e) {
  const t = +this._x.call(null, e), n = +this._y.call(null, e);
  return vS(this.cover(t, n), t, n, e);
}
function vS(e, t, n, r) {
  if (isNaN(t) || isNaN(n))
    return e;
  var i, a = e._root, o = { data: r }, s = e._x0, u = e._y0, l = e._x1, f = e._y1, c, p, d, y, v, _, m, E;
  if (!a)
    return e._root = o, e;
  for (; a.length; )
    if ((v = t >= (c = (s + l) / 2)) ? s = c : l = c, (_ = n >= (p = (u + f) / 2)) ? u = p : f = p, i = a, !(a = a[m = _ << 1 | v]))
      return i[m] = o, e;
  if (d = +e._x.call(null, a.data), y = +e._y.call(null, a.data), t === d && n === y)
    return o.next = a, i ? i[m] = o : e._root = o, e;
  do
    i = i ? i[m] = new Array(4) : e._root = new Array(4), (v = t >= (c = (s + l) / 2)) ? s = c : l = c, (_ = n >= (p = (u + f) / 2)) ? u = p : f = p;
  while ((m = _ << 1 | v) === (E = (y >= p) << 1 | d >= c));
  return i[E] = a, i[m] = o, e;
}
function iq(e) {
  var t, n, r = e.length, i, a, o = new Array(r), s = new Array(r), u = 1 / 0, l = 1 / 0, f = -1 / 0, c = -1 / 0;
  for (n = 0; n < r; ++n)
    isNaN(i = +this._x.call(null, t = e[n])) || isNaN(a = +this._y.call(null, t)) || (o[n] = i, s[n] = a, i < u && (u = i), i > f && (f = i), a < l && (l = a), a > c && (c = a));
  if (u > f || l > c)
    return this;
  for (this.cover(u, l).cover(f, c), n = 0; n < r; ++n)
    vS(this, o[n], s[n], e[n]);
  return this;
}
function aq(e, t) {
  if (isNaN(e = +e) || isNaN(t = +t))
    return this;
  var n = this._x0, r = this._y0, i = this._x1, a = this._y1;
  if (isNaN(n))
    i = (n = Math.floor(e)) + 1, a = (r = Math.floor(t)) + 1;
  else {
    for (var o = i - n || 1, s = this._root, u, l; n > e || e >= i || r > t || t >= a; )
      switch (l = (t < r) << 1 | e < n, u = new Array(4), u[l] = s, s = u, o *= 2, l) {
        case 0:
          i = n + o, a = r + o;
          break;
        case 1:
          n = i - o, a = r + o;
          break;
        case 2:
          i = n + o, r = a - o;
          break;
        case 3:
          n = i - o, r = a - o;
          break;
      }
    this._root && this._root.length && (this._root = s);
  }
  return this._x0 = n, this._y0 = r, this._x1 = i, this._y1 = a, this;
}
function oq() {
  var e = [];
  return this.visit(function(t) {
    if (!t.length)
      do
        e.push(t.data);
      while (t = t.next);
  }), e;
}
function sq(e) {
  return arguments.length ? this.cover(+e[0][0], +e[0][1]).cover(+e[1][0], +e[1][1]) : isNaN(this._x0) ? void 0 : [[this._x0, this._y0], [this._x1, this._y1]];
}
function Xe(e, t, n, r, i) {
  this.node = e, this.x0 = t, this.y0 = n, this.x1 = r, this.y1 = i;
}
function uq(e, t, n) {
  var r, i = this._x0, a = this._y0, o, s, u, l, f = this._x1, c = this._y1, p = [], d = this._root, y, v;
  for (d && p.push(new Xe(d, i, a, f, c)), n == null ? n = 1 / 0 : (i = e - n, a = t - n, f = e + n, c = t + n, n *= n); y = p.pop(); )
    if (!(!(d = y.node) || (o = y.x0) > f || (s = y.y0) > c || (u = y.x1) < i || (l = y.y1) < a))
      if (d.length) {
        var _ = (o + u) / 2, m = (s + l) / 2;
        p.push(
          new Xe(d[3], _, m, u, l),
          new Xe(d[2], o, m, _, l),
          new Xe(d[1], _, s, u, m),
          new Xe(d[0], o, s, _, m)
        ), (v = (t >= m) << 1 | e >= _) && (y = p[p.length - 1], p[p.length - 1] = p[p.length - 1 - v], p[p.length - 1 - v] = y);
      } else {
        var E = e - +this._x.call(null, d.data), L = t - +this._y.call(null, d.data), I = E * E + L * L;
        if (I < n) {
          var j = Math.sqrt(n = I);
          i = e - j, a = t - j, f = e + j, c = t + j, r = d.data;
        }
      }
  return r;
}
function lq(e) {
  if (isNaN(f = +this._x.call(null, e)) || isNaN(c = +this._y.call(null, e)))
    return this;
  var t, n = this._root, r, i, a, o = this._x0, s = this._y0, u = this._x1, l = this._y1, f, c, p, d, y, v, _, m;
  if (!n)
    return this;
  if (n.length)
    for (; ; ) {
      if ((y = f >= (p = (o + u) / 2)) ? o = p : u = p, (v = c >= (d = (s + l) / 2)) ? s = d : l = d, t = n, !(n = n[_ = v << 1 | y]))
        return this;
      if (!n.length)
        break;
      (t[_ + 1 & 3] || t[_ + 2 & 3] || t[_ + 3 & 3]) && (r = t, m = _);
    }
  for (; n.data !== e; )
    if (i = n, !(n = n.next))
      return this;
  return (a = n.next) && delete n.next, i ? (a ? i.next = a : delete i.next, this) : t ? (a ? t[_] = a : delete t[_], (n = t[0] || t[1] || t[2] || t[3]) && n === (t[3] || t[2] || t[1] || t[0]) && !n.length && (r ? r[m] = n : this._root = n), this) : (this._root = a, this);
}
function fq(e) {
  for (var t = 0, n = e.length; t < n; ++t)
    this.remove(e[t]);
  return this;
}
function cq() {
  return this._root;
}
function dq() {
  var e = 0;
  return this.visit(function(t) {
    if (!t.length)
      do
        ++e;
      while (t = t.next);
  }), e;
}
function hq(e) {
  var t = [], n, r = this._root, i, a, o, s, u;
  for (r && t.push(new Xe(r, this._x0, this._y0, this._x1, this._y1)); n = t.pop(); )
    if (!e(r = n.node, a = n.x0, o = n.y0, s = n.x1, u = n.y1) && r.length) {
      var l = (a + s) / 2, f = (o + u) / 2;
      (i = r[3]) && t.push(new Xe(i, l, f, s, u)), (i = r[2]) && t.push(new Xe(i, a, f, l, u)), (i = r[1]) && t.push(new Xe(i, l, o, s, f)), (i = r[0]) && t.push(new Xe(i, a, o, l, f));
    }
  return this;
}
function pq(e) {
  var t = [], n = [], r;
  for (this._root && t.push(new Xe(this._root, this._x0, this._y0, this._x1, this._y1)); r = t.pop(); ) {
    var i = r.node;
    if (i.length) {
      var a, o = r.x0, s = r.y0, u = r.x1, l = r.y1, f = (o + u) / 2, c = (s + l) / 2;
      (a = i[0]) && t.push(new Xe(a, o, s, f, c)), (a = i[1]) && t.push(new Xe(a, f, s, u, c)), (a = i[2]) && t.push(new Xe(a, o, c, f, l)), (a = i[3]) && t.push(new Xe(a, f, c, u, l));
    }
    n.push(r);
  }
  for (; r = n.pop(); )
    e(r.node, r.x0, r.y0, r.x1, r.y1);
  return this;
}
function gq(e) {
  return e[0];
}
function yq(e) {
  return arguments.length ? (this._x = e, this) : this._x;
}
function vq(e) {
  return e[1];
}
function _q(e) {
  return arguments.length ? (this._y = e, this) : this._y;
}
function Ap(e, t, n) {
  var r = new Rp(t ?? gq, n ?? vq, NaN, NaN, NaN, NaN);
  return e == null ? r : r.addAll(e);
}
function Rp(e, t, n, r, i, a) {
  this._x = e, this._y = t, this._x0 = n, this._y0 = r, this._x1 = i, this._y1 = a, this._root = void 0;
}
function Wy(e) {
  for (var t = { data: e.data }, n = t; e = e.next; )
    n = n.next = { data: e.data };
  return t;
}
var Qe = Ap.prototype = Rp.prototype;
Qe.copy = function() {
  var e = new Rp(this._x, this._y, this._x0, this._y0, this._x1, this._y1), t = this._root, n, r;
  if (!t)
    return e;
  if (!t.length)
    return e._root = Wy(t), e;
  for (n = [{ source: t, target: e._root = new Array(4) }]; t = n.pop(); )
    for (var i = 0; i < 4; ++i)
      (r = t.source[i]) && (r.length ? n.push({ source: r, target: t.target[i] = new Array(4) }) : t.target[i] = Wy(r));
  return e;
};
Qe.add = rq;
Qe.addAll = iq;
Qe.cover = aq;
Qe.data = oq;
Qe.extent = sq;
Qe.find = uq;
Qe.remove = lq;
Qe.removeAll = fq;
Qe.root = cq;
Qe.size = dq;
Qe.visit = hq;
Qe.visitAfter = pq;
Qe.x = yq;
Qe.y = _q;
function vr(e) {
  return function() {
    return e;
  };
}
function Wn(e) {
  return (e() - 0.5) * 1e-6;
}
function bq(e) {
  return e.x + e.vx;
}
function mq(e) {
  return e.y + e.vy;
}
function wq(e) {
  var t, n, r, i = 1, a = 1;
  typeof e != "function" && (e = vr(e == null ? 1 : +e));
  function o() {
    for (var l, f = t.length, c, p, d, y, v, _, m = 0; m < a; ++m)
      for (c = Ap(t, bq, mq).visitAfter(s), l = 0; l < f; ++l)
        p = t[l], v = n[p.index], _ = v * v, d = p.x + p.vx, y = p.y + p.vy, c.visit(E);
    function E(L, I, j, M, O) {
      var $ = L.data, q = L.r, N = v + q;
      if ($) {
        if ($.index > p.index) {
          var P = d - $.x - $.vx, H = y - $.y - $.vy, G = P * P + H * H;
          G < N * N && (P === 0 && (P = Wn(r), G += P * P), H === 0 && (H = Wn(r), G += H * H), G = (N - (G = Math.sqrt(G))) / G * i, p.vx += (P *= G) * (N = (q *= q) / (_ + q)), p.vy += (H *= G) * N, $.vx -= P * (N = 1 - N), $.vy -= H * N);
        }
        return;
      }
      return I > d + N || M < d - N || j > y + N || O < y - N;
    }
  }
  function s(l) {
    if (l.data)
      return l.r = n[l.data.index];
    for (var f = l.r = 0; f < 4; ++f)
      l[f] && l[f].r > l.r && (l.r = l[f].r);
  }
  function u() {
    if (t) {
      var l, f = t.length, c;
      for (n = new Array(f), l = 0; l < f; ++l)
        c = t[l], n[c.index] = +e(c, l, t);
    }
  }
  return o.initialize = function(l, f) {
    t = l, r = f, u();
  }, o.iterations = function(l) {
    return arguments.length ? (a = +l, o) : a;
  }, o.strength = function(l) {
    return arguments.length ? (i = +l, o) : i;
  }, o.radius = function(l) {
    return arguments.length ? (e = typeof l == "function" ? l : vr(+l), u(), o) : e;
  }, o;
}
function Eq(e) {
  return e.index;
}
function Jy(e, t) {
  var n = e.get(t);
  if (!n)
    throw new Error("node not found: " + t);
  return n;
}
function Sq(e) {
  var t = Eq, n = c, r, i = vr(30), a, o, s, u, l, f = 1;
  e == null && (e = []);
  function c(_) {
    return 1 / Math.min(s[_.source.index], s[_.target.index]);
  }
  function p(_) {
    for (var m = 0, E = e.length; m < f; ++m)
      for (var L = 0, I, j, M, O, $, q, N; L < E; ++L)
        I = e[L], j = I.source, M = I.target, O = M.x + M.vx - j.x - j.vx || Wn(l), $ = M.y + M.vy - j.y - j.vy || Wn(l), q = Math.sqrt(O * O + $ * $), q = (q - a[L]) / q * _ * r[L], O *= q, $ *= q, M.vx -= O * (N = u[L]), M.vy -= $ * N, j.vx += O * (N = 1 - N), j.vy += $ * N;
  }
  function d() {
    if (o) {
      var _, m = o.length, E = e.length, L = new Map(o.map((j, M) => [t(j, M, o), j])), I;
      for (_ = 0, s = new Array(m); _ < E; ++_)
        I = e[_], I.index = _, typeof I.source != "object" && (I.source = Jy(L, I.source)), typeof I.target != "object" && (I.target = Jy(L, I.target)), s[I.source.index] = (s[I.source.index] || 0) + 1, s[I.target.index] = (s[I.target.index] || 0) + 1;
      for (_ = 0, u = new Array(E); _ < E; ++_)
        I = e[_], u[_] = s[I.source.index] / (s[I.source.index] + s[I.target.index]);
      r = new Array(E), y(), a = new Array(E), v();
    }
  }
  function y() {
    if (o)
      for (var _ = 0, m = e.length; _ < m; ++_)
        r[_] = +n(e[_], _, e);
  }
  function v() {
    if (o)
      for (var _ = 0, m = e.length; _ < m; ++_)
        a[_] = +i(e[_], _, e);
  }
  return p.initialize = function(_, m) {
    o = _, l = m, d();
  }, p.links = function(_) {
    return arguments.length ? (e = _, d(), p) : e;
  }, p.id = function(_) {
    return arguments.length ? (t = _, p) : t;
  }, p.iterations = function(_) {
    return arguments.length ? (f = +_, p) : f;
  }, p.strength = function(_) {
    return arguments.length ? (n = typeof _ == "function" ? _ : vr(+_), y(), p) : n;
  }, p.distance = function(_) {
    return arguments.length ? (i = typeof _ == "function" ? _ : vr(+_), v(), p) : i;
  }, p;
}
const Aq = 1664525, Rq = 1013904223, zy = 4294967296;
function Iq() {
  let e = 1;
  return () => (e = (Aq * e + Rq) % zy) / zy;
}
function Tq(e) {
  return e.x;
}
function xq(e) {
  return e.y;
}
var Nq = 10, Oq = Math.PI * (3 - Math.sqrt(5));
function $q(e) {
  var t, n = 1, r = 1e-3, i = 1 - Math.pow(r, 1 / 300), a = 0, o = 0.6, s = /* @__PURE__ */ new Map(), u = wp(c), l = yp("tick", "end"), f = Iq();
  e == null && (e = []);
  function c() {
    p(), l.call("tick", t), n < r && (u.stop(), l.call("end", t));
  }
  function p(v) {
    var _, m = e.length, E;
    v === void 0 && (v = 1);
    for (var L = 0; L < v; ++L)
      for (n += (a - n) * i, s.forEach(function(I) {
        I(n);
      }), _ = 0; _ < m; ++_)
        E = e[_], E.fx == null ? E.x += E.vx *= o : (E.x = E.fx, E.vx = 0), E.fy == null ? E.y += E.vy *= o : (E.y = E.fy, E.vy = 0);
    return t;
  }
  function d() {
    for (var v = 0, _ = e.length, m; v < _; ++v) {
      if (m = e[v], m.index = v, m.fx != null && (m.x = m.fx), m.fy != null && (m.y = m.fy), isNaN(m.x) || isNaN(m.y)) {
        var E = Nq * Math.sqrt(0.5 + v), L = v * Oq;
        m.x = E * Math.cos(L), m.y = E * Math.sin(L);
      }
      (isNaN(m.vx) || isNaN(m.vy)) && (m.vx = m.vy = 0);
    }
  }
  function y(v) {
    return v.initialize && v.initialize(e, f), v;
  }
  return d(), t = {
    tick: p,
    restart: function() {
      return u.restart(c), t;
    },
    stop: function() {
      return u.stop(), t;
    },
    nodes: function(v) {
      return arguments.length ? (e = v, d(), s.forEach(y), t) : e;
    },
    alpha: function(v) {
      return arguments.length ? (n = +v, t) : n;
    },
    alphaMin: function(v) {
      return arguments.length ? (r = +v, t) : r;
    },
    alphaDecay: function(v) {
      return arguments.length ? (i = +v, t) : +i;
    },
    alphaTarget: function(v) {
      return arguments.length ? (a = +v, t) : a;
    },
    velocityDecay: function(v) {
      return arguments.length ? (o = 1 - v, t) : 1 - o;
    },
    randomSource: function(v) {
      return arguments.length ? (f = v, s.forEach(y), t) : f;
    },
    force: function(v, _) {
      return arguments.length > 1 ? (_ == null ? s.delete(v) : s.set(v, y(_)), t) : s.get(v);
    },
    find: function(v, _, m) {
      var E = 0, L = e.length, I, j, M, O, $;
      for (m == null ? m = 1 / 0 : m *= m, E = 0; E < L; ++E)
        O = e[E], I = v - O.x, j = _ - O.y, M = I * I + j * j, M < m && ($ = O, m = M);
      return $;
    },
    on: function(v, _) {
      return arguments.length > 1 ? (l.on(v, _), t) : l.on(v);
    }
  };
}
function Lq() {
  var e, t, n, r, i = vr(-30), a, o = 1, s = 1 / 0, u = 0.81;
  function l(d) {
    var y, v = e.length, _ = Ap(e, Tq, xq).visitAfter(c);
    for (r = d, y = 0; y < v; ++y)
      t = e[y], _.visit(p);
  }
  function f() {
    if (e) {
      var d, y = e.length, v;
      for (a = new Array(y), d = 0; d < y; ++d)
        v = e[d], a[v.index] = +i(v, d, e);
    }
  }
  function c(d) {
    var y = 0, v, _, m = 0, E, L, I;
    if (d.length) {
      for (E = L = I = 0; I < 4; ++I)
        (v = d[I]) && (_ = Math.abs(v.value)) && (y += v.value, m += _, E += _ * v.x, L += _ * v.y);
      d.x = E / m, d.y = L / m;
    } else {
      v = d, v.x = v.data.x, v.y = v.data.y;
      do
        y += a[v.data.index];
      while (v = v.next);
    }
    d.value = y;
  }
  function p(d, y, v, _) {
    if (!d.value)
      return !0;
    var m = d.x - t.x, E = d.y - t.y, L = _ - y, I = m * m + E * E;
    if (L * L / u < I)
      return I < s && (m === 0 && (m = Wn(n), I += m * m), E === 0 && (E = Wn(n), I += E * E), I < o && (I = Math.sqrt(o * I)), t.vx += m * d.value * r / I, t.vy += E * d.value * r / I), !0;
    if (d.length || I >= s)
      return;
    (d.data !== t || d.next) && (m === 0 && (m = Wn(n), I += m * m), E === 0 && (E = Wn(n), I += E * E), I < o && (I = Math.sqrt(o * I)));
    do
      d.data !== t && (L = a[d.data.index] * r / I, t.vx += m * L, t.vy += E * L);
    while (d = d.next);
  }
  return l.initialize = function(d, y) {
    e = d, n = y, f();
  }, l.strength = function(d) {
    return arguments.length ? (i = typeof d == "function" ? d : vr(+d), f(), l) : i;
  }, l.distanceMin = function(d) {
    return arguments.length ? (o = d * d, l) : Math.sqrt(o);
  }, l.distanceMax = function(d) {
    return arguments.length ? (s = d * d, l) : Math.sqrt(s);
  }, l.theta = function(d) {
    return arguments.length ? (u = d * d, l) : Math.sqrt(u);
  }, l;
}
function Ii(e, t, n) {
  this.k = e, this.x = t, this.y = n;
}
Ii.prototype = {
  constructor: Ii,
  scale: function(e) {
    return e === 1 ? this : new Ii(this.k * e, this.x, this.y);
  },
  translate: function(e, t) {
    return e === 0 & t === 0 ? this : new Ii(this.k, this.x + this.k * e, this.y + this.k * t);
  },
  apply: function(e) {
    return [e[0] * this.k + this.x, e[1] * this.k + this.y];
  },
  applyX: function(e) {
    return e * this.k + this.x;
  },
  applyY: function(e) {
    return e * this.k + this.y;
  },
  invert: function(e) {
    return [(e[0] - this.x) / this.k, (e[1] - this.y) / this.k];
  },
  invertX: function(e) {
    return (e - this.x) / this.k;
  },
  invertY: function(e) {
    return (e - this.y) / this.k;
  },
  rescaleX: function(e) {
    return e.copy().domain(e.range().map(this.invertX, this).map(e.invert, e));
  },
  rescaleY: function(e) {
    return e.copy().domain(e.range().map(this.invertY, this).map(e.invert, e));
  },
  toString: function() {
    return "translate(" + this.x + "," + this.y + ") scale(" + this.k + ")";
  }
};
Ii.prototype;
function Ip(e) {
  throw new Error('Could not dynamically require "' + e + '". Please configure the dynamicRequireTargets or/and ignoreDynamicRequires option of @rollup/plugin-commonjs appropriately for this require call to work.');
}
var Hs, Ky;
function Mq() {
  if (Ky)
    return Hs;
  Ky = 1;
  function e() {
    this.__data__ = [], this.size = 0;
  }
  return Hs = e, Hs;
}
var Gs, Xy;
function ui() {
  if (Xy)
    return Gs;
  Xy = 1;
  function e(t, n) {
    return t === n || t !== t && n !== n;
  }
  return Gs = e, Gs;
}
var Vs, Yy;
function $o() {
  if (Yy)
    return Vs;
  Yy = 1;
  var e = ui();
  function t(n, r) {
    for (var i = n.length; i--; )
      if (e(n[i][0], r))
        return i;
    return -1;
  }
  return Vs = t, Vs;
}
var Ws, Qy;
function Cq() {
  if (Qy)
    return Ws;
  Qy = 1;
  var e = $o(), t = Array.prototype, n = t.splice;
  function r(i) {
    var a = this.__data__, o = e(a, i);
    if (o < 0)
      return !1;
    var s = a.length - 1;
    return o == s ? a.pop() : n.call(a, o, 1), --this.size, !0;
  }
  return Ws = r, Ws;
}
var Js, Zy;
function Dq() {
  if (Zy)
    return Js;
  Zy = 1;
  var e = $o();
  function t(n) {
    var r = this.__data__, i = e(r, n);
    return i < 0 ? void 0 : r[i][1];
  }
  return Js = t, Js;
}
var zs, ev;
function qq() {
  if (ev)
    return zs;
  ev = 1;
  var e = $o();
  function t(n) {
    return e(this.__data__, n) > -1;
  }
  return zs = t, zs;
}
var Ks, tv;
function jq() {
  if (tv)
    return Ks;
  tv = 1;
  var e = $o();
  function t(n, r) {
    var i = this.__data__, a = e(i, n);
    return a < 0 ? (++this.size, i.push([n, r])) : i[a][1] = r, this;
  }
  return Ks = t, Ks;
}
var Xs, nv;
function Lo() {
  if (nv)
    return Xs;
  nv = 1;
  var e = Mq(), t = Cq(), n = Dq(), r = qq(), i = jq();
  function a(o) {
    var s = -1, u = o == null ? 0 : o.length;
    for (this.clear(); ++s < u; ) {
      var l = o[s];
      this.set(l[0], l[1]);
    }
  }
  return a.prototype.clear = e, a.prototype.delete = t, a.prototype.get = n, a.prototype.has = r, a.prototype.set = i, Xs = a, Xs;
}
var Ys, rv;
function Pq() {
  if (rv)
    return Ys;
  rv = 1;
  var e = Lo();
  function t() {
    this.__data__ = new e(), this.size = 0;
  }
  return Ys = t, Ys;
}
var Qs, iv;
function kq() {
  if (iv)
    return Qs;
  iv = 1;
  function e(t) {
    var n = this.__data__, r = n.delete(t);
    return this.size = n.size, r;
  }
  return Qs = e, Qs;
}
var Zs, av;
function Fq() {
  if (av)
    return Zs;
  av = 1;
  function e(t) {
    return this.__data__.get(t);
  }
  return Zs = e, Zs;
}
var eu, ov;
function Bq() {
  if (ov)
    return eu;
  ov = 1;
  function e(t) {
    return this.__data__.has(t);
  }
  return eu = e, eu;
}
var tu, sv;
function _S() {
  if (sv)
    return tu;
  sv = 1;
  var e = typeof X == "object" && X && X.Object === Object && X;
  return tu = e, tu;
}
var nu, uv;
function Ut() {
  if (uv)
    return nu;
  uv = 1;
  var e = _S(), t = typeof self == "object" && self && self.Object === Object && self, n = e || t || Function("return this")();
  return nu = n, nu;
}
var ru, lv;
function li() {
  if (lv)
    return ru;
  lv = 1;
  var e = Ut(), t = e.Symbol;
  return ru = t, ru;
}
var iu, fv;
function Uq() {
  if (fv)
    return iu;
  fv = 1;
  var e = li(), t = Object.prototype, n = t.hasOwnProperty, r = t.toString, i = e ? e.toStringTag : void 0;
  function a(o) {
    var s = n.call(o, i), u = o[i];
    try {
      o[i] = void 0;
      var l = !0;
    } catch {
    }
    var f = r.call(o);
    return l && (s ? o[i] = u : delete o[i]), f;
  }
  return iu = a, iu;
}
var au, cv;
function Hq() {
  if (cv)
    return au;
  cv = 1;
  var e = Object.prototype, t = e.toString;
  function n(r) {
    return t.call(r);
  }
  return au = n, au;
}
var ou, dv;
function Or() {
  if (dv)
    return ou;
  dv = 1;
  var e = li(), t = Uq(), n = Hq(), r = "[object Null]", i = "[object Undefined]", a = e ? e.toStringTag : void 0;
  function o(s) {
    return s == null ? s === void 0 ? i : r : a && a in Object(s) ? t(s) : n(s);
  }
  return ou = o, ou;
}
var su, hv;
function xt() {
  if (hv)
    return su;
  hv = 1;
  function e(t) {
    var n = typeof t;
    return t != null && (n == "object" || n == "function");
  }
  return su = e, su;
}
var uu, pv;
function na() {
  if (pv)
    return uu;
  pv = 1;
  var e = Or(), t = xt(), n = "[object AsyncFunction]", r = "[object Function]", i = "[object GeneratorFunction]", a = "[object Proxy]";
  function o(s) {
    if (!t(s))
      return !1;
    var u = e(s);
    return u == r || u == i || u == n || u == a;
  }
  return uu = o, uu;
}
var lu, gv;
function Gq() {
  if (gv)
    return lu;
  gv = 1;
  var e = Ut(), t = e["__core-js_shared__"];
  return lu = t, lu;
}
var fu, yv;
function Vq() {
  if (yv)
    return fu;
  yv = 1;
  var e = Gq(), t = function() {
    var r = /[^.]+$/.exec(e && e.keys && e.keys.IE_PROTO || "");
    return r ? "Symbol(src)_1." + r : "";
  }();
  function n(r) {
    return !!t && t in r;
  }
  return fu = n, fu;
}
var cu, vv;
function bS() {
  if (vv)
    return cu;
  vv = 1;
  var e = Function.prototype, t = e.toString;
  function n(r) {
    if (r != null) {
      try {
        return t.call(r);
      } catch {
      }
      try {
        return r + "";
      } catch {
      }
    }
    return "";
  }
  return cu = n, cu;
}
var du, _v;
function Wq() {
  if (_v)
    return du;
  _v = 1;
  var e = na(), t = Vq(), n = xt(), r = bS(), i = /[\\^$.*+?()[\]{}|]/g, a = /^\[object .+?Constructor\]$/, o = Function.prototype, s = Object.prototype, u = o.toString, l = s.hasOwnProperty, f = RegExp(
    "^" + u.call(l).replace(i, "\\$&").replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, "$1.*?") + "$"
  );
  function c(p) {
    if (!n(p) || t(p))
      return !1;
    var d = e(p) ? f : a;
    return d.test(r(p));
  }
  return du = c, du;
}
var hu, bv;
function Jq() {
  if (bv)
    return hu;
  bv = 1;
  function e(t, n) {
    return t == null ? void 0 : t[n];
  }
  return hu = e, hu;
}
var pu, mv;
function $r() {
  if (mv)
    return pu;
  mv = 1;
  var e = Wq(), t = Jq();
  function n(r, i) {
    var a = t(r, i);
    return e(a) ? a : void 0;
  }
  return pu = n, pu;
}
var gu, wv;
function Tp() {
  if (wv)
    return gu;
  wv = 1;
  var e = $r(), t = Ut(), n = e(t, "Map");
  return gu = n, gu;
}
var yu, Ev;
function Mo() {
  if (Ev)
    return yu;
  Ev = 1;
  var e = $r(), t = e(Object, "create");
  return yu = t, yu;
}
var vu, Sv;
function zq() {
  if (Sv)
    return vu;
  Sv = 1;
  var e = Mo();
  function t() {
    this.__data__ = e ? e(null) : {}, this.size = 0;
  }
  return vu = t, vu;
}
var _u, Av;
function Kq() {
  if (Av)
    return _u;
  Av = 1;
  function e(t) {
    var n = this.has(t) && delete this.__data__[t];
    return this.size -= n ? 1 : 0, n;
  }
  return _u = e, _u;
}
var bu, Rv;
function Xq() {
  if (Rv)
    return bu;
  Rv = 1;
  var e = Mo(), t = "__lodash_hash_undefined__", n = Object.prototype, r = n.hasOwnProperty;
  function i(a) {
    var o = this.__data__;
    if (e) {
      var s = o[a];
      return s === t ? void 0 : s;
    }
    return r.call(o, a) ? o[a] : void 0;
  }
  return bu = i, bu;
}
var mu, Iv;
function Yq() {
  if (Iv)
    return mu;
  Iv = 1;
  var e = Mo(), t = Object.prototype, n = t.hasOwnProperty;
  function r(i) {
    var a = this.__data__;
    return e ? a[i] !== void 0 : n.call(a, i);
  }
  return mu = r, mu;
}
var wu, Tv;
function Qq() {
  if (Tv)
    return wu;
  Tv = 1;
  var e = Mo(), t = "__lodash_hash_undefined__";
  function n(r, i) {
    var a = this.__data__;
    return this.size += this.has(r) ? 0 : 1, a[r] = e && i === void 0 ? t : i, this;
  }
  return wu = n, wu;
}
var Eu, xv;
function Zq() {
  if (xv)
    return Eu;
  xv = 1;
  var e = zq(), t = Kq(), n = Xq(), r = Yq(), i = Qq();
  function a(o) {
    var s = -1, u = o == null ? 0 : o.length;
    for (this.clear(); ++s < u; ) {
      var l = o[s];
      this.set(l[0], l[1]);
    }
  }
  return a.prototype.clear = e, a.prototype.delete = t, a.prototype.get = n, a.prototype.has = r, a.prototype.set = i, Eu = a, Eu;
}
var Su, Nv;
function ej() {
  if (Nv)
    return Su;
  Nv = 1;
  var e = Zq(), t = Lo(), n = Tp();
  function r() {
    this.size = 0, this.__data__ = {
      hash: new e(),
      map: new (n || t)(),
      string: new e()
    };
  }
  return Su = r, Su;
}
var Au, Ov;
function tj() {
  if (Ov)
    return Au;
  Ov = 1;
  function e(t) {
    var n = typeof t;
    return n == "string" || n == "number" || n == "symbol" || n == "boolean" ? t !== "__proto__" : t === null;
  }
  return Au = e, Au;
}
var Ru, $v;
function Co() {
  if ($v)
    return Ru;
  $v = 1;
  var e = tj();
  function t(n, r) {
    var i = n.__data__;
    return e(r) ? i[typeof r == "string" ? "string" : "hash"] : i.map;
  }
  return Ru = t, Ru;
}
var Iu, Lv;
function nj() {
  if (Lv)
    return Iu;
  Lv = 1;
  var e = Co();
  function t(n) {
    var r = e(this, n).delete(n);
    return this.size -= r ? 1 : 0, r;
  }
  return Iu = t, Iu;
}
var Tu, Mv;
function rj() {
  if (Mv)
    return Tu;
  Mv = 1;
  var e = Co();
  function t(n) {
    return e(this, n).get(n);
  }
  return Tu = t, Tu;
}
var xu, Cv;
function ij() {
  if (Cv)
    return xu;
  Cv = 1;
  var e = Co();
  function t(n) {
    return e(this, n).has(n);
  }
  return xu = t, xu;
}
var Nu, Dv;
function aj() {
  if (Dv)
    return Nu;
  Dv = 1;
  var e = Co();
  function t(n, r) {
    var i = e(this, n), a = i.size;
    return i.set(n, r), this.size += i.size == a ? 0 : 1, this;
  }
  return Nu = t, Nu;
}
var Ou, qv;
function xp() {
  if (qv)
    return Ou;
  qv = 1;
  var e = ej(), t = nj(), n = rj(), r = ij(), i = aj();
  function a(o) {
    var s = -1, u = o == null ? 0 : o.length;
    for (this.clear(); ++s < u; ) {
      var l = o[s];
      this.set(l[0], l[1]);
    }
  }
  return a.prototype.clear = e, a.prototype.delete = t, a.prototype.get = n, a.prototype.has = r, a.prototype.set = i, Ou = a, Ou;
}
var $u, jv;
function oj() {
  if (jv)
    return $u;
  jv = 1;
  var e = Lo(), t = Tp(), n = xp(), r = 200;
  function i(a, o) {
    var s = this.__data__;
    if (s instanceof e) {
      var u = s.__data__;
      if (!t || u.length < r - 1)
        return u.push([a, o]), this.size = ++s.size, this;
      s = this.__data__ = new n(u);
    }
    return s.set(a, o), this.size = s.size, this;
  }
  return $u = i, $u;
}
var Lu, Pv;
function Do() {
  if (Pv)
    return Lu;
  Pv = 1;
  var e = Lo(), t = Pq(), n = kq(), r = Fq(), i = Bq(), a = oj();
  function o(s) {
    var u = this.__data__ = new e(s);
    this.size = u.size;
  }
  return o.prototype.clear = t, o.prototype.delete = n, o.prototype.get = r, o.prototype.has = i, o.prototype.set = a, Lu = o, Lu;
}
var Mu, kv;
function Np() {
  if (kv)
    return Mu;
  kv = 1;
  function e(t, n) {
    for (var r = -1, i = t == null ? 0 : t.length; ++r < i && n(t[r], r, t) !== !1; )
      ;
    return t;
  }
  return Mu = e, Mu;
}
var Cu, Fv;
function mS() {
  if (Fv)
    return Cu;
  Fv = 1;
  var e = $r(), t = function() {
    try {
      var n = e(Object, "defineProperty");
      return n({}, "", {}), n;
    } catch {
    }
  }();
  return Cu = t, Cu;
}
var Du, Bv;
function qo() {
  if (Bv)
    return Du;
  Bv = 1;
  var e = mS();
  function t(n, r, i) {
    r == "__proto__" && e ? e(n, r, {
      configurable: !0,
      enumerable: !0,
      value: i,
      writable: !0
    }) : n[r] = i;
  }
  return Du = t, Du;
}
var qu, Uv;
function jo() {
  if (Uv)
    return qu;
  Uv = 1;
  var e = qo(), t = ui(), n = Object.prototype, r = n.hasOwnProperty;
  function i(a, o, s) {
    var u = a[o];
    (!(r.call(a, o) && t(u, s)) || s === void 0 && !(o in a)) && e(a, o, s);
  }
  return qu = i, qu;
}
var ju, Hv;
function ra() {
  if (Hv)
    return ju;
  Hv = 1;
  var e = jo(), t = qo();
  function n(r, i, a, o) {
    var s = !a;
    a || (a = {});
    for (var u = -1, l = i.length; ++u < l; ) {
      var f = i[u], c = o ? o(a[f], r[f], f, a, r) : void 0;
      c === void 0 && (c = r[f]), s ? t(a, f, c) : e(a, f, c);
    }
    return a;
  }
  return ju = n, ju;
}
var Pu, Gv;
function sj() {
  if (Gv)
    return Pu;
  Gv = 1;
  function e(t, n) {
    for (var r = -1, i = Array(t); ++r < t; )
      i[r] = n(r);
    return i;
  }
  return Pu = e, Pu;
}
var ku, Vv;
function fn() {
  if (Vv)
    return ku;
  Vv = 1;
  function e(t) {
    return t != null && typeof t == "object";
  }
  return ku = e, ku;
}
var Fu, Wv;
function uj() {
  if (Wv)
    return Fu;
  Wv = 1;
  var e = Or(), t = fn(), n = "[object Arguments]";
  function r(i) {
    return t(i) && e(i) == n;
  }
  return Fu = r, Fu;
}
var Bu, Jv;
function ia() {
  if (Jv)
    return Bu;
  Jv = 1;
  var e = uj(), t = fn(), n = Object.prototype, r = n.hasOwnProperty, i = n.propertyIsEnumerable, a = e(function() {
    return arguments;
  }()) ? e : function(o) {
    return t(o) && r.call(o, "callee") && !i.call(o, "callee");
  };
  return Bu = a, Bu;
}
var Uu, zv;
function Oe() {
  if (zv)
    return Uu;
  zv = 1;
  var e = Array.isArray;
  return Uu = e, Uu;
}
var Ti = { exports: {} }, Hu, Kv;
function lj() {
  if (Kv)
    return Hu;
  Kv = 1;
  function e() {
    return !1;
  }
  return Hu = e, Hu;
}
Ti.exports;
var Xv;
function fi() {
  return Xv || (Xv = 1, function(e, t) {
    var n = Ut(), r = lj(), i = t && !t.nodeType && t, a = i && !0 && e && !e.nodeType && e, o = a && a.exports === i, s = o ? n.Buffer : void 0, u = s ? s.isBuffer : void 0, l = u || r;
    e.exports = l;
  }(Ti, Ti.exports)), Ti.exports;
}
var Gu, Yv;
function Po() {
  if (Yv)
    return Gu;
  Yv = 1;
  var e = 9007199254740991, t = /^(?:0|[1-9]\d*)$/;
  function n(r, i) {
    var a = typeof r;
    return i = i ?? e, !!i && (a == "number" || a != "symbol" && t.test(r)) && r > -1 && r % 1 == 0 && r < i;
  }
  return Gu = n, Gu;
}
var Vu, Qv;
function Op() {
  if (Qv)
    return Vu;
  Qv = 1;
  var e = 9007199254740991;
  function t(n) {
    return typeof n == "number" && n > -1 && n % 1 == 0 && n <= e;
  }
  return Vu = t, Vu;
}
var Wu, Zv;
function fj() {
  if (Zv)
    return Wu;
  Zv = 1;
  var e = Or(), t = Op(), n = fn(), r = "[object Arguments]", i = "[object Array]", a = "[object Boolean]", o = "[object Date]", s = "[object Error]", u = "[object Function]", l = "[object Map]", f = "[object Number]", c = "[object Object]", p = "[object RegExp]", d = "[object Set]", y = "[object String]", v = "[object WeakMap]", _ = "[object ArrayBuffer]", m = "[object DataView]", E = "[object Float32Array]", L = "[object Float64Array]", I = "[object Int8Array]", j = "[object Int16Array]", M = "[object Int32Array]", O = "[object Uint8Array]", $ = "[object Uint8ClampedArray]", q = "[object Uint16Array]", N = "[object Uint32Array]", P = {};
  P[E] = P[L] = P[I] = P[j] = P[M] = P[O] = P[$] = P[q] = P[N] = !0, P[r] = P[i] = P[_] = P[a] = P[m] = P[o] = P[s] = P[u] = P[l] = P[f] = P[c] = P[p] = P[d] = P[y] = P[v] = !1;
  function H(G) {
    return n(G) && t(G.length) && !!P[e(G)];
  }
  return Wu = H, Wu;
}
var Ju, e_;
function ko() {
  if (e_)
    return Ju;
  e_ = 1;
  function e(t) {
    return function(n) {
      return t(n);
    };
  }
  return Ju = e, Ju;
}
var xi = { exports: {} };
xi.exports;
var t_;
function $p() {
  return t_ || (t_ = 1, function(e, t) {
    var n = _S(), r = t && !t.nodeType && t, i = r && !0 && e && !e.nodeType && e, a = i && i.exports === r, o = a && n.process, s = function() {
      try {
        var u = i && i.require && i.require("util").types;
        return u || o && o.binding && o.binding("util");
      } catch {
      }
    }();
    e.exports = s;
  }(xi, xi.exports)), xi.exports;
}
var zu, n_;
function aa() {
  if (n_)
    return zu;
  n_ = 1;
  var e = fj(), t = ko(), n = $p(), r = n && n.isTypedArray, i = r ? t(r) : e;
  return zu = i, zu;
}
var Ku, r_;
function wS() {
  if (r_)
    return Ku;
  r_ = 1;
  var e = sj(), t = ia(), n = Oe(), r = fi(), i = Po(), a = aa(), o = Object.prototype, s = o.hasOwnProperty;
  function u(l, f) {
    var c = n(l), p = !c && t(l), d = !c && !p && r(l), y = !c && !p && !d && a(l), v = c || p || d || y, _ = v ? e(l.length, String) : [], m = _.length;
    for (var E in l)
      (f || s.call(l, E)) && !(v && // Safari 9 has enumerable `arguments.length` in strict mode.
      (E == "length" || // Node.js 0.10 has enumerable non-index properties on buffers.
      d && (E == "offset" || E == "parent") || // PhantomJS 2 has enumerable non-index properties on typed arrays.
      y && (E == "buffer" || E == "byteLength" || E == "byteOffset") || // Skip index properties.
      i(E, m))) && _.push(E);
    return _;
  }
  return Ku = u, Ku;
}
var Xu, i_;
function Fo() {
  if (i_)
    return Xu;
  i_ = 1;
  var e = Object.prototype;
  function t(n) {
    var r = n && n.constructor, i = typeof r == "function" && r.prototype || e;
    return n === i;
  }
  return Xu = t, Xu;
}
var Yu, a_;
function ES() {
  if (a_)
    return Yu;
  a_ = 1;
  function e(t, n) {
    return function(r) {
      return t(n(r));
    };
  }
  return Yu = e, Yu;
}
var Qu, o_;
function cj() {
  if (o_)
    return Qu;
  o_ = 1;
  var e = ES(), t = e(Object.keys, Object);
  return Qu = t, Qu;
}
var Zu, s_;
function Lp() {
  if (s_)
    return Zu;
  s_ = 1;
  var e = Fo(), t = cj(), n = Object.prototype, r = n.hasOwnProperty;
  function i(a) {
    if (!e(a))
      return t(a);
    var o = [];
    for (var s in Object(a))
      r.call(a, s) && s != "constructor" && o.push(s);
    return o;
  }
  return Zu = i, Zu;
}
var el, u_;
function Mn() {
  if (u_)
    return el;
  u_ = 1;
  var e = na(), t = Op();
  function n(r) {
    return r != null && t(r.length) && !e(r);
  }
  return el = n, el;
}
var tl, l_;
function Zn() {
  if (l_)
    return tl;
  l_ = 1;
  var e = wS(), t = Lp(), n = Mn();
  function r(i) {
    return n(i) ? e(i) : t(i);
  }
  return tl = r, tl;
}
var nl, f_;
function dj() {
  if (f_)
    return nl;
  f_ = 1;
  var e = ra(), t = Zn();
  function n(r, i) {
    return r && e(i, t(i), r);
  }
  return nl = n, nl;
}
var rl, c_;
function hj() {
  if (c_)
    return rl;
  c_ = 1;
  function e(t) {
    var n = [];
    if (t != null)
      for (var r in Object(t))
        n.push(r);
    return n;
  }
  return rl = e, rl;
}
var il, d_;
function pj() {
  if (d_)
    return il;
  d_ = 1;
  var e = xt(), t = Fo(), n = hj(), r = Object.prototype, i = r.hasOwnProperty;
  function a(o) {
    if (!e(o))
      return n(o);
    var s = t(o), u = [];
    for (var l in o)
      l == "constructor" && (s || !i.call(o, l)) || u.push(l);
    return u;
  }
  return il = a, il;
}
var al, h_;
function Lr() {
  if (h_)
    return al;
  h_ = 1;
  var e = wS(), t = pj(), n = Mn();
  function r(i) {
    return n(i) ? e(i, !0) : t(i);
  }
  return al = r, al;
}
var ol, p_;
function gj() {
  if (p_)
    return ol;
  p_ = 1;
  var e = ra(), t = Lr();
  function n(r, i) {
    return r && e(i, t(i), r);
  }
  return ol = n, ol;
}
var Ni = { exports: {} };
Ni.exports;
var g_;
function SS() {
  return g_ || (g_ = 1, function(e, t) {
    var n = Ut(), r = t && !t.nodeType && t, i = r && !0 && e && !e.nodeType && e, a = i && i.exports === r, o = a ? n.Buffer : void 0, s = o ? o.allocUnsafe : void 0;
    function u(l, f) {
      if (f)
        return l.slice();
      var c = l.length, p = s ? s(c) : new l.constructor(c);
      return l.copy(p), p;
    }
    e.exports = u;
  }(Ni, Ni.exports)), Ni.exports;
}
var sl, y_;
function AS() {
  if (y_)
    return sl;
  y_ = 1;
  function e(t, n) {
    var r = -1, i = t.length;
    for (n || (n = Array(i)); ++r < i; )
      n[r] = t[r];
    return n;
  }
  return sl = e, sl;
}
var ul, v_;
function RS() {
  if (v_)
    return ul;
  v_ = 1;
  function e(t, n) {
    for (var r = -1, i = t == null ? 0 : t.length, a = 0, o = []; ++r < i; ) {
      var s = t[r];
      n(s, r, t) && (o[a++] = s);
    }
    return o;
  }
  return ul = e, ul;
}
var ll, __;
function IS() {
  if (__)
    return ll;
  __ = 1;
  function e() {
    return [];
  }
  return ll = e, ll;
}
var fl, b_;
function Mp() {
  if (b_)
    return fl;
  b_ = 1;
  var e = RS(), t = IS(), n = Object.prototype, r = n.propertyIsEnumerable, i = Object.getOwnPropertySymbols, a = i ? function(o) {
    return o == null ? [] : (o = Object(o), e(i(o), function(s) {
      return r.call(o, s);
    }));
  } : t;
  return fl = a, fl;
}
var cl, m_;
function yj() {
  if (m_)
    return cl;
  m_ = 1;
  var e = ra(), t = Mp();
  function n(r, i) {
    return e(r, t(r), i);
  }
  return cl = n, cl;
}
var dl, w_;
function Cp() {
  if (w_)
    return dl;
  w_ = 1;
  function e(t, n) {
    for (var r = -1, i = n.length, a = t.length; ++r < i; )
      t[a + r] = n[r];
    return t;
  }
  return dl = e, dl;
}
var hl, E_;
function Bo() {
  if (E_)
    return hl;
  E_ = 1;
  var e = ES(), t = e(Object.getPrototypeOf, Object);
  return hl = t, hl;
}
var pl, S_;
function TS() {
  if (S_)
    return pl;
  S_ = 1;
  var e = Cp(), t = Bo(), n = Mp(), r = IS(), i = Object.getOwnPropertySymbols, a = i ? function(o) {
    for (var s = []; o; )
      e(s, n(o)), o = t(o);
    return s;
  } : r;
  return pl = a, pl;
}
var gl, A_;
function vj() {
  if (A_)
    return gl;
  A_ = 1;
  var e = ra(), t = TS();
  function n(r, i) {
    return e(r, t(r), i);
  }
  return gl = n, gl;
}
var yl, R_;
function xS() {
  if (R_)
    return yl;
  R_ = 1;
  var e = Cp(), t = Oe();
  function n(r, i, a) {
    var o = i(r);
    return t(r) ? o : e(o, a(r));
  }
  return yl = n, yl;
}
var vl, I_;
function NS() {
  if (I_)
    return vl;
  I_ = 1;
  var e = xS(), t = Mp(), n = Zn();
  function r(i) {
    return e(i, n, t);
  }
  return vl = r, vl;
}
var _l, T_;
function _j() {
  if (T_)
    return _l;
  T_ = 1;
  var e = xS(), t = TS(), n = Lr();
  function r(i) {
    return e(i, n, t);
  }
  return _l = r, _l;
}
var bl, x_;
function bj() {
  if (x_)
    return bl;
  x_ = 1;
  var e = $r(), t = Ut(), n = e(t, "DataView");
  return bl = n, bl;
}
var ml, N_;
function mj() {
  if (N_)
    return ml;
  N_ = 1;
  var e = $r(), t = Ut(), n = e(t, "Promise");
  return ml = n, ml;
}
var wl, O_;
function OS() {
  if (O_)
    return wl;
  O_ = 1;
  var e = $r(), t = Ut(), n = e(t, "Set");
  return wl = n, wl;
}
var El, $_;
function wj() {
  if ($_)
    return El;
  $_ = 1;
  var e = $r(), t = Ut(), n = e(t, "WeakMap");
  return El = n, El;
}
var Sl, L_;
function ci() {
  if (L_)
    return Sl;
  L_ = 1;
  var e = bj(), t = Tp(), n = mj(), r = OS(), i = wj(), a = Or(), o = bS(), s = "[object Map]", u = "[object Object]", l = "[object Promise]", f = "[object Set]", c = "[object WeakMap]", p = "[object DataView]", d = o(e), y = o(t), v = o(n), _ = o(r), m = o(i), E = a;
  return (e && E(new e(new ArrayBuffer(1))) != p || t && E(new t()) != s || n && E(n.resolve()) != l || r && E(new r()) != f || i && E(new i()) != c) && (E = function(L) {
    var I = a(L), j = I == u ? L.constructor : void 0, M = j ? o(j) : "";
    if (M)
      switch (M) {
        case d:
          return p;
        case y:
          return s;
        case v:
          return l;
        case _:
          return f;
        case m:
          return c;
      }
    return I;
  }), Sl = E, Sl;
}
var Al, M_;
function Ej() {
  if (M_)
    return Al;
  M_ = 1;
  var e = Object.prototype, t = e.hasOwnProperty;
  function n(r) {
    var i = r.length, a = new r.constructor(i);
    return i && typeof r[0] == "string" && t.call(r, "index") && (a.index = r.index, a.input = r.input), a;
  }
  return Al = n, Al;
}
var Rl, C_;
function $S() {
  if (C_)
    return Rl;
  C_ = 1;
  var e = Ut(), t = e.Uint8Array;
  return Rl = t, Rl;
}
var Il, D_;
function Dp() {
  if (D_)
    return Il;
  D_ = 1;
  var e = $S();
  function t(n) {
    var r = new n.constructor(n.byteLength);
    return new e(r).set(new e(n)), r;
  }
  return Il = t, Il;
}
var Tl, q_;
function Sj() {
  if (q_)
    return Tl;
  q_ = 1;
  var e = Dp();
  function t(n, r) {
    var i = r ? e(n.buffer) : n.buffer;
    return new n.constructor(i, n.byteOffset, n.byteLength);
  }
  return Tl = t, Tl;
}
var xl, j_;
function Aj() {
  if (j_)
    return xl;
  j_ = 1;
  var e = /\w*$/;
  function t(n) {
    var r = new n.constructor(n.source, e.exec(n));
    return r.lastIndex = n.lastIndex, r;
  }
  return xl = t, xl;
}
var Nl, P_;
function Rj() {
  if (P_)
    return Nl;
  P_ = 1;
  var e = li(), t = e ? e.prototype : void 0, n = t ? t.valueOf : void 0;
  function r(i) {
    return n ? Object(n.call(i)) : {};
  }
  return Nl = r, Nl;
}
var Ol, k_;
function LS() {
  if (k_)
    return Ol;
  k_ = 1;
  var e = Dp();
  function t(n, r) {
    var i = r ? e(n.buffer) : n.buffer;
    return new n.constructor(i, n.byteOffset, n.length);
  }
  return Ol = t, Ol;
}
var $l, F_;
function Ij() {
  if (F_)
    return $l;
  F_ = 1;
  var e = Dp(), t = Sj(), n = Aj(), r = Rj(), i = LS(), a = "[object Boolean]", o = "[object Date]", s = "[object Map]", u = "[object Number]", l = "[object RegExp]", f = "[object Set]", c = "[object String]", p = "[object Symbol]", d = "[object ArrayBuffer]", y = "[object DataView]", v = "[object Float32Array]", _ = "[object Float64Array]", m = "[object Int8Array]", E = "[object Int16Array]", L = "[object Int32Array]", I = "[object Uint8Array]", j = "[object Uint8ClampedArray]", M = "[object Uint16Array]", O = "[object Uint32Array]";
  function $(q, N, P) {
    var H = q.constructor;
    switch (N) {
      case d:
        return e(q);
      case a:
      case o:
        return new H(+q);
      case y:
        return t(q, P);
      case v:
      case _:
      case m:
      case E:
      case L:
      case I:
      case j:
      case M:
      case O:
        return i(q, P);
      case s:
        return new H();
      case u:
      case c:
        return new H(q);
      case l:
        return n(q);
      case f:
        return new H();
      case p:
        return r(q);
    }
  }
  return $l = $, $l;
}
var Ll, B_;
function MS() {
  if (B_)
    return Ll;
  B_ = 1;
  var e = xt(), t = Object.create, n = function() {
    function r() {
    }
    return function(i) {
      if (!e(i))
        return {};
      if (t)
        return t(i);
      r.prototype = i;
      var a = new r();
      return r.prototype = void 0, a;
    };
  }();
  return Ll = n, Ll;
}
var Ml, U_;
function CS() {
  if (U_)
    return Ml;
  U_ = 1;
  var e = MS(), t = Bo(), n = Fo();
  function r(i) {
    return typeof i.constructor == "function" && !n(i) ? e(t(i)) : {};
  }
  return Ml = r, Ml;
}
var Cl, H_;
function Tj() {
  if (H_)
    return Cl;
  H_ = 1;
  var e = ci(), t = fn(), n = "[object Map]";
  function r(i) {
    return t(i) && e(i) == n;
  }
  return Cl = r, Cl;
}
var Dl, G_;
function xj() {
  if (G_)
    return Dl;
  G_ = 1;
  var e = Tj(), t = ko(), n = $p(), r = n && n.isMap, i = r ? t(r) : e;
  return Dl = i, Dl;
}
var ql, V_;
function Nj() {
  if (V_)
    return ql;
  V_ = 1;
  var e = ci(), t = fn(), n = "[object Set]";
  function r(i) {
    return t(i) && e(i) == n;
  }
  return ql = r, ql;
}
var jl, W_;
function Oj() {
  if (W_)
    return jl;
  W_ = 1;
  var e = Nj(), t = ko(), n = $p(), r = n && n.isSet, i = r ? t(r) : e;
  return jl = i, jl;
}
var Pl, J_;
function DS() {
  if (J_)
    return Pl;
  J_ = 1;
  var e = Do(), t = Np(), n = jo(), r = dj(), i = gj(), a = SS(), o = AS(), s = yj(), u = vj(), l = NS(), f = _j(), c = ci(), p = Ej(), d = Ij(), y = CS(), v = Oe(), _ = fi(), m = xj(), E = xt(), L = Oj(), I = Zn(), j = Lr(), M = 1, O = 2, $ = 4, q = "[object Arguments]", N = "[object Array]", P = "[object Boolean]", H = "[object Date]", G = "[object Error]", z = "[object Function]", W = "[object GeneratorFunction]", se = "[object Map]", ge = "[object Number]", re = "[object Object]", ke = "[object RegExp]", B = "[object Set]", te = "[object String]", ye = "[object Symbol]", gt = "[object WeakMap]", yt = "[object ArrayBuffer]", Ze = "[object DataView]", Gt = "[object Float32Array]", st = "[object Float64Array]", Fe = "[object Int8Array]", He = "[object Int16Array]", ut = "[object Int32Array]", vt = "[object Uint8Array]", _t = "[object Uint8ClampedArray]", C = "[object Uint16Array]", D = "[object Uint32Array]", k = {};
  k[q] = k[N] = k[yt] = k[Ze] = k[P] = k[H] = k[Gt] = k[st] = k[Fe] = k[He] = k[ut] = k[se] = k[ge] = k[re] = k[ke] = k[B] = k[te] = k[ye] = k[vt] = k[_t] = k[C] = k[D] = !0, k[G] = k[z] = k[gt] = !1;
  function V(F, ne, ie, Ce, De, _e) {
    var de, Ot = ne & M, R = ne & O, T = ne & $;
    if (ie && (de = De ? ie(F, Ce, De, _e) : ie(F)), de !== void 0)
      return de;
    if (!E(F))
      return F;
    var b = v(F);
    if (b) {
      if (de = p(F), !Ot)
        return o(F, de);
    } else {
      var h = c(F), g = h == z || h == W;
      if (_(F))
        return a(F, Ot);
      if (h == re || h == q || g && !De) {
        if (de = R || g ? {} : y(F), !Ot)
          return R ? u(F, i(de, F)) : s(F, r(de, F));
      } else {
        if (!k[h])
          return De ? F : {};
        de = d(F, h, Ot);
      }
    }
    _e || (_e = new e());
    var A = _e.get(F);
    if (A)
      return A;
    _e.set(F, de), L(F) ? F.forEach(function(x) {
      de.add(V(x, ne, ie, x, F, _e));
    }) : m(F) && F.forEach(function(x, U) {
      de.set(U, V(x, ne, ie, U, F, _e));
    });
    var w = T ? R ? f : l : R ? j : I, S = b ? void 0 : w(F);
    return t(S || F, function(x, U) {
      S && (U = x, x = F[U]), n(de, U, V(x, ne, ie, U, F, _e));
    }), de;
  }
  return Pl = V, Pl;
}
var kl, z_;
function $j() {
  if (z_)
    return kl;
  z_ = 1;
  var e = DS(), t = 4;
  function n(r) {
    return e(r, t);
  }
  return kl = n, kl;
}
var Fl, K_;
function qp() {
  if (K_)
    return Fl;
  K_ = 1;
  function e(t) {
    return function() {
      return t;
    };
  }
  return Fl = e, Fl;
}
var Bl, X_;
function Lj() {
  if (X_)
    return Bl;
  X_ = 1;
  function e(t) {
    return function(n, r, i) {
      for (var a = -1, o = Object(n), s = i(n), u = s.length; u--; ) {
        var l = s[t ? u : ++a];
        if (r(o[l], l, o) === !1)
          break;
      }
      return n;
    };
  }
  return Bl = e, Bl;
}
var Ul, Y_;
function jp() {
  if (Y_)
    return Ul;
  Y_ = 1;
  var e = Lj(), t = e();
  return Ul = t, Ul;
}
var Hl, Q_;
function Pp() {
  if (Q_)
    return Hl;
  Q_ = 1;
  var e = jp(), t = Zn();
  function n(r, i) {
    return r && e(r, i, t);
  }
  return Hl = n, Hl;
}
var Gl, Z_;
function Mj() {
  if (Z_)
    return Gl;
  Z_ = 1;
  var e = Mn();
  function t(n, r) {
    return function(i, a) {
      if (i == null)
        return i;
      if (!e(i))
        return n(i, a);
      for (var o = i.length, s = r ? o : -1, u = Object(i); (r ? s-- : ++s < o) && a(u[s], s, u) !== !1; )
        ;
      return i;
    };
  }
  return Gl = t, Gl;
}
var Vl, eb;
function Uo() {
  if (eb)
    return Vl;
  eb = 1;
  var e = Pp(), t = Mj(), n = t(e);
  return Vl = n, Vl;
}
var Wl, tb;
function Mr() {
  if (tb)
    return Wl;
  tb = 1;
  function e(t) {
    return t;
  }
  return Wl = e, Wl;
}
var Jl, nb;
function qS() {
  if (nb)
    return Jl;
  nb = 1;
  var e = Mr();
  function t(n) {
    return typeof n == "function" ? n : e;
  }
  return Jl = t, Jl;
}
var zl, rb;
function jS() {
  if (rb)
    return zl;
  rb = 1;
  var e = Np(), t = Uo(), n = qS(), r = Oe();
  function i(a, o) {
    var s = r(a) ? e : t;
    return s(a, n(o));
  }
  return zl = i, zl;
}
var Kl, ib;
function PS() {
  return ib || (ib = 1, Kl = jS()), Kl;
}
var Xl, ab;
function Cj() {
  if (ab)
    return Xl;
  ab = 1;
  var e = Uo();
  function t(n, r) {
    var i = [];
    return e(n, function(a, o, s) {
      r(a, o, s) && i.push(a);
    }), i;
  }
  return Xl = t, Xl;
}
var Yl, ob;
function Dj() {
  if (ob)
    return Yl;
  ob = 1;
  var e = "__lodash_hash_undefined__";
  function t(n) {
    return this.__data__.set(n, e), this;
  }
  return Yl = t, Yl;
}
var Ql, sb;
function qj() {
  if (sb)
    return Ql;
  sb = 1;
  function e(t) {
    return this.__data__.has(t);
  }
  return Ql = e, Ql;
}
var Zl, ub;
function kS() {
  if (ub)
    return Zl;
  ub = 1;
  var e = xp(), t = Dj(), n = qj();
  function r(i) {
    var a = -1, o = i == null ? 0 : i.length;
    for (this.__data__ = new e(); ++a < o; )
      this.add(i[a]);
  }
  return r.prototype.add = r.prototype.push = t, r.prototype.has = n, Zl = r, Zl;
}
var ef, lb;
function jj() {
  if (lb)
    return ef;
  lb = 1;
  function e(t, n) {
    for (var r = -1, i = t == null ? 0 : t.length; ++r < i; )
      if (n(t[r], r, t))
        return !0;
    return !1;
  }
  return ef = e, ef;
}
var tf, fb;
function FS() {
  if (fb)
    return tf;
  fb = 1;
  function e(t, n) {
    return t.has(n);
  }
  return tf = e, tf;
}
var nf, cb;
function BS() {
  if (cb)
    return nf;
  cb = 1;
  var e = kS(), t = jj(), n = FS(), r = 1, i = 2;
  function a(o, s, u, l, f, c) {
    var p = u & r, d = o.length, y = s.length;
    if (d != y && !(p && y > d))
      return !1;
    var v = c.get(o), _ = c.get(s);
    if (v && _)
      return v == s && _ == o;
    var m = -1, E = !0, L = u & i ? new e() : void 0;
    for (c.set(o, s), c.set(s, o); ++m < d; ) {
      var I = o[m], j = s[m];
      if (l)
        var M = p ? l(j, I, m, s, o, c) : l(I, j, m, o, s, c);
      if (M !== void 0) {
        if (M)
          continue;
        E = !1;
        break;
      }
      if (L) {
        if (!t(s, function(O, $) {
          if (!n(L, $) && (I === O || f(I, O, u, l, c)))
            return L.push($);
        })) {
          E = !1;
          break;
        }
      } else if (!(I === j || f(I, j, u, l, c))) {
        E = !1;
        break;
      }
    }
    return c.delete(o), c.delete(s), E;
  }
  return nf = a, nf;
}
var rf, db;
function Pj() {
  if (db)
    return rf;
  db = 1;
  function e(t) {
    var n = -1, r = Array(t.size);
    return t.forEach(function(i, a) {
      r[++n] = [a, i];
    }), r;
  }
  return rf = e, rf;
}
var af, hb;
function kp() {
  if (hb)
    return af;
  hb = 1;
  function e(t) {
    var n = -1, r = Array(t.size);
    return t.forEach(function(i) {
      r[++n] = i;
    }), r;
  }
  return af = e, af;
}
var of, pb;
function kj() {
  if (pb)
    return of;
  pb = 1;
  var e = li(), t = $S(), n = ui(), r = BS(), i = Pj(), a = kp(), o = 1, s = 2, u = "[object Boolean]", l = "[object Date]", f = "[object Error]", c = "[object Map]", p = "[object Number]", d = "[object RegExp]", y = "[object Set]", v = "[object String]", _ = "[object Symbol]", m = "[object ArrayBuffer]", E = "[object DataView]", L = e ? e.prototype : void 0, I = L ? L.valueOf : void 0;
  function j(M, O, $, q, N, P, H) {
    switch ($) {
      case E:
        if (M.byteLength != O.byteLength || M.byteOffset != O.byteOffset)
          return !1;
        M = M.buffer, O = O.buffer;
      case m:
        return !(M.byteLength != O.byteLength || !P(new t(M), new t(O)));
      case u:
      case l:
      case p:
        return n(+M, +O);
      case f:
        return M.name == O.name && M.message == O.message;
      case d:
      case v:
        return M == O + "";
      case c:
        var G = i;
      case y:
        var z = q & o;
        if (G || (G = a), M.size != O.size && !z)
          return !1;
        var W = H.get(M);
        if (W)
          return W == O;
        q |= s, H.set(M, O);
        var se = r(G(M), G(O), q, N, P, H);
        return H.delete(M), se;
      case _:
        if (I)
          return I.call(M) == I.call(O);
    }
    return !1;
  }
  return of = j, of;
}
var sf, gb;
function Fj() {
  if (gb)
    return sf;
  gb = 1;
  var e = NS(), t = 1, n = Object.prototype, r = n.hasOwnProperty;
  function i(a, o, s, u, l, f) {
    var c = s & t, p = e(a), d = p.length, y = e(o), v = y.length;
    if (d != v && !c)
      return !1;
    for (var _ = d; _--; ) {
      var m = p[_];
      if (!(c ? m in o : r.call(o, m)))
        return !1;
    }
    var E = f.get(a), L = f.get(o);
    if (E && L)
      return E == o && L == a;
    var I = !0;
    f.set(a, o), f.set(o, a);
    for (var j = c; ++_ < d; ) {
      m = p[_];
      var M = a[m], O = o[m];
      if (u)
        var $ = c ? u(O, M, m, o, a, f) : u(M, O, m, a, o, f);
      if (!($ === void 0 ? M === O || l(M, O, s, u, f) : $)) {
        I = !1;
        break;
      }
      j || (j = m == "constructor");
    }
    if (I && !j) {
      var q = a.constructor, N = o.constructor;
      q != N && "constructor" in a && "constructor" in o && !(typeof q == "function" && q instanceof q && typeof N == "function" && N instanceof N) && (I = !1);
    }
    return f.delete(a), f.delete(o), I;
  }
  return sf = i, sf;
}
var uf, yb;
function Bj() {
  if (yb)
    return uf;
  yb = 1;
  var e = Do(), t = BS(), n = kj(), r = Fj(), i = ci(), a = Oe(), o = fi(), s = aa(), u = 1, l = "[object Arguments]", f = "[object Array]", c = "[object Object]", p = Object.prototype, d = p.hasOwnProperty;
  function y(v, _, m, E, L, I) {
    var j = a(v), M = a(_), O = j ? f : i(v), $ = M ? f : i(_);
    O = O == l ? c : O, $ = $ == l ? c : $;
    var q = O == c, N = $ == c, P = O == $;
    if (P && o(v)) {
      if (!o(_))
        return !1;
      j = !0, q = !1;
    }
    if (P && !q)
      return I || (I = new e()), j || s(v) ? t(v, _, m, E, L, I) : n(v, _, O, m, E, L, I);
    if (!(m & u)) {
      var H = q && d.call(v, "__wrapped__"), G = N && d.call(_, "__wrapped__");
      if (H || G) {
        var z = H ? v.value() : v, W = G ? _.value() : _;
        return I || (I = new e()), L(z, W, m, E, I);
      }
    }
    return P ? (I || (I = new e()), r(v, _, m, E, L, I)) : !1;
  }
  return uf = y, uf;
}
var lf, vb;
function US() {
  if (vb)
    return lf;
  vb = 1;
  var e = Bj(), t = fn();
  function n(r, i, a, o, s) {
    return r === i ? !0 : r == null || i == null || !t(r) && !t(i) ? r !== r && i !== i : e(r, i, a, o, n, s);
  }
  return lf = n, lf;
}
var ff, _b;
function Uj() {
  if (_b)
    return ff;
  _b = 1;
  var e = Do(), t = US(), n = 1, r = 2;
  function i(a, o, s, u) {
    var l = s.length, f = l, c = !u;
    if (a == null)
      return !f;
    for (a = Object(a); l--; ) {
      var p = s[l];
      if (c && p[2] ? p[1] !== a[p[0]] : !(p[0] in a))
        return !1;
    }
    for (; ++l < f; ) {
      p = s[l];
      var d = p[0], y = a[d], v = p[1];
      if (c && p[2]) {
        if (y === void 0 && !(d in a))
          return !1;
      } else {
        var _ = new e();
        if (u)
          var m = u(y, v, d, a, o, _);
        if (!(m === void 0 ? t(v, y, n | r, u, _) : m))
          return !1;
      }
    }
    return !0;
  }
  return ff = i, ff;
}
var cf, bb;
function HS() {
  if (bb)
    return cf;
  bb = 1;
  var e = xt();
  function t(n) {
    return n === n && !e(n);
  }
  return cf = t, cf;
}
var df, mb;
function Hj() {
  if (mb)
    return df;
  mb = 1;
  var e = HS(), t = Zn();
  function n(r) {
    for (var i = t(r), a = i.length; a--; ) {
      var o = i[a], s = r[o];
      i[a] = [o, s, e(s)];
    }
    return i;
  }
  return df = n, df;
}
var hf, wb;
function GS() {
  if (wb)
    return hf;
  wb = 1;
  function e(t, n) {
    return function(r) {
      return r == null ? !1 : r[t] === n && (n !== void 0 || t in Object(r));
    };
  }
  return hf = e, hf;
}
var pf, Eb;
function Gj() {
  if (Eb)
    return pf;
  Eb = 1;
  var e = Uj(), t = Hj(), n = GS();
  function r(i) {
    var a = t(i);
    return a.length == 1 && a[0][2] ? n(a[0][0], a[0][1]) : function(o) {
      return o === i || e(o, i, a);
    };
  }
  return pf = r, pf;
}
var gf, Sb;
function di() {
  if (Sb)
    return gf;
  Sb = 1;
  var e = Or(), t = fn(), n = "[object Symbol]";
  function r(i) {
    return typeof i == "symbol" || t(i) && e(i) == n;
  }
  return gf = r, gf;
}
var yf, Ab;
function Fp() {
  if (Ab)
    return yf;
  Ab = 1;
  var e = Oe(), t = di(), n = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/, r = /^\w*$/;
  function i(a, o) {
    if (e(a))
      return !1;
    var s = typeof a;
    return s == "number" || s == "symbol" || s == "boolean" || a == null || t(a) ? !0 : r.test(a) || !n.test(a) || o != null && a in Object(o);
  }
  return yf = i, yf;
}
var vf, Rb;
function Vj() {
  if (Rb)
    return vf;
  Rb = 1;
  var e = xp(), t = "Expected a function";
  function n(r, i) {
    if (typeof r != "function" || i != null && typeof i != "function")
      throw new TypeError(t);
    var a = function() {
      var o = arguments, s = i ? i.apply(this, o) : o[0], u = a.cache;
      if (u.has(s))
        return u.get(s);
      var l = r.apply(this, o);
      return a.cache = u.set(s, l) || u, l;
    };
    return a.cache = new (n.Cache || e)(), a;
  }
  return n.Cache = e, vf = n, vf;
}
var _f, Ib;
function Wj() {
  if (Ib)
    return _f;
  Ib = 1;
  var e = Vj(), t = 500;
  function n(r) {
    var i = e(r, function(o) {
      return a.size === t && a.clear(), o;
    }), a = i.cache;
    return i;
  }
  return _f = n, _f;
}
var bf, Tb;
function Jj() {
  if (Tb)
    return bf;
  Tb = 1;
  var e = Wj(), t = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g, n = /\\(\\)?/g, r = e(function(i) {
    var a = [];
    return i.charCodeAt(0) === 46 && a.push(""), i.replace(t, function(o, s, u, l) {
      a.push(u ? l.replace(n, "$1") : s || o);
    }), a;
  });
  return bf = r, bf;
}
var mf, xb;
function Ho() {
  if (xb)
    return mf;
  xb = 1;
  function e(t, n) {
    for (var r = -1, i = t == null ? 0 : t.length, a = Array(i); ++r < i; )
      a[r] = n(t[r], r, t);
    return a;
  }
  return mf = e, mf;
}
var wf, Nb;
function zj() {
  if (Nb)
    return wf;
  Nb = 1;
  var e = li(), t = Ho(), n = Oe(), r = di(), i = 1 / 0, a = e ? e.prototype : void 0, o = a ? a.toString : void 0;
  function s(u) {
    if (typeof u == "string")
      return u;
    if (n(u))
      return t(u, s) + "";
    if (r(u))
      return o ? o.call(u) : "";
    var l = u + "";
    return l == "0" && 1 / u == -i ? "-0" : l;
  }
  return wf = s, wf;
}
var Ef, Ob;
function VS() {
  if (Ob)
    return Ef;
  Ob = 1;
  var e = zj();
  function t(n) {
    return n == null ? "" : e(n);
  }
  return Ef = t, Ef;
}
var Sf, $b;
function Go() {
  if ($b)
    return Sf;
  $b = 1;
  var e = Oe(), t = Fp(), n = Jj(), r = VS();
  function i(a, o) {
    return e(a) ? a : t(a, o) ? [a] : n(r(a));
  }
  return Sf = i, Sf;
}
var Af, Lb;
function oa() {
  if (Lb)
    return Af;
  Lb = 1;
  var e = di(), t = 1 / 0;
  function n(r) {
    if (typeof r == "string" || e(r))
      return r;
    var i = r + "";
    return i == "0" && 1 / r == -t ? "-0" : i;
  }
  return Af = n, Af;
}
var Rf, Mb;
function Vo() {
  if (Mb)
    return Rf;
  Mb = 1;
  var e = Go(), t = oa();
  function n(r, i) {
    i = e(i, r);
    for (var a = 0, o = i.length; r != null && a < o; )
      r = r[t(i[a++])];
    return a && a == o ? r : void 0;
  }
  return Rf = n, Rf;
}
var If, Cb;
function Kj() {
  if (Cb)
    return If;
  Cb = 1;
  var e = Vo();
  function t(n, r, i) {
    var a = n == null ? void 0 : e(n, r);
    return a === void 0 ? i : a;
  }
  return If = t, If;
}
var Tf, Db;
function Xj() {
  if (Db)
    return Tf;
  Db = 1;
  function e(t, n) {
    return t != null && n in Object(t);
  }
  return Tf = e, Tf;
}
var xf, qb;
function WS() {
  if (qb)
    return xf;
  qb = 1;
  var e = Go(), t = ia(), n = Oe(), r = Po(), i = Op(), a = oa();
  function o(s, u, l) {
    u = e(u, s);
    for (var f = -1, c = u.length, p = !1; ++f < c; ) {
      var d = a(u[f]);
      if (!(p = s != null && l(s, d)))
        break;
      s = s[d];
    }
    return p || ++f != c ? p : (c = s == null ? 0 : s.length, !!c && i(c) && r(d, c) && (n(s) || t(s)));
  }
  return xf = o, xf;
}
var Nf, jb;
function JS() {
  if (jb)
    return Nf;
  jb = 1;
  var e = Xj(), t = WS();
  function n(r, i) {
    return r != null && t(r, i, e);
  }
  return Nf = n, Nf;
}
var Of, Pb;
function Yj() {
  if (Pb)
    return Of;
  Pb = 1;
  var e = US(), t = Kj(), n = JS(), r = Fp(), i = HS(), a = GS(), o = oa(), s = 1, u = 2;
  function l(f, c) {
    return r(f) && i(c) ? a(o(f), c) : function(p) {
      var d = t(p, f);
      return d === void 0 && d === c ? n(p, f) : e(c, d, s | u);
    };
  }
  return Of = l, Of;
}
var $f, kb;
function zS() {
  if (kb)
    return $f;
  kb = 1;
  function e(t) {
    return function(n) {
      return n == null ? void 0 : n[t];
    };
  }
  return $f = e, $f;
}
var Lf, Fb;
function Qj() {
  if (Fb)
    return Lf;
  Fb = 1;
  var e = Vo();
  function t(n) {
    return function(r) {
      return e(r, n);
    };
  }
  return Lf = t, Lf;
}
var Mf, Bb;
function Zj() {
  if (Bb)
    return Mf;
  Bb = 1;
  var e = zS(), t = Qj(), n = Fp(), r = oa();
  function i(a) {
    return n(a) ? e(r(a)) : t(a);
  }
  return Mf = i, Mf;
}
var Cf, Ub;
function Cn() {
  if (Ub)
    return Cf;
  Ub = 1;
  var e = Gj(), t = Yj(), n = Mr(), r = Oe(), i = Zj();
  function a(o) {
    return typeof o == "function" ? o : o == null ? n : typeof o == "object" ? r(o) ? t(o[0], o[1]) : e(o) : i(o);
  }
  return Cf = a, Cf;
}
var Df, Hb;
function KS() {
  if (Hb)
    return Df;
  Hb = 1;
  var e = RS(), t = Cj(), n = Cn(), r = Oe();
  function i(a, o) {
    var s = r(a) ? e : t;
    return s(a, n(o, 3));
  }
  return Df = i, Df;
}
var qf, Gb;
function eP() {
  if (Gb)
    return qf;
  Gb = 1;
  var e = Object.prototype, t = e.hasOwnProperty;
  function n(r, i) {
    return r != null && t.call(r, i);
  }
  return qf = n, qf;
}
var jf, Vb;
function XS() {
  if (Vb)
    return jf;
  Vb = 1;
  var e = eP(), t = WS();
  function n(r, i) {
    return r != null && t(r, i, e);
  }
  return jf = n, jf;
}
var Pf, Wb;
function tP() {
  if (Wb)
    return Pf;
  Wb = 1;
  var e = Lp(), t = ci(), n = ia(), r = Oe(), i = Mn(), a = fi(), o = Fo(), s = aa(), u = "[object Map]", l = "[object Set]", f = Object.prototype, c = f.hasOwnProperty;
  function p(d) {
    if (d == null)
      return !0;
    if (i(d) && (r(d) || typeof d == "string" || typeof d.splice == "function" || a(d) || s(d) || n(d)))
      return !d.length;
    var y = t(d);
    if (y == u || y == l)
      return !d.size;
    if (o(d))
      return !e(d).length;
    for (var v in d)
      if (c.call(d, v))
        return !1;
    return !0;
  }
  return Pf = p, Pf;
}
var kf, Jb;
function YS() {
  if (Jb)
    return kf;
  Jb = 1;
  function e(t) {
    return t === void 0;
  }
  return kf = e, kf;
}
var Ff, zb;
function QS() {
  if (zb)
    return Ff;
  zb = 1;
  var e = Uo(), t = Mn();
  function n(r, i) {
    var a = -1, o = t(r) ? Array(r.length) : [];
    return e(r, function(s, u, l) {
      o[++a] = i(s, u, l);
    }), o;
  }
  return Ff = n, Ff;
}
var Bf, Kb;
function ZS() {
  if (Kb)
    return Bf;
  Kb = 1;
  var e = Ho(), t = Cn(), n = QS(), r = Oe();
  function i(a, o) {
    var s = r(a) ? e : n;
    return s(a, t(o, 3));
  }
  return Bf = i, Bf;
}
var Uf, Xb;
function nP() {
  if (Xb)
    return Uf;
  Xb = 1;
  function e(t, n, r, i) {
    var a = -1, o = t == null ? 0 : t.length;
    for (i && o && (r = t[++a]); ++a < o; )
      r = n(r, t[a], a, t);
    return r;
  }
  return Uf = e, Uf;
}
var Hf, Yb;
function rP() {
  if (Yb)
    return Hf;
  Yb = 1;
  function e(t, n, r, i, a) {
    return a(t, function(o, s, u) {
      r = i ? (i = !1, o) : n(r, o, s, u);
    }), r;
  }
  return Hf = e, Hf;
}
var Gf, Qb;
function eA() {
  if (Qb)
    return Gf;
  Qb = 1;
  var e = nP(), t = Uo(), n = Cn(), r = rP(), i = Oe();
  function a(o, s, u) {
    var l = i(o) ? e : r, f = arguments.length < 3;
    return l(o, n(s, 4), u, f, t);
  }
  return Gf = a, Gf;
}
var Vf, Zb;
function iP() {
  if (Zb)
    return Vf;
  Zb = 1;
  var e = Or(), t = Oe(), n = fn(), r = "[object String]";
  function i(a) {
    return typeof a == "string" || !t(a) && n(a) && e(a) == r;
  }
  return Vf = i, Vf;
}
var Wf, e0;
function aP() {
  if (e0)
    return Wf;
  e0 = 1;
  var e = zS(), t = e("length");
  return Wf = t, Wf;
}
var Jf, t0;
function oP() {
  if (t0)
    return Jf;
  t0 = 1;
  var e = "\\ud800-\\udfff", t = "\\u0300-\\u036f", n = "\\ufe20-\\ufe2f", r = "\\u20d0-\\u20ff", i = t + n + r, a = "\\ufe0e\\ufe0f", o = "\\u200d", s = RegExp("[" + o + e + i + a + "]");
  function u(l) {
    return s.test(l);
  }
  return Jf = u, Jf;
}
var zf, n0;
function sP() {
  if (n0)
    return zf;
  n0 = 1;
  var e = "\\ud800-\\udfff", t = "\\u0300-\\u036f", n = "\\ufe20-\\ufe2f", r = "\\u20d0-\\u20ff", i = t + n + r, a = "\\ufe0e\\ufe0f", o = "[" + e + "]", s = "[" + i + "]", u = "\\ud83c[\\udffb-\\udfff]", l = "(?:" + s + "|" + u + ")", f = "[^" + e + "]", c = "(?:\\ud83c[\\udde6-\\uddff]){2}", p = "[\\ud800-\\udbff][\\udc00-\\udfff]", d = "\\u200d", y = l + "?", v = "[" + a + "]?", _ = "(?:" + d + "(?:" + [f, c, p].join("|") + ")" + v + y + ")*", m = v + y + _, E = "(?:" + [f + s + "?", s, c, p, o].join("|") + ")", L = RegExp(u + "(?=" + u + ")|" + E + m, "g");
  function I(j) {
    for (var M = L.lastIndex = 0; L.test(j); )
      ++M;
    return M;
  }
  return zf = I, zf;
}
var Kf, r0;
function uP() {
  if (r0)
    return Kf;
  r0 = 1;
  var e = aP(), t = oP(), n = sP();
  function r(i) {
    return t(i) ? n(i) : e(i);
  }
  return Kf = r, Kf;
}
var Xf, i0;
function lP() {
  if (i0)
    return Xf;
  i0 = 1;
  var e = Lp(), t = ci(), n = Mn(), r = iP(), i = uP(), a = "[object Map]", o = "[object Set]";
  function s(u) {
    if (u == null)
      return 0;
    if (n(u))
      return r(u) ? i(u) : u.length;
    var l = t(u);
    return l == a || l == o ? u.size : e(u).length;
  }
  return Xf = s, Xf;
}
var Yf, a0;
function fP() {
  if (a0)
    return Yf;
  a0 = 1;
  var e = Np(), t = MS(), n = Pp(), r = Cn(), i = Bo(), a = Oe(), o = fi(), s = na(), u = xt(), l = aa();
  function f(c, p, d) {
    var y = a(c), v = y || o(c) || l(c);
    if (p = r(p, 4), d == null) {
      var _ = c && c.constructor;
      v ? d = y ? new _() : [] : u(c) ? d = s(_) ? t(i(c)) : {} : d = {};
    }
    return (v ? e : n)(c, function(m, E, L) {
      return p(d, m, E, L);
    }), d;
  }
  return Yf = f, Yf;
}
var Qf, o0;
function cP() {
  if (o0)
    return Qf;
  o0 = 1;
  var e = li(), t = ia(), n = Oe(), r = e ? e.isConcatSpreadable : void 0;
  function i(a) {
    return n(a) || t(a) || !!(r && a && a[r]);
  }
  return Qf = i, Qf;
}
var Zf, s0;
function Bp() {
  if (s0)
    return Zf;
  s0 = 1;
  var e = Cp(), t = cP();
  function n(r, i, a, o, s) {
    var u = -1, l = r.length;
    for (a || (a = t), s || (s = []); ++u < l; ) {
      var f = r[u];
      i > 0 && a(f) ? i > 1 ? n(f, i - 1, a, o, s) : e(s, f) : o || (s[s.length] = f);
    }
    return s;
  }
  return Zf = n, Zf;
}
var ec, u0;
function dP() {
  if (u0)
    return ec;
  u0 = 1;
  function e(t, n, r) {
    switch (r.length) {
      case 0:
        return t.call(n);
      case 1:
        return t.call(n, r[0]);
      case 2:
        return t.call(n, r[0], r[1]);
      case 3:
        return t.call(n, r[0], r[1], r[2]);
    }
    return t.apply(n, r);
  }
  return ec = e, ec;
}
var tc, l0;
function tA() {
  if (l0)
    return tc;
  l0 = 1;
  var e = dP(), t = Math.max;
  function n(r, i, a) {
    return i = t(i === void 0 ? r.length - 1 : i, 0), function() {
      for (var o = arguments, s = -1, u = t(o.length - i, 0), l = Array(u); ++s < u; )
        l[s] = o[i + s];
      s = -1;
      for (var f = Array(i + 1); ++s < i; )
        f[s] = o[s];
      return f[i] = a(l), e(r, this, f);
    };
  }
  return tc = n, tc;
}
var nc, f0;
function hP() {
  if (f0)
    return nc;
  f0 = 1;
  var e = qp(), t = mS(), n = Mr(), r = t ? function(i, a) {
    return t(i, "toString", {
      configurable: !0,
      enumerable: !1,
      value: e(a),
      writable: !0
    });
  } : n;
  return nc = r, nc;
}
var rc, c0;
function pP() {
  if (c0)
    return rc;
  c0 = 1;
  var e = 800, t = 16, n = Date.now;
  function r(i) {
    var a = 0, o = 0;
    return function() {
      var s = n(), u = t - (s - o);
      if (o = s, u > 0) {
        if (++a >= e)
          return arguments[0];
      } else
        a = 0;
      return i.apply(void 0, arguments);
    };
  }
  return rc = r, rc;
}
var ic, d0;
function nA() {
  if (d0)
    return ic;
  d0 = 1;
  var e = hP(), t = pP(), n = t(e);
  return ic = n, ic;
}
var ac, h0;
function Wo() {
  if (h0)
    return ac;
  h0 = 1;
  var e = Mr(), t = tA(), n = nA();
  function r(i, a) {
    return n(t(i, a, e), i + "");
  }
  return ac = r, ac;
}
var oc, p0;
function rA() {
  if (p0)
    return oc;
  p0 = 1;
  function e(t, n, r, i) {
    for (var a = t.length, o = r + (i ? 1 : -1); i ? o-- : ++o < a; )
      if (n(t[o], o, t))
        return o;
    return -1;
  }
  return oc = e, oc;
}
var sc, g0;
function gP() {
  if (g0)
    return sc;
  g0 = 1;
  function e(t) {
    return t !== t;
  }
  return sc = e, sc;
}
var uc, y0;
function yP() {
  if (y0)
    return uc;
  y0 = 1;
  function e(t, n, r) {
    for (var i = r - 1, a = t.length; ++i < a; )
      if (t[i] === n)
        return i;
    return -1;
  }
  return uc = e, uc;
}
var lc, v0;
function vP() {
  if (v0)
    return lc;
  v0 = 1;
  var e = rA(), t = gP(), n = yP();
  function r(i, a, o) {
    return a === a ? n(i, a, o) : e(i, t, o);
  }
  return lc = r, lc;
}
var fc, _0;
function _P() {
  if (_0)
    return fc;
  _0 = 1;
  var e = vP();
  function t(n, r) {
    var i = n == null ? 0 : n.length;
    return !!i && e(n, r, 0) > -1;
  }
  return fc = t, fc;
}
var cc, b0;
function bP() {
  if (b0)
    return cc;
  b0 = 1;
  function e(t, n, r) {
    for (var i = -1, a = t == null ? 0 : t.length; ++i < a; )
      if (r(n, t[i]))
        return !0;
    return !1;
  }
  return cc = e, cc;
}
var dc, m0;
function mP() {
  if (m0)
    return dc;
  m0 = 1;
  function e() {
  }
  return dc = e, dc;
}
var hc, w0;
function wP() {
  if (w0)
    return hc;
  w0 = 1;
  var e = OS(), t = mP(), n = kp(), r = 1 / 0, i = e && 1 / n(new e([, -0]))[1] == r ? function(a) {
    return new e(a);
  } : t;
  return hc = i, hc;
}
var pc, E0;
function EP() {
  if (E0)
    return pc;
  E0 = 1;
  var e = kS(), t = _P(), n = bP(), r = FS(), i = wP(), a = kp(), o = 200;
  function s(u, l, f) {
    var c = -1, p = t, d = u.length, y = !0, v = [], _ = v;
    if (f)
      y = !1, p = n;
    else if (d >= o) {
      var m = l ? null : i(u);
      if (m)
        return a(m);
      y = !1, p = r, _ = new e();
    } else
      _ = l ? [] : v;
    e:
      for (; ++c < d; ) {
        var E = u[c], L = l ? l(E) : E;
        if (E = f || E !== 0 ? E : 0, y && L === L) {
          for (var I = _.length; I--; )
            if (_[I] === L)
              continue e;
          l && _.push(L), v.push(E);
        } else
          p(_, L, f) || (_ !== v && _.push(L), v.push(E));
      }
    return v;
  }
  return pc = s, pc;
}
var gc, S0;
function iA() {
  if (S0)
    return gc;
  S0 = 1;
  var e = Mn(), t = fn();
  function n(r) {
    return t(r) && e(r);
  }
  return gc = n, gc;
}
var yc, A0;
function SP() {
  if (A0)
    return yc;
  A0 = 1;
  var e = Bp(), t = Wo(), n = EP(), r = iA(), i = t(function(a) {
    return n(e(a, 1, r, !0));
  });
  return yc = i, yc;
}
var vc, R0;
function AP() {
  if (R0)
    return vc;
  R0 = 1;
  var e = Ho();
  function t(n, r) {
    return e(r, function(i) {
      return n[i];
    });
  }
  return vc = t, vc;
}
var _c, I0;
function aA() {
  if (I0)
    return _c;
  I0 = 1;
  var e = AP(), t = Zn();
  function n(r) {
    return r == null ? [] : e(r, t(r));
  }
  return _c = n, _c;
}
var bc, T0;
function Nt() {
  if (T0)
    return bc;
  T0 = 1;
  var e;
  if (typeof Ip == "function")
    try {
      e = {
        clone: $j(),
        constant: qp(),
        each: PS(),
        filter: KS(),
        has: XS(),
        isArray: Oe(),
        isEmpty: tP(),
        isFunction: na(),
        isUndefined: YS(),
        keys: Zn(),
        map: ZS(),
        reduce: eA(),
        size: lP(),
        transform: fP(),
        union: SP(),
        values: aA()
      };
    } catch {
    }
  return e || (e = window._), bc = e, bc;
}
var mc, x0;
function Up() {
  if (x0)
    return mc;
  x0 = 1;
  var e = Nt();
  mc = i;
  var t = "\0", n = "\0", r = "";
  function i(f) {
    this._isDirected = e.has(f, "directed") ? f.directed : !0, this._isMultigraph = e.has(f, "multigraph") ? f.multigraph : !1, this._isCompound = e.has(f, "compound") ? f.compound : !1, this._label = void 0, this._defaultNodeLabelFn = e.constant(void 0), this._defaultEdgeLabelFn = e.constant(void 0), this._nodes = {}, this._isCompound && (this._parent = {}, this._children = {}, this._children[n] = {}), this._in = {}, this._preds = {}, this._out = {}, this._sucs = {}, this._edgeObjs = {}, this._edgeLabels = {};
  }
  i.prototype._nodeCount = 0, i.prototype._edgeCount = 0, i.prototype.isDirected = function() {
    return this._isDirected;
  }, i.prototype.isMultigraph = function() {
    return this._isMultigraph;
  }, i.prototype.isCompound = function() {
    return this._isCompound;
  }, i.prototype.setGraph = function(f) {
    return this._label = f, this;
  }, i.prototype.graph = function() {
    return this._label;
  }, i.prototype.setDefaultNodeLabel = function(f) {
    return e.isFunction(f) || (f = e.constant(f)), this._defaultNodeLabelFn = f, this;
  }, i.prototype.nodeCount = function() {
    return this._nodeCount;
  }, i.prototype.nodes = function() {
    return e.keys(this._nodes);
  }, i.prototype.sources = function() {
    var f = this;
    return e.filter(this.nodes(), function(c) {
      return e.isEmpty(f._in[c]);
    });
  }, i.prototype.sinks = function() {
    var f = this;
    return e.filter(this.nodes(), function(c) {
      return e.isEmpty(f._out[c]);
    });
  }, i.prototype.setNodes = function(f, c) {
    var p = arguments, d = this;
    return e.each(f, function(y) {
      p.length > 1 ? d.setNode(y, c) : d.setNode(y);
    }), this;
  }, i.prototype.setNode = function(f, c) {
    return e.has(this._nodes, f) ? (arguments.length > 1 && (this._nodes[f] = c), this) : (this._nodes[f] = arguments.length > 1 ? c : this._defaultNodeLabelFn(f), this._isCompound && (this._parent[f] = n, this._children[f] = {}, this._children[n][f] = !0), this._in[f] = {}, this._preds[f] = {}, this._out[f] = {}, this._sucs[f] = {}, ++this._nodeCount, this);
  }, i.prototype.node = function(f) {
    return this._nodes[f];
  }, i.prototype.hasNode = function(f) {
    return e.has(this._nodes, f);
  }, i.prototype.removeNode = function(f) {
    var c = this;
    if (e.has(this._nodes, f)) {
      var p = function(d) {
        c.removeEdge(c._edgeObjs[d]);
      };
      delete this._nodes[f], this._isCompound && (this._removeFromParentsChildList(f), delete this._parent[f], e.each(this.children(f), function(d) {
        c.setParent(d);
      }), delete this._children[f]), e.each(e.keys(this._in[f]), p), delete this._in[f], delete this._preds[f], e.each(e.keys(this._out[f]), p), delete this._out[f], delete this._sucs[f], --this._nodeCount;
    }
    return this;
  }, i.prototype.setParent = function(f, c) {
    if (!this._isCompound)
      throw new Error("Cannot set parent in a non-compound graph");
    if (e.isUndefined(c))
      c = n;
    else {
      c += "";
      for (var p = c; !e.isUndefined(p); p = this.parent(p))
        if (p === f)
          throw new Error("Setting " + c + " as parent of " + f + " would create a cycle");
      this.setNode(c);
    }
    return this.setNode(f), this._removeFromParentsChildList(f), this._parent[f] = c, this._children[c][f] = !0, this;
  }, i.prototype._removeFromParentsChildList = function(f) {
    delete this._children[this._parent[f]][f];
  }, i.prototype.parent = function(f) {
    if (this._isCompound) {
      var c = this._parent[f];
      if (c !== n)
        return c;
    }
  }, i.prototype.children = function(f) {
    if (e.isUndefined(f) && (f = n), this._isCompound) {
      var c = this._children[f];
      if (c)
        return e.keys(c);
    } else {
      if (f === n)
        return this.nodes();
      if (this.hasNode(f))
        return [];
    }
  }, i.prototype.predecessors = function(f) {
    var c = this._preds[f];
    if (c)
      return e.keys(c);
  }, i.prototype.successors = function(f) {
    var c = this._sucs[f];
    if (c)
      return e.keys(c);
  }, i.prototype.neighbors = function(f) {
    var c = this.predecessors(f);
    if (c)
      return e.union(c, this.successors(f));
  }, i.prototype.isLeaf = function(f) {
    var c;
    return this.isDirected() ? c = this.successors(f) : c = this.neighbors(f), c.length === 0;
  }, i.prototype.filterNodes = function(f) {
    var c = new this.constructor({
      directed: this._isDirected,
      multigraph: this._isMultigraph,
      compound: this._isCompound
    });
    c.setGraph(this.graph());
    var p = this;
    e.each(this._nodes, function(v, _) {
      f(_) && c.setNode(_, v);
    }), e.each(this._edgeObjs, function(v) {
      c.hasNode(v.v) && c.hasNode(v.w) && c.setEdge(v, p.edge(v));
    });
    var d = {};
    function y(v) {
      var _ = p.parent(v);
      return _ === void 0 || c.hasNode(_) ? (d[v] = _, _) : _ in d ? d[_] : y(_);
    }
    return this._isCompound && e.each(c.nodes(), function(v) {
      c.setParent(v, y(v));
    }), c;
  }, i.prototype.setDefaultEdgeLabel = function(f) {
    return e.isFunction(f) || (f = e.constant(f)), this._defaultEdgeLabelFn = f, this;
  }, i.prototype.edgeCount = function() {
    return this._edgeCount;
  }, i.prototype.edges = function() {
    return e.values(this._edgeObjs);
  }, i.prototype.setPath = function(f, c) {
    var p = this, d = arguments;
    return e.reduce(f, function(y, v) {
      return d.length > 1 ? p.setEdge(y, v, c) : p.setEdge(y, v), v;
    }), this;
  }, i.prototype.setEdge = function() {
    var f, c, p, d, y = !1, v = arguments[0];
    typeof v == "object" && v !== null && "v" in v ? (f = v.v, c = v.w, p = v.name, arguments.length === 2 && (d = arguments[1], y = !0)) : (f = v, c = arguments[1], p = arguments[3], arguments.length > 2 && (d = arguments[2], y = !0)), f = "" + f, c = "" + c, e.isUndefined(p) || (p = "" + p);
    var _ = s(this._isDirected, f, c, p);
    if (e.has(this._edgeLabels, _))
      return y && (this._edgeLabels[_] = d), this;
    if (!e.isUndefined(p) && !this._isMultigraph)
      throw new Error("Cannot set a named edge when isMultigraph = false");
    this.setNode(f), this.setNode(c), this._edgeLabels[_] = y ? d : this._defaultEdgeLabelFn(f, c, p);
    var m = u(this._isDirected, f, c, p);
    return f = m.v, c = m.w, Object.freeze(m), this._edgeObjs[_] = m, a(this._preds[c], f), a(this._sucs[f], c), this._in[c][_] = m, this._out[f][_] = m, this._edgeCount++, this;
  }, i.prototype.edge = function(f, c, p) {
    var d = arguments.length === 1 ? l(this._isDirected, arguments[0]) : s(this._isDirected, f, c, p);
    return this._edgeLabels[d];
  }, i.prototype.hasEdge = function(f, c, p) {
    var d = arguments.length === 1 ? l(this._isDirected, arguments[0]) : s(this._isDirected, f, c, p);
    return e.has(this._edgeLabels, d);
  }, i.prototype.removeEdge = function(f, c, p) {
    var d = arguments.length === 1 ? l(this._isDirected, arguments[0]) : s(this._isDirected, f, c, p), y = this._edgeObjs[d];
    return y && (f = y.v, c = y.w, delete this._edgeLabels[d], delete this._edgeObjs[d], o(this._preds[c], f), o(this._sucs[f], c), delete this._in[c][d], delete this._out[f][d], this._edgeCount--), this;
  }, i.prototype.inEdges = function(f, c) {
    var p = this._in[f];
    if (p) {
      var d = e.values(p);
      return c ? e.filter(d, function(y) {
        return y.v === c;
      }) : d;
    }
  }, i.prototype.outEdges = function(f, c) {
    var p = this._out[f];
    if (p) {
      var d = e.values(p);
      return c ? e.filter(d, function(y) {
        return y.w === c;
      }) : d;
    }
  }, i.prototype.nodeEdges = function(f, c) {
    var p = this.inEdges(f, c);
    if (p)
      return p.concat(this.outEdges(f, c));
  };
  function a(f, c) {
    f[c] ? f[c]++ : f[c] = 1;
  }
  function o(f, c) {
    --f[c] || delete f[c];
  }
  function s(f, c, p, d) {
    var y = "" + c, v = "" + p;
    if (!f && y > v) {
      var _ = y;
      y = v, v = _;
    }
    return y + r + v + r + (e.isUndefined(d) ? t : d);
  }
  function u(f, c, p, d) {
    var y = "" + c, v = "" + p;
    if (!f && y > v) {
      var _ = y;
      y = v, v = _;
    }
    var m = { v: y, w: v };
    return d && (m.name = d), m;
  }
  function l(f, c) {
    return s(f, c.v, c.w, c.name);
  }
  return mc;
}
var wc, N0;
function RP() {
  return N0 || (N0 = 1, wc = "2.1.8"), wc;
}
var Ec, O0;
function IP() {
  return O0 || (O0 = 1, Ec = {
    Graph: Up(),
    version: RP()
  }), Ec;
}
var Sc, $0;
function TP() {
  if ($0)
    return Sc;
  $0 = 1;
  var e = Nt(), t = Up();
  Sc = {
    write: n,
    read: a
  };
  function n(o) {
    var s = {
      options: {
        directed: o.isDirected(),
        multigraph: o.isMultigraph(),
        compound: o.isCompound()
      },
      nodes: r(o),
      edges: i(o)
    };
    return e.isUndefined(o.graph()) || (s.value = e.clone(o.graph())), s;
  }
  function r(o) {
    return e.map(o.nodes(), function(s) {
      var u = o.node(s), l = o.parent(s), f = { v: s };
      return e.isUndefined(u) || (f.value = u), e.isUndefined(l) || (f.parent = l), f;
    });
  }
  function i(o) {
    return e.map(o.edges(), function(s) {
      var u = o.edge(s), l = { v: s.v, w: s.w };
      return e.isUndefined(s.name) || (l.name = s.name), e.isUndefined(u) || (l.value = u), l;
    });
  }
  function a(o) {
    var s = new t(o.options).setGraph(o.value);
    return e.each(o.nodes, function(u) {
      s.setNode(u.v, u.value), u.parent && s.setParent(u.v, u.parent);
    }), e.each(o.edges, function(u) {
      s.setEdge({ v: u.v, w: u.w, name: u.name }, u.value);
    }), s;
  }
  return Sc;
}
var Ac, L0;
function xP() {
  if (L0)
    return Ac;
  L0 = 1;
  var e = Nt();
  Ac = t;
  function t(n) {
    var r = {}, i = [], a;
    function o(s) {
      e.has(r, s) || (r[s] = !0, a.push(s), e.each(n.successors(s), o), e.each(n.predecessors(s), o));
    }
    return e.each(n.nodes(), function(s) {
      a = [], o(s), a.length && i.push(a);
    }), i;
  }
  return Ac;
}
var Rc, M0;
function oA() {
  if (M0)
    return Rc;
  M0 = 1;
  var e = Nt();
  Rc = t;
  function t() {
    this._arr = [], this._keyIndices = {};
  }
  return t.prototype.size = function() {
    return this._arr.length;
  }, t.prototype.keys = function() {
    return this._arr.map(function(n) {
      return n.key;
    });
  }, t.prototype.has = function(n) {
    return e.has(this._keyIndices, n);
  }, t.prototype.priority = function(n) {
    var r = this._keyIndices[n];
    if (r !== void 0)
      return this._arr[r].priority;
  }, t.prototype.min = function() {
    if (this.size() === 0)
      throw new Error("Queue underflow");
    return this._arr[0].key;
  }, t.prototype.add = function(n, r) {
    var i = this._keyIndices;
    if (n = String(n), !e.has(i, n)) {
      var a = this._arr, o = a.length;
      return i[n] = o, a.push({ key: n, priority: r }), this._decrease(o), !0;
    }
    return !1;
  }, t.prototype.removeMin = function() {
    this._swap(0, this._arr.length - 1);
    var n = this._arr.pop();
    return delete this._keyIndices[n.key], this._heapify(0), n.key;
  }, t.prototype.decrease = function(n, r) {
    var i = this._keyIndices[n];
    if (r > this._arr[i].priority)
      throw new Error("New priority is greater than current priority. Key: " + n + " Old: " + this._arr[i].priority + " New: " + r);
    this._arr[i].priority = r, this._decrease(i);
  }, t.prototype._heapify = function(n) {
    var r = this._arr, i = 2 * n, a = i + 1, o = n;
    i < r.length && (o = r[i].priority < r[o].priority ? i : o, a < r.length && (o = r[a].priority < r[o].priority ? a : o), o !== n && (this._swap(n, o), this._heapify(o)));
  }, t.prototype._decrease = function(n) {
    for (var r = this._arr, i = r[n].priority, a; n !== 0 && (a = n >> 1, !(r[a].priority < i)); )
      this._swap(n, a), n = a;
  }, t.prototype._swap = function(n, r) {
    var i = this._arr, a = this._keyIndices, o = i[n], s = i[r];
    i[n] = s, i[r] = o, a[s.key] = n, a[o.key] = r;
  }, Rc;
}
var Ic, C0;
function sA() {
  if (C0)
    return Ic;
  C0 = 1;
  var e = Nt(), t = oA();
  Ic = r;
  var n = e.constant(1);
  function r(a, o, s, u) {
    return i(
      a,
      String(o),
      s || n,
      u || function(l) {
        return a.outEdges(l);
      }
    );
  }
  function i(a, o, s, u) {
    var l = {}, f = new t(), c, p, d = function(y) {
      var v = y.v !== c ? y.v : y.w, _ = l[v], m = s(y), E = p.distance + m;
      if (m < 0)
        throw new Error("dijkstra does not allow negative edge weights. Bad edge: " + y + " Weight: " + m);
      E < _.distance && (_.distance = E, _.predecessor = c, f.decrease(v, E));
    };
    for (a.nodes().forEach(function(y) {
      var v = y === o ? 0 : Number.POSITIVE_INFINITY;
      l[y] = { distance: v }, f.add(y, v);
    }); f.size() > 0 && (c = f.removeMin(), p = l[c], p.distance !== Number.POSITIVE_INFINITY); )
      u(c).forEach(d);
    return l;
  }
  return Ic;
}
var Tc, D0;
function NP() {
  if (D0)
    return Tc;
  D0 = 1;
  var e = sA(), t = Nt();
  Tc = n;
  function n(r, i, a) {
    return t.transform(r.nodes(), function(o, s) {
      o[s] = e(r, s, i, a);
    }, {});
  }
  return Tc;
}
var xc, q0;
function uA() {
  if (q0)
    return xc;
  q0 = 1;
  var e = Nt();
  xc = t;
  function t(n) {
    var r = 0, i = [], a = {}, o = [];
    function s(u) {
      var l = a[u] = {
        onStack: !0,
        lowlink: r,
        index: r++
      };
      if (i.push(u), n.successors(u).forEach(function(p) {
        e.has(a, p) ? a[p].onStack && (l.lowlink = Math.min(l.lowlink, a[p].index)) : (s(p), l.lowlink = Math.min(l.lowlink, a[p].lowlink));
      }), l.lowlink === l.index) {
        var f = [], c;
        do
          c = i.pop(), a[c].onStack = !1, f.push(c);
        while (u !== c);
        o.push(f);
      }
    }
    return n.nodes().forEach(function(u) {
      e.has(a, u) || s(u);
    }), o;
  }
  return xc;
}
var Nc, j0;
function OP() {
  if (j0)
    return Nc;
  j0 = 1;
  var e = Nt(), t = uA();
  Nc = n;
  function n(r) {
    return e.filter(t(r), function(i) {
      return i.length > 1 || i.length === 1 && r.hasEdge(i[0], i[0]);
    });
  }
  return Nc;
}
var Oc, P0;
function $P() {
  if (P0)
    return Oc;
  P0 = 1;
  var e = Nt();
  Oc = n;
  var t = e.constant(1);
  function n(i, a, o) {
    return r(
      i,
      a || t,
      o || function(s) {
        return i.outEdges(s);
      }
    );
  }
  function r(i, a, o) {
    var s = {}, u = i.nodes();
    return u.forEach(function(l) {
      s[l] = {}, s[l][l] = { distance: 0 }, u.forEach(function(f) {
        l !== f && (s[l][f] = { distance: Number.POSITIVE_INFINITY });
      }), o(l).forEach(function(f) {
        var c = f.v === l ? f.w : f.v, p = a(f);
        s[l][c] = { distance: p, predecessor: l };
      });
    }), u.forEach(function(l) {
      var f = s[l];
      u.forEach(function(c) {
        var p = s[c];
        u.forEach(function(d) {
          var y = p[l], v = f[d], _ = p[d], m = y.distance + v.distance;
          m < _.distance && (_.distance = m, _.predecessor = v.predecessor);
        });
      });
    }), s;
  }
  return Oc;
}
var $c, k0;
function lA() {
  if (k0)
    return $c;
  k0 = 1;
  var e = Nt();
  $c = t, t.CycleException = n;
  function t(r) {
    var i = {}, a = {}, o = [];
    function s(u) {
      if (e.has(a, u))
        throw new n();
      e.has(i, u) || (a[u] = !0, i[u] = !0, e.each(r.predecessors(u), s), delete a[u], o.push(u));
    }
    if (e.each(r.sinks(), s), e.size(i) !== r.nodeCount())
      throw new n();
    return o;
  }
  function n() {
  }
  return n.prototype = new Error(), $c;
}
var Lc, F0;
function LP() {
  if (F0)
    return Lc;
  F0 = 1;
  var e = lA();
  Lc = t;
  function t(n) {
    try {
      e(n);
    } catch (r) {
      if (r instanceof e.CycleException)
        return !1;
      throw r;
    }
    return !0;
  }
  return Lc;
}
var Mc, B0;
function fA() {
  if (B0)
    return Mc;
  B0 = 1;
  var e = Nt();
  Mc = t;
  function t(r, i, a) {
    e.isArray(i) || (i = [i]);
    var o = (r.isDirected() ? r.successors : r.neighbors).bind(r), s = [], u = {};
    return e.each(i, function(l) {
      if (!r.hasNode(l))
        throw new Error("Graph does not have node: " + l);
      n(r, l, a === "post", u, o, s);
    }), s;
  }
  function n(r, i, a, o, s, u) {
    e.has(o, i) || (o[i] = !0, a || u.push(i), e.each(s(i), function(l) {
      n(r, l, a, o, s, u);
    }), a && u.push(i));
  }
  return Mc;
}
var Cc, U0;
function MP() {
  if (U0)
    return Cc;
  U0 = 1;
  var e = fA();
  Cc = t;
  function t(n, r) {
    return e(n, r, "post");
  }
  return Cc;
}
var Dc, H0;
function CP() {
  if (H0)
    return Dc;
  H0 = 1;
  var e = fA();
  Dc = t;
  function t(n, r) {
    return e(n, r, "pre");
  }
  return Dc;
}
var qc, G0;
function DP() {
  if (G0)
    return qc;
  G0 = 1;
  var e = Nt(), t = Up(), n = oA();
  qc = r;
  function r(i, a) {
    var o = new t(), s = {}, u = new n(), l;
    function f(p) {
      var d = p.v === l ? p.w : p.v, y = u.priority(d);
      if (y !== void 0) {
        var v = a(p);
        v < y && (s[d] = l, u.decrease(d, v));
      }
    }
    if (i.nodeCount() === 0)
      return o;
    e.each(i.nodes(), function(p) {
      u.add(p, Number.POSITIVE_INFINITY), o.setNode(p);
    }), u.decrease(i.nodes()[0], 0);
    for (var c = !1; u.size() > 0; ) {
      if (l = u.removeMin(), e.has(s, l))
        o.setEdge(l, s[l]);
      else {
        if (c)
          throw new Error("Input graph is not connected: " + i);
        c = !0;
      }
      i.nodeEdges(l).forEach(f);
    }
    return o;
  }
  return qc;
}
var jc, V0;
function qP() {
  return V0 || (V0 = 1, jc = {
    components: xP(),
    dijkstra: sA(),
    dijkstraAll: NP(),
    findCycles: OP(),
    floydWarshall: $P(),
    isAcyclic: LP(),
    postorder: MP(),
    preorder: CP(),
    prim: DP(),
    tarjan: uA(),
    topsort: lA()
  }), jc;
}
var Pc, W0;
function jP() {
  if (W0)
    return Pc;
  W0 = 1;
  var e = IP();
  return Pc = {
    Graph: e.Graph,
    json: TP(),
    alg: qP(),
    version: e.version
  }, Pc;
}
var eo;
if (typeof Ip == "function")
  try {
    eo = jP();
  } catch {
  }
eo || (eo = window.graphlib);
var Ht = eo, kc, J0;
function PP() {
  if (J0)
    return kc;
  J0 = 1;
  var e = DS(), t = 1, n = 4;
  function r(i) {
    return e(i, t | n);
  }
  return kc = r, kc;
}
var Fc, z0;
function Jo() {
  if (z0)
    return Fc;
  z0 = 1;
  var e = ui(), t = Mn(), n = Po(), r = xt();
  function i(a, o, s) {
    if (!r(s))
      return !1;
    var u = typeof o;
    return (u == "number" ? t(s) && n(o, s.length) : u == "string" && o in s) ? e(s[o], a) : !1;
  }
  return Fc = i, Fc;
}
var Bc, K0;
function kP() {
  if (K0)
    return Bc;
  K0 = 1;
  var e = Wo(), t = ui(), n = Jo(), r = Lr(), i = Object.prototype, a = i.hasOwnProperty, o = e(function(s, u) {
    s = Object(s);
    var l = -1, f = u.length, c = f > 2 ? u[2] : void 0;
    for (c && n(u[0], u[1], c) && (f = 1); ++l < f; )
      for (var p = u[l], d = r(p), y = -1, v = d.length; ++y < v; ) {
        var _ = d[y], m = s[_];
        (m === void 0 || t(m, i[_]) && !a.call(s, _)) && (s[_] = p[_]);
      }
    return s;
  });
  return Bc = o, Bc;
}
var Uc, X0;
function FP() {
  if (X0)
    return Uc;
  X0 = 1;
  var e = Cn(), t = Mn(), n = Zn();
  function r(i) {
    return function(a, o, s) {
      var u = Object(a);
      if (!t(a)) {
        var l = e(o, 3);
        a = n(a), o = function(c) {
          return l(u[c], c, u);
        };
      }
      var f = i(a, o, s);
      return f > -1 ? u[l ? a[f] : f] : void 0;
    };
  }
  return Uc = r, Uc;
}
var Hc, Y0;
function BP() {
  if (Y0)
    return Hc;
  Y0 = 1;
  var e = /\s/;
  function t(n) {
    for (var r = n.length; r-- && e.test(n.charAt(r)); )
      ;
    return r;
  }
  return Hc = t, Hc;
}
var Gc, Q0;
function UP() {
  if (Q0)
    return Gc;
  Q0 = 1;
  var e = BP(), t = /^\s+/;
  function n(r) {
    return r && r.slice(0, e(r) + 1).replace(t, "");
  }
  return Gc = n, Gc;
}
var Vc, Z0;
function HP() {
  if (Z0)
    return Vc;
  Z0 = 1;
  var e = UP(), t = xt(), n = di(), r = 0 / 0, i = /^[-+]0x[0-9a-f]+$/i, a = /^0b[01]+$/i, o = /^0o[0-7]+$/i, s = parseInt;
  function u(l) {
    if (typeof l == "number")
      return l;
    if (n(l))
      return r;
    if (t(l)) {
      var f = typeof l.valueOf == "function" ? l.valueOf() : l;
      l = t(f) ? f + "" : f;
    }
    if (typeof l != "string")
      return l === 0 ? l : +l;
    l = e(l);
    var c = a.test(l);
    return c || o.test(l) ? s(l.slice(2), c ? 2 : 8) : i.test(l) ? r : +l;
  }
  return Vc = u, Vc;
}
var Wc, em;
function cA() {
  if (em)
    return Wc;
  em = 1;
  var e = HP(), t = 1 / 0, n = 17976931348623157e292;
  function r(i) {
    if (!i)
      return i === 0 ? i : 0;
    if (i = e(i), i === t || i === -t) {
      var a = i < 0 ? -1 : 1;
      return a * n;
    }
    return i === i ? i : 0;
  }
  return Wc = r, Wc;
}
var Jc, tm;
function GP() {
  if (tm)
    return Jc;
  tm = 1;
  var e = cA();
  function t(n) {
    var r = e(n), i = r % 1;
    return r === r ? i ? r - i : r : 0;
  }
  return Jc = t, Jc;
}
var zc, nm;
function VP() {
  if (nm)
    return zc;
  nm = 1;
  var e = rA(), t = Cn(), n = GP(), r = Math.max;
  function i(a, o, s) {
    var u = a == null ? 0 : a.length;
    if (!u)
      return -1;
    var l = s == null ? 0 : n(s);
    return l < 0 && (l = r(u + l, 0)), e(a, t(o, 3), l);
  }
  return zc = i, zc;
}
var Kc, rm;
function WP() {
  if (rm)
    return Kc;
  rm = 1;
  var e = FP(), t = VP(), n = e(t);
  return Kc = n, Kc;
}
var Xc, im;
function dA() {
  if (im)
    return Xc;
  im = 1;
  var e = Bp();
  function t(n) {
    var r = n == null ? 0 : n.length;
    return r ? e(n, 1) : [];
  }
  return Xc = t, Xc;
}
var Yc, am;
function JP() {
  if (am)
    return Yc;
  am = 1;
  var e = jp(), t = qS(), n = Lr();
  function r(i, a) {
    return i == null ? i : e(i, t(a), n);
  }
  return Yc = r, Yc;
}
var Qc, om;
function zP() {
  if (om)
    return Qc;
  om = 1;
  function e(t) {
    var n = t == null ? 0 : t.length;
    return n ? t[n - 1] : void 0;
  }
  return Qc = e, Qc;
}
var Zc, sm;
function KP() {
  if (sm)
    return Zc;
  sm = 1;
  var e = qo(), t = Pp(), n = Cn();
  function r(i, a) {
    var o = {};
    return a = n(a, 3), t(i, function(s, u, l) {
      e(o, u, a(s, u, l));
    }), o;
  }
  return Zc = r, Zc;
}
var ed, um;
function Hp() {
  if (um)
    return ed;
  um = 1;
  var e = di();
  function t(n, r, i) {
    for (var a = -1, o = n.length; ++a < o; ) {
      var s = n[a], u = r(s);
      if (u != null && (l === void 0 ? u === u && !e(u) : i(u, l)))
        var l = u, f = s;
    }
    return f;
  }
  return ed = t, ed;
}
var td, lm;
function XP() {
  if (lm)
    return td;
  lm = 1;
  function e(t, n) {
    return t > n;
  }
  return td = e, td;
}
var nd, fm;
function YP() {
  if (fm)
    return nd;
  fm = 1;
  var e = Hp(), t = XP(), n = Mr();
  function r(i) {
    return i && i.length ? e(i, n, t) : void 0;
  }
  return nd = r, nd;
}
var rd, cm;
function hA() {
  if (cm)
    return rd;
  cm = 1;
  var e = qo(), t = ui();
  function n(r, i, a) {
    (a !== void 0 && !t(r[i], a) || a === void 0 && !(i in r)) && e(r, i, a);
  }
  return rd = n, rd;
}
var id, dm;
function QP() {
  if (dm)
    return id;
  dm = 1;
  var e = Or(), t = Bo(), n = fn(), r = "[object Object]", i = Function.prototype, a = Object.prototype, o = i.toString, s = a.hasOwnProperty, u = o.call(Object);
  function l(f) {
    if (!n(f) || e(f) != r)
      return !1;
    var c = t(f);
    if (c === null)
      return !0;
    var p = s.call(c, "constructor") && c.constructor;
    return typeof p == "function" && p instanceof p && o.call(p) == u;
  }
  return id = l, id;
}
var ad, hm;
function pA() {
  if (hm)
    return ad;
  hm = 1;
  function e(t, n) {
    if (!(n === "constructor" && typeof t[n] == "function") && n != "__proto__")
      return t[n];
  }
  return ad = e, ad;
}
var od, pm;
function ZP() {
  if (pm)
    return od;
  pm = 1;
  var e = ra(), t = Lr();
  function n(r) {
    return e(r, t(r));
  }
  return od = n, od;
}
var sd, gm;
function ek() {
  if (gm)
    return sd;
  gm = 1;
  var e = hA(), t = SS(), n = LS(), r = AS(), i = CS(), a = ia(), o = Oe(), s = iA(), u = fi(), l = na(), f = xt(), c = QP(), p = aa(), d = pA(), y = ZP();
  function v(_, m, E, L, I, j, M) {
    var O = d(_, E), $ = d(m, E), q = M.get($);
    if (q) {
      e(_, E, q);
      return;
    }
    var N = j ? j(O, $, E + "", _, m, M) : void 0, P = N === void 0;
    if (P) {
      var H = o($), G = !H && u($), z = !H && !G && p($);
      N = $, H || G || z ? o(O) ? N = O : s(O) ? N = r(O) : G ? (P = !1, N = t($, !0)) : z ? (P = !1, N = n($, !0)) : N = [] : c($) || a($) ? (N = O, a(O) ? N = y(O) : (!f(O) || l(O)) && (N = i($))) : P = !1;
    }
    P && (M.set($, N), I(N, $, L, j, M), M.delete($)), e(_, E, N);
  }
  return sd = v, sd;
}
var ud, ym;
function tk() {
  if (ym)
    return ud;
  ym = 1;
  var e = Do(), t = hA(), n = jp(), r = ek(), i = xt(), a = Lr(), o = pA();
  function s(u, l, f, c, p) {
    u !== l && n(l, function(d, y) {
      if (p || (p = new e()), i(d))
        r(u, l, y, f, s, c, p);
      else {
        var v = c ? c(o(u, y), d, y + "", u, l, p) : void 0;
        v === void 0 && (v = d), t(u, y, v);
      }
    }, a);
  }
  return ud = s, ud;
}
var ld, vm;
function nk() {
  if (vm)
    return ld;
  vm = 1;
  var e = Wo(), t = Jo();
  function n(r) {
    return e(function(i, a) {
      var o = -1, s = a.length, u = s > 1 ? a[s - 1] : void 0, l = s > 2 ? a[2] : void 0;
      for (u = r.length > 3 && typeof u == "function" ? (s--, u) : void 0, l && t(a[0], a[1], l) && (u = s < 3 ? void 0 : u, s = 1), i = Object(i); ++o < s; ) {
        var f = a[o];
        f && r(i, f, o, u);
      }
      return i;
    });
  }
  return ld = n, ld;
}
var fd, _m;
function rk() {
  if (_m)
    return fd;
  _m = 1;
  var e = tk(), t = nk(), n = t(function(r, i, a) {
    e(r, i, a);
  });
  return fd = n, fd;
}
var cd, bm;
function gA() {
  if (bm)
    return cd;
  bm = 1;
  function e(t, n) {
    return t < n;
  }
  return cd = e, cd;
}
var dd, mm;
function ik() {
  if (mm)
    return dd;
  mm = 1;
  var e = Hp(), t = gA(), n = Mr();
  function r(i) {
    return i && i.length ? e(i, n, t) : void 0;
  }
  return dd = r, dd;
}
var hd, wm;
function ak() {
  if (wm)
    return hd;
  wm = 1;
  var e = Hp(), t = Cn(), n = gA();
  function r(i, a) {
    return i && i.length ? e(i, t(a, 2), n) : void 0;
  }
  return hd = r, hd;
}
var pd, Em;
function ok() {
  if (Em)
    return pd;
  Em = 1;
  var e = Ut(), t = function() {
    return e.Date.now();
  };
  return pd = t, pd;
}
var gd, Sm;
function sk() {
  if (Sm)
    return gd;
  Sm = 1;
  var e = jo(), t = Go(), n = Po(), r = xt(), i = oa();
  function a(o, s, u, l) {
    if (!r(o))
      return o;
    s = t(s, o);
    for (var f = -1, c = s.length, p = c - 1, d = o; d != null && ++f < c; ) {
      var y = i(s[f]), v = u;
      if (y === "__proto__" || y === "constructor" || y === "prototype")
        return o;
      if (f != p) {
        var _ = d[y];
        v = l ? l(_, y, d) : void 0, v === void 0 && (v = r(_) ? _ : n(s[f + 1]) ? [] : {});
      }
      e(d, y, v), d = d[y];
    }
    return o;
  }
  return gd = a, gd;
}
var yd, Am;
function uk() {
  if (Am)
    return yd;
  Am = 1;
  var e = Vo(), t = sk(), n = Go();
  function r(i, a, o) {
    for (var s = -1, u = a.length, l = {}; ++s < u; ) {
      var f = a[s], c = e(i, f);
      o(c, f) && t(l, n(f, i), c);
    }
    return l;
  }
  return yd = r, yd;
}
var vd, Rm;
function lk() {
  if (Rm)
    return vd;
  Rm = 1;
  var e = uk(), t = JS();
  function n(r, i) {
    return e(r, i, function(a, o) {
      return t(r, o);
    });
  }
  return vd = n, vd;
}
var _d, Im;
function fk() {
  if (Im)
    return _d;
  Im = 1;
  var e = dA(), t = tA(), n = nA();
  function r(i) {
    return n(t(i, void 0, e), i + "");
  }
  return _d = r, _d;
}
var bd, Tm;
function ck() {
  if (Tm)
    return bd;
  Tm = 1;
  var e = lk(), t = fk(), n = t(function(r, i) {
    return r == null ? {} : e(r, i);
  });
  return bd = n, bd;
}
var md, xm;
function dk() {
  if (xm)
    return md;
  xm = 1;
  var e = Math.ceil, t = Math.max;
  function n(r, i, a, o) {
    for (var s = -1, u = t(e((i - r) / (a || 1)), 0), l = Array(u); u--; )
      l[o ? u : ++s] = r, r += a;
    return l;
  }
  return md = n, md;
}
var wd, Nm;
function hk() {
  if (Nm)
    return wd;
  Nm = 1;
  var e = dk(), t = Jo(), n = cA();
  function r(i) {
    return function(a, o, s) {
      return s && typeof s != "number" && t(a, o, s) && (o = s = void 0), a = n(a), o === void 0 ? (o = a, a = 0) : o = n(o), s = s === void 0 ? a < o ? 1 : -1 : n(s), e(a, o, s, i);
    };
  }
  return wd = r, wd;
}
var Ed, Om;
function pk() {
  if (Om)
    return Ed;
  Om = 1;
  var e = hk(), t = e();
  return Ed = t, Ed;
}
var Sd, $m;
function gk() {
  if ($m)
    return Sd;
  $m = 1;
  function e(t, n) {
    var r = t.length;
    for (t.sort(n); r--; )
      t[r] = t[r].value;
    return t;
  }
  return Sd = e, Sd;
}
var Ad, Lm;
function yk() {
  if (Lm)
    return Ad;
  Lm = 1;
  var e = di();
  function t(n, r) {
    if (n !== r) {
      var i = n !== void 0, a = n === null, o = n === n, s = e(n), u = r !== void 0, l = r === null, f = r === r, c = e(r);
      if (!l && !c && !s && n > r || s && u && f && !l && !c || a && u && f || !i && f || !o)
        return 1;
      if (!a && !s && !c && n < r || c && i && o && !a && !s || l && i && o || !u && o || !f)
        return -1;
    }
    return 0;
  }
  return Ad = t, Ad;
}
var Rd, Mm;
function vk() {
  if (Mm)
    return Rd;
  Mm = 1;
  var e = yk();
  function t(n, r, i) {
    for (var a = -1, o = n.criteria, s = r.criteria, u = o.length, l = i.length; ++a < u; ) {
      var f = e(o[a], s[a]);
      if (f) {
        if (a >= l)
          return f;
        var c = i[a];
        return f * (c == "desc" ? -1 : 1);
      }
    }
    return n.index - r.index;
  }
  return Rd = t, Rd;
}
var Id, Cm;
function _k() {
  if (Cm)
    return Id;
  Cm = 1;
  var e = Ho(), t = Vo(), n = Cn(), r = QS(), i = gk(), a = ko(), o = vk(), s = Mr(), u = Oe();
  function l(f, c, p) {
    c.length ? c = e(c, function(v) {
      return u(v) ? function(_) {
        return t(_, v.length === 1 ? v[0] : v);
      } : v;
    }) : c = [s];
    var d = -1;
    c = e(c, a(n));
    var y = r(f, function(v, _, m) {
      var E = e(c, function(L) {
        return L(v);
      });
      return { criteria: E, index: ++d, value: v };
    });
    return i(y, function(v, _) {
      return o(v, _, p);
    });
  }
  return Id = l, Id;
}
var Td, Dm;
function bk() {
  if (Dm)
    return Td;
  Dm = 1;
  var e = Bp(), t = _k(), n = Wo(), r = Jo(), i = n(function(a, o) {
    if (a == null)
      return [];
    var s = o.length;
    return s > 1 && r(a, o[0], o[1]) ? o = [] : s > 2 && r(o[0], o[1], o[2]) && (o = [o[0]]), t(a, e(o, 1), []);
  });
  return Td = i, Td;
}
var xd, qm;
function mk() {
  if (qm)
    return xd;
  qm = 1;
  var e = VS(), t = 0;
  function n(r) {
    var i = ++t;
    return e(r) + i;
  }
  return xd = n, xd;
}
var Nd, jm;
function wk() {
  if (jm)
    return Nd;
  jm = 1;
  function e(t, n, r) {
    for (var i = -1, a = t.length, o = n.length, s = {}; ++i < a; ) {
      var u = i < o ? n[i] : void 0;
      r(s, t[i], u);
    }
    return s;
  }
  return Nd = e, Nd;
}
var Od, Pm;
function Ek() {
  if (Pm)
    return Od;
  Pm = 1;
  var e = jo(), t = wk();
  function n(r, i) {
    return t(r || [], i || [], e);
  }
  return Od = n, Od;
}
var to;
if (typeof Ip == "function")
  try {
    to = {
      cloneDeep: PP(),
      constant: qp(),
      defaults: kP(),
      each: PS(),
      filter: KS(),
      find: WP(),
      flatten: dA(),
      forEach: jS(),
      forIn: JP(),
      has: XS(),
      isUndefined: YS(),
      last: zP(),
      map: ZS(),
      mapValues: KP(),
      max: YP(),
      merge: rk(),
      min: ik(),
      minBy: ak(),
      now: ok(),
      pick: ck(),
      range: pk(),
      reduce: eA(),
      sortBy: bk(),
      uniqueId: mk(),
      values: aA(),
      zipObject: Ek()
    };
  } catch {
  }
to || (to = window._);
var Se = to, Sk = zo;
function zo() {
  var e = {};
  e._next = e._prev = e, this._sentinel = e;
}
zo.prototype.dequeue = function() {
  var e = this._sentinel, t = e._prev;
  if (t !== e)
    return yA(t), t;
};
zo.prototype.enqueue = function(e) {
  var t = this._sentinel;
  e._prev && e._next && yA(e), e._next = t._next, t._next._prev = e, t._next = e, e._prev = t;
};
zo.prototype.toString = function() {
  for (var e = [], t = this._sentinel, n = t._prev; n !== t; )
    e.push(JSON.stringify(n, Ak)), n = n._prev;
  return "[" + e.join(", ") + "]";
};
function yA(e) {
  e._prev._next = e._next, e._next._prev = e._prev, delete e._next, delete e._prev;
}
function Ak(e, t) {
  if (e !== "_next" && e !== "_prev")
    return t;
}
var bn = Se, Rk = Ht.Graph, Ik = Sk, Tk = Nk, xk = bn.constant(1);
function Nk(e, t) {
  if (e.nodeCount() <= 1)
    return [];
  var n = $k(e, t || xk), r = Ok(n.graph, n.buckets, n.zeroIdx);
  return bn.flatten(bn.map(r, function(i) {
    return e.outEdges(i.v, i.w);
  }), !0);
}
function Ok(e, t, n) {
  for (var r = [], i = t[t.length - 1], a = t[0], o; e.nodeCount(); ) {
    for (; o = a.dequeue(); )
      $d(e, t, n, o);
    for (; o = i.dequeue(); )
      $d(e, t, n, o);
    if (e.nodeCount()) {
      for (var s = t.length - 2; s > 0; --s)
        if (o = t[s].dequeue(), o) {
          r = r.concat($d(e, t, n, o, !0));
          break;
        }
    }
  }
  return r;
}
function $d(e, t, n, r, i) {
  var a = i ? [] : void 0;
  return bn.forEach(e.inEdges(r.v), function(o) {
    var s = e.edge(o), u = e.node(o.v);
    i && a.push({ v: o.v, w: o.w }), u.out -= s, Eh(t, n, u);
  }), bn.forEach(e.outEdges(r.v), function(o) {
    var s = e.edge(o), u = o.w, l = e.node(u);
    l.in -= s, Eh(t, n, l);
  }), e.removeNode(r.v), a;
}
function $k(e, t) {
  var n = new Rk(), r = 0, i = 0;
  bn.forEach(e.nodes(), function(s) {
    n.setNode(s, { v: s, in: 0, out: 0 });
  }), bn.forEach(e.edges(), function(s) {
    var u = n.edge(s.v, s.w) || 0, l = t(s), f = u + l;
    n.setEdge(s.v, s.w, f), i = Math.max(i, n.node(s.v).out += l), r = Math.max(r, n.node(s.w).in += l);
  });
  var a = bn.range(i + r + 3).map(function() {
    return new Ik();
  }), o = r + 1;
  return bn.forEach(n.nodes(), function(s) {
    Eh(a, o, n.node(s));
  }), { graph: n, buckets: a, zeroIdx: o };
}
function Eh(e, t, n) {
  n.out ? n.in ? e[n.out - n.in + t].enqueue(n) : e[e.length - 1].enqueue(n) : e[0].enqueue(n);
}
var cr = Se, Lk = Tk, Mk = {
  run: Ck,
  undo: qk
};
function Ck(e) {
  var t = e.graph().acyclicer === "greedy" ? Lk(e, n(e)) : Dk(e);
  cr.forEach(t, function(r) {
    var i = e.edge(r);
    e.removeEdge(r), i.forwardName = r.name, i.reversed = !0, e.setEdge(r.w, r.v, i, cr.uniqueId("rev"));
  });
  function n(r) {
    return function(i) {
      return r.edge(i).weight;
    };
  }
}
function Dk(e) {
  var t = [], n = {}, r = {};
  function i(a) {
    cr.has(r, a) || (r[a] = !0, n[a] = !0, cr.forEach(e.outEdges(a), function(o) {
      cr.has(n, o.w) ? t.push(o) : i(o.w);
    }), delete n[a]);
  }
  return cr.forEach(e.nodes(), i), t;
}
function qk(e) {
  cr.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    if (n.reversed) {
      e.removeEdge(t);
      var r = n.forwardName;
      delete n.reversed, delete n.forwardName, e.setEdge(t.w, t.v, n, r);
    }
  });
}
var fe = Se, vA = Ht.Graph, it = {
  addDummyNode: _A,
  simplify: jk,
  asNonCompoundGraph: Pk,
  successorWeights: kk,
  predecessorWeights: Fk,
  intersectRect: Bk,
  buildLayerMatrix: Uk,
  normalizeRanks: Hk,
  removeEmptyRanks: Gk,
  addBorderNode: Vk,
  maxRank: bA,
  partition: Wk,
  time: Jk,
  notime: zk
};
function _A(e, t, n, r) {
  var i;
  do
    i = fe.uniqueId(r);
  while (e.hasNode(i));
  return n.dummy = t, e.setNode(i, n), i;
}
function jk(e) {
  var t = new vA().setGraph(e.graph());
  return fe.forEach(e.nodes(), function(n) {
    t.setNode(n, e.node(n));
  }), fe.forEach(e.edges(), function(n) {
    var r = t.edge(n.v, n.w) || { weight: 0, minlen: 1 }, i = e.edge(n);
    t.setEdge(n.v, n.w, {
      weight: r.weight + i.weight,
      minlen: Math.max(r.minlen, i.minlen)
    });
  }), t;
}
function Pk(e) {
  var t = new vA({ multigraph: e.isMultigraph() }).setGraph(e.graph());
  return fe.forEach(e.nodes(), function(n) {
    e.children(n).length || t.setNode(n, e.node(n));
  }), fe.forEach(e.edges(), function(n) {
    t.setEdge(n, e.edge(n));
  }), t;
}
function kk(e) {
  var t = fe.map(e.nodes(), function(n) {
    var r = {};
    return fe.forEach(e.outEdges(n), function(i) {
      r[i.w] = (r[i.w] || 0) + e.edge(i).weight;
    }), r;
  });
  return fe.zipObject(e.nodes(), t);
}
function Fk(e) {
  var t = fe.map(e.nodes(), function(n) {
    var r = {};
    return fe.forEach(e.inEdges(n), function(i) {
      r[i.v] = (r[i.v] || 0) + e.edge(i).weight;
    }), r;
  });
  return fe.zipObject(e.nodes(), t);
}
function Bk(e, t) {
  var n = e.x, r = e.y, i = t.x - n, a = t.y - r, o = e.width / 2, s = e.height / 2;
  if (!i && !a)
    throw new Error("Not possible to find intersection inside of the rectangle");
  var u, l;
  return Math.abs(a) * o > Math.abs(i) * s ? (a < 0 && (s = -s), u = s * i / a, l = s) : (i < 0 && (o = -o), u = o, l = o * a / i), { x: n + u, y: r + l };
}
function Uk(e) {
  var t = fe.map(fe.range(bA(e) + 1), function() {
    return [];
  });
  return fe.forEach(e.nodes(), function(n) {
    var r = e.node(n), i = r.rank;
    fe.isUndefined(i) || (t[i][r.order] = n);
  }), t;
}
function Hk(e) {
  var t = fe.min(fe.map(e.nodes(), function(n) {
    return e.node(n).rank;
  }));
  fe.forEach(e.nodes(), function(n) {
    var r = e.node(n);
    fe.has(r, "rank") && (r.rank -= t);
  });
}
function Gk(e) {
  var t = fe.min(fe.map(e.nodes(), function(a) {
    return e.node(a).rank;
  })), n = [];
  fe.forEach(e.nodes(), function(a) {
    var o = e.node(a).rank - t;
    n[o] || (n[o] = []), n[o].push(a);
  });
  var r = 0, i = e.graph().nodeRankFactor;
  fe.forEach(n, function(a, o) {
    fe.isUndefined(a) && o % i !== 0 ? --r : r && fe.forEach(a, function(s) {
      e.node(s).rank += r;
    });
  });
}
function Vk(e, t, n, r) {
  var i = {
    width: 0,
    height: 0
  };
  return arguments.length >= 4 && (i.rank = n, i.order = r), _A(e, "border", i, t);
}
function bA(e) {
  return fe.max(fe.map(e.nodes(), function(t) {
    var n = e.node(t).rank;
    if (!fe.isUndefined(n))
      return n;
  }));
}
function Wk(e, t) {
  var n = { lhs: [], rhs: [] };
  return fe.forEach(e, function(r) {
    t(r) ? n.lhs.push(r) : n.rhs.push(r);
  }), n;
}
function Jk(e, t) {
  var n = fe.now();
  try {
    return t();
  } finally {
    console.log(e + " time: " + (fe.now() - n) + "ms");
  }
}
function zk(e, t) {
  return t();
}
var mA = Se, Kk = it, Xk = {
  run: Yk,
  undo: Zk
};
function Yk(e) {
  e.graph().dummyChains = [], mA.forEach(e.edges(), function(t) {
    Qk(e, t);
  });
}
function Qk(e, t) {
  var n = t.v, r = e.node(n).rank, i = t.w, a = e.node(i).rank, o = t.name, s = e.edge(t), u = s.labelRank;
  if (a !== r + 1) {
    e.removeEdge(t);
    var l, f, c;
    for (c = 0, ++r; r < a; ++c, ++r)
      s.points = [], f = {
        width: 0,
        height: 0,
        edgeLabel: s,
        edgeObj: t,
        rank: r
      }, l = Kk.addDummyNode(e, "edge", f, "_d"), r === u && (f.width = s.width, f.height = s.height, f.dummy = "edge-label", f.labelpos = s.labelpos), e.setEdge(n, l, { weight: s.weight }, o), c === 0 && e.graph().dummyChains.push(l), n = l;
    e.setEdge(n, i, { weight: s.weight }, o);
  }
}
function Zk(e) {
  mA.forEach(e.graph().dummyChains, function(t) {
    var n = e.node(t), r = n.edgeLabel, i;
    for (e.setEdge(n.edgeObj, r); n.dummy; )
      i = e.successors(t)[0], e.removeNode(t), r.points.push({ x: n.x, y: n.y }), n.dummy === "edge-label" && (r.x = n.x, r.y = n.y, r.width = n.width, r.height = n.height), t = i, n = e.node(t);
  });
}
var _a = Se, Ko = {
  longestPath: eF,
  slack: tF
};
function eF(e) {
  var t = {};
  function n(r) {
    var i = e.node(r);
    if (_a.has(t, r))
      return i.rank;
    t[r] = !0;
    var a = _a.min(_a.map(e.outEdges(r), function(o) {
      return n(o.w) - e.edge(o).minlen;
    }));
    return (a === Number.POSITIVE_INFINITY || // return value of _.map([]) for Lodash 3
    a === void 0 || // return value of _.map([]) for Lodash 4
    a === null) && (a = 0), i.rank = a;
  }
  _a.forEach(e.sources(), n);
}
function tF(e, t) {
  return e.node(t.w).rank - e.node(t.v).rank - e.edge(t).minlen;
}
var no = Se, nF = Ht.Graph, ro = Ko.slack, wA = rF;
function rF(e) {
  var t = new nF({ directed: !1 }), n = e.nodes()[0], r = e.nodeCount();
  t.setNode(n, {});
  for (var i, a; iF(t, e) < r; )
    i = aF(t, e), a = t.hasNode(i.v) ? ro(e, i) : -ro(e, i), oF(t, e, a);
  return t;
}
function iF(e, t) {
  function n(r) {
    no.forEach(t.nodeEdges(r), function(i) {
      var a = i.v, o = r === a ? i.w : a;
      !e.hasNode(o) && !ro(t, i) && (e.setNode(o, {}), e.setEdge(r, o, {}), n(o));
    });
  }
  return no.forEach(e.nodes(), n), e.nodeCount();
}
function aF(e, t) {
  return no.minBy(t.edges(), function(n) {
    if (e.hasNode(n.v) !== e.hasNode(n.w))
      return ro(t, n);
  });
}
function oF(e, t, n) {
  no.forEach(e.nodes(), function(r) {
    t.node(r).rank += n;
  });
}
var xn = Se, sF = wA, uF = Ko.slack, lF = Ko.longestPath, fF = Ht.alg.preorder, cF = Ht.alg.postorder, dF = it.simplify, hF = Cr;
Cr.initLowLimValues = Vp;
Cr.initCutValues = Gp;
Cr.calcCutValue = EA;
Cr.leaveEdge = AA;
Cr.enterEdge = RA;
Cr.exchangeEdges = IA;
function Cr(e) {
  e = dF(e), lF(e);
  var t = sF(e);
  Vp(t), Gp(t, e);
  for (var n, r; n = AA(t); )
    r = RA(t, e, n), IA(t, e, n, r);
}
function Gp(e, t) {
  var n = cF(e, e.nodes());
  n = n.slice(0, n.length - 1), xn.forEach(n, function(r) {
    pF(e, t, r);
  });
}
function pF(e, t, n) {
  var r = e.node(n), i = r.parent;
  e.edge(n, i).cutvalue = EA(e, t, n);
}
function EA(e, t, n) {
  var r = e.node(n), i = r.parent, a = !0, o = t.edge(n, i), s = 0;
  return o || (a = !1, o = t.edge(i, n)), s = o.weight, xn.forEach(t.nodeEdges(n), function(u) {
    var l = u.v === n, f = l ? u.w : u.v;
    if (f !== i) {
      var c = l === a, p = t.edge(u).weight;
      if (s += c ? p : -p, yF(e, n, f)) {
        var d = e.edge(n, f).cutvalue;
        s += c ? -d : d;
      }
    }
  }), s;
}
function Vp(e, t) {
  arguments.length < 2 && (t = e.nodes()[0]), SA(e, {}, 1, t);
}
function SA(e, t, n, r, i) {
  var a = n, o = e.node(r);
  return t[r] = !0, xn.forEach(e.neighbors(r), function(s) {
    xn.has(t, s) || (n = SA(e, t, n, s, r));
  }), o.low = a, o.lim = n++, i ? o.parent = i : delete o.parent, n;
}
function AA(e) {
  return xn.find(e.edges(), function(t) {
    return e.edge(t).cutvalue < 0;
  });
}
function RA(e, t, n) {
  var r = n.v, i = n.w;
  t.hasEdge(r, i) || (r = n.w, i = n.v);
  var a = e.node(r), o = e.node(i), s = a, u = !1;
  a.lim > o.lim && (s = o, u = !0);
  var l = xn.filter(t.edges(), function(f) {
    return u === km(e, e.node(f.v), s) && u !== km(e, e.node(f.w), s);
  });
  return xn.minBy(l, function(f) {
    return uF(t, f);
  });
}
function IA(e, t, n, r) {
  var i = n.v, a = n.w;
  e.removeEdge(i, a), e.setEdge(r.v, r.w, {}), Vp(e), Gp(e, t), gF(e, t);
}
function gF(e, t) {
  var n = xn.find(e.nodes(), function(i) {
    return !t.node(i).parent;
  }), r = fF(e, n);
  r = r.slice(1), xn.forEach(r, function(i) {
    var a = e.node(i).parent, o = t.edge(i, a), s = !1;
    o || (o = t.edge(a, i), s = !0), t.node(i).rank = t.node(a).rank + (s ? o.minlen : -o.minlen);
  });
}
function yF(e, t, n) {
  return e.hasEdge(t, n);
}
function km(e, t, n) {
  return n.low <= t.lim && t.lim <= n.lim;
}
var vF = Ko, TA = vF.longestPath, _F = wA, bF = hF, mF = wF;
function wF(e) {
  switch (e.graph().ranker) {
    case "network-simplex":
      Fm(e);
      break;
    case "tight-tree":
      SF(e);
      break;
    case "longest-path":
      EF(e);
      break;
    default:
      Fm(e);
  }
}
var EF = TA;
function SF(e) {
  TA(e), _F(e);
}
function Fm(e) {
  bF(e);
}
var Sh = Se, AF = RF;
function RF(e) {
  var t = TF(e);
  Sh.forEach(e.graph().dummyChains, function(n) {
    for (var r = e.node(n), i = r.edgeObj, a = IF(e, t, i.v, i.w), o = a.path, s = a.lca, u = 0, l = o[u], f = !0; n !== i.w; ) {
      if (r = e.node(n), f) {
        for (; (l = o[u]) !== s && e.node(l).maxRank < r.rank; )
          u++;
        l === s && (f = !1);
      }
      if (!f) {
        for (; u < o.length - 1 && e.node(l = o[u + 1]).minRank <= r.rank; )
          u++;
        l = o[u];
      }
      e.setParent(n, l), n = e.successors(n)[0];
    }
  });
}
function IF(e, t, n, r) {
  var i = [], a = [], o = Math.min(t[n].low, t[r].low), s = Math.max(t[n].lim, t[r].lim), u, l;
  u = n;
  do
    u = e.parent(u), i.push(u);
  while (u && (t[u].low > o || s > t[u].lim));
  for (l = u, u = r; (u = e.parent(u)) !== l; )
    a.push(u);
  return { path: i.concat(a.reverse()), lca: l };
}
function TF(e) {
  var t = {}, n = 0;
  function r(i) {
    var a = n;
    Sh.forEach(e.children(i), r), t[i] = { low: a, lim: n++ };
  }
  return Sh.forEach(e.children(), r), t;
}
var mn = Se, Ah = it, xF = {
  run: NF,
  cleanup: LF
};
function NF(e) {
  var t = Ah.addDummyNode(e, "root", {}, "_root"), n = OF(e), r = mn.max(mn.values(n)) - 1, i = 2 * r + 1;
  e.graph().nestingRoot = t, mn.forEach(e.edges(), function(o) {
    e.edge(o).minlen *= i;
  });
  var a = $F(e) + 1;
  mn.forEach(e.children(), function(o) {
    xA(e, t, i, a, r, n, o);
  }), e.graph().nodeRankFactor = i;
}
function xA(e, t, n, r, i, a, o) {
  var s = e.children(o);
  if (!s.length) {
    o !== t && e.setEdge(t, o, { weight: 0, minlen: n });
    return;
  }
  var u = Ah.addBorderNode(e, "_bt"), l = Ah.addBorderNode(e, "_bb"), f = e.node(o);
  e.setParent(u, o), f.borderTop = u, e.setParent(l, o), f.borderBottom = l, mn.forEach(s, function(c) {
    xA(e, t, n, r, i, a, c);
    var p = e.node(c), d = p.borderTop ? p.borderTop : c, y = p.borderBottom ? p.borderBottom : c, v = p.borderTop ? r : 2 * r, _ = d !== y ? 1 : i - a[o] + 1;
    e.setEdge(u, d, {
      weight: v,
      minlen: _,
      nestingEdge: !0
    }), e.setEdge(y, l, {
      weight: v,
      minlen: _,
      nestingEdge: !0
    });
  }), e.parent(o) || e.setEdge(t, u, { weight: 0, minlen: i + a[o] });
}
function OF(e) {
  var t = {};
  function n(r, i) {
    var a = e.children(r);
    a && a.length && mn.forEach(a, function(o) {
      n(o, i + 1);
    }), t[r] = i;
  }
  return mn.forEach(e.children(), function(r) {
    n(r, 1);
  }), t;
}
function $F(e) {
  return mn.reduce(e.edges(), function(t, n) {
    return t + e.edge(n).weight;
  }, 0);
}
function LF(e) {
  var t = e.graph();
  e.removeNode(t.nestingRoot), delete t.nestingRoot, mn.forEach(e.edges(), function(n) {
    var r = e.edge(n);
    r.nestingEdge && e.removeEdge(n);
  });
}
var Ld = Se, MF = it, CF = DF;
function DF(e) {
  function t(n) {
    var r = e.children(n), i = e.node(n);
    if (r.length && Ld.forEach(r, t), Ld.has(i, "minRank")) {
      i.borderLeft = [], i.borderRight = [];
      for (var a = i.minRank, o = i.maxRank + 1; a < o; ++a)
        Bm(e, "borderLeft", "_bl", n, i, a), Bm(e, "borderRight", "_br", n, i, a);
    }
  }
  Ld.forEach(e.children(), t);
}
function Bm(e, t, n, r, i, a) {
  var o = { width: 0, height: 0, rank: a, borderType: t }, s = i[t][a - 1], u = MF.addDummyNode(e, "border", o, n);
  i[t][a] = u, e.setParent(u, r), s && e.setEdge(s, u, { weight: 1 });
}
var en = Se, qF = {
  adjust: jF,
  undo: PF
};
function jF(e) {
  var t = e.graph().rankdir.toLowerCase();
  (t === "lr" || t === "rl") && NA(e);
}
function PF(e) {
  var t = e.graph().rankdir.toLowerCase();
  (t === "bt" || t === "rl") && kF(e), (t === "lr" || t === "rl") && (FF(e), NA(e));
}
function NA(e) {
  en.forEach(e.nodes(), function(t) {
    Um(e.node(t));
  }), en.forEach(e.edges(), function(t) {
    Um(e.edge(t));
  });
}
function Um(e) {
  var t = e.width;
  e.width = e.height, e.height = t;
}
function kF(e) {
  en.forEach(e.nodes(), function(t) {
    Md(e.node(t));
  }), en.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    en.forEach(n.points, Md), en.has(n, "y") && Md(n);
  });
}
function Md(e) {
  e.y = -e.y;
}
function FF(e) {
  en.forEach(e.nodes(), function(t) {
    Cd(e.node(t));
  }), en.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    en.forEach(n.points, Cd), en.has(n, "x") && Cd(n);
  });
}
function Cd(e) {
  var t = e.x;
  e.x = e.y, e.y = t;
}
var pn = Se, BF = UF;
function UF(e) {
  var t = {}, n = pn.filter(e.nodes(), function(s) {
    return !e.children(s).length;
  }), r = pn.max(pn.map(n, function(s) {
    return e.node(s).rank;
  })), i = pn.map(pn.range(r + 1), function() {
    return [];
  });
  function a(s) {
    if (!pn.has(t, s)) {
      t[s] = !0;
      var u = e.node(s);
      i[u.rank].push(s), pn.forEach(e.successors(s), a);
    }
  }
  var o = pn.sortBy(n, function(s) {
    return e.node(s).rank;
  });
  return pn.forEach(o, a), i;
}
var qn = Se, HF = GF;
function GF(e, t) {
  for (var n = 0, r = 1; r < t.length; ++r)
    n += VF(e, t[r - 1], t[r]);
  return n;
}
function VF(e, t, n) {
  for (var r = qn.zipObject(
    n,
    qn.map(n, function(l, f) {
      return f;
    })
  ), i = qn.flatten(qn.map(t, function(l) {
    return qn.sortBy(qn.map(e.outEdges(l), function(f) {
      return { pos: r[f.w], weight: e.edge(f).weight };
    }), "pos");
  }), !0), a = 1; a < n.length; )
    a <<= 1;
  var o = 2 * a - 1;
  a -= 1;
  var s = qn.map(new Array(o), function() {
    return 0;
  }), u = 0;
  return qn.forEach(i.forEach(function(l) {
    var f = l.pos + a;
    s[f] += l.weight;
    for (var c = 0; f > 0; )
      f % 2 && (c += s[f + 1]), f = f - 1 >> 1, s[f] += l.weight;
    u += l.weight * c;
  })), u;
}
var Hm = Se, WF = JF;
function JF(e, t) {
  return Hm.map(t, function(n) {
    var r = e.inEdges(n);
    if (r.length) {
      var i = Hm.reduce(r, function(a, o) {
        var s = e.edge(o), u = e.node(o.v);
        return {
          sum: a.sum + s.weight * u.order,
          weight: a.weight + s.weight
        };
      }, { sum: 0, weight: 0 });
      return {
        v: n,
        barycenter: i.sum / i.weight,
        weight: i.weight
      };
    } else
      return { v: n };
  });
}
var ct = Se, zF = KF;
function KF(e, t) {
  var n = {};
  ct.forEach(e, function(i, a) {
    var o = n[i.v] = {
      indegree: 0,
      in: [],
      out: [],
      vs: [i.v],
      i: a
    };
    ct.isUndefined(i.barycenter) || (o.barycenter = i.barycenter, o.weight = i.weight);
  }), ct.forEach(t.edges(), function(i) {
    var a = n[i.v], o = n[i.w];
    !ct.isUndefined(a) && !ct.isUndefined(o) && (o.indegree++, a.out.push(n[i.w]));
  });
  var r = ct.filter(n, function(i) {
    return !i.indegree;
  });
  return XF(r);
}
function XF(e) {
  var t = [];
  function n(a) {
    return function(o) {
      o.merged || (ct.isUndefined(o.barycenter) || ct.isUndefined(a.barycenter) || o.barycenter >= a.barycenter) && YF(a, o);
    };
  }
  function r(a) {
    return function(o) {
      o.in.push(a), --o.indegree === 0 && e.push(o);
    };
  }
  for (; e.length; ) {
    var i = e.pop();
    t.push(i), ct.forEach(i.in.reverse(), n(i)), ct.forEach(i.out, r(i));
  }
  return ct.map(
    ct.filter(t, function(a) {
      return !a.merged;
    }),
    function(a) {
      return ct.pick(a, ["vs", "i", "barycenter", "weight"]);
    }
  );
}
function YF(e, t) {
  var n = 0, r = 0;
  e.weight && (n += e.barycenter * e.weight, r += e.weight), t.weight && (n += t.barycenter * t.weight, r += t.weight), e.vs = t.vs.concat(e.vs), e.barycenter = n / r, e.weight = r, e.i = Math.min(t.i, e.i), t.merged = !0;
}
var Oi = Se, QF = it, ZF = eB;
function eB(e, t) {
  var n = QF.partition(e, function(f) {
    return Oi.has(f, "barycenter");
  }), r = n.lhs, i = Oi.sortBy(n.rhs, function(f) {
    return -f.i;
  }), a = [], o = 0, s = 0, u = 0;
  r.sort(tB(!!t)), u = Gm(a, i, u), Oi.forEach(r, function(f) {
    u += f.vs.length, a.push(f.vs), o += f.barycenter * f.weight, s += f.weight, u = Gm(a, i, u);
  });
  var l = { vs: Oi.flatten(a, !0) };
  return s && (l.barycenter = o / s, l.weight = s), l;
}
function Gm(e, t, n) {
  for (var r; t.length && (r = Oi.last(t)).i <= n; )
    t.pop(), e.push(r.vs), n++;
  return n;
}
function tB(e) {
  return function(t, n) {
    return t.barycenter < n.barycenter ? -1 : t.barycenter > n.barycenter ? 1 : e ? n.i - t.i : t.i - n.i;
  };
}
var Un = Se, nB = WF, rB = zF, iB = ZF, aB = OA;
function OA(e, t, n, r) {
  var i = e.children(t), a = e.node(t), o = a ? a.borderLeft : void 0, s = a ? a.borderRight : void 0, u = {};
  o && (i = Un.filter(i, function(y) {
    return y !== o && y !== s;
  }));
  var l = nB(e, i);
  Un.forEach(l, function(y) {
    if (e.children(y.v).length) {
      var v = OA(e, y.v, n, r);
      u[y.v] = v, Un.has(v, "barycenter") && sB(y, v);
    }
  });
  var f = rB(l, n);
  oB(f, u);
  var c = iB(f, r);
  if (o && (c.vs = Un.flatten([o, c.vs, s], !0), e.predecessors(o).length)) {
    var p = e.node(e.predecessors(o)[0]), d = e.node(e.predecessors(s)[0]);
    Un.has(c, "barycenter") || (c.barycenter = 0, c.weight = 0), c.barycenter = (c.barycenter * c.weight + p.order + d.order) / (c.weight + 2), c.weight += 2;
  }
  return c;
}
function oB(e, t) {
  Un.forEach(e, function(n) {
    n.vs = Un.flatten(n.vs.map(function(r) {
      return t[r] ? t[r].vs : r;
    }), !0);
  });
}
function sB(e, t) {
  Un.isUndefined(e.barycenter) ? (e.barycenter = t.barycenter, e.weight = t.weight) : (e.barycenter = (e.barycenter * e.weight + t.barycenter * t.weight) / (e.weight + t.weight), e.weight += t.weight);
}
var $i = Se, uB = Ht.Graph, lB = fB;
function fB(e, t, n) {
  var r = cB(e), i = new uB({ compound: !0 }).setGraph({ root: r }).setDefaultNodeLabel(function(a) {
    return e.node(a);
  });
  return $i.forEach(e.nodes(), function(a) {
    var o = e.node(a), s = e.parent(a);
    (o.rank === t || o.minRank <= t && t <= o.maxRank) && (i.setNode(a), i.setParent(a, s || r), $i.forEach(e[n](a), function(u) {
      var l = u.v === a ? u.w : u.v, f = i.edge(l, a), c = $i.isUndefined(f) ? 0 : f.weight;
      i.setEdge(l, a, { weight: e.edge(u).weight + c });
    }), $i.has(o, "minRank") && i.setNode(a, {
      borderLeft: o.borderLeft[t],
      borderRight: o.borderRight[t]
    }));
  }), i;
}
function cB(e) {
  for (var t; e.hasNode(t = $i.uniqueId("_root")); )
    ;
  return t;
}
var dB = Se, hB = pB;
function pB(e, t, n) {
  var r = {}, i;
  dB.forEach(n, function(a) {
    for (var o = e.parent(a), s, u; o; ) {
      if (s = e.parent(o), s ? (u = r[s], r[s] = o) : (u = i, i = o), u && u !== o) {
        t.setEdge(u, o);
        return;
      }
      o = s;
    }
  });
}
var zn = Se, gB = BF, yB = HF, vB = aB, _B = lB, bB = hB, mB = Ht.Graph, Vm = it, wB = EB;
function EB(e) {
  var t = Vm.maxRank(e), n = Wm(e, zn.range(1, t + 1), "inEdges"), r = Wm(e, zn.range(t - 1, -1, -1), "outEdges"), i = gB(e);
  Jm(e, i);
  for (var a = Number.POSITIVE_INFINITY, o, s = 0, u = 0; u < 4; ++s, ++u) {
    SB(s % 2 ? n : r, s % 4 >= 2), i = Vm.buildLayerMatrix(e);
    var l = yB(e, i);
    l < a && (u = 0, o = zn.cloneDeep(i), a = l);
  }
  Jm(e, o);
}
function Wm(e, t, n) {
  return zn.map(t, function(r) {
    return _B(e, r, n);
  });
}
function SB(e, t) {
  var n = new mB();
  zn.forEach(e, function(r) {
    var i = r.graph().root, a = vB(r, i, n, t);
    zn.forEach(a.vs, function(o, s) {
      r.node(o).order = s;
    }), bB(r, n, a.vs);
  });
}
function Jm(e, t) {
  zn.forEach(t, function(n) {
    zn.forEach(n, function(r, i) {
      e.node(r).order = i;
    });
  });
}
var Y = Se, AB = Ht.Graph, RB = it, IB = {
  positionX: NB,
  findType1Conflicts: $A,
  findType2Conflicts: LA,
  addConflict: Wp,
  hasConflict: MA,
  verticalAlignment: CA,
  horizontalCompaction: DA,
  alignCoordinates: jA,
  findSmallestWidthAlignment: qA,
  balance: PA
};
function $A(e, t) {
  var n = {};
  function r(i, a) {
    var o = 0, s = 0, u = i.length, l = Y.last(a);
    return Y.forEach(a, function(f, c) {
      var p = TB(e, f), d = p ? e.node(p).order : u;
      (p || f === l) && (Y.forEach(a.slice(s, c + 1), function(y) {
        Y.forEach(e.predecessors(y), function(v) {
          var _ = e.node(v), m = _.order;
          (m < o || d < m) && !(_.dummy && e.node(y).dummy) && Wp(n, v, y);
        });
      }), s = c + 1, o = d);
    }), a;
  }
  return Y.reduce(t, r), n;
}
function LA(e, t) {
  var n = {};
  function r(a, o, s, u, l) {
    var f;
    Y.forEach(Y.range(o, s), function(c) {
      f = a[c], e.node(f).dummy && Y.forEach(e.predecessors(f), function(p) {
        var d = e.node(p);
        d.dummy && (d.order < u || d.order > l) && Wp(n, p, f);
      });
    });
  }
  function i(a, o) {
    var s = -1, u, l = 0;
    return Y.forEach(o, function(f, c) {
      if (e.node(f).dummy === "border") {
        var p = e.predecessors(f);
        p.length && (u = e.node(p[0]).order, r(o, l, c, s, u), l = c, s = u);
      }
      r(o, l, o.length, u, a.length);
    }), o;
  }
  return Y.reduce(t, i), n;
}
function TB(e, t) {
  if (e.node(t).dummy)
    return Y.find(e.predecessors(t), function(n) {
      return e.node(n).dummy;
    });
}
function Wp(e, t, n) {
  if (t > n) {
    var r = t;
    t = n, n = r;
  }
  var i = e[t];
  i || (e[t] = i = {}), i[n] = !0;
}
function MA(e, t, n) {
  if (t > n) {
    var r = t;
    t = n, n = r;
  }
  return Y.has(e[t], n);
}
function CA(e, t, n, r) {
  var i = {}, a = {}, o = {};
  return Y.forEach(t, function(s) {
    Y.forEach(s, function(u, l) {
      i[u] = u, a[u] = u, o[u] = l;
    });
  }), Y.forEach(t, function(s) {
    var u = -1;
    Y.forEach(s, function(l) {
      var f = r(l);
      if (f.length) {
        f = Y.sortBy(f, function(v) {
          return o[v];
        });
        for (var c = (f.length - 1) / 2, p = Math.floor(c), d = Math.ceil(c); p <= d; ++p) {
          var y = f[p];
          a[l] === l && u < o[y] && !MA(n, l, y) && (a[y] = l, a[l] = i[l] = i[y], u = o[y]);
        }
      }
    });
  }), { root: i, align: a };
}
function DA(e, t, n, r, i) {
  var a = {}, o = xB(e, t, n, i), s = i ? "borderLeft" : "borderRight";
  function u(c, p) {
    for (var d = o.nodes(), y = d.pop(), v = {}; y; )
      v[y] ? c(y) : (v[y] = !0, d.push(y), d = d.concat(p(y))), y = d.pop();
  }
  function l(c) {
    a[c] = o.inEdges(c).reduce(function(p, d) {
      return Math.max(p, a[d.v] + o.edge(d));
    }, 0);
  }
  function f(c) {
    var p = o.outEdges(c).reduce(function(y, v) {
      return Math.min(y, a[v.w] - o.edge(v));
    }, Number.POSITIVE_INFINITY), d = e.node(c);
    p !== Number.POSITIVE_INFINITY && d.borderType !== s && (a[c] = Math.max(a[c], p));
  }
  return u(l, o.predecessors.bind(o)), u(f, o.successors.bind(o)), Y.forEach(r, function(c) {
    a[c] = a[n[c]];
  }), a;
}
function xB(e, t, n, r) {
  var i = new AB(), a = e.graph(), o = OB(a.nodesep, a.edgesep, r);
  return Y.forEach(t, function(s) {
    var u;
    Y.forEach(s, function(l) {
      var f = n[l];
      if (i.setNode(f), u) {
        var c = n[u], p = i.edge(c, f);
        i.setEdge(c, f, Math.max(o(e, l, u), p || 0));
      }
      u = l;
    });
  }), i;
}
function qA(e, t) {
  return Y.minBy(Y.values(t), function(n) {
    var r = Number.NEGATIVE_INFINITY, i = Number.POSITIVE_INFINITY;
    return Y.forIn(n, function(a, o) {
      var s = $B(e, o) / 2;
      r = Math.max(a + s, r), i = Math.min(a - s, i);
    }), r - i;
  });
}
function jA(e, t) {
  var n = Y.values(t), r = Y.min(n), i = Y.max(n);
  Y.forEach(["u", "d"], function(a) {
    Y.forEach(["l", "r"], function(o) {
      var s = a + o, u = e[s], l;
      if (u !== t) {
        var f = Y.values(u);
        l = o === "l" ? r - Y.min(f) : i - Y.max(f), l && (e[s] = Y.mapValues(u, function(c) {
          return c + l;
        }));
      }
    });
  });
}
function PA(e, t) {
  return Y.mapValues(e.ul, function(n, r) {
    if (t)
      return e[t.toLowerCase()][r];
    var i = Y.sortBy(Y.map(e, r));
    return (i[1] + i[2]) / 2;
  });
}
function NB(e) {
  var t = RB.buildLayerMatrix(e), n = Y.merge(
    $A(e, t),
    LA(e, t)
  ), r = {}, i;
  Y.forEach(["u", "d"], function(o) {
    i = o === "u" ? t : Y.values(t).reverse(), Y.forEach(["l", "r"], function(s) {
      s === "r" && (i = Y.map(i, function(c) {
        return Y.values(c).reverse();
      }));
      var u = (o === "u" ? e.predecessors : e.successors).bind(e), l = CA(e, i, n, u), f = DA(
        e,
        i,
        l.root,
        l.align,
        s === "r"
      );
      s === "r" && (f = Y.mapValues(f, function(c) {
        return -c;
      })), r[o + s] = f;
    });
  });
  var a = qA(e, r);
  return jA(r, a), PA(r, e.graph().align);
}
function OB(e, t, n) {
  return function(r, i, a) {
    var o = r.node(i), s = r.node(a), u = 0, l;
    if (u += o.width / 2, Y.has(o, "labelpos"))
      switch (o.labelpos.toLowerCase()) {
        case "l":
          l = -o.width / 2;
          break;
        case "r":
          l = o.width / 2;
          break;
      }
    if (l && (u += n ? l : -l), l = 0, u += (o.dummy ? t : e) / 2, u += (s.dummy ? t : e) / 2, u += s.width / 2, Y.has(s, "labelpos"))
      switch (s.labelpos.toLowerCase()) {
        case "l":
          l = s.width / 2;
          break;
        case "r":
          l = -s.width / 2;
          break;
      }
    return l && (u += n ? l : -l), l = 0, u;
  };
}
function $B(e, t) {
  return e.node(t).width;
}
var Li = Se, kA = it, LB = IB.positionX, MB = CB;
function CB(e) {
  e = kA.asNonCompoundGraph(e), DB(e), Li.forEach(LB(e), function(t, n) {
    e.node(n).x = t;
  });
}
function DB(e) {
  var t = kA.buildLayerMatrix(e), n = e.graph().ranksep, r = 0;
  Li.forEach(t, function(i) {
    var a = Li.max(Li.map(i, function(o) {
      return e.node(o).height;
    }));
    Li.forEach(i, function(o) {
      e.node(o).y = r + a / 2;
    }), r += a + n;
  });
}
var ee = Se, zm = Mk, Km = Xk, qB = mF, jB = it.normalizeRanks, PB = AF, kB = it.removeEmptyRanks, Xm = xF, FB = CF, Ym = qF, BB = wB, UB = MB, Xn = it, HB = Ht.Graph, GB = VB;
function VB(e, t) {
  var n = t && t.debugTiming ? Xn.time : Xn.notime;
  n("layout", function() {
    var r = n("  buildLayoutGraph", function() {
      return n8(e);
    });
    n("  runLayout", function() {
      WB(r, n);
    }), n("  updateInputGraph", function() {
      JB(e, r);
    });
  });
}
function WB(e, t) {
  t("    makeSpaceForEdgeLabels", function() {
    r8(e);
  }), t("    removeSelfEdges", function() {
    d8(e);
  }), t("    acyclic", function() {
    zm.run(e);
  }), t("    nestingGraph.run", function() {
    Xm.run(e);
  }), t("    rank", function() {
    qB(Xn.asNonCompoundGraph(e));
  }), t("    injectEdgeLabelProxies", function() {
    i8(e);
  }), t("    removeEmptyRanks", function() {
    kB(e);
  }), t("    nestingGraph.cleanup", function() {
    Xm.cleanup(e);
  }), t("    normalizeRanks", function() {
    jB(e);
  }), t("    assignRankMinMax", function() {
    a8(e);
  }), t("    removeEdgeLabelProxies", function() {
    o8(e);
  }), t("    normalize.run", function() {
    Km.run(e);
  }), t("    parentDummyChains", function() {
    PB(e);
  }), t("    addBorderSegments", function() {
    FB(e);
  }), t("    order", function() {
    BB(e);
  }), t("    insertSelfEdges", function() {
    h8(e);
  }), t("    adjustCoordinateSystem", function() {
    Ym.adjust(e);
  }), t("    position", function() {
    UB(e);
  }), t("    positionSelfEdges", function() {
    p8(e);
  }), t("    removeBorderNodes", function() {
    c8(e);
  }), t("    normalize.undo", function() {
    Km.undo(e);
  }), t("    fixupEdgeLabelCoords", function() {
    l8(e);
  }), t("    undoCoordinateSystem", function() {
    Ym.undo(e);
  }), t("    translateGraph", function() {
    s8(e);
  }), t("    assignNodeIntersects", function() {
    u8(e);
  }), t("    reversePoints", function() {
    f8(e);
  }), t("    acyclic.undo", function() {
    zm.undo(e);
  });
}
function JB(e, t) {
  ee.forEach(e.nodes(), function(n) {
    var r = e.node(n), i = t.node(n);
    r && (r.x = i.x, r.y = i.y, t.children(n).length && (r.width = i.width, r.height = i.height));
  }), ee.forEach(e.edges(), function(n) {
    var r = e.edge(n), i = t.edge(n);
    r.points = i.points, ee.has(i, "x") && (r.x = i.x, r.y = i.y);
  }), e.graph().width = t.graph().width, e.graph().height = t.graph().height;
}
var zB = ["nodesep", "edgesep", "ranksep", "marginx", "marginy"], KB = { ranksep: 50, edgesep: 20, nodesep: 50, rankdir: "tb" }, XB = ["acyclicer", "ranker", "rankdir", "align"], YB = ["width", "height"], QB = { width: 0, height: 0 }, ZB = ["minlen", "weight", "width", "height", "labeloffset"], e8 = {
  minlen: 1,
  weight: 1,
  width: 0,
  height: 0,
  labeloffset: 10,
  labelpos: "r"
}, t8 = ["labelpos"];
function n8(e) {
  var t = new HB({ multigraph: !0, compound: !0 }), n = qd(e.graph());
  return t.setGraph(ee.merge(
    {},
    KB,
    Dd(n, zB),
    ee.pick(n, XB)
  )), ee.forEach(e.nodes(), function(r) {
    var i = qd(e.node(r));
    t.setNode(r, ee.defaults(Dd(i, YB), QB)), t.setParent(r, e.parent(r));
  }), ee.forEach(e.edges(), function(r) {
    var i = qd(e.edge(r));
    t.setEdge(r, ee.merge(
      {},
      e8,
      Dd(i, ZB),
      ee.pick(i, t8)
    ));
  }), t;
}
function r8(e) {
  var t = e.graph();
  t.ranksep /= 2, ee.forEach(e.edges(), function(n) {
    var r = e.edge(n);
    r.minlen *= 2, r.labelpos.toLowerCase() !== "c" && (t.rankdir === "TB" || t.rankdir === "BT" ? r.width += r.labeloffset : r.height += r.labeloffset);
  });
}
function i8(e) {
  ee.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    if (n.width && n.height) {
      var r = e.node(t.v), i = e.node(t.w), a = { rank: (i.rank - r.rank) / 2 + r.rank, e: t };
      Xn.addDummyNode(e, "edge-proxy", a, "_ep");
    }
  });
}
function a8(e) {
  var t = 0;
  ee.forEach(e.nodes(), function(n) {
    var r = e.node(n);
    r.borderTop && (r.minRank = e.node(r.borderTop).rank, r.maxRank = e.node(r.borderBottom).rank, t = ee.max(t, r.maxRank));
  }), e.graph().maxRank = t;
}
function o8(e) {
  ee.forEach(e.nodes(), function(t) {
    var n = e.node(t);
    n.dummy === "edge-proxy" && (e.edge(n.e).labelRank = n.rank, e.removeNode(t));
  });
}
function s8(e) {
  var t = Number.POSITIVE_INFINITY, n = 0, r = Number.POSITIVE_INFINITY, i = 0, a = e.graph(), o = a.marginx || 0, s = a.marginy || 0;
  function u(l) {
    var f = l.x, c = l.y, p = l.width, d = l.height;
    t = Math.min(t, f - p / 2), n = Math.max(n, f + p / 2), r = Math.min(r, c - d / 2), i = Math.max(i, c + d / 2);
  }
  ee.forEach(e.nodes(), function(l) {
    u(e.node(l));
  }), ee.forEach(e.edges(), function(l) {
    var f = e.edge(l);
    ee.has(f, "x") && u(f);
  }), t -= o, r -= s, ee.forEach(e.nodes(), function(l) {
    var f = e.node(l);
    f.x -= t, f.y -= r;
  }), ee.forEach(e.edges(), function(l) {
    var f = e.edge(l);
    ee.forEach(f.points, function(c) {
      c.x -= t, c.y -= r;
    }), ee.has(f, "x") && (f.x -= t), ee.has(f, "y") && (f.y -= r);
  }), a.width = n - t + o, a.height = i - r + s;
}
function u8(e) {
  ee.forEach(e.edges(), function(t) {
    var n = e.edge(t), r = e.node(t.v), i = e.node(t.w), a, o;
    n.points ? (a = n.points[0], o = n.points[n.points.length - 1]) : (n.points = [], a = i, o = r), n.points.unshift(Xn.intersectRect(r, a)), n.points.push(Xn.intersectRect(i, o));
  });
}
function l8(e) {
  ee.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    if (ee.has(n, "x"))
      switch ((n.labelpos === "l" || n.labelpos === "r") && (n.width -= n.labeloffset), n.labelpos) {
        case "l":
          n.x -= n.width / 2 + n.labeloffset;
          break;
        case "r":
          n.x += n.width / 2 + n.labeloffset;
          break;
      }
  });
}
function f8(e) {
  ee.forEach(e.edges(), function(t) {
    var n = e.edge(t);
    n.reversed && n.points.reverse();
  });
}
function c8(e) {
  ee.forEach(e.nodes(), function(t) {
    if (e.children(t).length) {
      var n = e.node(t), r = e.node(n.borderTop), i = e.node(n.borderBottom), a = e.node(ee.last(n.borderLeft)), o = e.node(ee.last(n.borderRight));
      n.width = Math.abs(o.x - a.x), n.height = Math.abs(i.y - r.y), n.x = a.x + n.width / 2, n.y = r.y + n.height / 2;
    }
  }), ee.forEach(e.nodes(), function(t) {
    e.node(t).dummy === "border" && e.removeNode(t);
  });
}
function d8(e) {
  ee.forEach(e.edges(), function(t) {
    if (t.v === t.w) {
      var n = e.node(t.v);
      n.selfEdges || (n.selfEdges = []), n.selfEdges.push({ e: t, label: e.edge(t) }), e.removeEdge(t);
    }
  });
}
function h8(e) {
  var t = Xn.buildLayerMatrix(e);
  ee.forEach(t, function(n) {
    var r = 0;
    ee.forEach(n, function(i, a) {
      var o = e.node(i);
      o.order = a + r, ee.forEach(o.selfEdges, function(s) {
        Xn.addDummyNode(e, "selfedge", {
          width: s.label.width,
          height: s.label.height,
          rank: o.rank,
          order: a + ++r,
          e: s.e,
          label: s.label
        }, "_se");
      }), delete o.selfEdges;
    });
  });
}
function p8(e) {
  ee.forEach(e.nodes(), function(t) {
    var n = e.node(t);
    if (n.dummy === "selfedge") {
      var r = e.node(n.e.v), i = r.x + r.width / 2, a = r.y, o = n.x - i, s = r.height / 2;
      e.setEdge(n.e, n.label), e.removeNode(t), n.label.points = [
        { x: i + 2 * o / 3, y: a - s },
        { x: i + 5 * o / 6, y: a - s },
        { x: i + o, y: a },
        { x: i + 5 * o / 6, y: a + s },
        { x: i + 2 * o / 3, y: a + s }
      ], n.label.x = n.x, n.label.y = n.y;
    }
  });
}
function Dd(e, t) {
  return ee.mapValues(ee.pick(e, t), Number);
}
function qd(e) {
  var t = {};
  return ee.forEach(e, function(n, r) {
    t[r.toLowerCase()] = n;
  }), t;
}
var ba = Se, g8 = it, y8 = Ht.Graph, v8 = {
  debugOrdering: _8
};
function _8(e) {
  var t = g8.buildLayerMatrix(e), n = new y8({ compound: !0, multigraph: !0 }).setGraph({});
  return ba.forEach(e.nodes(), function(r) {
    n.setNode(r, { label: r }), n.setParent(r, "layer" + e.node(r).rank);
  }), ba.forEach(e.edges(), function(r) {
    n.setEdge(r.v, r.w, {}, r.name);
  }), ba.forEach(t, function(r, i) {
    var a = "layer" + i;
    n.setNode(a, { rank: "same" }), ba.reduce(r, function(o, s) {
      return n.setEdge(o, s, { style: "invis" }), s;
    });
  }), n;
}
var b8 = "0.8.5", Qm = {
  graphlib: Ht,
  layout: GB,
  debug: v8,
  util: {
    time: it.time,
    notime: it.notime
  },
  version: b8
};
const m8 = (e, t, n, r) => {
  const { d3Nodes: i, d3Edges: a, graphWidth: o } = N8(e, t), s = q8(n), u = D8(s, o, n);
  C8(i, a, o, u, n, r);
};
function w8(e, t) {
  return console.log(`Error fetching docmap:

`, e), Je`
    <div class="not-found-screen">
      <div class="not-found-message">
        <p>No data found for DOI</p>
        <p>${t}</p>
      </div>

      ${fR}
    </div>
  `;
}
function E8(e, t) {
  return e.append("g").attr("class", "labels").selectAll("text").data(t).enter().append("text").attr("class", "label clickable").attr("text-anchor", "middle").attr("dominant-baseline", "central").attr("fill", (n) => Zt[n.type].textColor).text((n) => Zt[n.type].shortLabel);
}
function S8(e, t) {
  return e.append("g").attr("class", "nodes").selectAll("circle").data(t).enter().append("circle").attr("class", "node clickable").attr("fill", (n) => Zt[n.type].backgroundColor).attr("r", (n, r) => r === 0 ? GE : gp).attr(
    "stroke",
    (n) => Zt[n.type].dottedBorder ? "#777" : "none"
  ).attr(
    "stroke-width",
    (n) => Zt[n.type].dottedBorder ? "2px" : "none"
  ).attr(
    "stroke-dasharray",
    (n) => Zt[n.type].dottedBorder ? "8 4" : "none"
  );
}
function A8(e, t) {
  return e.append("g").attr("class", "links").selectAll("line").data(t).enter().append("line").attr("stroke", "black").attr("class", "link");
}
function R8(e, t, n) {
  return $q(e).force(
    "link",
    Sq(t).id((r) => r.nodeId).distance(VE * 1.3).strength(0.2)
  ).force("charge", Lq()).force("collide", wq(gp * 1.3)).force(
    "center",
    nq(Math.floor(n / 2), Math.floor(ni / 2))
  );
}
function I8(e, t, n, r) {
  e.on("tick", () => {
    t.attr("x1", (i) => i.source.x ?? 0).attr("y1", (i) => i.source.y ?? 0).attr("x2", (i) => i.target.x ?? 0).attr("y2", (i) => i.target.y ?? 0), n.attr("cx", Zm).attr("cy", ew), r.attr("x", (i) => Zm(i) + 0.8).attr("y", ew);
  });
}
function T8(e, t) {
  const n = new Qm.graphlib.Graph();
  n.setGraph({
    nodesep: 50,
    marginy: 70,
    marginx: 30,
    ranksep: VE,
    width: Io,
    height: ni
  }), n.setDefaultEdgeLabel(() => ({}));
  for (let r = 0; r < e.length; r++) {
    const i = e[r], a = r === 0 ? GE : gp;
    n.setNode(i.nodeId, { ...i, width: a, height: a });
  }
  return t.forEach((r) => n.setEdge(r.sourceId, r.targetId)), Qm.layout(n), n;
}
function x8(e, t) {
  const n = /* @__PURE__ */ new Map();
  return e.forEach((r) => {
    var o;
    const i = t.node(r), a = i.y;
    n.has(a) || n.set(a, []), (o = n.get(a)) == null || o.push(i);
  }), n;
}
function N8(e, t) {
  const n = T8(e, t), { width: r, height: i } = n.graph();
  let a = Io;
  r && i && $8(r, i) && (a = L8(r, i));
  const o = x8(
    n.nodes(),
    n
  ), s = O8(n, o, a), u = t.map(
    (l) => ({ source: l.sourceId, target: l.targetId })
  );
  return { d3Nodes: s, d3Edges: u, graphWidth: a };
}
function O8(e, t, n) {
  return e.nodes().map((r) => {
    const i = e.node(r), a = t.get(i.y), o = (a == null ? void 0 : a.length) === 1;
    return {
      ...i,
      // Maintain vertical position from Dagre
      fy: i.y,
      // Fix the node at horizontal center if it is alone on its y-level
      ...o && { fx: Math.floor(n / 2) }
    };
  });
}
function $8(e, t) {
  return e > Io || t > ni;
}
function L8(e, t) {
  return 1.1 * e / t * ni;
}
const FA = (e) => {
  e && xo(e.querySelector(`#${pp} svg`)).remove();
}, Zm = (e) => e.x ?? 0, ew = (e) => e.y ?? 0, tw = (e, t) => {
  if (!t)
    return;
  const n = xo(t.querySelector("#graph-tooltip"));
  e.on("mouseover", function(r, i) {
    n.html(() => Zt[i.type].longLabel).style("visibility", "visible").style("opacity", 1).style("left", `${r.pageX}px`).style("top", `${r.pageY - 28}px`);
  }).on("mouseout", () => {
    n.style("visibility", "hidden").style("opacity", 0);
  });
}, M8 = (e, t, n, r) => {
  if (!n)
    return;
  const i = xo(n.querySelector("#graph-tooltip"));
  e.on("click", (a, o) => {
    i.style("visibility", "hidden").style("opacity", 0), r(o);
  }), t.on("click", (a, o) => {
    i.style("visibility", "hidden").style("opacity", 0), r(o);
  }), tw(e, n), tw(t, n);
}, C8 = (e, t, n, r, i, a) => {
  const o = R8(e, t, n), s = A8(r, t), u = S8(r, e), l = E8(r, e);
  I8(o, s, u, l), M8(u, l, i, a);
}, D8 = (e, t, n) => {
  FA(n);
  const r = xo(e).append("svg").attr("width", Io).attr("height", ni);
  return t && r.attr("viewBox", `0 0 ${t} ${ni}`), r;
}, q8 = (e) => {
  if (!e)
    return null;
  const t = e.querySelector(`#${pp}`);
  if (!t)
    throw new Error("SVG element not found");
  return t;
}, Ma = 368, nw = 6.5, j8 = Ma - 9.5, P8 = (e, t, n) => {
  const r = e.findIndex((o) => o.nodeId === t.nodeId), i = r > 0 ? r - 1 : e.length - 1, a = e[i];
  return jt`
    <svg class='docmaps-timeline-back clickable' width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'
        @click='${() => n(a)}'>
      <circle cx='17.5' cy='17.5' r='17' stroke='#474747'/>
      <path fill='#474747' transform='scale(-1,1) translate(-35,0)' d='M22.5 17.134C23.1667 17.5189 23.1667 18.4811 22.5 18.866L10.5 25.7942C9.83333 26.1791 9 25.698 9 24.9282L9 11.0718C9 10.302 9.83333 9.82087 10.5 10.2058L22.5 17.134Z'/>
      <path transform='scale(-1,1) translate(-35,0)'  d='M24 10L24 26' stroke='#474747' stroke-width='2' stroke-linecap='round'/>
    </svg>`;
}, k8 = (e, t, n) => {
  const r = e.findIndex((o) => o.nodeId === t.nodeId), i = r < e.length - 1 ? r + 1 : 0, a = e[i];
  return jt`
    <svg class='docmaps-timeline-forward clickable' width='36' height='36' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'
        @click='${() => n(a)}'>
      <circle cx='17.5' cy='17.5' r='17' stroke='#474747'/>
      <path d='M22.5 17.134C23.1667 17.5189 23.1667 18.4811 22.5 18.866L10.5 25.7942C9.83333 26.1791 9 25.698 9 24.9282L9 11.0718C9 10.302 9.83333 9.82087 10.5 10.2058L22.5 17.134Z' fill='#474747'/>
      <path d='M24 10L24 26' stroke='#474747' stroke-width='2' stroke-linecap='round'/>
    </svg>`;
};
function F8(e, t) {
  const n = (j8 - nw) / (t - 1);
  return nw + n * e;
}
const B8 = (e, t, n) => {
  const r = e.map((a, o) => {
    const s = F8(o, e.length), u = Zt[a.type], l = u.detailViewBackgroundColor ?? u.backgroundColor, c = a.nodeId === t.nodeId ? jt`<circle class='selected-node-outline' cx='${s}' cy='6.5' r='5.5' stroke='${l}'/>
          <path class='selected-node-line' d='M${s} 7L${s} 35' stroke='${l}' stroke-linecap='round'/>` : Ae;
    return jt`
      <circle class='timeline-node clickable' cx='${s}' cy='6.5' r='3.5' fill='${l}'
        @click='${() => n(a)}'/>
      ${c}
    `;
  }), i = jt`<line x1='3' y1='6.75' x2='${Ma - 6}' y2='6.75' stroke='#777777' stroke-width='0.5'/`;
  return jt`
    <svg width='${Ma}' height='35' viewBox='0 0 ${Ma} 35' fill='none' xmlns='http://www.w3.org/2000/svg' style='align-self: end;'>
      ${i}>
      ${r}
    </svg>`;
}, U8 = (e, t, n) => jt`
  ${P8(e, t, n)}
  ${k8(e, t, n)}
  ${B8(e, t, n)}`;
function H8(e, t, n, r, i) {
  const a = Zt[e.type], o = a.detailViewBackgroundColor || a.backgroundColor, s = a.detailViewTextColor || a.textColor, u = K8(e), l = u.length > 0 ? G8(u, i) : z8();
  return Je`
    <div class="detail-timeline no-select">
      ${U8(t, e, n)}
    </div>

    <div class="detail-header" style="background: ${o};">
      <span style="color: ${s};" class="no-select">${a.longLabel}</span>
      <div class="close-button clickable" @click="${r}">
        ${uR(s)}
      </div>
    </div>

    <div class="detail-body">${l}</div>
  `;
}
const G8 = (e, t) => {
  const n = e.map(
    ([r, i], a) => J8(r, i, a, t)
  );
  return Je` <div class="metadata-grid">${n}</div>`;
};
function V8(e, t, n) {
  if (Array.isArray(t)) {
    const r = n + 1, i = n + t.length + 1;
    return Je` <div
      class="metadata-grid-item key"
      style="grid-row-start: ${r}; grid-row-end: ${i};"
    >
      ${e}
    </div>`;
  }
  return Je` <div class="metadata-grid-item key">${e}</div>`;
}
function W8(e, t, n) {
  if (e === "url" && typeof t == "string") {
    const r = Je` <a href="${t}" target="_blank" class="metadata-link">${t}</a>`;
    return jd(r, t, n);
  }
  return Array.isArray(t) ? Je` ${t.map((r) => {
    const i = Je` <a href="${r}" target="_blank" class="content metadata-link"
        >${r}</a
      >`;
    return jd(i, r, n);
  })}` : jd(Je` <span>${t}</span>`, t, n);
}
function jd(e, t, n) {
  return Je`
    <div class="metadata-grid-item value">${e} ${oR(t, n)}</div>
  `;
}
const J8 = (e, t, n, r) => Je`
    ${V8(e, t, n)} ${W8(e, t, r)}
  `, z8 = () => Je` <div class="no-metadata-found-grid">
    <div class="metadata-grid-item">no metadata found</div>
  </div>`, K8 = (e) => {
  const t = ph(e);
  return Object.entries(t).filter(([n, r]) => NL(n) && r).filter(X8);
}, X8 = (e) => {
  const [t, n] = e, r = typeof n == "string", i = Array.isArray(n) && n.every((a) => typeof a == "string");
  return r || i;
};
function Y8() {
  Pd("preconnect", "https://fonts.googleapis.com"), Pd("preconnect", "https://fonts.gstatic.com", "anonymous"), Pd(
    "stylesheet",
    "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap"
  );
}
function Pd(e, t, n) {
  const r = document.createElement("link");
  r.rel = e, r.href = t, n && (r.crossOrigin = n), document.head.appendChild(r);
}
var Q8 = Object.defineProperty, Z8 = Object.getOwnPropertyDescriptor, hi = (e, t, n, r) => {
  for (var i = r > 1 ? void 0 : r ? Z8(t, n) : t, a = e.length - 1, o; a >= 0; a--)
    (o = e[a]) && (i = (r ? o(t, n, i) : o(i)) || i);
  return r && i && Q8(t, n, i), i;
}, BA = (e, t, n) => {
  if (!t.has(e))
    throw TypeError("Cannot " + n);
}, rw = (e, t, n) => (BA(e, t, "read from private field"), n ? n.call(e) : t.get(e)), iw = (e, t, n) => {
  if (t.has(e))
    throw TypeError("Cannot add the same private member more than once");
  t instanceof WeakSet ? t.add(e) : t.set(e, n);
}, e4 = (e, t, n, r) => (BA(e, t, "write to private field"), r ? r.call(e, n) : t.set(e, n), n), Ca, Rh;
let Yn = class extends Mi {
  constructor() {
    super(...arguments), this.doi = "", this.serverUrl = "", this.detailTooltip = {
      text: "",
      x: 0,
      y: 0
    }, iw(this, Ca, !1), iw(this, Rh, new dR(
      this,
      $L,
      () => [this.serverUrl, this.doi]
    )), this.showDetailViewForNode = (e) => {
      this.selectedNode = e;
    }, this.closeDetailView = () => {
      this.selectedNode = void 0;
    }, this.updateDetailTooltip = (e, t, n) => {
      this.detailTooltip = {
        text: e,
        x: t,
        y: n
      }, setTimeout(() => this.detailTooltip = { ...this.detailTooltip, text: "" }, 2e3);
    }, this.onFetchComplete = (e) => (this.graph = e, this.renderGraphView(e), Ae);
  }
  set docmap(e) {
    this.configureWithDocmap(e);
  }
  firstUpdated() {
    Y8(), e4(this, Ca, !0);
  }
  render() {
    const e = Je` <div id="${pp}"></div>`, t = this.selectedNode ? this.detailView() : this.graphView();
    return Je`
      <div class="docmaps-widget">
        <div class="widget-header">
          ${lR}
          <span>DOCMAP</span>
        </div>
        ${e} ${t}
        <div id="graph-tooltip" class="tooltip" style="opacity:0;"></div>
        ${this.renderDetailTooltip()}
      </div>
    `;
  }
  renderDetailTooltip() {
    const { text: e, x: t, y: n } = this.detailTooltip;
    return Je`
      <div
        id="detail-tooltip"
        class="tooltip"
        style="opacity: ${e ? 1 : 0}; left: ${t}px; top: ${n}px; visibility: ${e ? "visible" : "hidden"}"
      >
        ${e}
      </div>
    `;
  }
  configureWithDocmap(e) {
    this.graph = WE(e);
  }
  renderGraphView({ nodes: e, edges: t }) {
    this.shadowRoot && m8(e, t, this.shadowRoot, this.showDetailViewForNode);
  }
  graphView() {
    var e;
    if (this.graph)
      rw(this, Ca) && this.renderGraphView(this.graph);
    else
      return Je` ${(e = rw(this, Rh)) == null ? void 0 : e.render({
        complete: this.onFetchComplete,
        error: (t) => w8(t, this.doi)
      })}`;
    return Ae;
  }
  detailView() {
    return FA(this.shadowRoot), !this.selectedNode || !this.graph ? Ae : H8(
      this.selectedNode,
      this.graph.nodes,
      this.showDetailViewForNode,
      this.closeDetailView,
      this.updateDetailTooltip
    );
  }
};
Ca = /* @__PURE__ */ new WeakMap();
Rh = /* @__PURE__ */ new WeakMap();
Yn.styles = [aR];
hi([
  Oh({ type: String })
], Yn.prototype, "doi", 2);
hi([
  Oh({ type: String })
], Yn.prototype, "serverUrl", 2);
hi([
  $h()
], Yn.prototype, "selectedNode", 2);
hi([
  $h()
], Yn.prototype, "graph", 2);
hi([
  $h()
], Yn.prototype, "detailTooltip", 2);
Yn = hi([
  nR("docmaps-widget")
], Yn);
export {
  Yn as DocmapsWidget,
  WE as docmapToDisplayObjectGraph,
  $L as getDocmap,
  LL as getSteps,
  CL as stepsToGraph
};
